// src/actions/Button/Button.tsx
import { Text, getFieldValue } from "@sitecore-jss/sitecore-jss-react";

// src/navigation/RoutableSitecoreLink/RoutableSitecoreLink.tsx
import React from "react";
import {
  Link
} from "@sitecore-jss/sitecore-jss-react";
import { Link as RouterLink } from "react-router-dom";

// src/util/useIsSitecorePageEditing.ts
import { useSitecoreContext } from "@sitecore-jss/sitecore-jss-react";
var useIsSitecorePageEditing = () => {
  const { sitecoreContext } = useSitecoreContext();
  return Boolean(sitecoreContext && sitecoreContext.pageEditing);
};
var useIsSitecorePageEditing_default = useIsSitecorePageEditing;

// src/util/isImageFieldValue.ts
var isImageFieldValue = (imageField) => "src" in imageField;

// src/util/useIsFieldValid.ts
var isLinkFieldValue = (field) => "href" in field;
var hasValidValue = (value) => {
  if (typeof value === "undefined" || value === null) {
    return false;
  }
  if (typeof value === "string" && !value.trim()) {
    return false;
  }
  if (typeof value === "number" && isNaN(value)) {
    return false;
  }
  return true;
};
var useIsFieldValid = () => {
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  const isTextFieldValid = (field) => {
    if (isEditingInSitecore) {
      return true;
    }
    if (!field) {
      return false;
    }
    return hasValidValue(field.value);
  };
  const isRichTextFieldValid = (field) => {
    if (isEditingInSitecore) {
      return true;
    }
    if (!field) {
      return false;
    }
    return hasValidValue(field.value);
  };
  const isImageFieldValid = (field) => {
    var _a;
    if (isEditingInSitecore) {
      return true;
    }
    if (!field) {
      return false;
    }
    return isImageFieldValue(field) ? hasValidValue(field.src) : hasValidValue((_a = field.value) == null ? void 0 : _a.src);
  };
  const isLinkFieldValid = (field) => {
    var _a;
    if (isEditingInSitecore) {
      return true;
    }
    if (!field) {
      return false;
    }
    return isLinkFieldValue(field) ? hasValidValue(field.href) : hasValidValue((_a = field.value) == null ? void 0 : _a.href);
  };
  const isDateFieldValid = (field) => {
    if (isEditingInSitecore) {
      return true;
    }
    if (!field) {
      return false;
    }
    return hasValidValue(field.value);
  };
  const isDateFieldSet = (field) => (field == null ? void 0 : field.value) !== "0001-01-01T00:00:00Z";
  return {
    isTextFieldValid,
    isRichTextFieldValid,
    isImageFieldValid,
    isLinkFieldValid,
    isDateFieldValid,
    isDateFieldSet
  };
};
var useIsFieldValid_default = useIsFieldValid;

// src/navigation/RoutableSitecoreLink/RoutableSitecoreLink.tsx
import { jsx } from "react/jsx-runtime";
var RoutableSitecoreLink = React.forwardRef((props, ref) => {
  const {
    field,
    editable = true,
    children,
    "aria-label": customAriaLabel,
    ...restProps
  } = props;
  const { isLinkFieldValid } = useIsFieldValid_default();
  const isEditing = editable;
  const value = (field == null ? void 0 : field.value) || field;
  const ariaLabel = customAriaLabel ? customAriaLabel : (value == null ? void 0 : value.text) || (value == null ? void 0 : value.title) || "";
  if (isLinkFieldValid(field) && !isEditing) {
    if (value.href === "string" && value.href.startsWith("/")) {
      return /* @__PURE__ */ jsx(
        RouterLink,
        {
          "aria-label": ariaLabel,
          ref,
          to: value.href,
          title: value.title,
          target: value.target,
          className: value.class || value.className,
          ...restProps,
          children: children || value.text || value.href
        }
      );
    }
  }
  return /* @__PURE__ */ jsx(
    Link,
    {
      "aria-label": ariaLabel,
      field,
      editable,
      ref,
      ...restProps,
      children: children || value.text || value.href
    }
  );
});
RoutableSitecoreLink.displayName = "RoutableSitecoreLink";
var RoutableSitecoreLink_default = RoutableSitecoreLink;

// src/icons/direction/ArrowRightIcon.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var ArrowRightIcon = (props) => /* @__PURE__ */ jsx2(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx2(
      "path",
      {
        d: "M4 12H18M13 5L20 12L13 19",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ArrowRightIcon_default = ArrowRightIcon;

// src/actions/Button/Button.tsx
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var DEFAULT_BUTTON_VARIANT = "primary";
var Button = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  const getButtonClassName = (variant2) => {
    switch (variant2) {
      case "primary":
        return "btn_primary";
      case "secondary":
        return "btn_secondary";
      case "tertiary":
        return "btn_tertiary";
      default:
        return "";
    }
  };
  const variant = getFieldValue(
    fields,
    "ButtonVariant",
    DEFAULT_BUTTON_VARIANT
  );
  return /* @__PURE__ */ jsxs(
    RoutableSitecoreLink_default,
    {
      className: `btn ${getButtonClassName(variant)} ${className}`,
      field: fields.ButtonTarget,
      editable: false,
      ...restProps,
      children: [
        /* @__PURE__ */ jsx3(Text, { field: fields.ButtonText }),
        /* @__PURE__ */ jsx3(ArrowRightIcon_default, { className: "btn__icon" })
      ]
    }
  );
};
var Button_default = Button;

// src/actions/Hashtag/Hashtag.tsx
import { Text as Text2 } from "@sitecore-jss/sitecore-jss-react";

// src/util/debounce/useDebouncedCallback.ts
import { useCallback, useRef } from "react";
var useDebouncedCallback = (callback, delay = 1e3) => {
  const timeoutRef = useRef(null);
  return useCallback(
    (...args) => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => callback(...args), delay);
    },
    [callback, delay]
  );
};
var useDebouncedCallback_default = useDebouncedCallback;

// src/util/screen-dimensions/useScreenDimensions.ts
import { useLayoutEffect, useState } from "react";
var useScreenDimensions = () => {
  const [screenDimensions, setScreenDimensions] = useState({
    width: null,
    height: null
  });
  const calcScreenHeight = useDebouncedCallback_default(
    () => setScreenDimensions({
      width: window.innerWidth,
      height: window.innerHeight
    })
  );
  useLayoutEffect(() => {
    calcScreenHeight();
    window.addEventListener("resize", calcScreenHeight);
    return () => {
      window.removeEventListener("resize", calcScreenHeight);
    };
  }, []);
  return screenDimensions;
};
var useScreenDimensions_default = useScreenDimensions;

// src/util/getFieldArrayErrors.ts
var getUniqueValues = (array) => array.filter(
  (item, index, currentArray) => currentArray.indexOf(item) === index
);
var getFieldArrayErrors = (errors, mapFn) => {
  let errorMessages = [];
  if (Array.isArray(errors)) {
    errorMessages = errors.filter(Boolean).flatMap(mapFn);
  }
  if (errors == null ? void 0 : errors.root) {
    errorMessages.push(errors.root.message);
  }
  return getUniqueValues(errorMessages);
};

// src/util/hasValidFileType.ts
var hasValidFileType = (fileName, validFileTypes) => {
  var _a;
  if (validFileTypes && validFileTypes.length > 0) {
    const extension = (_a = fileName.split(".").pop()) == null ? void 0 : _a.toLowerCase();
    return extension ? validFileTypes.map((fileType) => fileType.toLowerCase()).includes(`.${extension}`) : false;
  }
  return true;
};

// src/util/getImageUrl.ts
var getImageUrl = (imageField) => {
  var _a;
  if (!imageField) {
    return "";
  } else if (isImageFieldValue(imageField)) {
    return imageField.src ?? "";
  } else {
    return ((_a = imageField.value) == null ? void 0 : _a.src) ?? "";
  }
};

// src/util/putElementInWrapper.tsx
var putElementInWrapper = (childNode, className, wrapperNode) => {
  var _a;
  const wrapper = document.createElement(wrapperNode || "div");
  wrapper.classList.add(className || "");
  (_a = childNode.parentNode) == null ? void 0 : _a.insertBefore(wrapper, childNode);
  wrapper.appendChild(childNode);
};

// src/util/useIsClientSide.tsx
import { useEffect, useState as useState2 } from "react";
var useIsClientSide = () => {
  const [isClientSide, setIsClientSide] = useState2(false);
  useEffect(() => {
    if (typeof window !== "undefined") {
      setIsClientSide(true);
    }
  }, []);
  return isClientSide;
};
var useIsClientSide_default = useIsClientSide;

// src/util/SmoothScroll/useSmoothScroll.ts
import { useCallback as useCallback2 } from "react";
var EASE_OUT_QUART = (t) => {
  return 1 - Math.pow(1 - t, 4);
};
var smoothScroll = (initialPosition, targetPosition, axis, duration, easingFn) => {
  let startTimestamp;
  let animationFrame;
  const maxAvailableScroll = document.documentElement.scrollHeight - document.documentElement.clientHeight;
  const delta = initialPosition - targetPosition;
  const smoothScrollStep = (timestamp) => {
    if (!startTimestamp) {
      startTimestamp = timestamp;
    }
    const elapsed = timestamp - startTimestamp;
    const relativeProgress = elapsed / duration;
    const easedProgress = easingFn(relativeProgress);
    const position = initialPosition - delta * Math.min(easedProgress, 1);
    if (axis === "x") {
      window.scrollTo(position, 0);
    } else {
      window.scrollTo(0, position);
    }
    if (initialPosition !== maxAvailableScroll && window.scrollY === maxAvailableScroll) {
      window.cancelAnimationFrame(animationFrame);
      return;
    }
    if (elapsed < duration) {
      animationFrame = window.requestAnimationFrame(smoothScrollStep);
    }
  };
  animationFrame = window.requestAnimationFrame(smoothScrollStep);
};
var getTargetPosition = (target) => {
  if (typeof target === "number") {
    return target;
  } else if (typeof target === "string") {
    const element = document.getElementById(target);
    if (element) {
      return element.offsetTop;
    }
    throw new Error(`Element with id ${target} not found`);
  } else if (target && "current" in target) {
    if (target.current) {
      return target.current.offsetTop;
    }
    throw new Error("Ref is not attached to an element");
  } else {
    throw new Error(
      "Target must be either a number, a string or a React ref object"
    );
  }
};
var useSmoothScroll = (target, options = {}) => {
  const { axis = "y", duration = 800, easingFn = EASE_OUT_QUART } = options;
  return useCallback2(() => {
    const initialPosition = window.scrollY;
    const targetPosition = getTargetPosition(target);
    smoothScroll(initialPosition, targetPosition, axis, duration, easingFn);
  }, [target, axis, duration, easingFn]);
};
var useSmoothScroll_default = useSmoothScroll;

// src/util/getAspectRatio.ts
var getRatio = (width, height) => {
  return height === 0 ? width : getRatio(height, width % height);
};
var getAspectRatio = (width, height) => {
  const r = getRatio(width, height);
  return `${width / r} / ${height / r}`;
};
var getAspectRatio_default = getAspectRatio;

// src/actions/Hashtag/Hashtag.tsx
import { Fragment, jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var Hashtag = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  const { isTextFieldValid } = useIsFieldValid_default();
  return /* @__PURE__ */ jsx4("div", { className: `hashtag ${className}`, ...restProps, children: isTextFieldValid(fields.HashtagText) && /* @__PURE__ */ jsxs2(Fragment, { children: [
    "#",
    /* @__PURE__ */ jsx4(Text2, { field: fields.HashtagText })
  ] }) });
};
var Hashtag_default = Hashtag;

// src/actions/InteractiveMapMarker/InteractiveMapMarker.tsx
import { motion, useAnimationControls } from "framer-motion";
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var InteractiveMapMarker = (props) => {
  const { className = "", children, ...restProps } = props;
  const controls = useAnimationControls();
  const interactiveMapMarkerAnimationVariants = {
    hidden: { display: "hidden", scale: 1, opacity: 0 },
    show: (custom) => ({
      scale: ["100%", "250%", "100%"],
      opacity: [1, 0, 0],
      transition: {
        times: [0, 0.9, 1],
        delay: custom * 0.2
      },
      transitionEnd: { scale: 1, display: "hidden" }
    })
  };
  return /* @__PURE__ */ jsxs3(
    "button",
    {
      onMouseEnter: () => controls.start("show"),
      className: `interactive-map-marker ${className}`,
      ...restProps,
      children: [
        /* @__PURE__ */ jsx5(
          motion.div,
          {
            variants: interactiveMapMarkerAnimationVariants,
            initial: "hidden",
            animate: controls,
            custom: 0,
            className: "interactive-map-marker__pulse"
          }
        ),
        /* @__PURE__ */ jsx5(
          motion.div,
          {
            variants: interactiveMapMarkerAnimationVariants,
            initial: "hidden",
            animate: controls,
            custom: 1,
            className: "interactive-map-marker__pulse"
          }
        ),
        children
      ]
    }
  );
};
var InteractiveMapMarker_default = InteractiveMapMarker;

// src/actions/LanguageSwitcher/LanguageSwitcher.tsx
import { useSitecoreContext as useSitecoreContext2 } from "@sitecore-jss/sitecore-jss-react";
import { useState as useState3, useEffect as useEffect2 } from "react";
import { useTranslation } from "react-i18next";

// src/icons/miscellaneous/GlobeIcon.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var GlobeIcon = (props) => /* @__PURE__ */ jsx6(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx6(
      "path",
      {
        d: "M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2M12 22C14.4546 22 16.4444 17.5228 16.4444 12C16.4444 6.47715 14.4546 2 12 2M12 22C9.5454 22 7.55556 17.5228 7.55556 12C7.55556 6.47715 9.5454 2 12 2M3.11111 8.66667H20.8889M3.11111 15.3333H20.8889",
        stroke: "currentColor",
        "stroke-width": "2",
        "stroke-linecap": "round",
        "stroke-linejoin": "round"
      }
    )
  }
);
var GlobeIcon_default = GlobeIcon;

// src/actions/LanguageSwitcher/LanguageSwitcher.tsx
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var switchLanguage = (language, pathName) => {
  const newUrl = new URL(document.URL);
  newUrl.searchParams.delete("sc_lang");
  newUrl.pathname = pathName;
  newUrl.searchParams.append("sc_lang", language);
  window.location.href = newUrl.toString();
};
var LanguageSwitcher = (props) => {
  var _a;
  const { className = "", ...restProps } = props;
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState3(false);
  const { sitecoreContext } = useSitecoreContext2();
  const { t } = useTranslation("ui");
  const languages = (_a = sitecoreContext == null ? void 0 : sitecoreContext.siteSettings) == null ? void 0 : _a.allowedLanguages;
  if (!languages || languages.length < 2)
    return null;
  const currentLanguage = sitecoreContext == null ? void 0 : sitecoreContext.language;
  const contextPathname = sitecoreContext == null ? void 0 : sitecoreContext.languageSwitcherUrl;
  useEffect2(() => {
    const closeLanguageDropdown = (event) => {
      if (!event.target.classList.contains("language-switcher__button")) {
        setIsLanguageDropdownOpen(false);
      }
    };
    window.addEventListener("click", closeLanguageDropdown);
    return () => {
      window.removeEventListener("click", closeLanguageDropdown);
    };
  });
  return /* @__PURE__ */ jsxs4("div", { className: `language-switcher ${className}`, ...restProps, children: [
    /* @__PURE__ */ jsxs4(
      "button",
      {
        className: "language-switcher__button",
        onClick: () => setIsLanguageDropdownOpen(!isLanguageDropdownOpen),
        "aria-label": t("switch-language").toString(),
        children: [
          /* @__PURE__ */ jsx7("span", { className: "language-switcher__button-current-language", children: t(currentLanguage) }),
          /* @__PURE__ */ jsx7(GlobeIcon_default, {})
        ]
      }
    ),
    /* @__PURE__ */ jsx7("div", { className: `language-switcher__dropdown ${isLanguageDropdownOpen ? "open" : ""}`, children: languages.map((language, index) => /* @__PURE__ */ jsx7(
      "button",
      {
        "aria-label": `${t("switch-language-to")} ${t(language)}`,
        name: `${"switch-language-to-"}${t(language)}`,
        className: `language-switcher__dropdown-item ${currentLanguage === language ? "active" : ""}`,
        onClick: () => switchLanguage(language, contextPathname),
        children: /* @__PURE__ */ jsx7("span", { children: t(language) })
      },
      index
    )) })
  ] });
};
var LanguageSwitcher_default = LanguageSwitcher;

// src/actions/MainMenuButton/MainMenuButton.tsx
import { useSitecoreContext as useSitecoreContext4 } from "@sitecore-jss/sitecore-jss-react";
import { useState as useState15 } from "react";

// src/icons/functional/MenuIcon.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var MenuIcon = (props) => /* @__PURE__ */ jsx8(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx8(
      "path",
      {
        d: "M5 6H19M5 12H19M5 18H19",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var MenuIcon_default = MenuIcon;

// src/actions/MainMenuButton/MainMenuButton.tsx
import { useTranslation as useTranslation12 } from "react-i18next";

// src/navigation/MainMenu/MainMenu.tsx
import React16, { useState as useState14 } from "react";

// src/navigation/MainMenu/MainMenuLinkItem.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var MainMenuLinkItem = (props) => {
  const { fields, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx9(
    RoutableSitecoreLink_default,
    {
      field: {
        href: fields.link,
        target: fields.linkTarget
      },
      className: `main-menu__link-item ${className}`,
      role: "menuitem",
      ...restProps,
      children: /* @__PURE__ */ jsx9("span", { className: "main-menu__item-text", children: fields.title })
    }
  );
};
var MainMenuLinkItem_default = MainMenuLinkItem;

// src/icons/direction/ChevronRightIcon.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
var ChevronRightIcon = (props) => /* @__PURE__ */ jsx10(
  "svg",
  {
    width: "24",
    height: "25",
    viewBox: "0 0 24 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx10(
      "path",
      {
        d: "M8.5 5.23633L15.5 12.2363L8.5 19.2363",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ChevronRightIcon_default = ChevronRightIcon;

// src/navigation/MainMenu/MainMenuLinkGroup.tsx
import { jsx as jsx11, jsxs as jsxs5 } from "react/jsx-runtime";
var MainMenuLinkGroup = (props) => {
  const { fields, active = false, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsxs5(
    "button",
    {
      className: `main-menu__link-group ${active ? "main-menu__link-group_active" : ""} ${className}`,
      role: "menuitem",
      ...restProps,
      children: [
        /* @__PURE__ */ jsx11("span", { className: "main-menu__item-text", children: fields.title }),
        /* @__PURE__ */ jsx11(ChevronRightIcon_default, { className: "main-menu__item-icon" })
      ]
    }
  );
};
var MainMenuLinkGroup_default = MainMenuLinkGroup;

// src/navigation/MainMenu/MainMenuLinkItemWithLargeImage.tsx
import { jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
var MainMenuLinkItemWithLargeImage = (props) => {
  const { fields, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsxs6(
    RoutableSitecoreLink_default,
    {
      field: {
        href: fields.link,
        target: fields.linkTarget
      },
      className: `main-menu__link-item-with-large-image ${className}`,
      role: "menuitem",
      ...restProps,
      children: [
        /* @__PURE__ */ jsx12("div", { className: "main-menu__item-image-wrapper", children: /* @__PURE__ */ jsx12(
          "img",
          {
            src: fields.imageUrl,
            alt: fields.title,
            className: "main-menu__item-image"
          }
        ) }),
        /* @__PURE__ */ jsxs6("div", { className: "main-menu__item-text-container", children: [
          /* @__PURE__ */ jsx12("h6", { className: "main-menu__item-text", children: fields.title }),
          /* @__PURE__ */ jsx12(ArrowRightIcon_default, { className: "main-menu__item-icon" })
        ] })
      ]
    }
  );
};
var MainMenuLinkItemWithLargeImage_default = MainMenuLinkItemWithLargeImage;

// src/navigation/MainMenu/MainMenuLinkItemWithSmallImage.tsx
import { jsx as jsx13, jsxs as jsxs7 } from "react/jsx-runtime";
var MainMenuLinkItemWithSmallImage = (props) => {
  const { fields, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsxs7(
    RoutableSitecoreLink_default,
    {
      field: {
        href: fields.link,
        target: fields.linkTarget
      },
      className: `main-menu__link-item-with-small-image ${className}`,
      role: "menuitem",
      ...restProps,
      children: [
        /* @__PURE__ */ jsx13("div", { className: "main-menu__item-image-wrapper", children: /* @__PURE__ */ jsx13(
          "img",
          {
            src: fields.imageUrl,
            alt: fields.title,
            className: "main-menu__item-image"
          }
        ) }),
        /* @__PURE__ */ jsxs7("div", { className: "main-menu__item-text-container", children: [
          /* @__PURE__ */ jsx13("span", { className: "main-menu__item-text", children: fields.title }),
          /* @__PURE__ */ jsx13(ArrowRightIcon_default, { className: "main-menu__item-icon" })
        ] })
      ]
    }
  );
};
var MainMenuLinkItemWithSmallImage_default = MainMenuLinkItemWithSmallImage;

// src/surfaces-and-overlays/ModalDrawer/ModalDrawer.tsx
import { Dialog } from "@headlessui/react";
import { AnimatePresence as AnimatePresence2, motion as motion3 } from "framer-motion";

// src/surfaces-and-overlays/ModalDrawer/ModalDrawerProvider.tsx
import React4, { useContext } from "react";
import { jsx as jsx14 } from "react/jsx-runtime";
var ModalDrawerContext = React4.createContext(
  null
);
var useModalDrawer = () => {
  const context = useContext(ModalDrawerContext);
  if (!context) {
    throw new Error(
      "Error: useModalDrawer() requires an <ModalDrawerProvider> parent"
    );
  }
  return context;
};
var ModalDrawerProvider = (props) => {
  const { position = "left", children } = props;
  const contextValue = {
    position
  };
  return /* @__PURE__ */ jsx14(ModalDrawerContext.Provider, { value: contextValue, children: typeof children === "function" ? children(contextValue) : children });
};
var ModalDrawerProvider_default = ModalDrawerProvider;

// src/surfaces-and-overlays/ModalDrawer/ModalDrawer.tsx
import { useTranslation as useTranslation2 } from "react-i18next";

// src/icons/miscellaneous/TimesIcon.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
var TimesIcon = (props) => /* @__PURE__ */ jsx15(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx15(
      "path",
      {
        d: "M19 5L5 19M5 5L19 19",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var TimesIcon_default = TimesIcon;

// src/surfaces-and-overlays/ModalDrawer/ModalDrawerSlide.tsx
import {
  AnimatePresence,
  motion as motion2
} from "framer-motion";
import { useEffect as useEffect3, useState as useState4 } from "react";
import { jsx as jsx16 } from "react/jsx-runtime";
var CLOSED_WIDTH = "8px";
var PEEK_WIDTH = "40px";
var getClosedCoordinates = (width, custom) => {
  const { showBar, position } = custom;
  const offset = showBar ? width : "0px";
  switch (position) {
    case "top":
      return {
        x: "0%",
        y: showBar ? `calc(${offset} - 100%)` : "-300%"
      };
    case "right":
      return {
        x: showBar ? `calc(-${offset} + 100%)` : "300%",
        y: "0%"
      };
    case "bottom":
      return {
        x: "0%",
        y: showBar ? `calc(-${offset} + 100%)` : "300%"
      };
    case "left":
      return {
        x: showBar ? `calc(${offset} - 100%)` : "-300%",
        y: "0%"
      };
    default:
      return { x: "0%", y: "0%" };
  }
};
var getHintCoordinates = (closedCoordinates, custom) => {
  const { showBar, position } = custom;
  const offset = showBar ? CLOSED_WIDTH : "0px";
  switch (position) {
    case "top":
      return {
        x: "0%",
        y: [
          closedCoordinates.y,
          `calc(${offset} - 20%)`,
          `calc(${offset} - 20%)`,
          closedCoordinates.y
        ]
      };
    case "right":
      return {
        x: [
          closedCoordinates.x,
          `calc(-${offset} + 20%)`,
          `calc(-${offset} + 20%)`,
          closedCoordinates.x
        ],
        y: "0%"
      };
    case "bottom":
      return {
        x: "0%",
        y: [
          closedCoordinates.y,
          `calc(-${offset} + 20%)`,
          `calc(-${offset} + 20%)`,
          closedCoordinates.y
        ]
      };
    case "left":
      return {
        x: [
          closedCoordinates.x,
          `calc(${offset} - 20%)`,
          `calc(${offset} - 20%)`,
          closedCoordinates.x
        ],
        y: "0%"
      };
    default:
      return { x: "0%", y: "0%" };
  }
};
var slideVariants = {
  // CLOSED - the menu is closed.  On desktop, a small line will be visible to
  // indicate that the menu is there.
  closed: (custom) => ({
    ...getClosedCoordinates(CLOSED_WIDTH, custom),
    transition: { duration: 0.2 }
  }),
  // PEEK - when the mouse gets near the edge of the screen, the drawer will
  // slide out for a little bit.  It is effectively closed, but gives a bigger
  // click area to open.
  peek: (custom) => ({
    ...getClosedCoordinates(PEEK_WIDTH, custom),
    transition: {
      duration: 0.2,
      ease: "easeInOut"
    }
  }),
  // HINT - when you first load the page, the drawer will slide out a little bit
  // and then go back to closed.  This is to show the user that a menu exists.
  hint: (custom) => ({
    ...getHintCoordinates(getClosedCoordinates(CLOSED_WIDTH, custom), custom),
    transition: {
      duration: 1,
      ease: "easeInOut",
      times: [0, 0.2, 0.8, 1],
      // Puts a pause in the middle of the animation
      delay: 1.2
    }
  }),
  // OPEN - the menu is open.
  open: {
    x: "0%",
    y: "0%",
    transition: { duration: 0.2 }
  }
};
var ModalDrawerSlide = (props) => {
  const {
    unstyled = false,
    isOpen = true,
    hint = false,
    showBar = false,
    peek = false,
    className = "",
    children,
    ...restProps
  } = props;
  const { position } = useModalDrawer();
  const [hintPlayed, setHintPlayed] = useState4(!hint);
  useEffect3(() => {
    if (isOpen || peek) {
      setHintPlayed(true);
    }
  }, [isOpen, hintPlayed, peek]);
  return /* @__PURE__ */ jsx16(AnimatePresence, { children: (isOpen || showBar) && /* @__PURE__ */ jsx16(
    motion2.div,
    {
      className: `modal-drawer__slide ${unstyled ? "" : "modal-drawer__slide_default-style"} ${isOpen ? "modal-drawer__slide_open" : ""} ${className}`,
      variants: slideVariants,
      custom: { showBar, position },
      initial: "closed",
      animate: isOpen ? "open" : peek ? "peek" : !hintPlayed ? "hint" : "closed",
      exit: "closed",
      ...restProps,
      children
    }
  ) });
};
var ModalDrawerSlide_default = ModalDrawerSlide;

// src/surfaces-and-overlays/ModalDrawer/ModalDrawer.tsx
import { jsx as jsx17, jsxs as jsxs8 } from "react/jsx-runtime";
var getPositionClassName = (position) => {
  switch (position) {
    case "right":
      return "modal-drawer__panel_right";
    case "left":
      return "modal-drawer__panel_left";
    case "top":
      return "modal-drawer__panel_top";
    case "bottom":
      return "modal-drawer__panel_bottom";
    default:
      return "";
  }
};
var getPanelSizeClassName = (position) => {
  switch (position) {
    case "top":
    case "bottom":
      return "modal-drawer__panel-horizontal-size";
    case "right":
    case "left":
    default:
      return "modal-drawer__panel-vertical-size";
  }
};
var ModalDrawer = (props) => {
  const {
    isOpen,
    onClose,
    position = "left",
    className = "",
    title,
    children
    // ...restProps
  } = props;
  const overlayMotionProps = {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 0.7,
      transition: { duration: 0.2 }
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };
  const positionClassName = getPositionClassName(position);
  const panelSizeClassName = getPanelSizeClassName(position);
  const { t } = useTranslation2("ui");
  return /* @__PURE__ */ jsx17(ModalDrawerProvider_default, { position, children: /* @__PURE__ */ jsxs8(Dialog, { static: true, as: motion3.div, open: isOpen, onClose, title, children: [
    /* @__PURE__ */ jsx17(AnimatePresence2, { children: isOpen && /* @__PURE__ */ jsx17(
      motion3.div,
      {
        className: "modal-drawer__overlay",
        "aria-hidden": "true",
        ...overlayMotionProps
      }
    ) }),
    /* @__PURE__ */ jsxs8(
      Dialog.Panel,
      {
        as: motion3.div,
        className: `modal-drawer__panel ${positionClassName} ${panelSizeClassName} ${className}`,
        children: [
          children,
          /* @__PURE__ */ jsx17(AnimatePresence2, { children: isOpen && /* @__PURE__ */ jsx17(
            motion3.div,
            {
              className: "modal-drawer__close",
              initial: { opacity: 0 },
              animate: { opacity: 1, transition: { duration: 0.2 } },
              exit: { opacity: 0, transition: { duration: 0 } },
              children: /* @__PURE__ */ jsx17(
                "button",
                {
                  "aria-label": `${t("close-button")} ${t("window")}`,
                  className: "modal-drawer__close-button",
                  onClick: onClose,
                  children: /* @__PURE__ */ jsx17(TimesIcon_default, { className: "modal-drawer__close-icon" })
                }
              )
            }
          ) })
        ]
      }
    )
  ] }) });
};
ModalDrawer.Slide = ModalDrawerSlide_default;
var ModalDrawer_default = ModalDrawer;

// src/navigation/MainMenu/MainMenu.tsx
import { useTranslation as useTranslation11 } from "react-i18next";

// src/icons/direction/ChevronLeftIcon.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
var ChevronLeftIcon = (props) => /* @__PURE__ */ jsx18(
  "svg",
  {
    width: "24",
    height: "25",
    viewBox: "0 0 24 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx18(
      "path",
      {
        d: "M15.5 5.23633L8.5 12.2363L15.5 19.2363",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ChevronLeftIcon_default = ChevronLeftIcon;

// src/media-query/useMediaQuery.ts
import { useEffect as useEffect4, useState as useState5 } from "react";
function useMediaQuery(query) {
  const getMatches = (query2) => {
    return window.matchMedia(query2).matches;
  };
  const [matches, setMatches] = useState5(false);
  function handleChange() {
    setMatches(getMatches(query));
  }
  useEffect4(() => {
    const matchMedia = window.matchMedia(query);
    handleChange();
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener("change", handleChange);
    }
    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener("change", handleChange);
      }
    };
  }, [query]);
  return matches;
}
var useMediaQuery_default = useMediaQuery;

// src/media-query/useBreakpoints.tsx
var useBreakpoints = () => {
  const isSm = useMediaQuery_default("(min-width: 640px)");
  const isMd = useMediaQuery_default("(min-width: 768px)");
  const isLg = useMediaQuery_default("(min-width: 1024px)");
  const isXl = useMediaQuery_default("(min-width: 1280px)");
  const is2Xl = useMediaQuery_default("(min-width: 1536px)");
  return { isSm, isMd, isLg, isXl, is2Xl };
};
var useBreakpoints_default = useBreakpoints;

// src/media-query/useResponsiveValue.ts
var getNearestValue = (value, breakpoint) => {
  switch (breakpoint) {
    case "2xl":
      if (value["2xl"]) {
        return value["2xl"];
      }
    case "xl":
      if (value.xl) {
        return value.xl;
      }
    case "lg":
      if (value.lg) {
        return value.lg;
      }
    case "md":
      if (value.md) {
        return value.md;
      }
    case "sm":
      if (value.sm) {
        return value.sm;
      }
    case "DEFAULT":
    default:
      return value.DEFAULT;
  }
};
var useResponsiveValue = (value) => {
  const { isSm, isMd, isLg, isXl, is2Xl } = useBreakpoints_default();
  if (typeof value !== "object") {
    return value;
  }
  const breakpoint = is2Xl ? "2xl" : isXl ? "xl" : isLg ? "lg" : isMd ? "md" : isSm ? "sm" : "DEFAULT";
  return getNearestValue(value, breakpoint);
};
var useResponsiveValue_default = useResponsiveValue;

// src/search/SearchBar/SearchBar.tsx
import { useState as useState7, useEffect as useEffect6 } from "react";
import {
  buildSearchBox
} from "@coveo/headless";

// src/search/SearchBox/SearchBox.tsx
import { useEffect as useEffect5, useState as useState6 } from "react";
import { useTranslation as useTranslation3 } from "react-i18next";

// src/icons/direction/ArrowLeftIcon.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var ArrowLeftIcon = (props) => /* @__PURE__ */ jsx19(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx19(
      "path",
      {
        d: "M20 12L6 12M11 5L4 12L11 19",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ArrowLeftIcon_default = ArrowLeftIcon;

// src/icons/direction/ArrowDownIcon.tsx
import { jsx as jsx20 } from "react/jsx-runtime";
var ArrowDownIcon = (props) => /* @__PURE__ */ jsx20(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx20(
      "path",
      {
        d: "M12 4L12 18M19 13L12 20L5 13",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ArrowDownIcon_default = ArrowDownIcon;

// src/icons/direction/ArrowDownWideIcon.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var ArrowDownWideIcon = (props) => /* @__PURE__ */ jsx21(
  "svg",
  {
    width: "64",
    height: "64",
    viewBox: "0 0 64 64",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx21(
      "path",
      {
        d: "M32 50C31.744 50 31.488 49.902 31.293 49.707L16.293 34.707C16.007 34.421 15.922 33.991 16.076 33.617C16.231 33.243 16.595 33 17 33H23V18C23 17.448 23.448 17 24 17H40C40.552 17 41 17.448 41 18V33H47C47.404 33 47.769 33.244 47.924 33.617C48.079 33.991 47.993 34.421 47.707 34.707L32.707 49.707C32.512 49.902 32.256 50 32 50ZM19.414 35L32 47.586L44.586 35H40C39.448 35 39 34.552 39 34V19H25V34C25 34.552 24.552 35 24 35H19.414Z",
        fill: "currentColor"
      }
    )
  }
);
var ArrowDownWideIcon_default = ArrowDownWideIcon;

// src/icons/direction/ChevronDownIcon.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
var ChevronDownIcon = (props) => /* @__PURE__ */ jsx22(
  "svg",
  {
    width: "24",
    height: "25",
    viewBox: "0 0 24 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx22(
      "path",
      {
        d: "M19 9.23828L12 16.2383L5 9.23828",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ChevronDownIcon_default = ChevronDownIcon;

// src/icons/direction/ChevronUpIcon.tsx
import { jsx as jsx23 } from "react/jsx-runtime";
var ChevronUpIcon = (props) => /* @__PURE__ */ jsx23(
  "svg",
  {
    width: "24",
    height: "25",
    viewBox: "0 0 24 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx23(
      "path",
      {
        d: "M5 16.2383L12 9.23828L19 16.2383",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ChevronUpIcon_default = ChevronUpIcon;

// src/icons/functional/ErrorIcon.tsx
import { jsx as jsx24, jsxs as jsxs9 } from "react/jsx-runtime";
var ErrorIcon = (props) => /* @__PURE__ */ jsxs9(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      /* @__PURE__ */ jsx24(
        "path",
        {
          d: "M12 7V14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ),
      /* @__PURE__ */ jsx24("circle", { cx: "12", cy: "17", r: "1", fill: "currentColor" })
    ]
  }
);
var ErrorIcon_default = ErrorIcon;

// src/icons/functional/ExternalLinkIcon.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var ExternalLinkIcon = (props) => /* @__PURE__ */ jsx25(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx25(
      "path",
      {
        d: "M20 12V19.5C20 19.775 19.841 20.159 19.646 20.354L19.353 20.647C19.159 20.841 18.774 21.001 18.499 21.001H4.50002C4.22502 21.001 3.84102 20.842 3.64602 20.647L3.35302 20.354C3.15902 20.16 2.99902 19.775 2.99902 19.5V5.5C2.99902 5.225 3.15802 4.841 3.35302 4.646L3.64602 4.353C3.84002 4.159 4.22502 3.999 4.50002 3.999H12M16 3H21M21 3V8M21 3L7.00002 17",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var ExternalLinkIcon_default = ExternalLinkIcon;

// src/icons/functional/CheckboxEmpty.tsx
import { jsx as jsx26, jsxs as jsxs10 } from "react/jsx-runtime";
var CheckboxEmpty = (props) => /* @__PURE__ */ jsxs10(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      /* @__PURE__ */ jsx26("rect", { x: "0.5", y: "0.5", width: "23", height: "23", rx: "3.5", stroke: "currentColor" }),
      /* @__PURE__ */ jsx26("rect", { width: "16", height: "16", transform: "translate(4 4)", fill: "white" })
    ]
  }
);
var CheckboxEmpty_default = CheckboxEmpty;

// src/icons/functional/CheckboxFull.tsx
import { jsx as jsx27, jsxs as jsxs11 } from "react/jsx-runtime";
var CheckboxFull = (props) => /* @__PURE__ */ jsxs11(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      /* @__PURE__ */ jsx27("rect", { x: "0.5", y: "0.5", width: "23", height: "23", rx: "3.5", stroke: "currentColor" }),
      /* @__PURE__ */ jsx27("rect", { width: "16", height: "16", transform: "translate(4 4)", fill: "white" }),
      /* @__PURE__ */ jsx27(
        "path",
        {
          d: "M19 7L10 17L5 12",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    ]
  }
);
var CheckboxFull_default = CheckboxFull;

// src/icons/media/PlayIcon.tsx
import { jsx as jsx28 } from "react/jsx-runtime";
var PlayIcon = (props) => /* @__PURE__ */ jsx28(
  "svg",
  {
    width: "24",
    height: "25",
    viewBox: "0 0 24 25",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx28(
      "path",
      {
        d: "M8 20.365V4.63504C8 3.7872 8.98886 3.32405 9.64018 3.86682L19.0781 11.7318C19.5579 12.1316 19.5579 12.8684 19.0781 13.2682L9.64018 21.1332C8.98886 21.676 8 21.2128 8 20.365Z",
        stroke: "currentColor",
        strokeWidth: "2"
      }
    )
  }
);
var PlayIcon_default = PlayIcon;

// src/icons/media/PauseIcon.tsx
import { jsx as jsx29 } from "react/jsx-runtime";
var PauseIcon = (props) => /* @__PURE__ */ jsx29(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx29(
      "path",
      {
        d: "M8 4V20M16 4V20",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round"
      }
    )
  }
);
var PauseIcon_default = PauseIcon;

// src/icons/miscellaneous/CheckmarkIcon.tsx
import { jsx as jsx30 } from "react/jsx-runtime";
var CheckmarkIcon = (props) => /* @__PURE__ */ jsx30(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx30(
      "path",
      {
        d: "M3 12L9 18L21 6",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var CheckmarkIcon_default = CheckmarkIcon;

// src/icons/miscellaneous/LoadingIcon.tsx
import { jsx as jsx31 } from "react/jsx-runtime";
var LoadingIcon = (props) => /* @__PURE__ */ jsx31(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    stroke: "currentColor",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx31("g", { className: "animation-spin", children: /* @__PURE__ */ jsx31("circle", { cx: "12", cy: "12", r: "9.5", fill: "none", "stroke-width": "3" }) })
  }
);
var LoadingIcon_default = LoadingIcon;

// src/icons/objects/MagnifierIcon.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
var MagnifierIcon = (props) => /* @__PURE__ */ jsx32(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx32(
      "path",
      {
        d: "M17 17L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var MagnifierIcon_default = MagnifierIcon;

// src/icons/objects/PhoneIcon.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
var PhoneIcon = (props) => /* @__PURE__ */ jsx33(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx33(
      "path",
      {
        d: "M10.0098 7.00977C9.34777 7.00977 8.09177 7.42777 7.25977 8.25977C6.42177 9.09777 6.00977 10.3218 6.00977 11.0098M10.0098 2.00976C7.86076 1.89076 5.15176 2.86776 3.50976 4.50976C1.86776 6.15176 0.890761 8.86076 1.00976 11.0098M21.5448 5.82381C21.7998 5.92581 21.9898 6.23381 21.9678 6.50781C21.9678 6.50781 21.5538 11.4658 16.0098 17.0098C10.4658 22.5538 5.50776 22.9678 5.50776 22.9678C5.23376 22.9908 4.92576 22.8008 4.82376 22.5448L3.19476 18.4728C3.09276 18.2178 3.21076 17.9078 3.45676 17.7848L6.56277 16.2318C6.80877 16.1088 7.19077 16.1418 7.41277 16.3048C7.41277 16.3048 9.31377 17.7048 13.0098 14.0088C16.7058 10.3128 15.3058 8.41181 15.3058 8.41181C15.1428 8.19081 15.1098 7.80781 15.2328 7.56181L16.7858 4.45581C16.9088 4.20981 17.2188 4.09181 17.4738 4.19381L21.5458 5.82281L21.5448 5.82381Z",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var PhoneIcon_default = PhoneIcon;

// src/icons/objects/DocumentIcon.tsx
import { jsx as jsx34 } from "react/jsx-runtime";
var DocumentIcon = (props) => /* @__PURE__ */ jsx34(
  "svg",
  {
    width: "16",
    height: "24",
    viewBox: "0 0 16 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx34(
      "path",
      {
        d: "M9 1.00002V7.00002H15M5 11H11M5 15H11M5 19H9M1.354 22.354C1.16 22.16 1 21.775 1 21.5V2.50002C1 2.22502 1.159 1.84102 1.354 1.64602L1.647 1.35302C1.841 1.15902 2.226 0.999023 2.501 0.999023H8.501C8.776 0.999023 9.16 1.15802 9.355 1.35302L14.648 6.64602C14.842 6.84002 15.002 7.22502 15.002 7.50002V21.5C15.002 21.775 14.843 22.159 14.648 22.354L14.355 22.647C14.161 22.841 13.776 23.001 13.501 23.001H2.5C2.225 23.001 1.841 22.842 1.646 22.647L1.354 22.354Z",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  }
);
var DocumentIcon_default = DocumentIcon;

// src/icons/social-media/FacebookIcon.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
var FacebookIcon = (props) => /* @__PURE__ */ jsx35(
  "svg",
  {
    width: "32",
    height: "32",
    viewBox: "0 0 32 32",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx35(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M28 26.6754C28 27.4069 27.4069 28 26.6754 28H20.5595V18.706H23.6792L24.1463 15.0839H20.5595V12.7714C20.5595 11.7227 20.8507 11.0081 22.3546 11.0081L24.2726 11.0072V7.7676C23.9408 7.72353 22.8023 7.62489 21.4777 7.62489C18.7123 7.62489 16.8191 9.31283 16.8191 12.4127V15.0839H13.6915V18.706H16.8191V28H5.32458C4.59287 28 4 27.4069 4 26.6754V5.32465C4 4.59295 4.59287 4 5.32458 4H26.6754C27.4069 4 28 4.59295 28 5.32465V26.6754Z",
        fill: "currentColor"
      }
    )
  }
);
var FacebookIcon_default = FacebookIcon;

// src/icons/social-media/InstagramIcon.tsx
import { jsx as jsx36 } from "react/jsx-runtime";
var InstagramIcon = (props) => /* @__PURE__ */ jsx36(
  "svg",
  {
    width: "32",
    height: "33",
    viewBox: "0 0 32 33",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx36(
      "path",
      {
        d: "M16.0012 4.33963C12.7422 4.33963 12.3332 4.35388 11.0532 4.41213C9.77575 4.47063 8.90374 4.67288 8.14074 4.96963C7.35149 5.27613 6.68199 5.68613 6.01499 6.35338C5.34749 7.02038 4.93749 7.68988 4.62999 8.47888C4.33249 9.24213 4.12999 10.1144 4.07249 11.3914C4.01524 12.6714 4.00024 13.0806 4.00024 16.3396C4.00024 19.5986 4.01474 20.0064 4.07274 21.2864C4.13149 22.5639 4.33374 23.4359 4.63024 24.1989C4.93699 24.9881 5.34699 25.6576 6.01424 26.3246C6.68099 26.9921 7.35049 27.4031 8.13924 27.7096C8.90274 28.0064 9.77499 28.2086 11.0522 28.2671C12.3322 28.3254 12.741 28.3396 15.9997 28.3396C19.259 28.3396 19.6667 28.3254 20.9467 28.2671C22.2242 28.2086 23.0972 28.0064 23.8607 27.7096C24.6497 27.4031 25.3182 26.9921 25.985 26.3246C26.6525 25.6576 27.0625 24.9881 27.37 24.1991C27.665 23.4359 27.8675 22.5636 27.9275 21.2866C27.985 20.0066 28 19.5986 28 16.3396C28 13.0806 27.985 12.6716 27.9275 11.3916C27.8675 10.1141 27.665 9.24213 27.37 8.47913C27.0625 7.68988 26.6525 7.02038 25.985 6.35338C25.3175 5.68588 24.65 5.27588 23.86 4.96963C23.095 4.67288 22.2225 4.47063 20.945 4.41213C19.665 4.35388 19.2575 4.33963 15.9975 4.33963H16.0012ZM14.9247 6.50213C15.2442 6.50163 15.6007 6.50213 16.0012 6.50213C19.2052 6.50213 19.585 6.51363 20.8502 6.57113C22.0202 6.62463 22.6552 6.82013 23.0782 6.98438C23.6382 7.20188 24.0375 7.46188 24.4572 7.88188C24.8772 8.30188 25.1372 8.70188 25.3552 9.26188C25.5195 9.68438 25.7152 10.3194 25.7685 11.4894C25.826 12.7544 25.8385 13.1344 25.8385 16.3369C25.8385 19.5394 25.826 19.9194 25.7685 21.1844C25.715 22.3544 25.5195 22.9894 25.3552 23.4119C25.1377 23.9719 24.8772 24.3706 24.4572 24.7904C24.0372 25.2104 23.6385 25.4704 23.0782 25.6879C22.6557 25.8529 22.0202 26.0479 20.8502 26.1014C19.5852 26.1589 19.2052 26.1714 16.0012 26.1714C12.797 26.1714 12.4172 26.1589 11.1522 26.1014C9.98225 26.0474 9.34724 25.8519 8.92399 25.6876C8.36399 25.4701 7.96399 25.2101 7.54399 24.7901C7.12399 24.3701 6.86399 23.9711 6.64599 23.4109C6.48174 22.9884 6.28599 22.3534 6.23274 21.1834C6.17524 19.9184 6.16374 19.5384 6.16374 16.3339C6.16374 13.1294 6.17524 12.7514 6.23274 11.4864C6.28624 10.3164 6.48174 9.68138 6.64599 9.25838C6.86349 8.69838 7.12399 8.29838 7.54399 7.87838C7.96399 7.45838 8.36399 7.19838 8.92399 6.98038C9.34699 6.81538 9.98225 6.62038 11.1522 6.56663C12.2592 6.51663 12.6882 6.50163 14.9247 6.49913V6.50213ZM22.4067 8.49463C21.6117 8.49463 20.9667 9.13888 20.9667 9.93413C20.9667 10.7291 21.6117 11.3741 22.4067 11.3741C23.2017 11.3741 23.8467 10.7291 23.8467 9.93413C23.8467 9.13913 23.2017 8.49413 22.4067 8.49413V8.49463ZM16.0012 10.1771C12.598 10.1771 9.83874 12.9364 9.83874 16.3396C9.83874 19.7429 12.598 22.5009 16.0012 22.5009C19.4045 22.5009 22.1627 19.7429 22.1627 16.3396C22.1627 12.9364 19.4045 10.1771 16.0012 10.1771ZM16.0012 12.3396C18.2102 12.3396 20.0012 14.1304 20.0012 16.3396C20.0012 18.5486 18.2102 20.3396 16.0012 20.3396C13.792 20.3396 12.0012 18.5486 12.0012 16.3396C12.0012 14.1304 13.792 12.3396 16.0012 12.3396Z",
        fill: "currentColor"
      }
    )
  }
);
var InstagramIcon_default = InstagramIcon;

// src/icons/social-media/LinkedInIcon.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
var LinkedInIcon = (props) => /* @__PURE__ */ jsx37(
  "svg",
  {
    width: "32",
    height: "32",
    viewBox: "0 0 32 32",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx37(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M25.3333 28H6.66667C5.19391 28 4 26.8061 4 25.3333V6.66667C4 5.19391 5.19391 4 6.66667 4H25.3333C26.8061 4 28 5.19391 28 6.66667V25.3333C28 26.8061 26.8061 28 25.3333 28ZM21.1052 24.6666H24.6667V17.3503C24.6667 14.2547 22.9119 12.758 20.4609 12.758C18.0087 12.758 16.9767 14.6676 16.9767 14.6676V13.111H13.5445V24.6666H16.9767V18.6006C16.9767 16.9753 17.7249 16.0081 19.1569 16.0081C20.4733 16.0081 21.1052 16.9375 21.1052 18.6006V24.6666ZM7.33334 9.46559C7.33334 10.6431 8.2807 11.5979 9.44979 11.5979C10.6189 11.5979 11.5657 10.6431 11.5657 9.46559C11.5657 8.28804 10.6189 7.33325 9.44979 7.33325C8.2807 7.33325 7.33334 8.28804 7.33334 9.46559ZM11.2565 24.6666H7.67753V13.111H11.2565V24.6666Z",
        fill: "currentColor"
      }
    )
  }
);
var LinkedInIcon_default = LinkedInIcon;

// src/icons/social-media/TwitterIcon.tsx
import { jsx as jsx38 } from "react/jsx-runtime";
var TwitterIcon = (props) => /* @__PURE__ */ jsx38(
  "svg",
  {
    width: "32",
    height: "32",
    viewBox: "0 0 32 32",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx38(
      "path",
      {
        d: "M27.0928 9.64321C27.1098 9.88915 27.1098 10.1351 27.1098 10.3833C27.1098 17.9461 21.3525 26.6683 10.8251 26.6683V26.6638C7.71523 26.6683 4.66999 25.7775 2.052 24.0979C2.5042 24.1523 2.95866 24.1795 3.41426 24.1806C5.99144 24.1829 8.49496 23.3181 10.5225 21.7258C8.07336 21.6793 5.92571 20.0824 5.17545 17.7512C6.03338 17.9166 6.91737 17.8826 7.75943 17.6526C5.08932 17.1131 3.16833 14.7671 3.16833 12.0425V11.97C3.96392 12.4131 4.85472 12.659 5.76591 12.6862C3.25106 11.0055 2.47587 7.65986 3.99452 5.04411C6.90037 8.61981 11.1877 10.7936 15.7902 11.0236C15.3289 9.03574 15.959 6.95266 17.446 5.55524C19.7512 3.38829 23.3767 3.49936 25.5436 5.80345C26.8254 5.55071 28.0539 5.08037 29.1782 4.41397C28.7509 5.73885 27.8567 6.86426 26.6622 7.5794C27.7966 7.44566 28.905 7.14193 29.9488 6.67839C29.1804 7.82986 28.2126 8.83287 27.0928 9.64321Z",
        fill: "currentColor"
      }
    )
  }
);
var TwitterIcon_default = TwitterIcon;

// src/icons/social-media/XIcon.tsx
import { jsx as jsx39 } from "react/jsx-runtime";
var XIcon = (props) => /* @__PURE__ */ jsx39(
  "svg",
  {
    width: "32",
    height: "33",
    viewBox: "0 0 32 33",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx39(
      "path",
      {
        d: "M22.3265 6H25.6999L18.3302 14.5429L27 26.1667H20.2112L14.8942 19.1174L8.80956 26.1667H5.43428L13.3168 17.03L5 6H11.9607L16.7668 12.4441L22.3265 6ZM21.1423 24.119H23.0121L10.9455 7.94061H8.93938L21.1423 24.119Z",
        fill: "currentColor"
      }
    )
  }
);
var XIcon_default = XIcon;

// src/icons/social-media/YoutubeIcon.tsx
import { jsx as jsx40 } from "react/jsx-runtime";
var YoutubeIcon = (props) => /* @__PURE__ */ jsx40(
  "svg",
  {
    width: "32",
    height: "33",
    viewBox: "0 0 32 33",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx40(
      "path",
      {
        d: "M12.886 20.928V11.8725L20.8451 16.4004L12.886 20.928ZM30.5899 9.02312C30.2397 7.70445 29.2078 6.66594 27.8977 6.31349C25.523 5.67297 16.0004 5.67297 16.0004 5.67297C16.0004 5.67297 6.47797 5.67297 4.10327 6.31349C2.79316 6.66594 1.76126 7.70445 1.41103 9.02312C0.774658 11.4133 0.774658 16.4003 0.774658 16.4003C0.774658 16.4003 0.774658 21.387 1.41103 23.7773C1.76126 25.0961 2.79303 26.1345 4.10327 26.4872C6.47797 27.1274 16.0004 27.1274 16.0004 27.1274C16.0004 27.1274 25.523 27.1274 27.8977 26.4872C29.2078 26.1345 30.2397 25.0961 30.5899 23.7773C31.2263 21.387 31.2263 16.4001 31.2263 16.4001C31.2263 16.4001 31.2263 11.4133 30.5899 9.02312Z",
        fill: "currentColor"
      }
    )
  }
);
var YoutubeIcon_default = YoutubeIcon;

// src/search/SearchBox/SearchBox.tsx
import { jsx as jsx41, jsxs as jsxs12 } from "react/jsx-runtime";
var SearchBox = (props) => {
  const { controller, className } = props;
  const [state, setState] = useState6(controller.state);
  const isEnterKey = (e) => e.key === "Enter";
  useEffect5(() => controller.subscribe(() => setState(controller.state)), []);
  const { t } = useTranslation3("features");
  const isLoading = state.isLoading || state.isLoadingSuggestions;
  const isDisabled = !state.value || isLoading;
  return /* @__PURE__ */ jsx41("div", { children: /* @__PURE__ */ jsxs12("div", { className: `${className}`, children: [
    /* @__PURE__ */ jsxs12("div", { className: "search-box", children: [
      /* @__PURE__ */ jsx41(
        "input",
        {
          value: state.value,
          onChange: (e) => controller.updateText(e.target.value),
          onKeyDown: (e) => isEnterKey(e) && controller.submit(),
          className: `search-bar__input ${!!state.suggestions.length ? "has-suggestions" : ""}`,
          placeholder: `${t("search-placeholder")}`
        }
      ),
      !!state.suggestions.length && /* @__PURE__ */ jsx41("div", { className: "search-suggestions", children: /* @__PURE__ */ jsx41("ul", { className: "search-suggestions__list", children: state.suggestions.map((suggestion, index) => {
        const value = suggestion.rawValue;
        return /* @__PURE__ */ jsx41("li", { onClick: () => controller.selectSuggestion(value), children: value }, index);
      }) }) })
    ] }),
    /* @__PURE__ */ jsx41(
      "button",
      {
        "aria-label": `${t("search-action")}`,
        className: "search-bar__button",
        disabled: isDisabled,
        onClick: () => controller.submit(),
        children: isLoading ? /* @__PURE__ */ jsx41(LoadingIcon_default, { className: "search-bar__icon" }) : /* @__PURE__ */ jsx41(MagnifierIcon_default, { className: "search-bar__icon" })
      }
    )
  ] }) });
};

// src/search/SearchBar/SearchBar.tsx
import { jsx as jsx42 } from "react/jsx-runtime";
var SearchBar = (props) => {
  const { className = "", engine } = props;
  const [controller, setController] = useState7();
  useEffect6(() => {
    const options = {
      numberOfSuggestions: 3
    };
    setController(buildSearchBox(engine, { options }));
  }, []);
  return /* @__PURE__ */ jsx42("div", { "data-enable-history": "true", children: controller ? /* @__PURE__ */ jsx42(
    SearchBox,
    {
      controller,
      className: `search-bar ${className}`
    }
  ) : /* @__PURE__ */ jsx42(LoadingIcon_default, {}) });
};
var SearchBar_default = SearchBar;

// src/search/SearchContainer/SearchContainer.tsx
import { jsx as jsx43, jsxs as jsxs13 } from "react/jsx-runtime";
var SearchContainer = (props) => {
  const { title, engine, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx43("div", { className: `search-container ${className}`, ...restProps, children: /* @__PURE__ */ jsxs13("div", { className: "search-container__content", children: [
    /* @__PURE__ */ jsx43("div", { className: "search-container__title", children: title }),
    engine ? /* @__PURE__ */ jsx43("div", { children: /* @__PURE__ */ jsx43(SearchBar_default, { engine, className: "search-container__search-bar" }) }) : /* @__PURE__ */ jsx43(LoadingIcon_default, {})
  ] }) });
};
var SearchContainer_default = SearchContainer;

// src/search/SearchModalButton/SearchModalButton.tsx
import { useSitecoreContext as useSitecoreContext3 } from "@sitecore-jss/sitecore-jss-react";
import { useTranslation as useTranslation4 } from "react-i18next";
import { jsx as jsx44, jsxs as jsxs14 } from "react/jsx-runtime";
var SearchModalButton = (props) => {
  const { className = "" } = props;
  const { t } = useTranslation4("ui");
  const { sitecoreContext } = useSitecoreContext3();
  const searchPageUrl = (sitecoreContext == null ? void 0 : sitecoreContext.siteSettings).searchPageUrl;
  return /* @__PURE__ */ jsxs14(
    RoutableSitecoreLink_default,
    {
      field: {
        href: searchPageUrl
      },
      className: `search-modal-button ${className} noindex`,
      title: t("search") || "",
      children: [
        /* @__PURE__ */ jsx44("span", { className: "search-modal-button__text", children: t("search") }),
        /* @__PURE__ */ jsx44(MagnifierIcon_default, {})
      ]
    }
  );
};
var SearchModalButton_default = SearchModalButton;

// src/search/SearchResults/SearchResults.tsx
import { useState as useState13, useEffect as useEffect11 } from "react";
import {
  buildResultList,
  buildQueryExpression,
  buildStaticFilterValue,
  buildPager
} from "@coveo/headless";

// src/search/SearchResults/SearchResultsLink.tsx
import { useState as useState8, useEffect as useEffect7 } from "react";
import {
  buildInteractiveResult
} from "@coveo/headless";
import { Link as Link2 } from "@sitecore-jss/sitecore-jss-react";
import { useNavigate } from "react-router-dom";
import { jsx as jsx45 } from "react/jsx-runtime";
var SearchResultsLink = (props) => {
  const { engine, item, children, className = "" } = props;
  const navigate = useNavigate();
  const [interactiveResult, setInteractiveResult] = useState8();
  useEffect7(() => {
    const options = {
      result: item
    };
    const result = buildInteractiveResult(engine, { options });
    setInteractiveResult(result);
  }, []);
  const clickurl = item == null ? void 0 : item.clickUri;
  return /* @__PURE__ */ jsx45(
    Link2,
    {
      field: {
        href: clickurl
      },
      onClick: async (e) => {
        await (interactiveResult == null ? void 0 : interactiveResult.select());
      },
      className: className || "",
      target: "_blank",
      rel: "noopener noreferrer",
      children
    }
  );
};
var SearchResultsLink_default = SearchResultsLink;

// src/search/SearchResults/SearchResultsItem.tsx
import { useTranslation as useTranslation5 } from "react-i18next";
import { jsx as jsx46, jsxs as jsxs15 } from "react/jsx-runtime";
var SearchResultsItem = (props) => {
  const { engine, item, className = "" } = props;
  const { t } = useTranslation5("ui");
  const type = item.raw.z95xtemplatename == "Pdf" ? "pdf" : "page";
  const title = type === "pdf" ? item.raw.z95xname : item.title;
  const icon = type === "pdf" ? /* @__PURE__ */ jsx46(DocumentIcon_default, {}) : /* @__PURE__ */ jsx46(ExternalLinkIcon_default, {});
  return /* @__PURE__ */ jsxs15("div", { className: `search-results__list-item ${className}`, children: [
    /* @__PURE__ */ jsx46(SearchResultsLink_default, { engine, item, className: "search-results__list-item-title", children: title }),
    /* @__PURE__ */ jsx46("p", { className: "search-results__list-item-excerpt", children: item.excerpt }),
    /* @__PURE__ */ jsxs15("p", { className: "search-results__list-item-footer", children: [
      /* @__PURE__ */ jsxs15("span", { children: [
        icon,
        ` ${t(`searchResultType.${type}`)}`
      ] }),
      /* @__PURE__ */ jsx46(SearchResultsLink_default, { engine, item, children: /* @__PURE__ */ jsx46(ArrowRightIcon_default, {}) })
    ] })
  ] });
};
var SearchResultsItem_default = SearchResultsItem;

// src/search/ResultsPerPage/ResultsPerPage.tsx
import { useEffect as useEffect8, useState as useState9 } from "react";
import {
  buildResultsPerPage
} from "@coveo/headless";
import { Fragment as Fragment2, jsx as jsx47 } from "react/jsx-runtime";
var DEFAULT_RESULTS_PER_PAGE = 10;
var ResultsPerPage = (props) => {
  const { engine, perPage, resultListState } = props;
  const [controller, setController] = useState9();
  const [, setState] = useState9();
  const numberOfResults = perPage || DEFAULT_RESULTS_PER_PAGE;
  useEffect8(() => {
    const options = {
      initialState: {
        numberOfResults
      }
    };
    setController(buildResultsPerPage(engine, options));
  }, []);
  useEffect8(() => {
    controller == null ? void 0 : controller.subscribe(() => setState(controller.state));
  }, [controller]);
  const { firstSearchExecuted, hasResults, isLoading } = resultListState || {};
  useEffect8(() => {
    if (engine && !firstSearchExecuted && !hasResults && !isLoading) {
      engine.executeFirstSearch();
    }
  }, [engine, firstSearchExecuted, hasResults, isLoading]);
  return /* @__PURE__ */ jsx47(Fragment2, {});
};
var ResultsPerPage_default = ResultsPerPage;

// src/search/Pagination/Pagination.tsx
import { Fragment as Fragment3, jsx as jsx48, jsxs as jsxs16 } from "react/jsx-runtime";
var Pagination = (props) => {
  const { controller } = props;
  if (!controller || !controller.state.currentPages.length)
    return /* @__PURE__ */ jsx48(Fragment3, {});
  return /* @__PURE__ */ jsx48("div", { className: "search-pagination__wrapper", children: /* @__PURE__ */ jsxs16("nav", { className: "search-pagination__list", children: [
    /* @__PURE__ */ jsx48(
      "button",
      {
        className: "search-pagination__list-item first",
        disabled: !controller.state.hasPreviousPage,
        onClick: () => controller.previousPage(),
        children: /* @__PURE__ */ jsx48(ArrowLeftIcon_default, {})
      }
    ),
    controller.state.currentPages.map((page) => /* @__PURE__ */ jsx48(
      "button",
      {
        className: "search-pagination__list-item num",
        disabled: controller.isCurrentPage(page),
        onClick: () => controller.selectPage(page),
        children: page
      },
      page
    )),
    /* @__PURE__ */ jsx48(
      "button",
      {
        className: "search-pagination__list-item last",
        disabled: !controller.state.hasNextPage,
        onClick: () => controller.nextPage(),
        children: /* @__PURE__ */ jsx48(ArrowRightIcon_default, {})
      }
    )
  ] }) });
};
var Pagination_default = Pagination;

// src/search/QuerySummary/QuerySummary.tsx
import { useEffect as useEffect9, useState as useState10 } from "react";
import {
  buildQuerySummary
} from "@coveo/headless";
import { useTranslation as useTranslation6 } from "react-i18next";
import { jsx as jsx49 } from "react/jsx-runtime";
var QuerySummary = (props) => {
  var _a;
  const { engine, className = "" } = props;
  const [controller, setController] = useState10();
  useEffect9(() => {
    setController(buildQuerySummary(engine));
  }, []);
  const { t } = useTranslation6("ui");
  if (!(controller == null ? void 0 : controller.state) || !((_a = controller == null ? void 0 : controller.state) == null ? void 0 : _a.hasResults))
    return null;
  const {
    firstResult,
    lastResult,
    total,
    query
  } = controller.state;
  const translationKey = !!query ? "query-summary" : "query-summary-noinput";
  const summary = t(translationKey, { from: firstResult.toString(), to: lastResult.toString(), total: total.toString(), query: query.toString() });
  return /* @__PURE__ */ jsx49("div", { className: `search-query-summary ${className}`, children: summary });
};
var QuerySummary_default = QuerySummary;

// src/search/StaticFilter/StaticFilter.tsx
import { useState as useState12, useEffect as useEffect10 } from "react";
import {
  buildStaticFilter
} from "@coveo/headless";

// src/forms/FormGroup/CheckboxGroup.tsx
import { useTranslation as useTranslation7 } from "react-i18next";
import { jsx as jsx50, jsxs as jsxs17 } from "react/jsx-runtime";
var CheckboxGroup = (props) => {
  const {
    as = "label",
    label,
    description,
    required = false,
    error = [],
    className = "",
    children,
    name,
    ...restProps
  } = props;
  const Component = as;
  const errors = Array.isArray(error) ? error : [error];
  const filteredErrors = errors.filter((error2) => error2 !== "");
  const hasErrors = filteredErrors.length > 0;
  const { t } = useTranslation7("ui");
  return /* @__PURE__ */ jsxs17(
    "div",
    {
      className: `form-group form-group_checkbox ${hasErrors ? "form-group_error" : ""} ${className}`,
      ...restProps,
      children: [
        /* @__PURE__ */ jsxs17(Component, { className: "form-group__checkbox", children: [
          children,
          label && /* @__PURE__ */ jsxs17("span", { className: "form-group__label", children: [
            label,
            required && " *"
          ] })
        ] }),
        !hasErrors && description && /* @__PURE__ */ jsx50("div", { className: "form-group__description", children: description }),
        hasErrors && filteredErrors.map((error2) => /* @__PURE__ */ jsxs17("div", { className: "form-group__error", children: [
          /* @__PURE__ */ jsx50(ErrorIcon_default, { className: "form-group__error-icon" }),
          t(error2)
        ] }, error2))
      ]
    }
  );
};
var CheckboxGroup_default = CheckboxGroup;

// src/forms/FileDropzone/FileDropzone.tsx
import { useRef as useRef2, useState as useState11 } from "react";
import { useTranslation as useTranslation8 } from "react-i18next";
import { jsx as jsx51, jsxs as jsxs18 } from "react/jsx-runtime";
var FileDropzone = (props) => {
  const { onChange, className, ...restProps } = props;
  const ref = useRef2(null);
  const [isDragActive, setIsDragActive] = useState11(false);
  const { t } = useTranslation8("ui");
  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setIsDragActive(true);
    } else if (event.type === "dragleave") {
      setIsDragActive(false);
    }
  };
  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
      await onChange(event.dataTransfer.files[0]);
    }
  };
  const handleChange = async (event) => {
    if (event && event.target.files && event.target.files[0]) {
      await onChange(event.target.files[0]);
      event.target.value = "";
    }
  };
  const handleClick = () => {
    if (ref.current) {
      ref.current.click();
    }
  };
  return /* @__PURE__ */ jsxs18(
    "div",
    {
      className: `file-dropzone ${className}`,
      onDragEnter: handleDrag,
      onClick: handleClick,
      ...restProps,
      children: [
        /* @__PURE__ */ jsx51(
          "input",
          {
            ref,
            onChange: handleChange,
            type: "file",
            className: "file-dropzone__input",
            name: "attachments"
          }
        ),
        /* @__PURE__ */ jsx51("div", { className: "file-dropzone__area", children: t("drag-and-drop-file") }),
        isDragActive && /* @__PURE__ */ jsx51(
          "div",
          {
            className: "file-dropzone__indicator",
            onDragLeave: handleDrag,
            onDragOver: handleDrag,
            onDrop: handleDrop,
            children: t("drop-your-file-here")
          }
        )
      ]
    }
  );
};
var FileDropzone_default = FileDropzone;

// src/forms/FormGroup/FormGroup.tsx
import { useTranslation as useTranslation9 } from "react-i18next";
import { jsx as jsx52, jsxs as jsxs19 } from "react/jsx-runtime";
var FormGroup = (props) => {
  const {
    as = "label",
    label,
    description,
    required = false,
    error = [],
    className = "",
    children,
    ...restProps
  } = props;
  const Component = as;
  const errors = Array.isArray(error) ? error : [error];
  const filteredErrors = errors.filter((error2) => error2 !== "");
  const hasErrors = filteredErrors.length > 0;
  const { t } = useTranslation9("ui");
  return /* @__PURE__ */ jsxs19(
    "div",
    {
      className: `form-group ${hasErrors ? "form-group_error" : ""} ${className}`,
      ...restProps,
      children: [
        /* @__PURE__ */ jsxs19(Component, { className: "form-group__input", children: [
          children,
          hasErrors && /* @__PURE__ */ jsx52(ErrorIcon_default, { className: "form-group__error-icon" }),
          label && /* @__PURE__ */ jsxs19("div", { className: "form-group__label", children: [
            label,
            required && " *"
          ] })
        ] }),
        !hasErrors && description && /* @__PURE__ */ jsx52("div", { className: "form-group__description", children: description }),
        hasErrors && filteredErrors.map((error2) => /* @__PURE__ */ jsx52("div", { className: "form-group__error", children: t(error2) }, error2))
      ]
    }
  );
};
var FormGroup_default = FormGroup;

// src/forms/Input/Checkbox.tsx
import { forwardRef } from "react";
import { Fragment as Fragment4, jsx as jsx53, jsxs as jsxs20 } from "react/jsx-runtime";
var Checkbox = forwardRef((props, ref) => {
  const { className = "", isChecked = false, ...restProps } = props;
  return /* @__PURE__ */ jsxs20(Fragment4, { children: [
    /* @__PURE__ */ jsx53("input", { type: "checkbox", id: props.name, className: `checkbox ${className}`, ref, ...restProps }),
    isChecked ? /* @__PURE__ */ jsx53(CheckboxFull_default, { className: "checkbox-icon" }) : /* @__PURE__ */ jsx53(CheckboxEmpty_default, { className: "checkbox-icon" })
  ] });
});
Checkbox.displayName = "Checkbox";
var Checkbox_default = Checkbox;

// src/forms/Input/Input.tsx
import { forwardRef as forwardRef2 } from "react";
import { jsx as jsx54 } from "react/jsx-runtime";
var Input = forwardRef2((props, ref) => {
  const { className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx54("input", { className: `input w-full ${className}`, ref, ...restProps });
});
Input.displayName = "Input";
var Input_default = Input;

// src/forms/Select/Select.tsx
import { jsx as jsx55 } from "react/jsx-runtime";
var Select = (props) => {
  const {
    placeholder,
    options: optionsInput = [],
    className = "",
    defaultValue,
    ...restProps
  } = props;
  let options = optionsInput;
  if (placeholder) {
    const placeholderOption = {
      id: "",
      name: placeholder
    };
    options = [placeholderOption, ...options];
  }
  return /* @__PURE__ */ jsx55(
    "select",
    {
      className: `select
        ${className}
      `,
      defaultValue: placeholder ? "" : defaultValue,
      ...restProps,
      children: options.map((option) => /* @__PURE__ */ jsx55(
        "option",
        {
          value: option.id,
          className: option.id === "" ? "select__option" : "",
          ...{
            disabled: option.id === ""
          },
          children: option.name
        },
        option.id
      ))
    }
  );
};
var Select_default = Select;

// src/forms/TextArea/TextArea.tsx
import { forwardRef as forwardRef3 } from "react";
import { jsx as jsx56 } from "react/jsx-runtime";
var TextArea = forwardRef3(
  (props, ref) => {
    const { className = "", children, ...restProps } = props;
    return /* @__PURE__ */ jsx56("textarea", { className: `textarea ${className}`, ref, ...restProps });
  }
);
TextArea.displayName = "TextArea";
var TextArea_default = TextArea;

// src/icons/functional/SuccessIcon.tsx
import { jsx as jsx57 } from "react/jsx-runtime";
var SuccessIcon = (props) => /* @__PURE__ */ jsx57(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    ...props,
    children: /* @__PURE__ */ jsx57(
      "path",
      {
        d: "M8 12L11 15L16 10M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z",
        stroke: "currentColor",
        "stroke-width": "2",
        "stroke-linecap": "round",
        "stroke-linejoin": "round"
      }
    )
  }
);
var SuccessIcon_default = SuccessIcon;

// src/icons/functional/DeleteIcon.tsx
import { jsx as jsx58 } from "react/jsx-runtime";
var DeleteIcon = (props) => /* @__PURE__ */ jsx58(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    ...props,
    children: /* @__PURE__ */ jsx58(
      "path",
      {
        d: "M9 15L15 9M15 15L9 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z",
        stroke: "currentColor",
        "stroke-width": "2",
        "stroke-linecap": "round",
        "stroke-linejoin": "round"
      }
    )
  }
);
var DeleteIcon_default = DeleteIcon;

// src/forms/Notification/Notification.tsx
import { jsx as jsx59, jsxs as jsxs21 } from "react/jsx-runtime";
var Notification = (props) => {
  const {
    type = "",
    text = "",
    ...restProps
  } = props;
  const notificationStyle = type === "success" ? "notification__success" : type === "error" ? "notification__error" : type === "warning" ? "notification__warning" : "";
  return /* @__PURE__ */ jsxs21("div", { className: `notification ${notificationStyle}`, children: [
    type === "success" ? /* @__PURE__ */ jsx59(SuccessIcon_default, { className: "notification__icon" }) : type === "error" ? /* @__PURE__ */ jsx59(DeleteIcon_default, { className: "notification__icon" }) : type === "warning" ? /* @__PURE__ */ jsx59(ErrorIcon_default, { className: "notification__icon" }) : /* @__PURE__ */ jsx59(DeleteIcon_default, { className: "notification__icon" }),
    text
  ] });
};
var Notification_default = Notification;

// src/search/StaticFilter/StaticFilter.tsx
import { jsx as jsx60, jsxs as jsxs22 } from "react/jsx-runtime";
var StaticFilter = (props) => {
  const { engine, filterOptions, label, className = "", paginationController } = props;
  const [state, setState] = useState12();
  const [staticFilter, setStaticFilter] = useState12();
  useEffect10(() => {
    const filter = buildStaticFilter(engine, {
      options: filterOptions
    });
    setStaticFilter(filter);
  }, []);
  useEffect10(() => {
    staticFilter == null ? void 0 : staticFilter.subscribe(() => setState(staticFilter.state));
  }, [staticFilter]);
  return /* @__PURE__ */ jsxs22("div", { className: `static-filter ${className}`, children: [
    /* @__PURE__ */ jsxs22("div", { className: "static-filter__label", children: [
      label,
      ":"
    ] }),
    state == null ? void 0 : state.values.map((value) => {
      return /* @__PURE__ */ jsx60(
        CheckboxGroup_default,
        {
          name: state.id,
          label: value.caption,
          className: "static-filter__option",
          children: /* @__PURE__ */ jsx60(
            Checkbox_default,
            {
              name: state.id,
              isChecked: !!(staticFilter == null ? void 0 : staticFilter.isValueSelected(value)),
              checked: !!(staticFilter == null ? void 0 : staticFilter.isValueSelected(value)),
              onChange: () => {
                paginationController == null ? void 0 : paginationController.selectPage(1);
                staticFilter == null ? void 0 : staticFilter.toggleSelect(value);
              }
            }
          )
        },
        value.caption
      );
    })
  ] });
};
var StaticFilter_default = StaticFilter;

// src/search/SearchResults/SearchResults.tsx
import { useTranslation as useTranslation10 } from "react-i18next";
import { jsx as jsx61, jsxs as jsxs23 } from "react/jsx-runtime";
var SearchResults = (props) => {
  var _a;
  const { engine, className = "" } = props;
  const { t } = useTranslation10("ui");
  const [state, setState] = useState13();
  const [resultList, setResultList] = useState13();
  const [paginationController, setPaginationController] = useState13();
  useEffect11(() => {
    const options = {
      fieldsToInclude: ["defaulturi", "z95xlanguage", "z95xname", "filetype", "z95xtemplatename"]
    };
    const list = buildResultList(engine, { options });
    setResultList(list);
  }, []);
  useEffect11(() => {
    resultList == null ? void 0 : resultList.subscribe(() => setState(resultList.state));
  }, [resultList]);
  useEffect11(() => {
    const options = {
      numberOfPages: 5
    };
    setPaginationController(buildPager(engine, { options }));
  }, []);
  const pdfExpression = buildQueryExpression().addStringField({
    field: "z95xtemplatename",
    operator: "isExactly",
    values: ["Pdf"]
  }).toQuerySyntax();
  const isPdf = buildStaticFilterValue({
    caption: t(`searchResultType.pdf`),
    expression: pdfExpression
  });
  const htmlExpression = buildQueryExpression().addStringField({
    field: "filetype",
    operator: "isExactly",
    values: ["html"]
  }).toQuerySyntax();
  const isHtml = buildStaticFilterValue({
    caption: t(`searchResultType.page`),
    expression: htmlExpression
  });
  const filterOptions = {
    id: "fileType",
    values: [
      isPdf,
      isHtml
    ]
  };
  return /* @__PURE__ */ jsxs23("div", { className: `search-results ${className}`, children: [
    /* @__PURE__ */ jsx61(ResultsPerPage_default, { engine }),
    /* @__PURE__ */ jsxs23("div", { className: "search-results__list", children: [
      /* @__PURE__ */ jsx61(StaticFilter_default, { engine, filterOptions, label: t("filter-show"), className: "search-results__filters", paginationController }),
      /* @__PURE__ */ jsx61(QuerySummary_default, { engine, className: "search-results__query-summary" }),
      (state == null ? void 0 : state.results) && ((_a = state.results) == null ? void 0 : _a.length) ? state.results.map((item, index) => /* @__PURE__ */ jsx61(SearchResultsItem_default, { engine, item }, index)) : t("nothing-found")
    ] }),
    /* @__PURE__ */ jsx61(Pagination_default, { controller: paginationController })
  ] });
};
var SearchResults_default = SearchResults;

// src/search/NoIndex.tsx
import HTMLComment from "react-html-comment";
import { Fragment as Fragment5, jsx as jsx62, jsxs as jsxs24 } from "react/jsx-runtime";
var NoIndex = (props) => {
  const { children } = props;
  return /* @__PURE__ */ jsxs24(Fragment5, { children: [
    /* @__PURE__ */ jsx62(HTMLComment, { text: "BEGIN NOINDEX" }),
    children,
    /* @__PURE__ */ jsx62(HTMLComment, { text: "END NOINDEX" })
  ] });
};
var NoIndex_default = NoIndex;

// src/navigation/MainMenu/MainMenu.tsx
import { Fragment as Fragment6, jsx as jsx63, jsxs as jsxs25 } from "react/jsx-runtime";
var renderMainMenuLinkItem = (slideItem, active, onClick) => {
  switch (slideItem.item.templateName) {
    case "LinkItem":
      return /* @__PURE__ */ jsx63(MainMenuLinkItem_default, { fields: slideItem.item });
    case "LinkGroup":
      return /* @__PURE__ */ jsx63(
        MainMenuLinkGroup_default,
        {
          fields: slideItem.item,
          active,
          onClick
        }
      );
    case "LinkItemWithLargeImage":
      return /* @__PURE__ */ jsx63(
        MainMenuLinkItemWithLargeImage_default,
        {
          fields: slideItem.item
        }
      );
    case "LinkItemWithSmallImage":
      return /* @__PURE__ */ jsx63(
        MainMenuLinkItemWithSmallImage_default,
        {
          fields: slideItem.item
        }
      );
    default:
      return null;
  }
};
var MainMenu = (props) => {
  const {
    items,
    isOpen = false,
    onClose,
    onClick,
    peek,
    hint = false,
    variant = "default",
    className = "",
    ...restProps
  } = props;
  const { isSm } = useBreakpoints_default();
  const showBar = variant === "peek" && isSm;
  const [selectedSecondSlideItems, setSelectedSecondSlideItems] = useState14(null);
  const [selectedThirdSlideItems, setSelectedThirdSlideItems] = useState14(null);
  const { t } = useTranslation11("ui");
  return /* @__PURE__ */ jsx63("div", { className: `main-menu ${className}`, ...restProps, children: /* @__PURE__ */ jsxs25(
    ModalDrawer_default,
    {
      isOpen,
      onClose: () => {
        onClose();
        setSelectedSecondSlideItems(null);
        setSelectedThirdSlideItems(null);
      },
      position: "left",
      className: `main-menu__modal-drawer ${selectedSecondSlideItems ? "main-menu__modal-drawer_hide-close-button" : ""}`,
      children: [
        /* @__PURE__ */ jsx63(
          ModalDrawer_default.Slide,
          {
            className: "main-menu__slide",
            hint,
            showBar,
            peek,
            isOpen,
            unstyled: true,
            onClick,
            role: "menu",
            children: /* @__PURE__ */ jsx63(NoIndex_default, { children: items.map((firstSlideItem, index) => /* @__PURE__ */ jsx63(React16.Fragment, { children: firstSlideItem.item.templateName === "LinkGroup" ? renderMainMenuLinkItem(
              firstSlideItem,
              firstSlideItem.children === selectedSecondSlideItems,
              () => {
                setSelectedThirdSlideItems(null);
                setSelectedSecondSlideItems(firstSlideItem.children);
              }
            ) : renderMainMenuLinkItem(firstSlideItem) }, index)) })
          }
        ),
        /* @__PURE__ */ jsx63(
          ModalDrawer_default.Slide,
          {
            className: "main-menu__slide",
            isOpen: Boolean(selectedSecondSlideItems),
            unstyled: true,
            role: "menu",
            children: /* @__PURE__ */ jsxs25(NoIndex_default, { children: [
              selectedSecondSlideItems && /* @__PURE__ */ jsx63(Fragment6, { children: selectedSecondSlideItems.map((secondSlideItem, index) => /* @__PURE__ */ jsx63(React16.Fragment, { children: secondSlideItem.item.templateName === "LinkGroup" ? renderMainMenuLinkItem(
                secondSlideItem,
                secondSlideItem.children === selectedThirdSlideItems,
                () => {
                  setSelectedThirdSlideItems(secondSlideItem.children);
                }
              ) : renderMainMenuLinkItem(secondSlideItem) }, index)) }),
              /* @__PURE__ */ jsx63(
                "button",
                {
                  "aria-label": `${t("back-button")} Main menu slide`,
                  className: "main-menu__slide-back-button",
                  onClick: () => {
                    setSelectedSecondSlideItems(null);
                  },
                  children: /* @__PURE__ */ jsx63(ChevronLeftIcon_default, {})
                }
              )
            ] })
          }
        ),
        /* @__PURE__ */ jsx63(
          ModalDrawer_default.Slide,
          {
            className: "main-menu__slide",
            isOpen: Boolean(selectedThirdSlideItems),
            unstyled: true,
            role: "menu",
            children: /* @__PURE__ */ jsx63(NoIndex_default, { children: selectedThirdSlideItems && /* @__PURE__ */ jsxs25(Fragment6, { children: [
              selectedThirdSlideItems.map((thirdSlideItem, index) => /* @__PURE__ */ jsx63(React16.Fragment, { children: renderMainMenuLinkItem(thirdSlideItem) }, index)),
              /* @__PURE__ */ jsx63(
                "button",
                {
                  "aria-label": `${t("back-button")} Main menu slide`,
                  className: "main-menu__slide-back-button",
                  onClick: () => setSelectedThirdSlideItems(null),
                  children: /* @__PURE__ */ jsx63(ChevronLeftIcon_default, {})
                }
              )
            ] }) })
          }
        )
      ]
    }
  ) });
};
var MainMenu_default = MainMenu;

// src/actions/MainMenuButton/MainMenuButton.tsx
import { Fragment as Fragment7, jsx as jsx64, jsxs as jsxs26 } from "react/jsx-runtime";
var MainMenuButton = (props) => {
  const {
    showInitialHint = false,
    variant = "default",
    className = "",
    "aria-label": ariaLabel,
    ...restProps
  } = props;
  const [isMainMenuOpen, setIsMainMenuOpen] = useState15(false);
  const [isMouseProximity, setIsMouseProximity] = useState15(false);
  const { sitecoreContext } = useSitecoreContext4();
  const { navigation = { mainMenu: [] } } = sitecoreContext;
  const { mainMenu } = navigation;
  const { t } = useTranslation12("ui");
  return /* @__PURE__ */ jsxs26(Fragment7, { children: [
    /* @__PURE__ */ jsxs26(
      "button",
      {
        "aria-label": ariaLabel ? ariaLabel : `${t("opens")} main menu`,
        className: `main-menu-button ${className}`,
        name: "main-menu-button",
        onClick: () => {
          setIsMainMenuOpen(true);
        },
        ...restProps,
        children: [
          /* @__PURE__ */ jsx64(MenuIcon_default, {}),
          /* @__PURE__ */ jsx64("span", { className: "main-menu-button__text", children: "Menu" })
        ]
      }
    ),
    mainMenu && /* @__PURE__ */ jsx64(
      "div",
      {
        className: "main-menu__proximity",
        onMouseEnter: () => setIsMouseProximity(true),
        onMouseLeave: () => setIsMouseProximity(false),
        children: /* @__PURE__ */ jsx64(
          MainMenu_default,
          {
            onClick: (e) => {
              if (!isMainMenuOpen) {
                setIsMainMenuOpen(true);
                e.preventDefault();
              }
            },
            hint: showInitialHint,
            variant,
            peek: isMouseProximity,
            items: mainMenu,
            isOpen: isMainMenuOpen,
            onClose: () => {
              setIsMainMenuOpen(false);
              setIsMouseProximity(false);
            }
          }
        )
      }
    )
  ] });
};
var MainMenuButton_default = MainMenuButton;

// src/actions/ShareButtons/ShareButtons.tsx
import { useTranslation as useTranslation13 } from "react-i18next";
import { jsx as jsx65 } from "react/jsx-runtime";
var ShareButtons = (props) => {
  const { shareButtons, className = "", children, ...restProps } = props;
  const { t } = useTranslation13("ui");
  const handleShareX = () => {
    const url = window.location.href;
    const text = document.title;
    const xShareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      url
    )}&text=${encodeURIComponent(text)}`;
    window.open(xShareUrl, "_blank");
  };
  const handleShareFacebook = () => {
    const url = window.location.href;
    const facebookShareUrl = `https://www.facebook.com/sharer.php?u=${encodeURIComponent(
      url
    )}`;
    window.open(facebookShareUrl, "_blank");
  };
  const handleShareLinkedIn = () => {
    const url = window.location.href;
    const linkedInShareUrl = `https://www.linkedin.com/cws/share?url=${encodeURIComponent(
      url
    )}`;
    window.open(linkedInShareUrl, "_blank");
  };
  const getSocials = () => shareButtons.map((shareButton, index) => {
    switch (shareButton) {
      case "facebook":
        return /* @__PURE__ */ jsx65(
          "button",
          {
            "aria-label": `${t("share-page-on", { platform: "Facebook" })}`,
            title: `${t("share-page-on", { platform: "Facebook" })}`,
            className: "share-buttons__button",
            onClick: handleShareFacebook,
            children: /* @__PURE__ */ jsx65(FacebookIcon_default, {})
          },
          index
        );
      case "linkedin":
        return /* @__PURE__ */ jsx65(
          "button",
          {
            "aria-label": `${t("share-page-on", { platform: "LinkedIn" })}`,
            title: `${t("share-page-on", { platform: "LinkedIn" })}`,
            className: "share-buttons__button",
            onClick: handleShareLinkedIn,
            children: /* @__PURE__ */ jsx65(LinkedInIcon_default, {})
          },
          index
        );
      case "twitter":
      case "x":
        return /* @__PURE__ */ jsx65(
          "button",
          {
            "aria-label": `${t("share-page-on", { platform: "X" })}`,
            title: `${t("share-page-on", { platform: "X" })}`,
            className: "share-buttons__button",
            onClick: handleShareX,
            children: /* @__PURE__ */ jsx65(XIcon_default, {})
          },
          index
        );
      default:
        return null;
    }
  });
  return /* @__PURE__ */ jsx65("div", { className: `share-buttons ${className}`, ...restProps, children: getSocials() });
};
var ShareButtons_default = ShareButtons;

// src/actions/Socials/Socials.tsx
import { useTranslation as useTranslation14 } from "react-i18next";
import { jsx as jsx66, jsxs as jsxs27 } from "react/jsx-runtime";
var Socials = (props) => {
  const {
    youTubeUrl,
    instagramUrl,
    twitterUrl,
    xUrl,
    facebookUrl,
    linkedInUrl,
    className = "",
    ...restProps
  } = props;
  const { t } = useTranslation14("ui");
  return /* @__PURE__ */ jsxs27("div", { className: `socials ${className}`, ...restProps, children: [
    youTubeUrl && /* @__PURE__ */ jsx66(
      RoutableSitecoreLink_default,
      {
        "aria-label": `${t("link-to")} youtube account`,
        field: { href: youTubeUrl },
        className: "socials__link",
        children: /* @__PURE__ */ jsx66(YoutubeIcon_default, { className: "socials__link-icon" })
      }
    ),
    instagramUrl && /* @__PURE__ */ jsx66(
      RoutableSitecoreLink_default,
      {
        "aria-label": `${t("link-to")} instagram account`,
        field: { href: instagramUrl },
        className: "socials__link",
        children: /* @__PURE__ */ jsx66(InstagramIcon_default, { className: "socials__link-icon" })
      }
    ),
    (xUrl || twitterUrl) && /* @__PURE__ */ jsx66(
      RoutableSitecoreLink_default,
      {
        "aria-label": `${t("link-to")} X account`,
        field: { href: xUrl || twitterUrl },
        className: "socials__link",
        children: /* @__PURE__ */ jsx66(XIcon_default, { className: "socials__link-icon" })
      }
    ),
    facebookUrl && /* @__PURE__ */ jsx66(
      RoutableSitecoreLink_default,
      {
        "aria-label": `${t("link-to")} facebook account`,
        field: { href: facebookUrl },
        className: "socials__link",
        children: /* @__PURE__ */ jsx66(FacebookIcon_default, { className: "socials__link-icon" })
      }
    ),
    linkedInUrl && /* @__PURE__ */ jsx66(
      RoutableSitecoreLink_default,
      {
        "aria-label": `${t("link-to")} linkedin account`,
        field: { href: linkedInUrl },
        className: "socials__link",
        children: /* @__PURE__ */ jsx66(LinkedInIcon_default, { className: "socials__link-icon" })
      }
    )
  ] });
};
var Socials_default = Socials;

// src/layout/AccordionProvider/AccordionProvider.tsx
import { createContext, useContext as useContext2, useState as useState16 } from "react";
import { jsx as jsx67 } from "react/jsx-runtime";
var AccordionContext = createContext(null);
var useAccordion = () => {
  const context = useContext2(AccordionContext);
  if (!context) {
    throw new Error(
      "Error: useAccordion() requires a <AccordionProvider> parent"
    );
  }
  return context;
};
var AccordionProvider = (props) => {
  const { children, initialState = null } = props;
  const [activeItem, setActiveItem] = useState16(
    initialState
  );
  const contextValue = {
    activeItem,
    setActiveItem
  };
  return /* @__PURE__ */ jsx67(AccordionContext.Provider, { value: contextValue, children: typeof children === "function" ? children(contextValue) : children });
};
var AccordionProvider_default = AccordionProvider;

// src/blocks/AccordionBlock/Accordion.tsx
import { jsx as jsx68 } from "react/jsx-runtime";
var Accordion = (props) => {
  const { className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsx68(AccordionProvider_default, { children: /* @__PURE__ */ jsx68("div", { className: `accordion ${className}`, ...restProps, children }) });
};
var Accordion_default = Accordion;

// src/blocks/AccordionBlock/AccordionItem.tsx
import { useRef as useRef8 } from "react";
import {
  Text as Text8,
  useSitecoreContext as useSitecoreContext12
} from "@sitecore-jss/sitecore-jss-react";

// src/layout/Collapsible/CollapsibleProvider.tsx
import { createContext as createContext2, useContext as useContext3, useState as useState17 } from "react";
import { jsx as jsx69 } from "react/jsx-runtime";
var CollapsibleContext = createContext2(null);
var useCollapsible = () => {
  const context = useContext3(CollapsibleContext);
  if (!context) {
    throw new Error(
      "Error: useCollapsible() requires a <CollapsibleProvider> parent"
    );
  }
  return context;
};
var CollapsibleProvider = (props) => {
  const { children, initialIsOpen, onOpenChange } = props;
  const [isOpen, setIsOpen] = useState17(Boolean(initialIsOpen));
  if (typeof initialIsOpen !== "undefined" && typeof props.isOpen !== "undefined") {
    console.warn(
      "CollapsibleProvider: both initialIsOpen and isOpen provided - initialIsOpen will not have any effect"
    );
  }
  const contextValue = {
    isOpen: typeof props.isOpen !== "undefined" ? props.isOpen : isOpen,
    setIsOpen: (value) => {
      if (typeof props.isOpen !== "undefined" && typeof onOpenChange !== "undefined") {
        onOpenChange(value);
      } else if (typeof setIsOpen === "function") {
        setIsOpen(value);
      }
    }
  };
  return /* @__PURE__ */ jsx69(CollapsibleContext.Provider, { value: contextValue, children: typeof children === "function" ? children(contextValue) : children });
};
var CollapsibleProvider_default = CollapsibleProvider;

// src/typography/RichText/RichText.tsx
import {
  RichText as SitecoreRichText
} from "@sitecore-jss/sitecore-jss-react";

// src/typography/RichText/improveRichText.tsx
import { useEffect as useEffect12, useRef as useRef3 } from "react";
import { useNavigate as useNavigate2 } from "react-router-dom";
var improveRichText = (options) => {
  const { enabled } = options;
  const ref = useRef3(null);
  const navigate = useNavigate2();
  const handleRouteChange = (event) => {
    if (event.target instanceof HTMLAnchorElement) {
      event.preventDefault();
      navigate(event.target.pathname);
    }
  };
  useEffect12(() => {
    if (enabled && ref.current) {
      const internalLinks = ref.current.querySelectorAll('a[href^="/"]');
      internalLinks.forEach((link) => {
        link.removeEventListener("click", handleRouteChange, false);
        link.addEventListener("click", handleRouteChange, false);
      });
      const tables = ref.current.querySelectorAll("table");
      tables.forEach((table) => {
        putElementInWrapper(table, "table-wrapper");
      });
    }
    return () => {
    };
  }, [enabled, ref]);
  return ref;
};

// src/typography/RichText/RichText.tsx
import { jsx as jsx70 } from "react/jsx-runtime";
var RichText = (props) => {
  const { field, className, ...restProps } = props;
  const hasText = Boolean(field == null ? void 0 : field.value);
  const isEditing = Boolean(field == null ? void 0 : field.editable);
  const ref = improveRichText({
    enabled: hasText && !isEditing
  });
  return /* @__PURE__ */ jsx70(
    SitecoreRichText,
    {
      ref,
      field,
      className,
      ...restProps
    }
  );
};
var RichText_default = RichText;

// src/layout/ButtonContainer/ButtonContainer.tsx
import { Placeholder } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx71 } from "react/jsx-runtime";
var ButtonContainer = (props) => {
  var _a;
  const { rendering, placeholderName = "container-buttons", className = "", children, ...restProps } = props;
  if (!(rendering == null ? void 0 : rendering.placeholders) || !((_a = rendering == null ? void 0 : rendering.placeholders[placeholderName]) == null ? void 0 : _a.length))
    return null;
  return /* @__PURE__ */ jsx71("div", { className: `btn-container ${className}`, ...restProps, children: /* @__PURE__ */ jsx71(Placeholder, { name: placeholderName, rendering }) });
};
var ButtonContainer_default = ButtonContainer;

// src/layout/Carousel/Carousel.tsx
import { AnimatePresence as AnimatePresence3, motion as motion4 } from "framer-motion";

// src/layout/Carousel/CarouselProvider.tsx
import React20, { createContext as createContext3, useContext as useContext4, useState as useState18 } from "react";
import { jsx as jsx72 } from "react/jsx-runtime";
var CarouselContext = createContext3(null);
var useCarousel = () => {
  const context = useContext4(CarouselContext);
  if (!context) {
    throw new Error(
      "Error: useCarousel() requires a <CarouselProvider> parent"
    );
  }
  return context;
};
var CarouselProvider = (props) => {
  const { children, onItemChange } = props;
  const [current, setCurrent] = useState18(0);
  const [direction, setDirection] = useState18(
    1 /* RIGHT */
  );
  const total = React20.Children.count(props.items);
  const items = React20.Children.toArray(props.items);
  const goTo = (step) => {
    setCurrent((current2) => {
      if (step > current2) {
        setDirection(1 /* RIGHT */);
      } else {
        setDirection(-1 /* LEFT */);
      }
      return (step + total) % total;
    });
  };
  const hasNext = current < total - 1;
  const goNext = () => {
    setDirection(1 /* RIGHT */);
    setCurrent((current + 1) % total);
    if (onItemChange) {
      onItemChange((current + 1) % total);
    }
  };
  const hasPrev = current > 0;
  const goPrev = () => {
    setDirection(-1 /* LEFT */);
    setCurrent((current - 1 + total) % total);
    if (onItemChange) {
      onItemChange((current - 1 + total) % total);
    }
  };
  const contextValue = {
    current,
    total,
    items,
    goTo,
    hasNext,
    goNext,
    hasPrev,
    goPrev,
    direction
  };
  return /* @__PURE__ */ jsx72(CarouselContext.Provider, { value: contextValue, children: typeof children === "function" ? children(
    contextValue
  ) : children });
};
var CarouselProvider_default = CarouselProvider;

// src/layout/Carousel/Carousel.tsx
import { useTranslation as useTranslation15 } from "react-i18next";
import { jsx as jsx73, jsxs as jsxs28 } from "react/jsx-runtime";
var CarouselItem = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx73("div", { className: `carousel__item ${className}`, ...restProps, children });
};
var CarouselNavigation = (props) => {
  const { variant } = props;
  const { hasNext, goNext, hasPrev, goPrev } = useCarousel();
  const { t } = useTranslation15("ui");
  return /* @__PURE__ */ jsxs28("div", { className: `carousel__navigation container_tightest ${variant === "default" ? "carousel__navigation-default" : "carousel__navigation-bottom"}`, children: [
    /* @__PURE__ */ jsx73(
      "button",
      {
        className: `carousel__navigation-button ${variant === "default" ? "carousel__button-previous_default" : "carousel__button-previous_bottom"}`,
        "aria-label": `${t("go-to-previous-carousel-item")}`,
        disabled: !hasPrev,
        onClick: goPrev,
        children: /* @__PURE__ */ jsx73(ChevronLeftIcon_default, {})
      }
    ),
    /* @__PURE__ */ jsx73(
      "button",
      {
        className: `carousel__navigation-button ${variant === "default" ? "carousel__button-next_default" : "carousel__button-next_bottom"}`,
        disabled: !hasNext,
        "aria-label": `${t("go-to-next-carousel-item")}`,
        onClick: goNext,
        children: /* @__PURE__ */ jsx73(ChevronRightIcon_default, {})
      }
    )
  ] });
};
var CarouselPagination = (props) => {
  const { className = "", ...restProps } = props;
  const { current, items, goTo } = useCarousel();
  const { t } = useTranslation15("ui");
  return /* @__PURE__ */ jsx73("div", { className: `carousel__pagination ${className}`, ...restProps, children: items.map((_item, index) => /* @__PURE__ */ jsx73(
    "div",
    {
      onClick: () => {
        goTo(index);
      },
      className: `carousel__pagination-button ${index === current ? "carousel__pagination-button_active" : ""}`
    },
    index
  )) });
};
var variants = {
  enter: (direction) => {
    return {
      x: direction > 0 ? 600 : -600,
      opacity: 0
    };
  },
  center: (direction) => {
    return {
      zIndex: 1,
      x: 0,
      opacity: 1,
      transition: {
        x: {
          type: "spring",
          stiffness: 200,
          damping: 40,
          from: direction > 0 ? 600 : -600
        },
        opacity: { duration: 0.4 }
      }
    };
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 600 : -600,
      opacity: 0
    };
  }
};
var Carousel = (props) => {
  const { variant = "default", className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsx73(CarouselProvider_default, { items: children, children: ({ current, items, direction }) => /* @__PURE__ */ jsxs28(
    "div",
    {
      className: `carousel ${variant === "default" ? "carousel_default" : "carousel_navigation-bottom"} ${className}`,
      ...restProps,
      children: [
        /* @__PURE__ */ jsx73("div", { className: "carousel__items-wrapper", children: /* @__PURE__ */ jsx73(AnimatePresence3, { initial: false, custom: direction, children: /* @__PURE__ */ jsx73(
          motion4.div,
          {
            className: "carousel__items-motion-wrapper",
            initial: "enter",
            animate: "center",
            exit: "exit",
            variants,
            transition: {
              x: { type: "spring", stiffness: 200, damping: 40 },
              opacity: { duration: 0.4 }
            },
            custom: direction,
            children: items[current]
          },
          current
        ) }) }),
        /* @__PURE__ */ jsx73(Carousel.Pagination, {}),
        /* @__PURE__ */ jsx73(Carousel.Navigation, { variant })
      ]
    }
  ) });
};
Carousel.Navigation = CarouselNavigation;
Carousel.Pagination = CarouselPagination;
Carousel.Item = CarouselItem;
var Carousel_default = Carousel;

// src/layout/Carousel/CarouselCardItem.tsx
import { Image, Text as Text3 } from "@sitecore-jss/sitecore-jss-react";

// src/surfaces-and-overlays/Card/Card.tsx
import { jsx as jsx74 } from "react/jsx-runtime";
var CardHeader = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx74("div", { className: `card__header ${className}`, ...restProps, children });
};
var CardBody = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx74("div", { className: `card__body ${className}`, ...restProps, children });
};
var CardFooter = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx74("div", { className: `card__footer ${className}`, ...restProps, children });
};
var Card = (props) => {
  const { className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsx74("div", { className: `card ${className}`, ...restProps, children });
};
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
var Card_default = Card;

// src/messages/NullDatasourceMessage.tsx
import { jsx as jsx75 } from "react/jsx-runtime";
var NullDatasourceMessage = () => {
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  if (!isEditingInSitecore)
    return null;
  return /* @__PURE__ */ jsx75("div", { children: "Datasource is not set or is not published" });
};
var NullDatasourceMessage_default = NullDatasourceMessage;

// src/layout/Carousel/CarouselCardItem.tsx
import { jsx as jsx76, jsxs as jsxs29 } from "react/jsx-runtime";
var CarouselCardItem = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx76(NullDatasourceMessage_default, {});
  const { isImageFieldValid } = useIsFieldValid_default();
  return /* @__PURE__ */ jsx76(
    RoutableSitecoreLink_default,
    {
      field: fields.LinkTarget,
      editable: false,
      className: "h-full carousel-card__wrapper-link",
      children: /* @__PURE__ */ jsxs29(
        Card_default,
        {
          className: "carousel-card",
          ...restProps,
          children: [
            /* @__PURE__ */ jsx76(Card_default.Header, { className: "carousel-card__header", children: /* @__PURE__ */ jsx76(
              Image,
              {
                className: "carousel-card__header-image",
                field: fields.Image,
                srcSet: [{ mw: 416 }, { mw: 1280 }],
                sizes: "(max-width: 640px) 416px, 1280px"
              }
            ) }),
            /* @__PURE__ */ jsxs29(Card_default.Body, { className: "carousel-card__body", children: [
              isImageFieldValid(fields.Logo) && /* @__PURE__ */ jsx76(
                Image,
                {
                  className: "carousel-card__logo",
                  field: fields.Logo,
                  srcSet: [{ mw: 192 }],
                  sizes: "192px"
                }
              ),
              /* @__PURE__ */ jsxs29("div", { className: "carousel-card__content-container", children: [
                /* @__PURE__ */ jsx76(
                  Text3,
                  {
                    className: "carousel-card__title",
                    tag: "div",
                    field: fields.Title
                  }
                ),
                /* @__PURE__ */ jsxs29("div", { className: "carousel-card__link", children: [
                  /* @__PURE__ */ jsx76(
                    Text3,
                    {
                      field: fields.SubTitle,
                      tag: "span",
                      className: "carousel-card__subtitle"
                    }
                  ),
                  /* @__PURE__ */ jsx76(ArrowRightIcon_default, { className: "carousel-card__icon" })
                ] })
              ] })
            ] })
          ]
        }
      )
    }
  );
};
var CarouselCardItem_default = CarouselCardItem;

// src/layout/Carousel/CarouselImageItem.tsx
import { Image as Image2 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx77 } from "react/jsx-runtime";
var CarouselImageItem = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx77(NullDatasourceMessage_default, {});
  const { isImageFieldValid } = useIsFieldValid_default();
  return /* @__PURE__ */ jsx77("div", { className: `carousel__image-item ${className}`, ...restProps, children: isImageFieldValid(fields.Image) && /* @__PURE__ */ jsx77("div", { className: "carousel__image-item-image-wrapper", children: /* @__PURE__ */ jsx77(
    Image2,
    {
      draggable: false,
      className: "carousel__image-item-image",
      field: fields.Image,
      srcSet: [{ mw: 416 }, { mw: 1280 }],
      sizes: "(max-width: 640px) 416px, 1280px"
    }
  ) }) });
};
var CarouselImageItem_default = CarouselImageItem;

// src/players/VideoPlayer/VideoPlayer.tsx
import ReactPlayer from "react-player";
import { useTranslation as useTranslation16 } from "react-i18next";
import { jsx as jsx78 } from "react/jsx-runtime";
var Player = ReactPlayer.default;
var VideoPlayer = (props) => {
  const { url, thumbnailUrl, className = "" } = props;
  const isClientSide = useIsClientSide_default();
  const { t } = useTranslation16();
  return isClientSide ? /* @__PURE__ */ jsx78(
    Player,
    {
      className: `video-player ${className}`,
      url,
      playing: true,
      controls: true,
      playsinline: true,
      height: "100%",
      width: "100%",
      playIcon: /* @__PURE__ */ jsx78("button", { className: "video-player__button", "aria-label": t("play-video") || "Play video", children: /* @__PURE__ */ jsx78(PlayIcon_default, { className: "w-6 h-6" }) }),
      light: thumbnailUrl
    }
  ) : null;
};
var VideoPlayer_default = VideoPlayer;

// src/layout/Carousel/CarouselVideoItem.tsx
import { getFieldValue as getFieldValue2 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx79 } from "react/jsx-runtime";
var CarouselVideoItem = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx79(NullDatasourceMessage_default, {});
  const youtubeUrl = getFieldValue2(fields, "YouTubeUrl", null);
  const vimeoUrl = getFieldValue2(fields, "VimeoUrl", null);
  const youtubeUrlExists = youtubeUrl && (youtubeUrl == null ? void 0 : youtubeUrl.length) > 0;
  const vimeoUrlExists = vimeoUrl && (vimeoUrl == null ? void 0 : vimeoUrl.length) > 0;
  const url = youtubeUrlExists ? youtubeUrl : vimeoUrlExists ? vimeoUrl : "";
  const thumbnail = getImageUrl(fields.PreviewImage);
  return /* @__PURE__ */ jsx79(
    VideoPlayer_default,
    {
      url,
      thumbnailUrl: thumbnail,
      className,
      ...restProps
    }
  );
};
var CarouselVideoItem_default = CarouselVideoItem;

// src/layout/Collapsible/Collapsible.tsx
import { jsx as jsx80, jsxs as jsxs30 } from "react/jsx-runtime";
var CollapsibleHeader = (props) => {
  const { className = "", children, ...restProps } = props;
  const { isOpen, setIsOpen } = useCollapsible();
  return /* @__PURE__ */ jsxs30(
    "div",
    {
      className: `collapsible__header ${className}`,
      onClick: () => {
        setIsOpen(!isOpen);
      },
      ...restProps,
      children: [
        !isOpen ? /* @__PURE__ */ jsx80(ChevronDownIcon_default, { className: "collapsible__header-icon" }) : /* @__PURE__ */ jsx80(ChevronUpIcon_default, { className: "collapsible__header-icon" }),
        /* @__PURE__ */ jsx80("div", { className: "collapsible__header-content", children })
      ]
    }
  );
};
var CollapsibleBody = (props) => {
  const { className = "", children, ...restProps } = props;
  const { isOpen } = useCollapsible();
  return /* @__PURE__ */ jsx80(
    "div",
    {
      className: `collapsible__body ${isOpen ? "" : "collapsible__body_closed"} ${className}`,
      ...restProps,
      children
    }
  );
};
var Collapsible = (props) => {
  const {
    children,
    initialIsOpen,
    isOpen,
    onOpenChange,
    className = "",
    ...restProps
  } = props;
  return /* @__PURE__ */ jsx80(
    CollapsibleProvider_default,
    {
      initialIsOpen,
      isOpen,
      onOpenChange,
      children: /* @__PURE__ */ jsx80("div", { className: `collapsible ${className}`, ...restProps, children })
    }
  );
};
Collapsible.Header = CollapsibleHeader;
Collapsible.Body = CollapsibleBody;
var Collapsible_default = Collapsible;

// src/layout/DragSlider/DragSlider.tsx
import React21, { useEffect as useEffect13, useRef as useRef4, useState as useState19 } from "react";
import { motion as motion5, useAnimation } from "framer-motion";
import { Fragment as Fragment8, jsx as jsx81, jsxs as jsxs31 } from "react/jsx-runtime";
var DragSlider = (props) => {
  const {
    itemsToSlide = 3,
    showButtons = true,
    children,
    className = "",
    ...restProps
  } = props;
  const motionRef = useRef4(null);
  const [sliderConstraints, setSliderConstraints] = useState19(0);
  const calcSliderConstraints = useDebouncedCallback_default(() => {
    if (!motionRef.current)
      return;
    const childrenWidth = motionRef.current.scrollWidth;
    const sliderWidth = motionRef.current.clientWidth;
    setSliderConstraints(childrenWidth - sliderWidth);
  });
  useEffect13(() => {
    if (!motionRef.current)
      return;
    calcSliderConstraints();
    setItemSpace(
      motionRef.current.scrollWidth / React21.Children.toArray(children).length
    );
    window.addEventListener("resize", calcSliderConstraints);
    return () => {
      window.removeEventListener("resize", calcSliderConstraints);
    };
  }, [motionRef]);
  const xPos = useRef4(0);
  const controls = useAnimation();
  const updatePosition = (latest) => {
    xPos.current = latest.x;
    setSliderPosition(
      latest.x >= -1 ? "start" : latest.x <= -1 * sliderConstraints ? "end" : "middle"
    );
  };
  const [itemSpace, setItemSpace] = useState19(0);
  const [sliderPosition, setSliderPosition] = useState19("start");
  const goLeft = () => {
    const newXPosition = xPos.current + itemSpace * itemsToSlide;
    controls.start({
      x: newXPosition > 0 ? 0 : newXPosition,
      transition: {
        ease: "easeOut",
        duration: 0.5
      }
    });
  };
  const goRight = () => {
    const newXPosition = xPos.current - itemSpace * itemsToSlide;
    controls.start({
      x: newXPosition < -1 * sliderConstraints ? -1 * sliderConstraints : newXPosition,
      transition: {
        ease: "easeOut",
        duration: 0.5
      }
    });
  };
  return /* @__PURE__ */ jsxs31(Fragment8, { children: [
    /* @__PURE__ */ jsx81(
      motion5.div,
      {
        ref: motionRef,
        className: "drag-slider",
        drag: "x",
        dragConstraints: {
          left: -1 * sliderConstraints,
          right: 0
        },
        animate: controls,
        onUpdate: updatePosition,
        ...restProps,
        children: /* @__PURE__ */ jsx81("div", { className: `${className}`, children })
      }
    ),
    showButtons && /* @__PURE__ */ jsxs31("div", { className: "drag-slider__buttons", children: [
      /* @__PURE__ */ jsx81("button", { "aria-label": "Drag slider go left button", onClick: goLeft, children: /* @__PURE__ */ jsx81(
        ArrowLeftIcon_default,
        {
          className: `drag-slider__button-icon ${sliderPosition === "start" ? "drag-slider__button-icon_disabled" : ""}`
        }
      ) }),
      /* @__PURE__ */ jsx81("button", { "aria-label": "Drag slider go right button", onClick: goRight, children: /* @__PURE__ */ jsx81(
        ArrowRightIcon_default,
        {
          className: `drag-slider__button-icon ${sliderPosition === "end" ? "drag-slider__button-icon_disabled" : ""}`
        }
      ) })
    ] })
  ] });
};
var DragSlider_default = DragSlider;

// src/layout/InsuranceSelectorItem/InsuranceSelectorItem.tsx
import { Image as Image3, Text as Text4 } from "@sitecore-jss/sitecore-jss-react";

// src/layout/MediaObject/MediaObject.tsx
import { jsx as jsx82, jsxs as jsxs32 } from "react/jsx-runtime";
var MediaObject = (props) => {
  const { media, children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsxs32("div", { className: `media-object ${className}`, ...restProps, children: [
    /* @__PURE__ */ jsx82("div", { className: "media-object__media", children: media }),
    /* @__PURE__ */ jsx82("div", { className: "media-object__body", children })
  ] });
};
var MediaObject_default = MediaObject;

// src/layout/InsuranceSelectorItem/InsuranceSelectorItem.tsx
import { Fragment as Fragment9, jsx as jsx83, jsxs as jsxs33 } from "react/jsx-runtime";
var InsuranceSelectorItem = (props) => {
  const { item, onClick, className = "", ...restProps } = props;
  const { isImageFieldValid } = useIsFieldValid_default();
  const renderItem = (item2) => /* @__PURE__ */ jsx83(
    MediaObject_default,
    {
      className: "insurance-selector-block__item-media-object",
      media: isImageFieldValid(item2.Image) ? /* @__PURE__ */ jsx83("div", { className: "insurance-selector-block__item-media-wrapper", children: /* @__PURE__ */ jsx83(
        Image3,
        {
          className: "insurance-selector-block__item-media",
          field: item2.Image,
          srcSet: [{ mw: 192 }],
          sizes: "192px"
        }
      ) }) : /* @__PURE__ */ jsx83(Fragment9, {}),
      children: /* @__PURE__ */ jsxs33("div", { className: "insurance-selector-block__item-body", children: [
        /* @__PURE__ */ jsxs33("div", { children: [
          /* @__PURE__ */ jsx83(
            Text4,
            {
              tag: "h3",
              className: "insurance-selector-block__item-title",
              field: item2.Title
            }
          ),
          /* @__PURE__ */ jsx83(
            Text4,
            {
              tag: "p",
              className: "insurance-selector-block__item-subtitle",
              field: item2.Subtitle
            }
          )
        ] }),
        /* @__PURE__ */ jsx83(ChevronRightIcon_default, { className: "insurance-selector-block__item-icon" })
      ] })
    }
  );
  const isBrandLink = item.hasOwnProperty("LinkTarget");
  return /* @__PURE__ */ jsx83(
    "div",
    {
      className: `insurance-selector-block__item ${className}`,
      ...restProps,
      children: isBrandLink ? /* @__PURE__ */ jsx83(
        RoutableSitecoreLink_default,
        {
          className: "insurance-selector-block__item-link",
          field: item.LinkTarget,
          editable: false,
          children: renderItem(item)
        }
      ) : /* @__PURE__ */ jsx83("button", { className: "insurance-selector-block__item-group", onClick, children: renderItem(item) })
    }
  );
};
var InsuranceSelectorItem_default = InsuranceSelectorItem;

// src/media/Decorations/Decorations.tsx
import { Fragment as Fragment10, jsx as jsx84, jsxs as jsxs34 } from "react/jsx-runtime";
var Decorations = (props) => {
  const { className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs34(Fragment10, { children: [
    /* @__PURE__ */ jsx84("div", { className: `decoration-first ${className}`, ...restProps }),
    /* @__PURE__ */ jsx84("div", { className: `decoration-second ${className}`, ...restProps })
  ] });
};
var Decorations_default = Decorations;

// src/layout/MediaBlockWrapper/MediaBlockWrapper.tsx
import { jsx as jsx85, jsxs as jsxs35 } from "react/jsx-runtime";
var getVariantClassName = (variant) => {
  switch (variant) {
    case "white":
      return "media-block-wrapper_white";
    case "light":
      return "media-block-wrapper_light";
    case "dark":
      return "media-block-wrapper_dark";
    default:
      return "";
  }
};
var MediaBlockWrapper = (props) => {
  const {
    variant,
    className = "",
    showDecorations = false,
    children,
    ...restProps
  } = props;
  return /* @__PURE__ */ jsxs35(
    "div",
    {
      className: `media-block-wrapper ${getVariantClassName(
        variant
      )} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx85(Decorations_default, {}),
        children
      ]
    }
  );
};
var MediaBlockWrapper_default = MediaBlockWrapper;

// src/layout/DragSlider/NonDraggableElement.tsx
import React27, { useRef as useRef5 } from "react";

// src/navigation/Breadcrumbs/Breadcrumbs.tsx
import React22 from "react";
import { useNavigate as useNavigate3 } from "react-router-dom";
import { useTranslation as useTranslation17 } from "react-i18next";
import { useSitecoreContext as useSitecoreContext5 } from "@sitecore-jss/sitecore-jss-react";
import { Fragment as Fragment11, jsx as jsx86, jsxs as jsxs36 } from "react/jsx-runtime";
var Breadcrumbs = (props) => {
  var _a;
  const { items, className = "", children, ...restProps } = props;
  const navigate = useNavigate3();
  const { t } = useTranslation17("ui");
  const { sitecoreContext } = useSitecoreContext5();
  const siteUrl = (_a = sitecoreContext == null ? void 0 : sitecoreContext.currentSite) == null ? void 0 : _a.homepageUrl;
  const breadcrumbItems = items || [];
  if (!breadcrumbItems.length)
    return null;
  return /* @__PURE__ */ jsx86(Fragment11, { children: /* @__PURE__ */ jsxs36(NoIndex_default, { children: [
    /* @__PURE__ */ jsxs36(
      "div",
      {
        className: `breadcrumbs breadcrumbs_mobile subtext ${className} noindex`,
        ...restProps,
        children: [
          /* @__PURE__ */ jsx86(ArrowLeftIcon_default, { className: "breadcrumbs__separator" }),
          /* @__PURE__ */ jsx86(
            "button",
            {
              "aria-label": `${t("back button")} ${t("previous page")}`,
              onClick: () => navigate(-1),
              className: "breadcrumbs__back-button",
              children: t("previous page")
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx86(
      "div",
      {
        className: `breadcrumbs breadcrumbs_desktop subtext ${className} noindex container_normal`,
        ...restProps,
        children: breadcrumbItems.map((breadcrumbItem, index) => {
          return breadcrumbItems.length - 1 > index ? /* @__PURE__ */ jsxs36(React22.Fragment, { children: [
            /* @__PURE__ */ jsx86(
              RoutableSitecoreLink_default,
              {
                field: { href: breadcrumbItem.link },
                className: "breadcrumbs__link",
                children: breadcrumbItem.name
              }
            ),
            /* @__PURE__ */ jsx86(ChevronRightIcon_default, { className: "breadcrumbs__separator" })
          ] }, index) : /* @__PURE__ */ jsx86(React22.Fragment, { children: /* @__PURE__ */ jsx86(
            "div",
            {
              className: "breadcrumbs__link",
              children: breadcrumbItem.name
            }
          ) }, index);
        })
      }
    )
  ] }) });
};
var Breadcrumbs_default = Breadcrumbs;

// src/navigation/FooterMenu/FooterLinkItem.tsx
import { jsx as jsx87 } from "react/jsx-runtime";
var FooterLinkItem = (props) => {
  const { fields, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx87(
    RoutableSitecoreLink_default,
    {
      field: { href: fields.link },
      className: `footer-menu__link-item ${className}`,
      target: fields.externalLinkTarget,
      "aria-label": fields.title,
      ...restProps,
      children: /* @__PURE__ */ jsx87("span", { children: fields.title })
    }
  );
};
var FooterLinkItem_default = FooterLinkItem;

// src/navigation/FooterLinks/FooterLinks.tsx
import { useSitecoreContext as useSitecoreContext6 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx88, jsxs as jsxs37 } from "react/jsx-runtime";
var FooterLinks = (props) => {
  const { links = [], className = "", ...restProps } = props;
  const { sitecoreContext } = useSitecoreContext6();
  const siteSettings = sitecoreContext == null ? void 0 : sitecoreContext.siteSettings;
  const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
  return /* @__PURE__ */ jsx88("div", { className: `footer-links ${className}`, ...restProps, children: /* @__PURE__ */ jsx88("div", { className: "container_normal", children: /* @__PURE__ */ jsxs37("div", { className: "footer-links__text", children: [
    links && links.map((link, index) => /* @__PURE__ */ jsx88(FooterLinkItem_default, { fields: link.item }, index)),
    /* @__PURE__ */ jsx88("div", { className: "footer-links__date", children: `\xA9 ${currentYear} ${(siteSettings == null ? void 0 : siteSettings.footerCopyrightText) || "Achmea"}` })
  ] }) }) });
};
var FooterLinks_default = FooterLinks;

// src/navigation/FooterMenu/FooterColumn.tsx
import { jsx as jsx89, jsxs as jsxs38 } from "react/jsx-runtime";
var FooterColumn = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs38("div", { className: `footer-menu__column ${className}`, ...restProps, children: [
    /* @__PURE__ */ jsx89("h2", { className: "heading-6", children: fields.title }),
    /* @__PURE__ */ jsx89("div", { className: "footer-menu__column-text", children })
  ] });
};
var FooterColumn_default = FooterColumn;

// src/navigation/FooterMenu/FooterMenu.tsx
import { useSitecoreContext as useSitecoreContext7 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx90, jsxs as jsxs39 } from "react/jsx-runtime";
var renderFooterLinkItems = (children) => children.map((child, index) => /* @__PURE__ */ jsx90(
  FooterLinkItem_default,
  {
    fields: child.item
  },
  index
));
var FooterMenu = (props) => {
  var _a;
  const { columns = [], className = "", ...restProps } = props;
  const { sitecoreContext } = useSitecoreContext7();
  const siteSettings = sitecoreContext == null ? void 0 : sitecoreContext.siteSettings;
  return /* @__PURE__ */ jsx90("div", { className: `footer-menu ${className}`, ...restProps, children: /* @__PURE__ */ jsxs39("div", { className: "container_normal", children: [
    /* @__PURE__ */ jsxs39("div", { className: "footer-menu__logo-and-socials", children: [
      ((_a = siteSettings == null ? void 0 : siteSettings.footerLogo) == null ? void 0 : _a.url) && /* @__PURE__ */ jsx90(
        "img",
        {
          className: "footer-menu__logo",
          src: siteSettings.footerLogo.url,
          alt: siteSettings.footerLogo.alt
        }
      ),
      siteSettings && /* @__PURE__ */ jsx90(
        Socials_default,
        {
          youTubeUrl: siteSettings.youTubeUrl,
          instagramUrl: siteSettings.instagramUrl,
          xUrl: siteSettings.xUrl,
          facebookUrl: siteSettings.facebookUrl,
          linkedInUrl: siteSettings.linkedInUrl
        }
      )
    ] }),
    /* @__PURE__ */ jsx90("div", { className: "footer-menu__columns", children: columns && columns.map((column, index) => /* @__PURE__ */ jsx90(
      FooterColumn_default,
      {
        fields: column.item,
        children: column.children && renderFooterLinkItems(column.children)
      },
      index
    )) })
  ] }) });
};
var FooterMenu_default = FooterMenu;

// src/navigation/Footer/Footer.tsx
import { useSitecoreContext as useSitecoreContext8 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx91, jsxs as jsxs40 } from "react/jsx-runtime";
var Footer = (props) => {
  const { className = "", ...restProps } = props;
  const { sitecoreContext } = useSitecoreContext8();
  const { navigation = { footerMenu: [], footerLinks: [] } } = sitecoreContext;
  const { footerMenu, footerLinks } = navigation;
  return /* @__PURE__ */ jsx91("footer", { className: `footer ${className} noindex`, ...restProps, children: /* @__PURE__ */ jsxs40(NoIndex_default, { children: [
    footerMenu && /* @__PURE__ */ jsx91(FooterMenu_default, { columns: footerMenu }),
    footerLinks && /* @__PURE__ */ jsx91(FooterLinks_default, { links: footerLinks })
  ] }) });
};
var Footer_default = Footer;

// src/navigation/Header/Header.tsx
import { jsx as jsx92, jsxs as jsxs41 } from "react/jsx-runtime";
var getHeaderVariant = (variant) => {
  switch (variant) {
    case "white":
      return "header_white";
    case "transparent":
      return "header_transparent";
    default:
      return "";
  }
};
var Header = (props) => {
  const {
    variant = "white",
    startArea,
    centerArea,
    endArea,
    className = "",
    ...restProps
  } = props;
  return /* @__PURE__ */ jsx92(
    "nav",
    {
      className: `header ${getHeaderVariant(variant)} ${className} noindex`,
      ...restProps,
      children: /* @__PURE__ */ jsxs41("div", { className: "container_normal", children: [
        /* @__PURE__ */ jsx92("div", { className: "header__start", children: startArea }),
        /* @__PURE__ */ jsx92("div", { className: "header__center", children: centerArea }),
        /* @__PURE__ */ jsx92("div", { className: "header__end", children: endArea }),
        /* @__PURE__ */ jsx92("div", { className: "header__underline" })
      ] })
    }
  );
};
var Header_default = Header;

// src/navigation/HeroLinkContainer/HeroLinkContainer.tsx
import { Placeholder as Placeholder2 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx93 } from "react/jsx-runtime";
var HeroLinkContainer = (props) => {
  const { rendering, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx93("div", { className: `hero-link-container ${className}`, ...restProps, children: /* @__PURE__ */ jsx93(Placeholder2, { name: "container-hero-links", rendering }) });
};
var HeroLinkContainer_default = HeroLinkContainer;

// src/navigation/HeroLinkContainer/HeroLinkItem.tsx
import { Text as Text5 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx94, jsxs as jsxs42 } from "react/jsx-runtime";
var HeroLinkItem = (props) => {
  const { fields, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsxs42(
    RoutableSitecoreLink_default,
    {
      className: `hero-link__item ${className}`,
      field: fields.LinkTarget,
      editable: false,
      ...restProps,
      children: [
        /* @__PURE__ */ jsx94(
          Text5,
          {
            tag: "span",
            className: "hero-link__item__text",
            field: fields.LinkText
          }
        ),
        /* @__PURE__ */ jsx94(ArrowRightIcon_default, { className: "hero-link__item__icon" })
      ]
    }
  );
};
var HeroLinkItem_default = HeroLinkItem;

// src/navigation/PreHeaderMenu/PreHeaderMenu.tsx
import React23 from "react";
import { useSitecoreContext as useSitecoreContext9 } from "@sitecore-jss/sitecore-jss-react";

// src/navigation/PreHeaderMenu/PreHeaderMenuItem.tsx
import { jsx as jsx95 } from "react/jsx-runtime";
var PreHeaderMenuItem = (props) => {
  const { fields, ...restProps } = props;
  return /* @__PURE__ */ jsx95(
    RoutableSitecoreLink_default,
    {
      field: {
        href: fields.link,
        target: fields.linkTarget
      },
      className: `pre-header-menu__link-item ${fields.isActive ? "active" : ""}`,
      ...restProps,
      children: /* @__PURE__ */ jsx95("span", { className: "pre-header-menu__link-item-text", children: fields.title })
    }
  );
};
var PreHeaderMenuItem_default = PreHeaderMenuItem;

// src/navigation/PreHeaderMenu/PreHeaderMenu.tsx
import { jsx as jsx96 } from "react/jsx-runtime";
var PreHeaderMenu = (props) => {
  const { className = "" } = props;
  const { sitecoreContext } = useSitecoreContext9();
  const { navigation = { preHeaderMenu: [] } } = sitecoreContext;
  const { preHeaderMenu } = navigation;
  return /* @__PURE__ */ jsx96("div", { className: `pre-header-menu ${className}`, children: preHeaderMenu && preHeaderMenu.length && preHeaderMenu.map((menuItem, index) => /* @__PURE__ */ jsx96(React23.Fragment, { children: /* @__PURE__ */ jsx96(PreHeaderMenuItem_default, { fields: menuItem.item }) }, index)) });
};
var PreHeaderMenu_default = PreHeaderMenu;

// src/navigation/PreHeader/PreHeader.tsx
import { jsx as jsx97, jsxs as jsxs43 } from "react/jsx-runtime";
var PreHeader = (props) => {
  const {
    className = "",
    ...restProps
  } = props;
  return /* @__PURE__ */ jsx97(
    "nav",
    {
      className: `pre-header header_white ${className} noindex`,
      ...restProps,
      children: /* @__PURE__ */ jsxs43("div", { className: "container_normal", children: [
        /* @__PURE__ */ jsx97(PreHeaderMenu_default, {}),
        /* @__PURE__ */ jsx97(LanguageSwitcher_default, {})
      ] })
    }
  );
};
var PreHeader_default = PreHeader;

// src/navigation/QuickLinks/QuickLinks.tsx
import { jsx as jsx98 } from "react/jsx-runtime";
var getQuickLinksClassName = (itemsPerRow) => {
  switch (itemsPerRow) {
    case "4":
      return "items-per-row-4";
    case "6":
    default:
      return "items-per-row-6";
  }
};
var QuickLinks = (props) => {
  const { className = "", itemsPerRow = "6", children, ...restProps } = props;
  const itemsPerRowClassname = getQuickLinksClassName(itemsPerRow);
  return /* @__PURE__ */ jsx98("div", { className: `quicklinks ${itemsPerRowClassname} ${className}`, ...restProps, children });
};
var QuickLinks_default = QuickLinks;

// src/navigation/ResponsiveSubmenu/ResponsiveSubmenu.tsx
import React25 from "react";

// src/navigation/ResponsiveSubmenu/ResponsiveSubmenuLink.tsx
import {
  Text as Text6,
  getFieldValue as getFieldValue3
} from "@sitecore-jss/sitecore-jss-react";

// src/util/useIsCurrentPage.tsx
import { useEffect as useEffect14, useState as useState20 } from "react";
import { useSitecoreContext as useSitecoreContext10 } from "@sitecore-jss/sitecore-jss-react";
var useIsCurrentPage = (link) => {
  var _a;
  const { sitecoreContext } = useSitecoreContext10();
  const currentPageUrl = (_a = sitecoreContext == null ? void 0 : sitecoreContext.currentPage) == null ? void 0 : _a.canonicalUrl;
  const [isCurrentPage, setIsCurrentPage] = useState20(false);
  useEffect14(() => {
    const origin = new URL(document.URL).origin;
    const url = new URL(link, origin);
    setIsCurrentPage(url.toString() === currentPageUrl);
  }, []);
  return isCurrentPage;
};

// src/navigation/ResponsiveSubmenu/ResponsiveSubmenuLink.tsx
import { jsx as jsx99, jsxs as jsxs44 } from "react/jsx-runtime";
var ResponsiveSubmenuLink = (props) => {
  const { fields, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx99(NullDatasourceMessage_default, {});
  const { isLinkFieldValid } = useIsFieldValid_default();
  const link = getFieldValue3(fields, "LinkTarget");
  const isActive = (link == null ? void 0 : link.href) && useIsCurrentPage(link.href);
  return isLinkFieldValid(fields.LinkTarget) ? /* @__PURE__ */ jsxs44(
    NonDraggableElement_default,
    {
      as: "a",
      className: `responsive-submenu__link ${isActive ? "responsive-submenu__link_active" : ""} ${className}`,
      link: fields.LinkTarget,
      ...restProps,
      children: [
        /* @__PURE__ */ jsx99(
          Text6,
          {
            tag: "span",
            field: fields.LinkText,
            className: "responsive-submenu__text"
          }
        ),
        /* @__PURE__ */ jsx99(ArrowRightIcon_default, { className: "responsive-submenu__link-icon" })
      ]
    }
  ) : null;
};
var ResponsiveSubmenuLink_default = ResponsiveSubmenuLink;

// src/navigation/ResponsiveSubmenu/ResponsiveSubmenu.tsx
import {
  Placeholder as Placeholder3
} from "@sitecore-jss/sitecore-jss-react";

// src/navigation/SubmenuCollapsible/SubmenuCollapsible.tsx
import { useState as useState21 } from "react";
import { useSitecoreContext as useSitecoreContext11 } from "@sitecore-jss/sitecore-jss-react";
import { Fragment as Fragment12, jsx as jsx100, jsxs as jsxs45 } from "react/jsx-runtime";
var SubmenuCollapsible = (props) => {
  const { className = "", children, ...restProps } = props;
  const { sitecoreContext } = useSitecoreContext11();
  const isEditingInSitecore = sitecoreContext.pageEditing;
  const [open, setIsOpen] = useState21(isEditingInSitecore);
  return /* @__PURE__ */ jsxs45(
    Collapsible_default,
    {
      className: `submenu-collapsible ${className}`,
      isOpen: open,
      onOpenChange: setIsOpen,
      ...restProps,
      children: [
        /* @__PURE__ */ jsx100(Collapsible_default.Header, { className: "submenu-collapsible__header", children: open ? /* @__PURE__ */ jsxs45(Fragment12, { children: [
          /* @__PURE__ */ jsx100(TimesIcon_default, {}),
          /* @__PURE__ */ jsx100("span", { children: "Sluiten" })
        ] }) : /* @__PURE__ */ jsxs45(Fragment12, { children: [
          /* @__PURE__ */ jsx100(MenuIcon_default, {}),
          /* @__PURE__ */ jsx100("span", { children: "Onderwerpen" })
        ] }) }),
        /* @__PURE__ */ jsx100(Collapsible_default.Body, { className: "submenu-collapsible__body", children })
      ]
    }
  );
};
var SubmenuCollapsible_default = SubmenuCollapsible;

// src/navigation/SubmenuDragSlider/SubmenuDragSlider.tsx
import { jsx as jsx101 } from "react/jsx-runtime";
var SubmenuDragSlider = (props) => {
  const { className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsx101("div", { className: `submenu-dragslider ${className}`, ...restProps, children: /* @__PURE__ */ jsx101(DragSlider_default, { className: "submenu-dragslider__slider", showButtons: false, children }) });
};
var SubmenuDragSlider_default = SubmenuDragSlider;

// src/navigation/ResponsiveSubmenu/ResponsiveSubmenu.tsx
import { Fragment as Fragment13, jsx as jsx102, jsxs as jsxs46 } from "react/jsx-runtime";
var ResponsiveSubmenu = (props) => {
  const { rendering, className = "", ...restProps } = props;
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  const renderSubLinks = (components, data) => {
    const links = data.map((dataItem, index) => {
      if ("type" in dataItem && (dataItem == null ? void 0 : dataItem.type) === "text/sitecore")
        return components[index];
      const fields = "fields" in dataItem ? dataItem.fields : {};
      return React25.isValidElement(components[index]) ? /* @__PURE__ */ jsx102(ResponsiveSubmenuLink_default, { fields }, index) : null;
    });
    return isEditingInSitecore ? /* @__PURE__ */ jsx102("div", { className: "responsive-submenu__sitecore-ee-wrapper", children: links }) : /* @__PURE__ */ jsxs46(Fragment13, { children: [
      /* @__PURE__ */ jsx102(SubmenuCollapsible_default, { children: links }),
      /* @__PURE__ */ jsx102(SubmenuDragSlider_default, { children: links })
    ] });
  };
  return /* @__PURE__ */ jsx102("div", { className: `responsive-submenu ${className}`, ...restProps, children: /* @__PURE__ */ jsx102(
    Placeholder3,
    {
      name: "container-hero-links",
      rendering,
      render: renderSubLinks
    }
  ) });
};
var ResponsiveSubmenu_default = ResponsiveSubmenu;

// src/navigation/QuickLinks/QuickLinksItem.tsx
import { Image as Image4, Text as Text7, getFieldValue as getFieldValue4 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx103, jsxs as jsxs47 } from "react/jsx-runtime";
var DEFAULT_IMAGE_SIZE = "icon";
var QuickLinksItem = (props) => {
  var _a;
  const { fields, className = "", children, ...restProps } = props;
  const imageSize = getFieldValue4(fields, "ImageSize", DEFAULT_IMAGE_SIZE);
  const { isLinkFieldValid, isImageFieldValid } = useIsFieldValid_default();
  const image = isImageFieldValid(fields.Image) ? /* @__PURE__ */ jsx103("div", { className: `quicklinks__item__image-wrapper size-${imageSize}`, children: /* @__PURE__ */ jsx103(
    Image4,
    {
      className: "quicklinks__item__image",
      field: fields.Image,
      srcSet: [{ mw: 192 }],
      sizes: "192px"
    }
  ) }) : null;
  const text = /* @__PURE__ */ jsx103(
    Text7,
    {
      className: "quicklinks__item__text",
      tag: "p",
      field: fields.LinkText
    }
  );
  return /* @__PURE__ */ jsx103("div", { className: `quicklinks__item ${className}`, ...restProps, children: isLinkFieldValid(fields.LinkTarget) ? /* @__PURE__ */ jsxs47(
    RoutableSitecoreLink_default,
    {
      className: "quicklinks__item__link",
      field: fields.LinkTarget,
      editable: false,
      "aria-label": (_a = fields.LinkText.value) == null ? void 0 : _a.toString(),
      children: [
        image,
        text
      ]
    }
  ) : /* @__PURE__ */ jsxs47("div", { className: "quicklinks__item__link", children: [
    image,
    text
  ] }) });
};
var QuickLinksItem_default = QuickLinksItem;

// src/navigation/StepHistoryProvider/StepHistoryProvider.tsx
import { createContext as createContext4, useContext as useContext5, useState as useState22 } from "react";
import { jsx as jsx104 } from "react/jsx-runtime";
var StepHistoryContext = createContext4(null);
var useStepHistory = () => {
  const context = useContext5(StepHistoryContext);
  if (!context) {
    throw new Error(
      "Error: useStepHistory() requires a <StepHistoryProvider> parent"
    );
  }
  return context;
};
var StepHistoryProvider = (props) => {
  const { initialStep, children } = props;
  const [history2, setHistory] = useState22([initialStep]);
  const [direction, setDirection] = useState22(
    1 /* FORWARD */
  );
  const current = history2.length > 0 ? history2[history2.length - 1] : null;
  const push = (id) => {
    setDirection(1 /* FORWARD */);
    setHistory((history3) => [...history3, id]);
  };
  const replace = (id) => {
    setDirection(1 /* FORWARD */);
    setHistory((history3) => [...history3.slice(0, -1), id]);
  };
  const back = () => {
    setDirection(-1 /* BACKWARD */);
    setHistory(
      (history3) => [...history3].slice(0, history3.length > 1 ? -1 : void 0)
    );
    return history2.length > 1;
  };
  const stepHistoryContextValue = {
    current,
    history: history2,
    push,
    replace,
    back,
    direction
  };
  return /* @__PURE__ */ jsx104(StepHistoryContext.Provider, { value: stepHistoryContextValue, children: typeof children === "function" ? children(stepHistoryContextValue) : children });
};
var StepHistoryProvider_default = StepHistoryProvider;

// src/navigation/SubMenu/SubMenuLinkItem.tsx
import { jsx as jsx105 } from "react/jsx-runtime";
var SubMenuLinkItem = (props) => {
  const { title, link, className = "", ...restProps } = props;
  const isActive = useIsCurrentPage(link);
  return /* @__PURE__ */ jsx105(
    NonDraggableElement_default,
    {
      as: "a",
      link: { value: { href: link } },
      className: `sub-menu__link-item ${isActive ? "sub-menu__link-item_active" : ""} ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsx105("span", { className: "sub-menu__link-item-text", children: title })
    }
  );
};
var SubMenuLinkItem_default = SubMenuLinkItem;

// src/navigation/SubMenu/SubMenu.tsx
import { jsx as jsx106, jsxs as jsxs48 } from "react/jsx-runtime";
var SubMenu = (props) => {
  const { links = [], className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx106("div", { className: `sub-menu ${className} noindex`, ...restProps, children: /* @__PURE__ */ jsxs48(NoIndex_default, { children: [
    /* @__PURE__ */ jsx106(MainMenuButton_default, { className: "sub-menu__button" }),
    /* @__PURE__ */ jsx106(SubmenuDragSlider_default, { className: "sub-menu__links", children: links && links.filter(
      (subMenuItem) => subMenuItem.item.templateName !== "LinkGroup"
    ).map((subMenuItem, index) => /* @__PURE__ */ jsx106(
      SubMenuLinkItem_default,
      {
        title: subMenuItem.item.title,
        link: subMenuItem.item.link
      },
      index
    )) })
  ] }) });
};
var SubMenu_default = SubMenu;

// src/layout/DragSlider/NonDraggableElement.tsx
import { jsx as jsx107 } from "react/jsx-runtime";
var isNonDraggableButtonAndHasOnClick = (props) => {
  return Boolean(props.onClick && props.onClick.length === 0);
};
var NonDraggableElement = React27.forwardRef((props, ref) => {
  const { as = "a", onMouseDown, onClick, className, ...restProps } = props;
  const lastClickCoordinates = useRef5({ x: 0, y: 0 });
  const handleMouseDown = (e) => {
    lastClickCoordinates.current = { x: e.pageX, y: e.pageY };
  };
  const handleClick = (e) => {
    const delta = Math.abs(e.pageX - lastClickCoordinates.current.x) + Math.abs(e.pageY - lastClickCoordinates.current.y);
    if (delta > 10) {
      e.preventDefault();
    } else if (isNonDraggableButtonAndHasOnClick(props)) {
      props.onClick && props.onClick();
    }
  };
  const Component = as;
  return Component === "a" ? (
    // @ts-ignore
    /* @__PURE__ */ jsx107(
      RoutableSitecoreLink_default,
      {
        ref,
        className: `non-draggable-element ${className}`,
        draggable: false,
        onMouseDown: handleMouseDown,
        onClick: handleClick,
        field: props.link,
        editable: false,
        ...restProps
      }
    )
  ) : Component === "button" ? (
    // @ts-ignore
    /* @__PURE__ */ jsx107(
      Component,
      {
        ref,
        className: `non-draggable-element ${className}`,
        draggable: false,
        onMouseDown: handleMouseDown,
        onClick: handleClick,
        ...restProps
      }
    )
  ) : (
    // @ts-ignore
    /* @__PURE__ */ jsx107(
      Component,
      {
        ref,
        className: `non-draggable-element ${className}`,
        draggable: false,
        onMouseDown: handleMouseDown,
        onClick: handleClick,
        ...restProps
      }
    )
  );
});
NonDraggableElement.displayName = "NonDraggableElement";
var NonDraggableElement_default = NonDraggableElement;

// src/layout/PageBlockWrapper/PageBlockWrapper.tsx
import { jsx as jsx108, jsxs as jsxs49 } from "react/jsx-runtime";
var getVariantClassName2 = (variant) => {
  switch (variant) {
    case "white":
      return "page-block-wrapper_white";
    case "light":
      return "page-block-wrapper_light";
    case "dark":
      return "page-block-wrapper_dark";
    default:
      return "";
  }
};
var PageBlockWrapper = (props) => {
  const {
    variant,
    showDecorations = false,
    className = "",
    children,
    ...restProps
  } = props;
  return /* @__PURE__ */ jsxs49(
    "div",
    {
      className: `page-block-wrapper ${getVariantClassName2(
        variant
      )} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx108(Decorations_default, {}),
        children
      ]
    }
  );
};
var PageBlockWrapper_default = PageBlockWrapper;

// src/layout/PageLayout/PageLayout.tsx
import { jsx as jsx109 } from "react/jsx-runtime";
var PageLayoutHeader = (props) => {
  const { position = "sticky", children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx109(
    "div",
    {
      className: `page-layout__header ${position === "fixed" ? "page-layout__header_fixed" : ""} ${className}`,
      ...restProps,
      children
    }
  );
};
var PageLayoutMain = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx109("div", { className: `page-layout__main ${className}`, ...restProps, children });
};
var PageLayoutFooter = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx109("div", { className: `page-layout__footer ${className}`, ...restProps, children });
};
var PageLayout = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx109("div", { className: `page-layout ${className}`, ...restProps, children });
};
PageLayout.Header = PageLayoutHeader;
PageLayout.Main = PageLayoutMain;
PageLayout.Footer = PageLayoutFooter;
var PageLayout_default = PageLayout;

// src/layout/Reel/ReelProvider.tsx
import React28, {
  createContext as createContext5,
  useContext as useContext6,
  useRef as useRef6,
  useState as useState23,
  useEffect as useEffect15,
  useMemo
} from "react";
import { Fragment as Fragment14, jsx as jsx110 } from "react/jsx-runtime";
var ReelContext = createContext5(null);
var useReel = () => {
  const context = useContext6(ReelContext);
  if (!context) {
    throw new Error("Error: useReel() requires a <ReelProvider> parent");
  }
  return context;
};
var DEFAULT_SLIDE_AMOUNT = 1;
var DEFAULT_SLIDE_UNIT = "item";
var ReelProvider = (props) => {
  const {
    children,
    slideAmount = DEFAULT_SLIDE_AMOUNT,
    slideAmountUnit = DEFAULT_SLIDE_UNIT
  } = props;
  const containerRef = useRef6(null);
  const itemsRef = useRef6([]);
  useEffect15(() => {
    itemsRef.current = itemsRef.current.slice(
      0,
      React28.Children.count(props.items)
    );
  }, [props.items]);
  const items = useMemo(
    () => React28.Children.map(props.items, (child, index) => /* @__PURE__ */ jsx110(
      "div",
      {
        ref: (el) => {
          itemsRef.current[index] = el;
        },
        draggable: false,
        className: "flex-shrink-0",
        children: child
      }
    )),
    [props.items]
  );
  const [isStart, setIsStart] = useState23(true);
  const [isEnd, setIsEnd] = useState23(false);
  const [position, setPosition] = useState23({
    index: 0,
    inner: 0,
    outer: 0
  });
  const scrollToIndex = (index) => {
    if (!containerRef.current)
      return;
    const el = itemsRef.current[index];
    if (!el) {
      return;
    }
    const paddingLeft = parseFloat(
      getComputedStyle(containerRef.current).getPropertyValue("padding-left")
    ) || 0;
    containerRef.current.scrollTo({
      left: el.offsetLeft - paddingLeft,
      behavior: "smooth"
    });
  };
  const scrollByFullWidth = (screens) => {
    if (!containerRef.current)
      return;
    const delta = screens * containerRef.current.clientWidth;
    if (slideAmountUnit === "viewport") {
      containerRef.current.scrollTo({
        left: containerRef.current.scrollLeft + delta,
        behavior: "smooth"
      });
    }
  };
  const goLeft = () => {
    if (slideAmountUnit === "viewport") {
      scrollByFullWidth(-1);
    } else {
      const previousIndex = position.index + Math.ceil(position.inner + position.outer);
      const targetIndex = Math.max(previousIndex - slideAmount, 0);
      scrollToIndex(targetIndex);
      setPosition({
        index: targetIndex,
        inner: 0,
        outer: 0
      });
    }
  };
  const goRight = () => {
    if (slideAmountUnit === "viewport") {
      scrollByFullWidth(1);
    } else {
      const targetIndex = Math.min(
        position.index + slideAmount,
        itemsRef.current.length - 1
      );
      scrollToIndex(targetIndex);
      setPosition({
        index: targetIndex,
        inner: 0,
        outer: 0
      });
    }
  };
  const getCurrentPosition = () => {
    if (!containerRef.current) {
      return null;
    }
    const paddingLeft = parseFloat(
      getComputedStyle(containerRef.current).getPropertyValue("padding-left")
    ) || 0;
    for (let i = 0; i < itemsRef.current.length; i++) {
      const el = itemsRef.current[i];
      if (!el) {
        return null;
      }
      const position2 = Math.round(containerRef.current.scrollLeft);
      const left = Math.floor(el.offsetLeft - paddingLeft);
      const width = el.offsetWidth;
      const right = left + width;
      if (position2 >= left && containerRef.current.scrollLeft <= right) {
        return {
          index: i,
          inner: (position2 - left) / width,
          outer: 0
        };
      }
      if (i + 1 === itemsRef.current.length) {
        return null;
      }
      const nextChild = itemsRef.current[i + 1];
      if (!nextChild) {
        return null;
      }
      const nextChildLeft = Math.floor(nextChild.offsetLeft - paddingLeft);
      if (position2 > right && position2 < nextChildLeft) {
        return {
          index: i,
          inner: 0,
          outer: (position2 - right) / (nextChildLeft - right)
        };
      }
    }
    return null;
  };
  const handleScroll = useDebouncedCallback_default(() => {
    if (!containerRef.current) {
      return;
    }
    const scrollLeft = Math.round(containerRef.current.scrollLeft);
    const scrollWidth = containerRef.current.scrollWidth;
    const clientWidth = containerRef.current.clientWidth;
    setIsStart(scrollLeft === 0);
    setIsEnd(scrollLeft + clientWidth >= scrollWidth);
    setPosition(
      getCurrentPosition() || {
        index: 0,
        inner: 0,
        outer: 0
      }
    );
  }, 100);
  useEffect15(() => {
    handleScroll();
  }, [items]);
  const contextValue = {
    items: items ?? /* @__PURE__ */ jsx110(Fragment14, {}),
    containerRef,
    goLeft,
    goRight,
    isStart,
    isEnd,
    handleScroll
  };
  return /* @__PURE__ */ jsx110(ReelContext.Provider, { value: contextValue, children: typeof children === "function" ? children(
    contextValue
  ) : children });
};
var ReelProvider_default = ReelProvider;

// src/layout/Reel/ReelContainer.tsx
import { jsx as jsx111 } from "react/jsx-runtime";
var ReelContainer = (props) => {
  const {
    hideScrollbar = true,
    className = "",
    children,
    ...restProps
  } = props;
  const { items, containerRef, handleScroll } = useReel();
  return /* @__PURE__ */ jsx111(
    "div",
    {
      ref: containerRef,
      className: `reel ${className} ${hideScrollbar ? "reel__hide-scrollbar" : ""}`,
      onScroll: handleScroll,
      ...restProps,
      children: items
    }
  );
};
var ReelContainer_default = ReelContainer;

// src/layout/Reel/ReelNavigation.tsx
import { useTranslation as useTranslation18 } from "react-i18next";
import { jsx as jsx112, jsxs as jsxs50 } from "react/jsx-runtime";
var ReelNavigation = (props) => {
  const { children, className = "", ...restProps } = props;
  const { goLeft, goRight, isStart, isEnd } = useReel();
  const { t } = useTranslation18("ui");
  return /* @__PURE__ */ jsxs50("div", { className: `reel__navigation container_tightest ${className}`, ...restProps, children: [
    /* @__PURE__ */ jsx112(
      "button",
      {
        className: "reel__navigation-button",
        onClick: goLeft,
        disabled: isStart,
        "aria-label": `${t("go-to-left-in")} reel`,
        children: /* @__PURE__ */ jsx112(
          ChevronLeftIcon_default,
          {
            className: `reel__navigation-icon ${isStart ? "reel__navigation-icon_disabled" : ""}`
          }
        )
      }
    ),
    /* @__PURE__ */ jsx112(
      "button",
      {
        className: "reel__navigation-button",
        onClick: goRight,
        disabled: isEnd,
        "aria-label": `${t("go-to-right-in")} reel`,
        children: /* @__PURE__ */ jsx112(
          ChevronRightIcon_default,
          {
            className: `reel__navigation-icon ${isEnd ? "reel__navigation-icon_disabled" : ""}`
          }
        )
      }
    )
  ] });
};
var ReelNavigation_default = ReelNavigation;

// src/layout/Reel/Reel.tsx
import { jsx as jsx113, jsxs as jsxs51 } from "react/jsx-runtime";
var Reel = (props) => {
  const {
    slideAmount = 1,
    slideAmountUnit = "item",
    hideScrollbar = true,
    children,
    className = ""
  } = props;
  return /* @__PURE__ */ jsxs51(
    ReelProvider_default,
    {
      items: children,
      slideAmount,
      slideAmountUnit,
      children: [
        /* @__PURE__ */ jsx113(ReelContainer_default, { hideScrollbar, className }),
        /* @__PURE__ */ jsx113(ReelNavigation_default, {})
      ]
    }
  );
};
var Reel_default = Reel;

// src/layout/Tabs/TabsProvider.tsx
import React29, {
  createContext as createContext6,
  useContext as useContext7,
  useState as useState24,
  useMemo as useMemo2
} from "react";

// src/layout/Tabs/Tab.tsx
import { motion as motion6 } from "framer-motion";
import { jsx as jsx114 } from "react/jsx-runtime";
var tabVariants = {
  active: {
    display: "block",
    opacity: 1,
    position: "relative",
    transition: {
      duration: 0.5
    }
  },
  inactive: {
    display: "none",
    opacity: 0,
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    transition: {
      duration: 0.5
    }
  }
};
var Tab = (props) => {
  const { active, children } = props;
  return /* @__PURE__ */ jsx114(
    motion6.div,
    {
      role: "tabpanel",
      variants: tabVariants,
      animate: active ? "active" : "inactive",
      initial: "inactive",
      children
    }
  );
};
var Tab_default = Tab;

// src/layout/Tabs/TabsProvider.tsx
import { Fragment as Fragment15, jsx as jsx115 } from "react/jsx-runtime";
var TabsContext = createContext6(null);
var useTabs = () => {
  const context = useContext7(TabsContext);
  if (!context) {
    throw new Error("Error: useTabs() requires a <TabsProvider> parent");
  }
  return context;
};
var TabsProvider = (props) => {
  const {
    children,
    defaultIndex = 0
  } = props;
  const [activeTab, setActiveTab] = useState24(defaultIndex);
  const isActiveTab = (index) => index === activeTab;
  const items = useMemo2(
    () => React29.Children.map(props.items, (child, index) => /* @__PURE__ */ jsx115(
      Tab_default,
      {
        active: isActiveTab(index),
        children: child
      }
    )),
    [props.items, activeTab]
  );
  const hasPrev = () => !!(items && activeTab > 0);
  const hasNext = () => !!(items && activeTab < (items == null ? void 0 : items.length) - 1);
  const goPrev = () => setActiveTab(activeTab - 1);
  const goNext = () => setActiveTab(activeTab + 1);
  const contextValue = {
    items: items ?? /* @__PURE__ */ jsx115(Fragment15, {}),
    setActiveTab,
    isActiveTab,
    hasPrev,
    hasNext,
    goPrev,
    goNext
  };
  return /* @__PURE__ */ jsx115(TabsContext.Provider, { value: contextValue, children: typeof children === "function" ? children(
    contextValue
  ) : children });
};
var TabsProvider_default = TabsProvider;

// src/layout/Tabs/TabsContainer.tsx
import { jsx as jsx116 } from "react/jsx-runtime";
var TabsContainer = (props) => {
  const {
    className = "",
    children,
    ...restProps
  } = props;
  const { items } = useTabs();
  return /* @__PURE__ */ jsx116(
    "div",
    {
      className: `tabs ${className}`,
      ...restProps,
      children: items
    }
  );
};
var TabsContainer_default = TabsContainer;

// src/layout/Tabs/TabsPaginationProvider.tsx
import {
  createContext as createContext7,
  useContext as useContext8,
  useRef as useRef7,
  useState as useState25,
  useEffect as useEffect16
} from "react";
import { Fragment as Fragment16, jsx as jsx117 } from "react/jsx-runtime";
var TabsPaginationContext = createContext7(null);
var useTabsPagination = () => {
  const context = useContext8(TabsPaginationContext);
  if (!context) {
    throw new Error("Error: useTabsPagination() requires a <TabsPaginationProvider> parent");
  }
  return context;
};
var TabsPaginationProvider = (props) => {
  const { children, titles } = props;
  const { setActiveTab, isActiveTab } = useTabs();
  const containerRef = useRef7(null);
  const itemsRef = useRef7([]);
  useEffect16(() => {
    itemsRef.current = itemsRef.current.slice(
      0,
      titles.length
    );
  }, [titles]);
  const items = titles.map((title, index) => {
    return /* @__PURE__ */ jsx117(
      "button",
      {
        ref: (el) => {
          itemsRef.current[index] = el;
        },
        onClick: () => {
          setActiveTab(index);
          goTo(index);
        },
        "aria-label": title,
        className: `btn ${isActiveTab(index) ? "btn_primary" : "btn_secondary"}`,
        children: title
      },
      index
    );
  });
  const [isStart, setIsStart] = useState25(true);
  const [isEnd, setIsEnd] = useState25(false);
  const [position, setPosition] = useState25({
    index: 0,
    inner: 0,
    outer: 0
  });
  const scrollToIndex = (index) => {
    if (!containerRef.current)
      return;
    const el = itemsRef.current[index];
    if (!el) {
      return;
    }
    const paddingLeft = parseFloat(
      getComputedStyle(containerRef.current).getPropertyValue("padding-left")
    ) || 0;
    containerRef.current.scrollTo({
      left: el.offsetLeft - paddingLeft,
      behavior: "smooth"
    });
  };
  const goLeft = () => {
    const previousIndex = position.index + Math.ceil(position.inner + position.outer);
    const targetIndex = Math.max(previousIndex - 1, 0);
    scrollToIndex(targetIndex);
    setPosition({
      index: targetIndex,
      inner: 0,
      outer: 0
    });
  };
  const goRight = () => {
    const targetIndex = Math.min(
      position.index + 1,
      itemsRef.current.length - 1
    );
    scrollToIndex(targetIndex);
    setPosition({
      index: targetIndex,
      inner: 0,
      outer: 0
    });
  };
  const goTo = (targetIndex) => {
    scrollToIndex(targetIndex);
    setPosition({
      index: targetIndex,
      inner: 0,
      outer: 0
    });
  };
  const getCurrentPosition = () => {
    if (!containerRef.current) {
      return null;
    }
    const paddingLeft = parseFloat(
      getComputedStyle(containerRef.current).getPropertyValue("padding-left")
    ) || 0;
    for (let i = 0; i < itemsRef.current.length; i++) {
      const el = itemsRef.current[i];
      if (!el) {
        return null;
      }
      const position2 = Math.round(containerRef.current.scrollLeft);
      const left = Math.floor(el.offsetLeft - paddingLeft);
      const width = el.offsetWidth;
      const right = left + width;
      if (position2 >= left && containerRef.current.scrollLeft <= right) {
        return {
          index: i,
          inner: (position2 - left) / width,
          outer: 0
        };
      }
      if (i + 1 === itemsRef.current.length) {
        return null;
      }
      const nextChild = itemsRef.current[i + 1];
      if (!nextChild) {
        return null;
      }
      const nextChildLeft = Math.floor(nextChild.offsetLeft - paddingLeft);
      if (position2 > right && position2 < nextChildLeft) {
        return {
          index: i,
          inner: 0,
          outer: (position2 - right) / (nextChildLeft - right)
        };
      }
    }
    return null;
  };
  const handleScroll = useDebouncedCallback_default(() => {
    if (!containerRef.current) {
      return;
    }
    const scrollLeft = Math.round(containerRef.current.scrollLeft);
    const scrollWidth = containerRef.current.scrollWidth;
    const clientWidth = containerRef.current.clientWidth;
    setIsStart(scrollLeft === 0);
    setIsEnd(scrollLeft + clientWidth >= scrollWidth);
    setPosition(
      getCurrentPosition() || {
        index: 0,
        inner: 0,
        outer: 0
      }
    );
  }, 100);
  useEffect16(() => {
    handleScroll();
  }, [items]);
  const contextValue = {
    items: items ?? /* @__PURE__ */ jsx117(Fragment16, {}),
    containerRef,
    goLeft,
    goRight,
    isStart,
    isEnd,
    handleScroll
  };
  return /* @__PURE__ */ jsx117(TabsPaginationContext.Provider, { value: contextValue, children: typeof children === "function" ? children(
    contextValue
  ) : children });
};
var TabsPaginationProvider_default = TabsPaginationProvider;

// src/layout/Tabs/TabsPaginationContainer.tsx
import { useTranslation as useTranslation19 } from "react-i18next";
import { jsx as jsx118, jsxs as jsxs52 } from "react/jsx-runtime";
var TabsPaginationContainer = (props) => {
  const {
    hideScrollbar = true,
    className = "",
    children,
    ...restProps
  } = props;
  const { items, containerRef, handleScroll, goLeft, goRight, isStart, isEnd } = useTabsPagination();
  const { t } = useTranslation19();
  return /* @__PURE__ */ jsxs52("div", { className: "tabs__pagination", children: [
    /* @__PURE__ */ jsx118(
      "button",
      {
        onClick: () => goLeft(),
        className: `btn btn_primary btn_prev`,
        disabled: isStart,
        "aria-label": t("previous") || "Previous",
        children: /* @__PURE__ */ jsx118(ArrowLeftIcon_default, {})
      }
    ),
    /* @__PURE__ */ jsx118(
      "div",
      {
        ref: containerRef,
        className: "tabs__pagination-pages btn-container",
        onScroll: handleScroll,
        ...restProps,
        children: items
      }
    ),
    /* @__PURE__ */ jsx118(
      "button",
      {
        onClick: () => goRight(),
        className: "btn btn_primary btn_next",
        disabled: isEnd,
        "aria-label": t("next") || "Next",
        children: /* @__PURE__ */ jsx118(ArrowRightIcon_default, {})
      }
    )
  ] });
};
var TabsPaginationContainer_default = TabsPaginationContainer;

// src/layout/Tabs/TabsPagination.tsx
import { jsx as jsx119 } from "react/jsx-runtime";
var TabsPagination = (props) => {
  const { children, className = "", titles = [] } = props;
  return /* @__PURE__ */ jsx119(
    TabsPaginationProvider_default,
    {
      items: children,
      titles,
      children: /* @__PURE__ */ jsx119(TabsPaginationContainer_default, { className })
    }
  );
};
var TabsPagination_default = TabsPagination;

// src/layout/Tabs/Tabs.tsx
import { jsx as jsx120, jsxs as jsxs53 } from "react/jsx-runtime";
var Tabs = (props) => {
  const {
    children,
    className = "",
    titles = []
  } = props;
  return /* @__PURE__ */ jsxs53(
    TabsProvider_default,
    {
      items: children,
      children: [
        /* @__PURE__ */ jsx120(TabsPagination_default, { titles }),
        /* @__PURE__ */ jsx120(TabsContainer_default, { className })
      ]
    }
  );
};
var Tabs_default = Tabs;

// src/blocks/AccordionBlock/AccordionItem.tsx
import { jsx as jsx121, jsxs as jsxs54 } from "react/jsx-runtime";
var AccordionItemHeader = (props) => {
  const { className = "", children, ...restProps } = props;
  const { isOpen, setIsOpen } = useCollapsible();
  return /* @__PURE__ */ jsxs54(
    "button",
    {
      className: `accordion__item-header ${isOpen ? "accordion__item-header_open" : ""} ${className}`,
      onClick: () => {
        setIsOpen(!isOpen);
      },
      ...restProps,
      children: [
        /* @__PURE__ */ jsx121("div", { className: "accordion__item-title", children }),
        !isOpen ? /* @__PURE__ */ jsx121(ChevronDownIcon_default, { className: "w-6 h-6 accordion__item-icon" }) : /* @__PURE__ */ jsx121(ChevronUpIcon_default, { className: "w-6 h-6 accordion__item-icon" })
      ]
    }
  );
};
var AccordionItemBody = (props) => {
  const { className = "", children, ...restProps } = props;
  const { isOpen } = useCollapsible();
  return /* @__PURE__ */ jsx121(
    "div",
    {
      className: `accordion__item-body ${isOpen ? "block" : "hidden"} ${className}`,
      ...restProps,
      children
    }
  );
};
var AccordionItem = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  const itemRef = useRef8(null);
  const { activeItem, setActiveItem } = useAccordion();
  const onOpenChangeHandler = () => {
    setActiveItem(activeItem === itemRef.current ? null : itemRef.current);
  };
  const { sitecoreContext } = useSitecoreContext12();
  const isEditingInSitecore = sitecoreContext.pageEditing;
  return /* @__PURE__ */ jsx121(
    CollapsibleProvider_default,
    {
      isOpen: isEditingInSitecore || itemRef.current !== null && activeItem === itemRef.current,
      onOpenChange: onOpenChangeHandler,
      children: /* @__PURE__ */ jsxs54(
        "div",
        {
          className: `accordion__item ${className}`,
          ref: itemRef,
          ...restProps,
          children: [
            /* @__PURE__ */ jsx121(AccordionItemHeader, { children: /* @__PURE__ */ jsx121(Text8, { field: fields.Title }) }),
            /* @__PURE__ */ jsxs54(AccordionItemBody, { children: [
              /* @__PURE__ */ jsx121(RichText_default, { className: "rich-text", field: fields.RichTextSimple }),
              /* @__PURE__ */ jsx121(ButtonContainer_default, { rendering })
            ] })
          ]
        }
      )
    }
  );
};
var AccordionItem_default = AccordionItem;

// src/blocks/AccordionBlock/AccordionBlock.tsx
import {
  Text as Text9,
  Placeholder as Placeholder4,
  getFieldValue as getFieldValue5
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx122, jsxs as jsxs55 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT = "white";
var DEFAULT_SHOW_DECORATIONS = true;
var AccordionBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx122(NullDatasourceMessage_default, {});
  const variant = getFieldValue5(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT
  );
  const showDecorations = getFieldValue5(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS
  );
  return /* @__PURE__ */ jsx122(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `accordion-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs55("div", { className: "container_tightest", children: [
        /* @__PURE__ */ jsx122(
          Text9,
          {
            tag: "h2",
            field: fields.Title,
            className: "accordion-block__title heading-2"
          }
        ),
        /* @__PURE__ */ jsx122(
          RichText_default,
          {
            className: "accordion-block__subtitle",
            field: fields.RichTextSimple
          }
        ),
        /* @__PURE__ */ jsx122(Accordion_default, { children: /* @__PURE__ */ jsx122(Placeholder4, { name: "container-accordion", rendering }) })
      ] })
    }
  );
};
var AccordionBlock_default = AccordionBlock;

// src/blocks/AccordionWithImagesBlock/AccordionWithImages.tsx
import { jsx as jsx123 } from "react/jsx-runtime";
var AccordionWithImages = (props) => {
  const { className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsx123(AccordionProvider_default, { children: /* @__PURE__ */ jsx123("div", { className: `accordion-with-images ${className}`, ...restProps, children }) });
};
var AccordionWithImages_default = AccordionWithImages;

// src/blocks/AccordionWithImagesBlock/AccordionWithImagesItem.tsx
import { useRef as useRef9 } from "react";
import {
  Text as Text10,
  Image as Image5,
  useSitecoreContext as useSitecoreContext13
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx124, jsxs as jsxs56 } from "react/jsx-runtime";
var AccordionWithImagesItemMediaObject = (props) => {
  const { className = "", children, ...restProps } = props;
  const { isOpen, setIsOpen } = useCollapsible();
  return /* @__PURE__ */ jsx124(
    "div",
    {
      className: `accordion-with-images__item-media-object ${isOpen ? "accordion-with-images__item-media-object_open" : ""} ${className}`,
      onClick: () => {
        setIsOpen(!isOpen);
      },
      ...restProps,
      children
    }
  );
};
var AccordionWithImagesItemHeader = (props) => {
  const { className = "", children, ...restProps } = props;
  const { isOpen, setIsOpen } = useCollapsible();
  return /* @__PURE__ */ jsxs56(
    "div",
    {
      className: `accordion-with-images__item-header ${isOpen ? "accordion-with-images__item-header_open" : ""} ${className}`,
      onClick: () => {
        setIsOpen(!isOpen);
      },
      ...restProps,
      children: [
        /* @__PURE__ */ jsx124("div", { children }),
        !isOpen ? /* @__PURE__ */ jsx124(ChevronDownIcon_default, { className: "w-6 h-6 accordion-with-images__item-icon" }) : /* @__PURE__ */ jsx124(ChevronUpIcon_default, { className: "w-6 h-6 accordion-with-images__item-icon" })
      ]
    }
  );
};
var AccordionWithImagesItemBody = (props) => {
  const { className = "", children, ...restProps } = props;
  const { isOpen } = useCollapsible();
  return /* @__PURE__ */ jsx124(
    "div",
    {
      className: `accordion-with-images__item-body ${isOpen ? "block" : "hidden"} ${className}`,
      ...restProps,
      children
    }
  );
};
var AccordionWithImagesItem = (props) => {
  const {
    fields,
    allowMultipleOpen,
    className = "",
    children,
    ...restProps
  } = props;
  const itemRef = useRef9(null);
  const { activeItem, setActiveItem } = useAccordion();
  const onOpenChangeHandler = () => {
    setActiveItem(activeItem === itemRef.current ? null : itemRef.current);
  };
  const { sitecoreContext } = useSitecoreContext13();
  const isEditingInSitecore = sitecoreContext.pageEditing;
  const { isImageFieldValid } = useIsFieldValid_default();
  return /* @__PURE__ */ jsx124(
    CollapsibleProvider_default,
    {
      isOpen: isEditingInSitecore || itemRef.current !== null && activeItem === itemRef.current,
      onOpenChange: onOpenChangeHandler,
      children: /* @__PURE__ */ jsxs56(
        "div",
        {
          className: `accordion-with-images__item ${className}`,
          ref: itemRef,
          ...restProps,
          children: [
            isImageFieldValid(fields.Image) && /* @__PURE__ */ jsx124(AccordionWithImagesItemMediaObject, { children: /* @__PURE__ */ jsx124("div", { className: "accordion-with-images__image-wrapper", children: /* @__PURE__ */ jsx124(
              Image5,
              {
                field: fields.Image,
                className: "accordion-with-images__image",
                srcSet: [{ mw: 192 }],
                sizes: "192px"
              }
            ) }) }),
            /* @__PURE__ */ jsxs56("div", { style: { display: "flex", flexDirection: "column", flex: 1 }, children: [
              /* @__PURE__ */ jsxs56(AccordionWithImagesItemHeader, { children: [
                /* @__PURE__ */ jsx124(
                  Text10,
                  {
                    field: fields.Title,
                    tag: "p",
                    className: "accordion-with-images__item-title"
                  }
                ),
                /* @__PURE__ */ jsx124(
                  Text10,
                  {
                    field: fields.SubTitle,
                    tag: "p",
                    className: "accordion-with-images__item-subtitle"
                  }
                )
              ] }),
              /* @__PURE__ */ jsx124(AccordionWithImagesItemBody, { children: /* @__PURE__ */ jsx124(RichText_default, { className: "rich-text", field: fields.RichTextSimple }) })
            ] })
          ]
        }
      )
    }
  );
};
var AccordionWithImagesItem_default = AccordionWithImagesItem;

// src/blocks/AccordionWithImagesBlock/AccordionWithImagesBlock.tsx
import {
  Text as Text11,
  Placeholder as Placeholder5,
  getFieldValue as getFieldValue6
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx125, jsxs as jsxs57 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT2 = "white";
var DEFAULT_SHOW_DECORATIONS2 = true;
var AccordionWithImagesBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx125(NullDatasourceMessage_default, {});
  const variant = getFieldValue6(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT2
  );
  const showDecorations = getFieldValue6(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS2
  );
  return /* @__PURE__ */ jsx125(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `accordion-with-images-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs57("div", { className: "container_tightest", children: [
        /* @__PURE__ */ jsx125(
          Text11,
          {
            tag: "h2",
            field: fields.Title,
            className: "accordion-with-images-block__title heading-2"
          }
        ),
        /* @__PURE__ */ jsx125(
          RichText_default,
          {
            className: "accordion-with-images-block__subtitle",
            field: fields.RichTextSimple
          }
        ),
        /* @__PURE__ */ jsx125(AccordionWithImages_default, { children: /* @__PURE__ */ jsx125(
          Placeholder5,
          {
            name: "container-accordion-with-images",
            rendering
          }
        ) })
      ] })
    }
  );
};
var AccordionWithImagesBlock_default = AccordionWithImagesBlock;

// src/blocks/AccordionWithSideImageBlock/AccordionWithSideImageBlock.tsx
import {
  Text as Text12,
  Placeholder as Placeholder6,
  getFieldValue as getFieldValue7,
  Image as Image6
} from "@sitecore-jss/sitecore-jss-react";
import { Fragment as Fragment17, jsx as jsx126, jsxs as jsxs58 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT3 = "white";
var DEFAULT_SHOW_DECORATIONS3 = true;
var DEFAULT_IMAGE_ALIGNMENT = "left";
var AccordionWithSideImageBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx126(NullDatasourceMessage_default, {});
  const variant = getFieldValue7(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT3
  );
  const showDecorations = getFieldValue7(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS3
  );
  const imageAlignment = getFieldValue7(
    fields,
    "ImageAlignment",
    DEFAULT_IMAGE_ALIGNMENT
  );
  const { isTextFieldValid, isRichTextFieldValid, isImageFieldValid } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  const wrapperClassName = `accordion-with-side-image-block ${imageAlignment === "left" ? "accordion-with-side-image-block_image-left" : "accordion-with-side-image-block_image-right"} ${className}`;
  return /* @__PURE__ */ jsx126(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: wrapperClassName,
      ...restProps,
      children: /* @__PURE__ */ jsxs58("div", { className: "accordion-with-side-image-block__container", children: [
        /* @__PURE__ */ jsxs58("div", { className: "accordion-with-side-image-block__text-wrapper", children: [
          showText && /* @__PURE__ */ jsxs58(Fragment17, { children: [
            /* @__PURE__ */ jsx126(
              Text12,
              {
                tag: "h2",
                field: fields.Title,
                className: "heading-2 accordion-with-side-image-block__title"
              }
            ),
            /* @__PURE__ */ jsx126(
              RichText_default,
              {
                className: "rich-text accordion-with-side-image-block__subtitle",
                field: fields.RichTextSimple
              }
            )
          ] }),
          /* @__PURE__ */ jsx126(ButtonContainer_default, { rendering }),
          /* @__PURE__ */ jsx126(Accordion_default, { children: /* @__PURE__ */ jsx126(Placeholder6, { name: "container-accordion", rendering }) })
        ] }),
        /* @__PURE__ */ jsx126("div", { className: "accordion-with-side-image-block__image-wrapper", children: isImageFieldValid(fields.Image) && /* @__PURE__ */ jsx126(
          Image6,
          {
            field: fields.Image,
            className: "accordion-with-side-image-block__image",
            srcSet: [{ mw: 1280 }, { mw: 640 }],
            sizes: "(max-width: 1280px) 1280px, 640px"
          }
        ) })
      ] })
    }
  );
};
var AccordionWithSideImageBlock_default = AccordionWithSideImageBlock;

// src/blocks/ArticleOverviewBlock/ArticleOverview.tsx
import { jsx as jsx127 } from "react/jsx-runtime";
var ArticleOverview = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx127("div", { className: `article-overview ${className}`, ...restProps, children });
};
var ArticleOverview_default = ArticleOverview;

// src/blocks/ArticleOverviewBlock/ArticleOverviewItem.tsx
import { Image as Image7, Text as Text13 } from "@sitecore-jss/sitecore-jss-react";

// src/typography/Date/Date.tsx
import { DateField } from "@sitecore-jss/sitecore-jss-react";
import { useTranslation as useTranslation20 } from "react-i18next";
import { format } from "date-fns";
import enUS from "date-fns/locale/en-US/index.js";
import nl from "date-fns/locale/nl/index.js";
import { jsx as jsx128 } from "react/jsx-runtime";
var Date2 = (props) => {
  const {
    field,
    dateFormat = "d MMMM yyyy",
    className = "",
    ...restProps
  } = props;
  const { i18n } = useTranslation20("ui");
  const formatDate = (date) => format(date, dateFormat, {
    locale: i18n.language === "en" ? enUS : nl
  });
  return /* @__PURE__ */ jsx128("div", { className: `date ${className}`, ...restProps, children: /* @__PURE__ */ jsx128(
    DateField,
    {
      field,
      render: (date) => date ? formatDate(date) : ""
    }
  ) });
};
var Date_default = Date2;

// src/blocks/ArticleOverviewBlock/ArticleOverviewItem.tsx
import { jsx as jsx129, jsxs as jsxs59 } from "react/jsx-runtime";
var ArticleOverviewItem = (props) => {
  const { fields, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx129(NullDatasourceMessage_default, {});
  const { isDateFieldValid, isDateFieldSet } = useIsFieldValid_default();
  return /* @__PURE__ */ jsx129(
    RoutableSitecoreLink_default,
    {
      className: `article-overview__item ${className}`,
      field: fields.LinkTarget,
      editable: false,
      ...restProps,
      children: /* @__PURE__ */ jsx129(
        MediaObject_default,
        {
          className: "article-overview__item-media-object",
          media: /* @__PURE__ */ jsx129("div", { className: "article-overview__item-media-wrapper", children: /* @__PURE__ */ jsx129(
            Image7,
            {
              className: "article-overview__item-media",
              field: fields.Image,
              srcSet: [{ mw: 192 }],
              sizes: "192px"
            }
          ) }),
          children: /* @__PURE__ */ jsxs59("div", { className: "article-overview__item-body", children: [
            /* @__PURE__ */ jsxs59("div", { className: "article-overview__text", children: [
              isDateFieldValid(fields.Date) && isDateFieldSet(fields.Date) && /* @__PURE__ */ jsx129(
                Date_default,
                {
                  className: "article-overview__item-date",
                  field: fields.Date
                }
              ),
              /* @__PURE__ */ jsx129(
                Text13,
                {
                  tag: "h3",
                  className: "article-overview__item-title",
                  field: fields.Title
                }
              ),
              /* @__PURE__ */ jsx129(
                Text13,
                {
                  tag: "p",
                  className: "article-overview__item-subtitle",
                  field: fields.SubTitle
                }
              )
            ] }),
            /* @__PURE__ */ jsx129(ArrowRightIcon_default, { className: "article-overview__item-icon" })
          ] })
        }
      )
    }
  );
};
var ArticleOverviewItem_default = ArticleOverviewItem;

// src/blocks/ArticleOverviewAutomaticBlock/ArticleOverviewAutomaticBlock.tsx
import {
  Text as Text14,
  getFieldValue as getFieldValue8
} from "@sitecore-jss/sitecore-jss-react";
import { Fragment as Fragment18, jsx as jsx130, jsxs as jsxs60 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT4 = "white";
var DEFAULT_SHOW_DECORATIONS4 = true;
var ArticleOverviewAutomaticBlock = (props) => {
  const { fields, rendering, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx130(NullDatasourceMessage_default, {});
  const variant = getFieldValue8(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT4
  );
  const showDecorations = getFieldValue8(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS4
  );
  const items = fields.Items || [];
  return /* @__PURE__ */ jsxs60(
    PageBlockWrapper_default,
    {
      variant,
      className: `article-overview-block ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsxs60(Fragment18, { children: [
          /* @__PURE__ */ jsx130("div", { className: "decoration-first" }),
          /* @__PURE__ */ jsx130("div", { className: "decoration-second" })
        ] }),
        /* @__PURE__ */ jsxs60("div", { className: "container_tightest", children: [
          /* @__PURE__ */ jsx130(Text14, { tag: "h2", className: "article-overview-block__title heading-2", field: fields.Title }),
          /* @__PURE__ */ jsx130(
            RichText_default,
            {
              className: "rich-text article-overview-block__text",
              field: fields.RichTextSimple
            }
          ),
          /* @__PURE__ */ jsx130(ArticleOverview_default, { className: "article-overview-block__list", children: items && items.map((item, index) => /* @__PURE__ */ jsx130(ArticleOverviewItem_default, { ...item }, index)) }),
          /* @__PURE__ */ jsx130(
            ButtonContainer_default,
            {
              className: "article-overview-block__button-container",
              rendering,
              placeholderName: "container-buttons"
            }
          )
        ] })
      ]
    }
  );
};
var ArticleOverviewAutomaticBlock_default = ArticleOverviewAutomaticBlock;

// src/blocks/ArticleOverviewBlock/ArticleOverviewBlock.tsx
import {
  Text as Text15,
  Placeholder as Placeholder7,
  getFieldValue as getFieldValue9
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx131, jsxs as jsxs61 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT5 = "white";
var DEFAULT_SHOW_DECORATIONS5 = true;
var ArticleOverviewBlock = (props) => {
  const { fields, rendering, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx131(NullDatasourceMessage_default, {});
  const variant = getFieldValue9(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT5
  );
  const showDecorations = getFieldValue9(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS5
  );
  return /* @__PURE__ */ jsx131(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `article-overview-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs61("div", { className: "container_tightest", children: [
        /* @__PURE__ */ jsx131(
          Text15,
          {
            tag: "h2",
            className: "heading-2 article-overview-block__title",
            field: fields.Title
          }
        ),
        /* @__PURE__ */ jsx131(
          RichText_default,
          {
            className: "rich-text article-overview-block__text",
            field: fields.RichTextSimple
          }
        ),
        /* @__PURE__ */ jsx131(ArticleOverview_default, { className: "article-overview-block__list", children: /* @__PURE__ */ jsx131(
          Placeholder7,
          {
            name: "container-article-overview",
            rendering
          }
        ) }),
        /* @__PURE__ */ jsx131(
          ButtonContainer_default,
          {
            className: "article-overview-block__button-container",
            rendering
          }
        )
      ] })
    }
  );
};
var ArticleOverviewBlock_default = ArticleOverviewBlock;

// src/blocks/CallToActionBlock/CallToActionBlock.tsx
import {
  Image as Image8,
  Placeholder as Placeholder8,
  Text as Text16,
  getFieldValue as getFieldValue10
} from "@sitecore-jss/sitecore-jss-react";
import { Fragment as Fragment19, jsx as jsx132, jsxs as jsxs62 } from "react/jsx-runtime";
var DEFAULT_IMAGE_ALIGNMENT2 = "left";
var DEFAULT_IMAGE_SHAPE = "rectangular";
var DEFAULT_BLOCK_VARIANT6 = "white";
var DEFAULT_SHOW_DECORATIONS6 = true;
var CallToActionBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx132(NullDatasourceMessage_default, {});
  const variant = getFieldValue10(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT6
  );
  const showDecorations = getFieldValue10(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS6
  );
  const imageAlignment = getFieldValue10(
    fields,
    "ImageAlignment",
    DEFAULT_IMAGE_ALIGNMENT2
  );
  const imageShape = getFieldValue10(
    fields,
    "ImageShape",
    DEFAULT_IMAGE_SHAPE
  );
  const {
    isTextFieldValid,
    isRichTextFieldValid,
    isImageFieldValid,
    isLinkFieldValid
  } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  const showStartingButton = isLinkFieldValid(fields.ButtonTarget) && isTextFieldValid(fields.ButtonText);
  return /* @__PURE__ */ jsx132(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `call-to-action-block ${showDecorations ? "" : "call-to-action-block_circles-none"} ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs62("div", { className: `container_tightest call-to-action-block__container ${imageShape === "round" ? "image-round" : "image-square"}`, children: [
        isImageFieldValid(fields.Image) && /* @__PURE__ */ jsxs62(
          "div",
          {
            className: `${imageAlignment === "right" ? "lg:order-last" : ""} call-to-action-block__image-container`,
            children: [
              /* @__PURE__ */ jsx132(
                "div",
                {
                  className: `${imageShape === "round" ? "call-to-action-block__image-round-wrapper" : "call-to-action-block__image-rectangular-wrapper"}`,
                  children: /* @__PURE__ */ jsx132(
                    Image8,
                    {
                      className: "call-to-action-block__image",
                      field: fields.Image,
                      srcSet: [{ mw: 416 }],
                      sizes: "416px"
                    }
                  )
                }
              ),
              imageShape === "round" && /* @__PURE__ */ jsxs62(Fragment19, { children: [
                /* @__PURE__ */ jsx132("div", { className: "call-to-action-block__image-round-decoration-background" }),
                /* @__PURE__ */ jsx132("div", { className: "call-to-action-block__image-round-decoration-foreground" })
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsxs62("div", { className: "call-to-action-block__text", children: [
          showText && /* @__PURE__ */ jsxs62("div", { className: "call-to-action-block__text-container", children: [
            /* @__PURE__ */ jsx132(
              Text16,
              {
                field: fields.Title,
                tag: "h2",
                className: "heading-2 call-to-action-block__title"
              }
            ),
            /* @__PURE__ */ jsx132(
              RichText_default,
              {
                field: fields.RichTextSimple,
                className: "rich-text call-to-action-block__subtitle"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs62("div", { className: "btn-container", children: [
            showStartingButton && /* @__PURE__ */ jsx132(
              Button_default,
              {
                fields: {
                  ButtonTarget: fields.ButtonTarget,
                  ButtonText: fields.ButtonText,
                  ButtonVariant: { value: "primary" }
                }
              }
            ),
            /* @__PURE__ */ jsx132(Placeholder8, { name: "container-buttons", rendering })
          ] })
        ] })
      ] })
    }
  );
};
var CallToActionBlock_default = CallToActionBlock;

// src/blocks/CardSliderBlock/CardSliderBlock.tsx
import React33 from "react";
import {
  Text as Text17,
  Placeholder as Placeholder9,
  getFieldValue as getFieldValue11
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx133, jsxs as jsxs63 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT7 = "white";
var DEFAULT_SHOW_DECORATIONS7 = true;
var CardSliderBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx133(NullDatasourceMessage_default, {});
  const variant = getFieldValue11(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT7
  );
  const showDecorations = getFieldValue11(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS7
  );
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  return /* @__PURE__ */ jsxs63(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `card-slider-block ${className}`,
      ...restProps,
      children: [
        /* @__PURE__ */ jsxs63("div", { className: "card-slider-block__text container_tightest", children: [
          /* @__PURE__ */ jsx133(
            Text17,
            {
              tag: "h2",
              className: "card-slider-block__title heading-2",
              field: fields.Title
            }
          ),
          /* @__PURE__ */ jsx133(
            RichText_default,
            {
              field: fields.RichTextSimple,
              className: "card-slider-block__description rich-text"
            }
          )
        ] }),
        /* @__PURE__ */ jsx133("div", { className: "container_normal", children: /* @__PURE__ */ jsx133(
          Placeholder9,
          {
            name: "container-cards",
            rendering,
            render: (components) => isEditingInSitecore ? /* @__PURE__ */ jsx133("div", { className: "card-slider-block__sitecore-ee-wrapper", children: components.map((component, index) => /* @__PURE__ */ jsx133(React33.Fragment, { children: component }, index)) }) : /* @__PURE__ */ jsx133(Reel_default, { className: "card-slider-block__reel", children: components.map((component, index) => /* @__PURE__ */ jsx133("div", { className: "card-slider-block__card", children: component }, index)) })
          }
        ) })
      ]
    }
  );
};
var CardSliderBlock_default = CardSliderBlock;

// src/blocks/CarouselBlock/CarouselBlock.tsx
import {
  Placeholder as Placeholder10,
  Text as Text18,
  getFieldValue as getFieldValue12
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx134, jsxs as jsxs64 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT8 = "white";
var DEFAULT_SHOW_DECORATIONS8 = true;
var DEFAULT_NAVIGATION_POSITION = "default";
var CarouselBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx134(NullDatasourceMessage_default, {});
  const variant = getFieldValue12(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT8
  );
  const showDecorations = getFieldValue12(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS8
  );
  const navigationPosition = getFieldValue12(
    fields,
    "NavigationPosition",
    DEFAULT_NAVIGATION_POSITION
  );
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  const { isTextFieldValid, isRichTextFieldValid } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  return /* @__PURE__ */ jsxs64(
    MediaBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `carousel-block ${className}`,
      ...restProps,
      children: [
        showText && /* @__PURE__ */ jsxs64("div", { className: "container_tightest", children: [
          /* @__PURE__ */ jsx134(
            Text18,
            {
              tag: "h2",
              field: fields.Title,
              className: `heading-2 ${isRichTextFieldValid(fields.RichTextSimple) ? "mb-3" : ""}`
            }
          ),
          /* @__PURE__ */ jsx134(RichText_default, { field: fields.RichTextSimple })
        ] }),
        /* @__PURE__ */ jsx134("div", { className: "container_normal", children: /* @__PURE__ */ jsx134(
          Placeholder10,
          {
            name: "container-carousel",
            rendering,
            render: (components) => !isEditingInSitecore ? /* @__PURE__ */ jsx134(Carousel_default, { variant: navigationPosition, children: components.map((component, index) => /* @__PURE__ */ jsx134(Carousel_default.Item, { children: component }, index)) }) : /* @__PURE__ */ jsx134("div", { className: "carousel-block__sitecore-ee-wrapper", children: components.map((component) => component) })
          }
        ) })
      ]
    }
  );
};
var CarouselBlock_default = CarouselBlock;

// src/blocks/ContactCardsBlock/ContactCards.tsx
import { jsx as jsx135 } from "react/jsx-runtime";
var getLayoutClassName = (layout) => {
  switch (layout) {
    case "two-column":
      return "contact-cards_two-columns";
    case "list":
    default:
      return "";
  }
};
var ContactCards = (props) => {
  const { layout = "list", className = "", children, ...restProps } = props;
  const layoutClassName = getLayoutClassName(layout);
  return /* @__PURE__ */ jsx135(
    "div",
    {
      className: `contact-cards ${layoutClassName} ${className}`,
      ...restProps,
      children
    }
  );
};
var ContactCards_default = ContactCards;

// src/blocks/ContactCardsBlock/ContactCardsItem.tsx
import { Image as Image9, Text as Text19, getFieldValue as getFieldValue13 } from "@sitecore-jss/sitecore-jss-react";
import { useTranslation as useTranslation21 } from "react-i18next";
import { jsx as jsx136, jsxs as jsxs65 } from "react/jsx-runtime";
var styledPhoneNumbers = ["06", "+316", "316"];
var getPhoneNumber = (phoneNumber) => {
  const phoneNumberStart = phoneNumber.substring(0, phoneNumber.length - 8);
  if (!styledPhoneNumbers.includes(phoneNumberStart)) {
    return phoneNumber;
  }
  const phoneNumberRest = phoneNumber.substring(phoneNumber.length - 8, phoneNumber.length).match(/.{1,2}/g);
  return `
      ${phoneNumberStart} 
      - ${phoneNumberRest == null ? void 0 : phoneNumberRest.join(" ")}`;
};
var removeWhitespaceAndSymbols = (phoneNumber) => String(phoneNumber).replace(/[^a-zA-Z0-9+ ]|\s+/g, "");
var ContactCardsItem = (props) => {
  var _a;
  const { fields, className = "", ...restProps } = props;
  const { isTextFieldValid, isLinkFieldValid } = useIsFieldValid_default();
  const phoneNumber = getFieldValue13(fields, "PhoneNumberButtonTitle", null);
  const formattedPhoneNumber = isTextFieldValid(fields.Title) ? getPhoneNumber(
    String(
      getFieldValue13(
        fields,
        "PhoneNumberButtonTitle",
        ""
      )
    )
  ) : null;
  const { t } = useTranslation21("ui");
  const name = getFieldValue13(fields, "Title", t("contact-person"));
  return /* @__PURE__ */ jsx136(Card_default, { className: `contact-cards__item ${className}`, ...restProps, children: /* @__PURE__ */ jsxs65(
    MediaObject_default,
    {
      media: /* @__PURE__ */ jsxs65("div", { className: "contact-cards__image-wrapper", children: [
        /* @__PURE__ */ jsx136(
          Image9,
          {
            className: "contact-cards__image",
            field: fields.Image,
            srcSet: [{ mw: 416 }],
            sizes: "416px"
          }
        ),
        /* @__PURE__ */ jsxs65("div", { className: "contact-cards__socials-container", children: [
          isLinkFieldValid(fields.LinkedInTarget) && /* @__PURE__ */ jsx136(
            RoutableSitecoreLink_default,
            {
              "aria-label": `${t("link-to")} ${name} linkedin account`,
              editable: false,
              field: fields.LinkedInTarget,
              target: "_blank",
              className: "contact-cards__social-link",
              children: /* @__PURE__ */ jsx136(LinkedInIcon_default, { className: "contact-cards__social-icon" })
            }
          ),
          isLinkFieldValid(fields.XTarget) || isLinkFieldValid(fields.TwitterTarget) && /* @__PURE__ */ jsx136(
            RoutableSitecoreLink_default,
            {
              "aria-label": `${t("link-to")} ${name} X account`,
              editable: false,
              field: fields.XTarget || fields.TwitterTarget,
              target: "_blank",
              className: "contact-cards__social-link",
              children: /* @__PURE__ */ jsx136(XIcon_default, { className: "contact-cards__social-icon" })
            }
          )
        ] })
      ] }),
      children: [
        /* @__PURE__ */ jsxs65("div", { className: "contact-cards__text", children: [
          /* @__PURE__ */ jsx136(
            Text19,
            {
              tag: "h3",
              field: fields.Title,
              className: "contact-cards__item-title"
            }
          ),
          /* @__PURE__ */ jsx136(
            Text19,
            {
              field: fields.SubTitle,
              className: "contact-cards__item-subtitle"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs65("div", { className: "contact-cards__buttons", children: [
          isTextFieldValid(fields.MailButtonTarget) && /* @__PURE__ */ jsxs65(
            RoutableSitecoreLink_default,
            {
              "aria-label": `${t("send-mail-to")} ${name}`,
              editable: false,
              field: {
                href: `mailto:${fields.MailButtonTarget.value}${((_a = fields.MailButtonSubject) == null ? void 0 : _a.value) ? `?subject=${fields.MailButtonSubject.value}` : ""}`
              },
              target: "_blank",
              className: "btn btn_primary",
              children: [
                isTextFieldValid(fields.MailButtonTitle) ? /* @__PURE__ */ jsx136(Text19, { field: fields.MailButtonTitle }) : /* @__PURE__ */ jsx136("span", { children: t("send a message") }),
                /* @__PURE__ */ jsx136(ArrowRightIcon_default, { className: "contact-cards__button-icon" })
              ]
            }
          ),
          phoneNumber && formattedPhoneNumber && /* @__PURE__ */ jsxs65(
            RoutableSitecoreLink_default,
            {
              "aria-label": `${t("call")} ${name}`,
              editable: false,
              field: {
                href: `tel:${removeWhitespaceAndSymbols(phoneNumber)}`
              },
              target: "_blank",
              className: "btn btn_secondary",
              children: [
                /* @__PURE__ */ jsx136("div", { className: "contact-cards__phone-number", children: formattedPhoneNumber }),
                /* @__PURE__ */ jsx136(PhoneIcon_default, { className: "contact-cards__button-icon" })
              ]
            }
          )
        ] })
      ]
    }
  ) });
};
var ContactCardsItem_default = ContactCardsItem;

// src/blocks/ContactCardsBlock/ContactCardsBlock.tsx
import {
  Placeholder as Placeholder11,
  Text as Text20,
  getFieldValue as getFieldValue14
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx137, jsxs as jsxs66 } from "react/jsx-runtime";
var DEFAULT_LAYOUT = "list";
var DEFAULT_BLOCK_VARIANT9 = "white";
var DEFAULT_SHOW_DECORATIONS9 = true;
var ContactCardsBlock = (props) => {
  const { fields, rendering, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx137(NullDatasourceMessage_default, {});
  const variant = getFieldValue14(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT9
  );
  const showDecorations = getFieldValue14(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS9
  );
  const layout = getFieldValue14(
    fields,
    "Layout",
    DEFAULT_LAYOUT
  );
  return /* @__PURE__ */ jsx137(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `contact-cards-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs66("div", { className: "contact-cards-block__container", children: [
        /* @__PURE__ */ jsx137(
          Text20,
          {
            tag: "h2",
            className: "heading-2 contact-cards-block__title",
            field: fields.Title
          }
        ),
        /* @__PURE__ */ jsx137(
          RichText_default,
          {
            field: fields.RichTextSimple,
            className: "rich-text contact-cards-block__subtitle"
          }
        ),
        /* @__PURE__ */ jsx137(ContactCards_default, { layout, children: /* @__PURE__ */ jsx137(Placeholder11, { name: "container-contact-cards", rendering }) })
      ] })
    }
  );
};
var ContactCardsBlock_default = ContactCardsBlock;

// src/blocks/HeroArticleBlock/HeroArticleBlock.tsx
import {
  Image as Image10,
  Text as Text21,
  useSitecoreContext as useSitecoreContext14,
  getFieldValue as getFieldValue15
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx138, jsxs as jsxs67 } from "react/jsx-runtime";
var DEFAULT_HERO_BLOCK_VARIANT = "light";
var DEFAULT_SHOW_DECORATIONS10 = true;
var DEFAULT_SHOW_SHARE_BUTTONS = true;
var DEFAULT_SHOW_DATE_TIME = true;
var DEFAULT_SHOW_TAGS = true;
var DEFAULT_SHOW_STATUS = true;
var HeroArticleBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx138(NullDatasourceMessage_default, {});
  const getVariantClassName3 = (variant2) => {
    switch (variant2) {
      case "light":
        return "hero-article-block_light";
      case "dark":
        return "hero-article-block_dark";
      default:
        return "";
    }
  };
  const { isImageFieldValid, isDateFieldValid } = useIsFieldValid_default();
  const variant = getFieldValue15(
    fields,
    "HeroBlockVariant",
    DEFAULT_HERO_BLOCK_VARIANT
  );
  const showDecorations = getFieldValue15(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS10
  );
  const { sitecoreContext } = useSitecoreContext14();
  const breadcrumbs = sitecoreContext.breadcrumbs;
  const projectPageProps = sitecoreContext.projectPageProps;
  const {
    tags = [],
    date = "",
    showTags = DEFAULT_SHOW_TAGS,
    showDateTime = DEFAULT_SHOW_DATE_TIME,
    showShareButtons = DEFAULT_SHOW_SHARE_BUTTONS,
    showStatus = DEFAULT_SHOW_STATUS
  } = projectPageProps;
  const dateField = {
    value: date
  };
  const showDate = showDateTime && isDateFieldValid(dateField);
  const showImage = isImageFieldValid(fields.Image);
  return /* @__PURE__ */ jsxs67(
    "div",
    {
      className: `hero-article-block ${showDecorations ? "" : "hero-article-block_circles-none"} ${getVariantClassName3(variant)} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx138(Decorations_default, {}),
        showImage && /* @__PURE__ */ jsx138("div", { className: "hero-article-block__image-wrapper", children: /* @__PURE__ */ jsx138(Image10, { field: fields.Image, className: "hero-article-block__image" }) }),
        /* @__PURE__ */ jsxs67("div", { className: "container_tightest mt-7 sm:mt-10 md:mt-12", children: [
          breadcrumbs && /* @__PURE__ */ jsx138(Breadcrumbs_default, { items: breadcrumbs }),
          /* @__PURE__ */ jsx138(
            Text21,
            {
              tag: "h1",
              className: "hero-article-block__title heading-1_serif",
              field: fields.Title
            }
          ),
          /* @__PURE__ */ jsx138(
            Text21,
            {
              tag: "h4",
              className: "hero-article-block__subtitle heading-4",
              field: fields.Subtitle
            }
          ),
          /* @__PURE__ */ jsxs67("div", { className: "hero-article-block__container", children: [
            /* @__PURE__ */ jsx138("div", { className: "hero-article-block__hashtag-container", children: showTags ? tags.map((tag) => /* @__PURE__ */ jsx138(Hashtag_default, { fields: {
              HashtagText: {
                value: tag.name
              }
            } }, tag.id)) : "" }),
            /* @__PURE__ */ jsxs67("div", { className: "hero-article-block__share-buttons-and-date-container", children: [
              showShareButtons && /* @__PURE__ */ jsx138(
                ShareButtons_default,
                {
                  className: "hero-article-block__share-buttons",
                  shareButtons: ["facebook", "linkedin", "twitter"]
                }
              ),
              showDate && /* @__PURE__ */ jsx138("div", { className: "hero-article-block__location-and-date", children: /* @__PURE__ */ jsx138(
                Date_default,
                {
                  field: dateField,
                  className: "hero-article-block__date"
                }
              ) })
            ] })
          ] })
        ] })
      ]
    }
  );
};
var HeroArticleBlock_default = HeroArticleBlock;

// src/blocks/HeroBlock/HeroBlock.tsx
import {
  Text as Text22,
  getFieldValue as getFieldValue16,
  useSitecoreContext as useSitecoreContext15
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx139, jsxs as jsxs68 } from "react/jsx-runtime";
var DEFAULT_HERO_BLOCK_VARIANT2 = "light";
var DEFAULT_SHOW_DECORATIONS11 = true;
var HeroBlock = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx139(NullDatasourceMessage_default, {});
  const variant = getFieldValue16(
    fields,
    "HeroBlockVariant",
    DEFAULT_HERO_BLOCK_VARIANT2
  );
  const showDecorations = getFieldValue16(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS11
  );
  const getVariantClassName3 = (variant2) => {
    switch (variant2) {
      case "light":
        return "hero-block_light";
      case "dark":
        return "hero-block_dark";
      default:
        return "";
    }
  };
  const { sitecoreContext } = useSitecoreContext15();
  const breadcrumbs = sitecoreContext.breadcrumbs;
  return /* @__PURE__ */ jsxs68(
    "div",
    {
      className: `hero-block ${showDecorations ? "" : "hero-block_circles-none"} ${getVariantClassName3(variant)} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx139(Decorations_default, {}),
        breadcrumbs && /* @__PURE__ */ jsx139(Breadcrumbs_default, { className: "hero-block__breadcrumbs", items: breadcrumbs }),
        /* @__PURE__ */ jsxs68("div", { className: "container_tightest mt-6 sm:mt-10 xl:mt-16", children: [
          /* @__PURE__ */ jsx139(Text22, { tag: "h1", className: "heading-1", field: fields.Title }),
          /* @__PURE__ */ jsx139(
            RichText_default,
            {
              className: "rich-text introtext mt-5",
              field: fields.RichTextSimple
            }
          )
        ] })
      ]
    }
  );
};
var HeroBlock_default = HeroBlock;

// src/blocks/HeroWithCarouselBlock/HeroWithCarouselBlock.tsx
import React34 from "react";
import {
  Text as Text23,
  useSitecoreContext as useSitecoreContext16,
  Placeholder as Placeholder12,
  getFieldValue as getFieldValue17
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx140, jsxs as jsxs69 } from "react/jsx-runtime";
var DEFAULT_HERO_BLOCK_VARIANT3 = "light";
var DEFAULT_SHOW_DECORATIONS12 = true;
var HeroWithCarouselBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx140(NullDatasourceMessage_default, {});
  const variant = getFieldValue17(
    fields,
    "HeroBlockVariant",
    DEFAULT_HERO_BLOCK_VARIANT3
  );
  const showDecorations = getFieldValue17(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS12
  );
  const getVariantClassName3 = (variant2) => {
    switch (variant2) {
      case "light":
        return "hero-with-carousel-block_light";
      case "dark":
        return "hero-with-carousel-block_dark";
      default:
        return "";
    }
  };
  const { sitecoreContext } = useSitecoreContext16();
  const breadcrumbs = sitecoreContext.breadcrumbs;
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  const { isTextFieldValid, isRichTextFieldValid } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  return /* @__PURE__ */ jsxs69(
    "div",
    {
      className: `hero-with-carousel-block ${showDecorations ? "" : "hero-with-carousel-block_circles-none"} ${getVariantClassName3(variant)} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx140(Decorations_default, {}),
        breadcrumbs && /* @__PURE__ */ jsx140(
          Breadcrumbs_default,
          {
            className: "hero-with-carousel-block__breadcrumbs",
            items: breadcrumbs
          }
        ),
        showText && /* @__PURE__ */ jsx140("div", { className: "container_tightest", children: /* @__PURE__ */ jsxs69("div", { className: "hero-with-carousel-block__text-container", children: [
          /* @__PURE__ */ jsx140(Text23, { tag: "h1", className: "heading-1", field: fields.Title }),
          /* @__PURE__ */ jsx140(
            RichText_default,
            {
              className: "rich-text introtext",
              field: fields.RichTextSimple
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx140("div", { className: "container_normal", children: /* @__PURE__ */ jsx140("div", { className: "hero-with-carousel-block__carousel-container", children: /* @__PURE__ */ jsx140(
          Placeholder12,
          {
            name: "container-hero-with-carousel",
            rendering,
            render: (components) => isEditingInSitecore ? /* @__PURE__ */ jsx140("div", { className: "hero-with-carousel-block__sitecore-ee-wrapper", children: components.map((component, index) => /* @__PURE__ */ jsx140(React34.Fragment, { children: component }, index)) }) : /* @__PURE__ */ jsx140(
              Carousel_default,
              {
                className: "hero-with-carousel-block__carousel",
                variant: "navigation-position-bottom",
                children: components.map((component, index) => /* @__PURE__ */ jsx140(Carousel_default.Item, { children: component }, index))
              }
            )
          }
        ) }) })
      ]
    }
  );
};
var HeroWithCarouselBlock_default = HeroWithCarouselBlock;

// src/blocks/HeroWithCoverImageBlock/HeroWithCoverImageBlock.tsx
import { Text as Text24, getFieldValue as getFieldValue18 } from "@sitecore-jss/sitecore-jss-react";

// src/media/CoverImage/CoverImage.tsx
import { Image as Image11 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx141, jsxs as jsxs70 } from "react/jsx-runtime";
var CoverImage = (props) => {
  const { image, children, className = "", ...restProps } = props;
  const { isImageFieldValid } = useIsFieldValid_default();
  return /* @__PURE__ */ jsxs70("div", { className: `cover-image ${className}`, ...restProps, children: [
    isImageFieldValid(image) && /* @__PURE__ */ jsx141("div", { className: "cover-image__image-wrapper", children: /* @__PURE__ */ jsx141(
      Image11,
      {
        className: "cover-image__image",
        field: image,
        srcSet: [{ mw: 416 }, { mw: 1280 }, { mw: 1600 }],
        sizes: "(max-width: 640px) 416px, (max-width: 1280px) 1280px, 1600"
      }
    ) }),
    children && /* @__PURE__ */ jsx141("div", { className: "cover-image__content", children })
  ] });
};
var CoverImage_default = CoverImage;

// src/blocks/HeroWithCoverImageBlock/HeroWithCoverImageBlock.tsx
import {
  motion as motion7,
  useTransform
} from "framer-motion";
import { Fragment as Fragment20, jsx as jsx142, jsxs as jsxs71 } from "react/jsx-runtime";
var DEFAULT_SHOW_DECORATIONS13 = true;
var SCROLL_DOWN_BUTTON_ICON_VARIANTS = {
  up: {
    y: -10,
    transition: {
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut",
      duration: 0.75
    }
  },
  down: { y: 10 }
};
var HeroWithCoverImageBlock = (props) => {
  const { fields, rendering, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx142(NullDatasourceMessage_default, {});
  const showDecorations = getFieldValue18(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS13
  );
  const content = /* @__PURE__ */ jsxs71(Fragment20, { children: [
    /* @__PURE__ */ jsx142(Text24, { tag: "h1", className: "heading-1", field: fields.Title }),
    /* @__PURE__ */ jsx142(RichText_default, { className: "rich-text introtext", field: fields.RichTextSimple })
  ] });
  const scrollToContent = () => {
    var _a, _b;
    const bodyTop = document.body.getBoundingClientRect().top || 0;
    const headerHeight = ((_a = document.querySelector(".page-layout__header")) == null ? void 0 : _a.getBoundingClientRect().height) || 0;
    const firstContentBlockTop = ((_b = document.querySelector(".page-block-wrapper")) == null ? void 0 : _b.getBoundingClientRect().top) || 0;
    window.scrollTo({
      top: firstContentBlockTop - bodyTop - headerHeight,
      behavior: "smooth"
    });
  };
  return /* @__PURE__ */ jsxs71(
    "div",
    {
      className: `hero-with-cover-image-block ${showDecorations ? "" : "hero-with-cover-image-block_circles-none"} ${className}`,
      ...restProps,
      children: [
        /* @__PURE__ */ jsxs71("div", { className: "hero-with-cover-image-block__visuals-container", children: [
          showDecorations && /* @__PURE__ */ jsx142(Decorations_default, {}),
          /* @__PURE__ */ jsx142(CoverImage_default, { image: fields.Image, children: /* @__PURE__ */ jsxs71("div", { className: "hero-with-cover-image-block__text container_tightest", children: [
            content,
            /* @__PURE__ */ jsx142("div", { className: "hero-with-cover-image-block__buttons-container", children: /* @__PURE__ */ jsx142("button", { className: "hero-with-cover-image-block__scroll-down-button", onClick: scrollToContent, children: /* @__PURE__ */ jsx142(
              motion7.div,
              {
                variants: SCROLL_DOWN_BUTTON_ICON_VARIANTS,
                initial: "down",
                animate: "up",
                children: /* @__PURE__ */ jsx142(ArrowDownWideIcon_default, {})
              }
            ) }) })
          ] }) })
        ] }),
        /* @__PURE__ */ jsx142("div", { className: "hero-with-cover-image-block__responsive-submenu", children: /* @__PURE__ */ jsx142(ResponsiveSubmenu_default, { rendering }) }),
        /* @__PURE__ */ jsx142("div", { className: "hero-with-cover-image-block__text-below-image", children: content })
      ]
    }
  );
};
var HeroWithCoverImageBlock_default = HeroWithCoverImageBlock;

// src/blocks/HeroWithCoverVideoBlock/HeroWithCoverVideoBlock.tsx
import React35, { useState as useState26 } from "react";
import { Text as Text25, getFieldValue as getFieldValue19 } from "@sitecore-jss/sitecore-jss-react";
import { motion as motion8 } from "framer-motion";
import { useTranslation as useTranslation22 } from "react-i18next";
import { jsx as jsx143, jsxs as jsxs72 } from "react/jsx-runtime";
var DEFAULT_SHOW_DECORATIONS14 = true;
var SCROLL_DOWN_BUTTON_ICON_VARIANTS2 = {
  up: {
    y: -10,
    transition: {
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut",
      duration: 0.75
    }
  },
  down: { y: 10 }
};
var HeroWithCoverVideoBlock = (props) => {
  var _a, _b;
  const { fields, rendering, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx143(NullDatasourceMessage_default, {});
  const showDecorations = getFieldValue19(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS14
  );
  const videoRef = React35.useRef(null);
  const videoUrl = fields.Video;
  const [isVideoPaused, setIsVideoPaused] = useState26(
    Boolean((_a = videoRef.current) == null ? void 0 : _a.paused)
  );
  const handlePauseVideo = () => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
        setIsVideoPaused(false);
      } else {
        video.pause();
        setIsVideoPaused(true);
      }
    }
  };
  const { t } = useTranslation22();
  const scrollToContent = () => {
    var _a2, _b2;
    const bodyTop = document.body.getBoundingClientRect().top || 0;
    const headerHeight = ((_a2 = document.querySelector(".page-layout__header")) == null ? void 0 : _a2.getBoundingClientRect().height) || 0;
    const firstContentBlockTop = ((_b2 = document.querySelector(".page-block-wrapper")) == null ? void 0 : _b2.getBoundingClientRect().top) || 0;
    window.scrollTo({
      top: firstContentBlockTop - bodyTop - headerHeight,
      behavior: "smooth"
    });
  };
  return /* @__PURE__ */ jsx143(
    "div",
    {
      className: `hero-with-cover-video-block ${showDecorations ? "" : "hero-with-cover-video-block_circles-none"} ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs72("div", { className: "hero-with-cover-video-block__visuals-container", children: [
        /* @__PURE__ */ jsxs72("div", { className: "cover-video", children: [
          /* @__PURE__ */ jsx143("div", { className: "cover-video__video-wrapper", children: /* @__PURE__ */ jsx143(
            "video",
            {
              ref: videoRef,
              src: (_b = videoUrl.value) == null ? void 0 : _b.src,
              className: "cover-video__video",
              autoPlay: true,
              muted: true,
              loop: true
            }
          ) }),
          /* @__PURE__ */ jsx143("div", { className: "hero-with-cover-video-block__text", children: /* @__PURE__ */ jsx143(
            Text25,
            {
              tag: "h1",
              className: "hero-with-cover-video-block__title heading-1",
              field: fields.Title
            }
          ) }),
          /* @__PURE__ */ jsx143("div", { className: "hero-with-cover-video-block__buttons-container", children: /* @__PURE__ */ jsxs72("div", { className: "container_normal", children: [
            /* @__PURE__ */ jsx143("div", { className: "hero-with-cover-video-block__top-buttons", children: /* @__PURE__ */ jsx143("div", { className: "hero-with-cover-video-block__button-wrapper", children: /* @__PURE__ */ jsx143(
              "button",
              {
                className: "hero-with-cover-video-block__scroll-down-button",
                onClick: scrollToContent,
                "aria-label": t("scroll-to-content") || "Scroll to content",
                title: t("scroll-to-content") || "Scroll to content",
                children: /* @__PURE__ */ jsx143(
                  motion8.div,
                  {
                    variants: SCROLL_DOWN_BUTTON_ICON_VARIANTS2,
                    initial: "down",
                    animate: "up",
                    children: /* @__PURE__ */ jsx143(ArrowDownWideIcon_default, {})
                  }
                )
              }
            ) }) }),
            /* @__PURE__ */ jsxs72("div", { className: "hero-with-cover-video-block__bottom-buttons", children: [
              /* @__PURE__ */ jsx143("div", { className: "hero-with-cover-video-block__button-wrapper", children: /* @__PURE__ */ jsxs72(
                RoutableSitecoreLink_default,
                {
                  field: fields.VideoLink,
                  target: "_blank",
                  className: "btn btn_primary hero-with-cover-video-block__video-button",
                  "aria-label": t("watch-full-video").toString(),
                  children: [
                    t("watch-full-video"),
                    " ",
                    /* @__PURE__ */ jsx143(ExternalLinkIcon_default, {})
                  ]
                }
              ) }),
              /* @__PURE__ */ jsx143("div", { className: "hero-with-cover-video-block__button-wrapper", children: /* @__PURE__ */ jsxs72(
                "button",
                {
                  className: "btn btn_secondary hero-with-cover-video-block__pause-button",
                  "aria-label": `${t(`${isVideoPaused ? "play-video" : "pause-video"}`)}`,
                  title: `${t(`${isVideoPaused ? "play-video" : "pause-video"}`)}`,
                  onClick: handlePauseVideo,
                  children: [
                    /* @__PURE__ */ jsx143("span", { className: "noindex", children: `${t(`${isVideoPaused ? "play-video" : "pause-video"}`)}` }),
                    " ",
                    /* @__PURE__ */ jsx143(PauseIcon_default, {})
                  ]
                }
              ) })
            ] })
          ] }) })
        ] }),
        showDecorations && /* @__PURE__ */ jsx143(Decorations_default, {})
      ] })
    }
  );
};
var HeroWithCoverVideoBlock_default = HeroWithCoverVideoBlock;

// src/blocks/HeroWithImageBlock/HeroWithImageBlock.tsx
import {
  Text as Text26,
  Image as Image12,
  useSitecoreContext as useSitecoreContext17,
  getFieldValue as getFieldValue20
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx144, jsxs as jsxs73 } from "react/jsx-runtime";
var DEFAULT_HERO_BLOCK_VARIANT4 = "light";
var DEFAULT_SHOW_DECORATIONS15 = true;
var HeroWithImageBlock = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx144(NullDatasourceMessage_default, {});
  const variant = getFieldValue20(
    fields,
    "HeroBlockVariant",
    DEFAULT_HERO_BLOCK_VARIANT4
  );
  const showDecorations = getFieldValue20(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS15
  );
  const getVariantClassName3 = (variant2) => {
    switch (variant2) {
      case "light":
        return "hero-with-image-block_light";
      case "dark":
        return "hero-with-image-block_dark";
      default:
        return "";
    }
  };
  const { sitecoreContext } = useSitecoreContext17();
  const breadcrumbs = sitecoreContext.breadcrumbs;
  const { isImageFieldValid } = useIsFieldValid_default();
  return /* @__PURE__ */ jsxs73(
    "div",
    {
      className: `hero-with-image-block ${showDecorations ? "" : "hero-with-image-block_circles-none"} ${getVariantClassName3(variant)} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx144(Decorations_default, {}),
        breadcrumbs && /* @__PURE__ */ jsx144(
          Breadcrumbs_default,
          {
            className: "hero-with-image-block__breadcrumbs",
            items: breadcrumbs
          }
        ),
        /* @__PURE__ */ jsx144("div", { className: "container_tightest mt-6 sm:mt-10 xl:mt-22", children: /* @__PURE__ */ jsxs73("div", { children: [
          /* @__PURE__ */ jsx144(Text26, { tag: "h1", className: "heading-1", field: fields.Title }),
          /* @__PURE__ */ jsx144(
            RichText_default,
            {
              className: "rich-text introtext mt-5",
              field: fields.RichTextSimple
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx144("div", { className: "container_normal mt-8 sm:mt-10 xl:mt-14", children: isImageFieldValid(fields.Image) && /* @__PURE__ */ jsx144("div", { className: "hero-with-image-block__image-wrapper", children: /* @__PURE__ */ jsx144(
          Image12,
          {
            editable: true,
            className: "hero-with-image-block__image",
            field: fields.Image,
            srcSet: [{ mw: 416 }, { mw: 1280 }],
            sizes: "(max-width: 640px) 416px, 1280px"
          }
        ) }) })
      ]
    }
  );
};
var HeroWithImageBlock_default = HeroWithImageBlock;

// src/blocks/HeroWithLargeCardSliderBlock/HeroWithLargeCardSliderBlock.tsx
import React36 from "react";
import {
  Placeholder as Placeholder13,
  Text as Text27,
  getFieldValue as getFieldValue21,
  useSitecoreContext as useSitecoreContext18
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx145, jsxs as jsxs74 } from "react/jsx-runtime";
var DEFAULT_HERO_BLOCK_VARIANT5 = "light";
var DEFAULT_SHOW_DECORATIONS16 = true;
var HeroWithLargeCardSliderBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx145(NullDatasourceMessage_default, {});
  const variant = getFieldValue21(
    fields,
    "HeroBlockVariant",
    DEFAULT_HERO_BLOCK_VARIANT5
  );
  const showDecorations = getFieldValue21(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS16
  );
  const getVariantClassName3 = (variant2) => {
    switch (variant2) {
      case "light":
        return "hero-with-large-card-slider-block_light";
      case "dark":
        return "hero-with-large-card-slider-block_dark";
      default:
        return "";
    }
  };
  const { sitecoreContext } = useSitecoreContext18();
  const breadcrumbs = sitecoreContext.breadcrumbs;
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  const { isTextFieldValid, isRichTextFieldValid } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  return /* @__PURE__ */ jsxs74(
    "div",
    {
      className: `hero-with-large-card-slider-block ${showDecorations ? "" : "hero-with-large-card-slider-block_circles-none"} ${getVariantClassName3(variant)} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx145(Decorations_default, {}),
        breadcrumbs && /* @__PURE__ */ jsx145(
          Breadcrumbs_default,
          {
            className: "hero-with-large-card-slider-block__breadcrumbs",
            items: breadcrumbs
          }
        ),
        showText && /* @__PURE__ */ jsxs74("div", { className: "container_tight mt-6 sm:mt-10 xl:mt-22 mb-8 md:mb-10 xl:mb-14", children: [
          /* @__PURE__ */ jsx145(Text27, { tag: "h1", className: "heading-1", field: fields.Title }),
          /* @__PURE__ */ jsx145(
            RichText_default,
            {
              className: "rich-text introtext mt-3",
              field: fields.RichTextSimple
            }
          )
        ] }),
        /* @__PURE__ */ jsx145("div", { className: "container_normal", children: /* @__PURE__ */ jsx145(
          Placeholder13,
          {
            name: "container-cards",
            rendering,
            render: (components) => isEditingInSitecore ? /* @__PURE__ */ jsx145("div", { className: "hero-with-large-card-slider-block__sitecore-ee-wrapper", children: components.map((component, index) => /* @__PURE__ */ jsx145(React36.Fragment, { children: component }, index)) }) : /* @__PURE__ */ jsx145(Reel_default, { className: "hero-with-large-card-slider-block__reel", children: components.map((component, index) => /* @__PURE__ */ jsx145(
              "div",
              {
                className: "hero-with-large-card-slider-block__card",
                children: component
              },
              index
            )) })
          }
        ) })
      ]
    }
  );
};
var HeroWithLargeCardSliderBlock_default = HeroWithLargeCardSliderBlock;

// src/blocks/HeroWithOverviewBlock/HeroWithOverviewBlock.tsx
import {
  Placeholder as Placeholder14,
  Text as Text29,
  getFieldValue as getFieldValue22
} from "@sitecore-jss/sitecore-jss-react";

// src/media/ImageCard/ImageCard.tsx
import { Image as Image13, Text as Text28 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx146, jsxs as jsxs75 } from "react/jsx-runtime";
var ImageCard = (props) => {
  const {
    fields,
    variant = "default",
    className = "",
    children,
    ...restProps
  } = props;
  const { isImageFieldValid } = useIsFieldValid_default();
  return /* @__PURE__ */ jsx146(
    RoutableSitecoreLink_default,
    {
      field: fields.LinkTarget,
      editable: false,
      className: "h-full image-card-link",
      children: /* @__PURE__ */ jsxs75(
        Card_default,
        {
          className: `image-card ${variant === "default" ? "image-card_default" : "image-card_body-position-bottom-right"} ${className}`,
          ...restProps,
          children: [
            /* @__PURE__ */ jsx146(Card_default.Header, { className: "image-card__header", children: /* @__PURE__ */ jsx146(
              Image13,
              {
                className: "image-card__header-image",
                field: fields.Image,
                srcSet: [{ mw: 416 }, { mw: 1280 }],
                sizes: "(max-width: 640px) 416px, 1280px"
              }
            ) }),
            /* @__PURE__ */ jsxs75(Card_default.Body, { className: "image-card__body", children: [
              isImageFieldValid(fields.Logo) && /* @__PURE__ */ jsx146(
                Image13,
                {
                  className: "image-card__logo",
                  field: fields.Logo,
                  srcSet: [{ mw: 192 }],
                  sizes: "192px"
                }
              ),
              /* @__PURE__ */ jsxs75("div", { className: "image-card__content-container", children: [
                /* @__PURE__ */ jsx146(
                  Text28,
                  {
                    className: "image-card__title",
                    tag: "div",
                    field: fields.Title
                  }
                ),
                /* @__PURE__ */ jsxs75("div", { className: "image-card__link", children: [
                  /* @__PURE__ */ jsx146(
                    Text28,
                    {
                      field: fields.SubTitle,
                      tag: "span",
                      className: "image-card__subtitle"
                    }
                  ),
                  /* @__PURE__ */ jsx146(ArrowRightIcon_default, { className: "image-card__icon" })
                ] })
              ] })
            ] })
          ]
        }
      )
    }
  );
};
var ImageCard_default = ImageCard;

// src/blocks/HeroWithOverviewBlock/HeroWithOverviewBlock.tsx
import { jsx as jsx147, jsxs as jsxs76 } from "react/jsx-runtime";
var DEFAULT_HERO_BLOCK_VARIANT6 = "light";
var DEFAULT_SHOW_DECORATIONS17 = true;
var HeroWithOverviewBlock = (props) => {
  const { fields, rendering, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx147(NullDatasourceMessage_default, {});
  const variant = getFieldValue22(
    fields,
    "HeroBlockVariant",
    DEFAULT_HERO_BLOCK_VARIANT6
  );
  const showDecorations = getFieldValue22(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS17
  );
  const getVariantClassName3 = (variant2) => {
    switch (variant2) {
      case "light":
        return "hero-with-overview-block_light";
      case "dark":
        return "hero-with-overview-block_dark";
      default:
        return "";
    }
  };
  return /* @__PURE__ */ jsxs76(
    "div",
    {
      className: `hero-with-overview-block ${showDecorations ? "" : "hero-with-overview-block_circles-none"} ${getVariantClassName3(variant)} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx147(Decorations_default, {}),
        /* @__PURE__ */ jsxs76("div", { className: "hero-with-overview-block__container", children: [
          /* @__PURE__ */ jsx147("div", { className: "hero-with-overview-block__text-and-select container_tightest", children: /* @__PURE__ */ jsx147(
            Text29,
            {
              tag: "h1",
              className: "heading-1 hero-with-overview-block__title",
              field: fields.Title
            }
          ) }),
          /* @__PURE__ */ jsx147("div", { children: /* @__PURE__ */ jsx147(
            Placeholder14,
            {
              name: "container-article-overview",
              rendering,
              render: (components, data) => {
                return /* @__PURE__ */ jsx147("div", { className: "hero-with-overview-block__grid", children: data.map((dataItem, index) => {
                  if ("type" in dataItem && (dataItem == null ? void 0 : dataItem.type) === "text/sitecore")
                    return components[index];
                  const fields2 = "fields" in dataItem ? dataItem.fields : {};
                  return /* @__PURE__ */ jsx147(
                    ImageCard_default,
                    {
                      fields: fields2,
                      variant: "body-position-bottom-right"
                    },
                    index
                  );
                }) });
              }
            }
          ) })
        ] })
      ]
    }
  );
};
var HeroWithOverviewBlock_default = HeroWithOverviewBlock;

// src/blocks/HeroWithQuickLinksBlock/HeroWithQuickLinksBlock.tsx
import {
  Image as Image14,
  Text as Text30,
  Placeholder as Placeholder15,
  useSitecoreContext as useSitecoreContext19,
  getFieldValue as getFieldValue23
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx148, jsxs as jsxs77 } from "react/jsx-runtime";
var DEFAULT_SHOW_QUICKLINKS = true;
var DEFAULT_HERO_BLOCK_VARIANT7 = "light";
var DEFAULT_SHOW_DECORATIONS18 = true;
var DEFAULT_ITEMS_PER_ROW = "6";
var HeroWithQuickLinksBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx148(NullDatasourceMessage_default, {});
  const variant = getFieldValue23(
    fields,
    "HeroBlockVariant",
    DEFAULT_HERO_BLOCK_VARIANT7
  );
  const showDecorations = getFieldValue23(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS18
  );
  const showQuickLinks = getFieldValue23(
    fields,
    "ShowQuickLinks",
    DEFAULT_SHOW_QUICKLINKS
  );
  const itemsPerRow = getFieldValue23(
    fields,
    "ItemsPerRow",
    DEFAULT_ITEMS_PER_ROW
  );
  const getVariantClassName3 = (variant2) => {
    switch (variant2) {
      case "light":
        return "hero-with-quicklinks-block_light";
      case "dark":
        return "hero-with-quicklinks-block_dark";
      default:
        return "";
    }
  };
  const { sitecoreContext } = useSitecoreContext19();
  const breadcrumbs = sitecoreContext.breadcrumbs;
  return /* @__PURE__ */ jsxs77(
    "div",
    {
      className: `hero-with-quicklinks-block ${showDecorations ? "" : "hero-with-quicklinks-block_circles-none"} ${getVariantClassName3(variant)} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx148(Decorations_default, {}),
        /* @__PURE__ */ jsx148("div", { className: "hero-with-quicklinks-block__image-wrapper", children: /* @__PURE__ */ jsx148(
          Image14,
          {
            field: fields.Image,
            className: "hero-with-quicklinks-block__image",
            srcSet: [{ mw: 416 }, { mw: 1280 }],
            sizes: "(max-width: 640px) 416px, 1280px"
          }
        ) }),
        breadcrumbs && /* @__PURE__ */ jsx148(
          Breadcrumbs_default,
          {
            className: "hero-with-quicklinks-block__breadcrumbs",
            items: breadcrumbs
          }
        ),
        /* @__PURE__ */ jsx148("div", { className: "container_tightest mt-3 sm:mt-4 xl:mt-24", children: /* @__PURE__ */ jsxs77("div", { className: "hero-with-quicklinks-block__text", children: [
          /* @__PURE__ */ jsx148(
            Text30,
            {
              tag: "h1",
              className: "heading-1 hero-with-quicklinks-block__title",
              field: fields.Title
            }
          ),
          /* @__PURE__ */ jsx148(
            RichText_default,
            {
              className: "rich-text introtext hero-with-quicklinks-block__subtitle",
              field: fields.RichTextSimple
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx148("div", { className: "container_normal", children: showQuickLinks && /* @__PURE__ */ jsx148(QuickLinks_default, { itemsPerRow, children: /* @__PURE__ */ jsx148(Placeholder15, { name: "container-quicklinks", rendering }) }) })
      ]
    }
  );
};
var HeroWithQuickLinksBlock_default = HeroWithQuickLinksBlock;

// src/blocks/HeroWithVideoBlock/HeroWithVideoBlock.tsx
import {
  Text as Text31,
  useSitecoreContext as useSitecoreContext20,
  getFieldValue as getFieldValue24
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx149, jsxs as jsxs78 } from "react/jsx-runtime";
var DEFAULT_HERO_BLOCK_VARIANT8 = "light";
var DEFAULT_SHOW_DECORATIONS19 = true;
var HeroWithVideoBlock = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx149(NullDatasourceMessage_default, {});
  const youtubeUrl = getFieldValue24(fields, "YouTubeUrl", null);
  const vimeoUrl = getFieldValue24(fields, "VimeoUrl", null);
  const youtubeUrlExists = youtubeUrl && youtubeUrl.length > 0;
  const vimeoUrlExists = vimeoUrl && vimeoUrl.length > 0;
  const url = youtubeUrlExists ? youtubeUrl : vimeoUrlExists ? vimeoUrl : "";
  const thumbnail = getImageUrl(fields.PreviewImage);
  const variant = getFieldValue24(
    fields,
    "HeroBlockVariant",
    DEFAULT_HERO_BLOCK_VARIANT8
  );
  const showDecorations = getFieldValue24(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS19
  );
  const getVariantClassName3 = (variant2) => {
    switch (variant2) {
      case "light":
        return "hero-with-video-block_light";
      case "dark":
        return "hero-with-video-block_dark";
      default:
        return "";
    }
  };
  const { sitecoreContext } = useSitecoreContext20();
  const breadcrumbs = sitecoreContext.breadcrumbs;
  return /* @__PURE__ */ jsxs78(
    "div",
    {
      className: `hero-with-video-block ${showDecorations ? "" : "hero-with-video-block_circles-none"} ${getVariantClassName3(variant)} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx149(Decorations_default, {}),
        breadcrumbs && /* @__PURE__ */ jsx149(
          Breadcrumbs_default,
          {
            className: "hero-with-video-block__breadcrumbs",
            items: breadcrumbs
          }
        ),
        /* @__PURE__ */ jsx149("div", { className: "container_tight mt-6 sm:mt-10 xl:mt-22", children: /* @__PURE__ */ jsxs78("div", { children: [
          /* @__PURE__ */ jsx149(Text31, { tag: "h1", className: "heading-1", field: fields.Title }),
          /* @__PURE__ */ jsx149(
            RichText_default,
            {
              className: "rich-text introtext mt-3",
              field: fields.RichTextSimple
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx149("div", { className: "container_normal mt-8 sm:mt-10 xl:mt-14", children: /* @__PURE__ */ jsx149(
          VideoPlayer_default,
          {
            url,
            thumbnailUrl: thumbnail,
            className: "w-full aspect-video rounded-2xl"
          }
        ) })
      ]
    }
  );
};
var HeroWithVideoBlock_default = HeroWithVideoBlock;

// src/blocks/HeroWithEmbedBlock/HeroWithEmbedBlock.tsx
import {
  Text as Text32,
  useSitecoreContext as useSitecoreContext21,
  getFieldValue as getFieldValue25
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx150, jsxs as jsxs79 } from "react/jsx-runtime";
var DEFAULT_HERO_BLOCK_VARIANT9 = "light";
var DEFAULT_SHOW_DECORATIONS20 = true;
var HeroWithEmbedBlock = (props) => {
  var _a;
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx150(NullDatasourceMessage_default, {});
  const variant = getFieldValue25(
    fields,
    "HeroBlockVariant",
    DEFAULT_HERO_BLOCK_VARIANT9
  );
  const showDecorations = getFieldValue25(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS20
  );
  const getVariantClassName3 = (variant2) => {
    switch (variant2) {
      case "light":
        return "hero-with-embed-block_light";
      case "dark":
        return "hero-with-embed-block_dark";
      default:
        return "";
    }
  };
  const { sitecoreContext } = useSitecoreContext21();
  const breadcrumbs = sitecoreContext.breadcrumbs;
  return /* @__PURE__ */ jsxs79(
    "div",
    {
      className: `hero-with-embed-block ${showDecorations ? "" : "hero-with-embed-block_circles-none"} ${getVariantClassName3(variant)} ${className}`,
      ...restProps,
      children: [
        showDecorations && /* @__PURE__ */ jsx150(Decorations_default, {}),
        breadcrumbs && /* @__PURE__ */ jsx150(
          Breadcrumbs_default,
          {
            className: "hero-with-embed-block__breadcrumbs",
            items: breadcrumbs
          }
        ),
        /* @__PURE__ */ jsx150("div", { className: "container_tightest mt-6 sm:mt-10 xl:mt-22", children: /* @__PURE__ */ jsxs79("div", { children: [
          /* @__PURE__ */ jsx150(Text32, { tag: "h1", className: "heading-1", field: fields.Title }),
          /* @__PURE__ */ jsx150(
            RichText_default,
            {
              className: "rich-text introtext mt-3",
              field: fields.RichTextSimple
            }
          )
        ] }) }),
        /* @__PURE__ */ jsx150("div", { className: "container_normal mt-8 sm:mt-10 xl:mt-14", children: /* @__PURE__ */ jsx150("div", { dangerouslySetInnerHTML: { __html: ((_a = fields.Embed.value) == null ? void 0 : _a.toString()) || "" } }) })
      ]
    }
  );
};
var HeroWithEmbedBlock_default = HeroWithEmbedBlock;

// src/blocks/ImageBlock/ImageBlock.tsx
import { Image as Image15, Text as Text33, getFieldValue as getFieldValue26 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx151, jsxs as jsxs80 } from "react/jsx-runtime";
var DEFAULT_IMAGE_VARIANT = "default";
var DEFAULT_BLOCK_VARIANT10 = "white";
var DEFAULT_SHOW_DECORATIONS21 = true;
var getImageAttributes = (imageVariant) => {
  switch (imageVariant) {
    case "full-width":
      return {
        srcSet: [{ mw: 416 }, { mw: 1280 }, { mw: 1600 }],
        sizes: "(max-width: 640px) 416px, (max-width: 1280px) 1280px, 1600"
      };
    case "default":
    default:
      return {
        srcSet: [{ mw: 416 }, { mw: 1280 }],
        sizes: "(max-width: 640px) 416px, 1280px"
      };
  }
};
var ImageBlock = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx151(NullDatasourceMessage_default, {});
  const variant = getFieldValue26(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT10
  );
  const showDecorations = getFieldValue26(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS21
  );
  const imageVariant = getFieldValue26(
    fields,
    "ImageVariant",
    DEFAULT_IMAGE_VARIANT
  );
  const { isTextFieldValid, isRichTextFieldValid, isImageFieldValid } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  return /* @__PURE__ */ jsxs80(
    MediaBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `image-block ${imageVariant === "full-width" ? "image-block_full-width" : ""} ${className}`,
      ...restProps,
      children: [
        showText && /* @__PURE__ */ jsxs80("div", { className: "container_tightest", children: [
          /* @__PURE__ */ jsx151(
            Text33,
            {
              tag: "h2",
              field: fields.Title,
              className: "image-block__title heading-2"
            }
          ),
          /* @__PURE__ */ jsx151(
            RichText_default,
            {
              field: fields.RichTextSimple,
              className: "image-block__subtitle"
            }
          )
        ] }),
        /* @__PURE__ */ jsx151("div", { className: "container_normal", children: isImageFieldValid(fields.Image) && /* @__PURE__ */ jsx151("div", { className: "image-block__image-wrapper", children: /* @__PURE__ */ jsx151(
          Image15,
          {
            field: fields.Image,
            className: "image-block__image",
            ...getImageAttributes(imageVariant)
          }
        ) }) })
      ]
    }
  );
};
var ImageBlock_default = ImageBlock;

// src/blocks/InteractiveMapBlock/InteractiveMapBlock.tsx
import { useState as useState27 } from "react";

// src/blocks/InteractiveMapBlock/InteractiveMapImage.tsx
import { jsx as jsx152, jsxs as jsxs81 } from "react/jsx-runtime";
var InteractiveMapImage = (props) => /* @__PURE__ */ jsxs81(
  "svg",
  {
    width: "274",
    height: "322",
    viewBox: "0 0 274 322",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      /* @__PURE__ */ jsxs81("g", { clipPath: "url(#clip0_1_8160)", children: [
        /* @__PURE__ */ jsx152(
          "path",
          {
            d: "M208.92 55.2401L210.844 55.9511L211.737 53.6002L212.257 53.4402L212.422 51.9426L213.497 49.0896L213.617 48.2941L214.052 47.8631L214.528 47.0765L214.803 46.9831L215.119 46.6543L215.217 45.7566L215.337 45.5211L216.026 45.2278L216.999 44.5745L217.074 44.4323L217.572 44.3034L218.194 43.7079L218.234 43.2946L219.425 43.0636L220.612 42.6769L221.243 42.6858L221.834 42.5347L223.74 43.3658L224.234 43.3924L224.705 44.3034L224.678 44.539L224.945 44.5478L225.985 46.3654L226.491 47.7786L227.384 47.392L227.553 47.4187L227.86 47.943V48.3874L228.078 49.2407H228.664L229.571 50.0362L229.922 50.5028L230.237 50.6228L230.708 51.9204L231.02 52.1648L231.18 52.7159L231.482 52.867L231.499 53.6713L234.401 52.3515L235.61 52.0226L238.53 52.1782L239.085 52.5514L239.436 53.0536L242.463 56.16L243.854 57.7598L244.965 58.7997L248.973 63.3725L252.32 66.7411L254.324 69.5052L257.968 75.0113L258.052 74.9891L258.648 75.6023L260.132 75.9979L258.137 80.8462L259.808 81.024L260.025 81.8461L260.288 81.8239L261.327 83.3482L261.51 83.4148L261.785 83.8104L261.976 83.8681L262.261 83.7081L262.341 83.7526L262.163 84.2992L262.101 84.9613L263.514 84.8902L262.043 88.2232L262.052 89.5342L260.807 107.288L260.865 107.555L259.888 108.71L259.457 108.443L258.937 108.466L257.799 108.19L257.443 107.946L257.257 107.67L256.404 107.728L255.75 107.288L255.204 107.115L254.719 107.626L254.164 107.568L253.697 108.039L253.164 107.972L252.511 108.154L251.96 107.914L251.822 107.75L251.2 107.763L251 107.057L250.24 106.839L250.053 106.87L249.698 106.608L248.587 106.723L248.084 106.306L247.333 106.768L246.769 106.746L246.533 106.848L245.489 106.457L245.205 106.524L244.48 106.963L243.423 106.79L243.2 106.537L242.476 106.617L242.241 106.408L241.863 106.386L241.334 106.492L240.934 106.71L240.703 107.079L240.063 106.848L239.676 107.137L239.53 106.928L238.908 107.195L237.952 108.212L237.672 108.639L237.01 108.963L236.743 109.439L235.935 109.39L236.188 108.674L236.046 108.434L236.499 107.937L236.397 107.706L236.25 107.657L236.61 106.839L234.21 106.004L229.931 103.968L225.442 104.977L224.865 105.301L222.763 107.035L222.714 107.252L222.869 108.594L223.087 109.243L223.283 111.181L223.18 111.381H223.016L222.98 111.136L222.82 111.123L222.7 110.834L222.385 110.812L222.194 110.456H221.749L221.327 110.27L220.878 110.248L220.732 110.372L220.198 110.314L220.118 110.132L219.825 110.016L219.839 109.728L219.527 109.563L219.256 110.421L219.03 110.661L218.256 110.892L217.843 110.825L217.421 110.559L217.185 110.256L217.208 109.808L217.017 109.621L216.941 109.359L216.746 109.714L216.323 110.025L216.274 110.292L216.07 110.456L215.435 110.132L215.212 110.536L214.541 110.514L214.368 110.812L213.732 111.15L213.635 111.123L213.573 110.803H213.395L213.137 110.456L213.101 110.083L212.893 109.728L212.959 109.252L212.657 108.825L212.542 108.261L212.097 107.706L212.168 107.555L211.857 107.323L211.639 107.346L211.497 107.195L211.457 106.955L210.817 106.572L210.639 106.341L210.093 106.146L209.573 105.679L209.551 105.159L209.742 104.928L209.502 104.515L209.08 104.537L208.209 104.95L207.671 104.986L207.418 105.359L207.182 105.39L206.978 105.275L206.547 104.688L206.289 104.653L205.978 105.044L205.787 105.079L205.431 104.848L205.191 104.524L204.689 104.488L204.276 104.177L203.902 103.55L203.516 103.586L202.983 103.319L202.907 103.159L202.707 103.231L202.076 102.871L202 102.568L201.44 102.777L201.063 102.791L200.685 103.311L199.783 103.204L199.614 103.008L199.072 103.231L198.734 102.533L198.503 102.315L198.414 102.373L198.174 102.222L197.739 101.644L197.379 100.395L196.583 98.6577L195.161 94.9958L195.481 94.4403L195.632 94.3337L197.25 93.9426L198.614 93.2582L198.699 92.6983L200.992 90.1608L200.534 89.6986L199.201 87.8633L198.699 87.5833L196.205 84.6814L195.854 84.2192L195.699 83.7659L195.161 83.0682L198.125 80.7796L201.076 78.882L202.947 76.2778L203.84 75.5712L204.165 74.9402L204.676 74.6158L205.12 74.5802L208.009 74.8602L210.217 77.1266L214.381 74.1492L216.834 69.5052L212.235 62.3593L212.542 58.3908L208.346 56.8043L208.92 55.2401Z",
            fill: "var(--main-200)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            d: "M146.367 120.878L146.087 120.775L146.065 120.544L146.207 120.393L146.385 119.469L147.301 117.287L147.847 116.745L149.007 116.18L149.154 115.914L149.14 115.683L148.332 113.189L144.43 106.906L144.59 106.79L148.523 113.074L149.354 115.763L149.176 116.327L147.852 116.949L147.438 117.411L146.505 119.682L146.367 120.878ZM166.383 85.0991L167.085 85.2369L167.641 85.7835L168.121 85.5879L167.685 84.277L169.094 84.708L171.64 87.2855L172.076 88.9698L172.338 88.8187L172.965 89.9342L173.916 90.7696L173.929 90.9918H175.218L175.364 91.094L176.195 91.2451L176.813 91.5473L178.191 92.4983L178.604 93.076L179.32 93.6893L179.742 93.956L179.995 93.9693L181.35 95.258L181.604 95.6536L182.101 96.9379L182.812 99.3954L183.306 99.6487L183.546 100.066V100.471L183.688 100.457L183.919 100.644L184.221 101.315L184.235 101.72L184.052 102.022L183.826 102.057L183.426 102.533L182.772 102.986L181.977 103.888L182.004 104.284L182.173 104.479L182.475 104.515L182.484 104.355L183.421 104.724L183.906 104.759L184.586 104.99L185.706 105.972L186.852 106.284L187.688 107.115L187.865 107.403L187.879 107.577L187.514 108.003L185.932 109.057L183.604 109.563L183.497 109.816L181.879 110.728L180.839 110.856L180.488 110.474L179.924 110.256L175.782 111.167L175.418 111.319L175.227 111.63L171.058 111.616L171.032 114.665L172.689 114.643L173.338 114.758L176.333 117.953L176.551 118.611L177.106 121.784L176.622 123.939L176.187 124.575L176.004 125.681L175.56 126.904L175.569 127.121L175.435 127.259L174.942 127.099L174.747 127.192L173.765 129.068L173.742 129.197L174.142 129.783L173.369 130.912L173.222 131.361L171.854 132.707L171.298 133.041L169.801 134.423L169.547 134.494L169.463 134.654L168.365 135.538L168.183 135.525L167.916 135.285L167.77 135.32L167.73 135.48L167.85 135.698L167.49 135.987L167.321 136.276L166.814 136.645L166.041 136.956L165.45 137.529L164.788 137.725L164.134 137.711L162.215 139.356L161.877 139L161.624 138.92L159.882 139.747L159.402 140.138L159.077 139.738L158.837 139.702L157.82 139.92L157.642 140.093L156.82 141.369L154.491 148.564L154.02 149.848L153.762 150.217L145.95 152.452L144.91 152.852L143.546 151.683L139.973 148.15L138.279 146.475L136.213 145.111L131.889 144.022L127.001 144.857L126.521 144.582L126.005 143.937L126.103 143.844L125.983 143.626L126.463 143.191L125.836 142.595L125.654 142.582L125.579 142.675L125.521 142.573L125.739 142.333L125.814 142.022L125.694 139.796L124.97 138.418L124.983 137.738L127.387 136.289L127.747 135.463L130.938 133.578L131.769 133.556L132.093 133.383L132.311 133.232L132.742 132.472L134.164 131.596L143.248 125.197L143.959 125.095L144.394 124.842L147.114 122.397L147.247 121.957L147.021 121.566L146.981 121.024L146.799 121.126L146.545 121.113L146.607 120.5L146.874 120.429L146.936 120.602L147.021 120.415L147.345 120.322L147.674 120.024L148.301 118.571L148.758 117.971L150.896 117.842L151.194 117.713L153.576 115.438L153.651 115.323L153.362 115.012L153.607 114.767L153.86 114.998L153.753 115.078L153.838 115.149L155.829 113.105L160.535 111.741L161.197 111.15H161.401L162.099 110.456L159.877 106.875L158.619 105.95L158.593 105.848L158.851 105.755L158.522 105.328L158.077 105.408L158.197 93.5382L162.895 85.8946L163.33 85.6279L166.108 85.2813L166.312 85.2458L166.383 85.0991Z",
            fill: "var(--main-800)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            d: "M156.219 85.8776L156.264 86.0287L156.117 86.0421L155.49 85.7976L155.588 85.691H155.997L156.219 85.8776ZM153.344 85.3577L153.033 85.2199L153.109 85.1622L153.34 85.2421V85.3577H153.344ZM144.794 71.568L144.696 71.4525L144.767 71.2214L144.887 71.3592L144.794 71.568ZM144.181 66.9063L144.047 66.5197L144.105 66.4397L144.238 66.8352L144.181 66.9063ZM142.185 57.2585L142.367 57.5962L142.63 57.7029L142.945 57.6229L143.318 58.0095L143.474 58.6983L143.932 59.5871L142.576 59.4938L142.43 59.0716L141.967 58.405L141.812 57.3651L141.852 57.2718L142.185 57.2585ZM139.714 57.3296L139.808 57.6673L139.745 57.8184L139.501 57.7384L139.212 57.9117L139.017 57.854L137.195 58.3783L131.422 63.4667L131.386 63.7689L131.058 64.0355L130.898 63.92L130.653 64.1199L130.978 64.2488L131.231 64.0488L131.28 64.1066L131.013 64.3288L130.578 64.1999L128.08 66.3686L127.791 65.982L130.24 63.8355L130.2 63.5778L130.524 63.4045L130.262 63.6267L130.306 63.7778L136.879 57.9784L137.177 57.8139L138.963 57.3251L139.008 57.2451L139.234 57.3829L139.501 57.3118L139.563 57.2051L139.714 57.3296ZM121.276 29.9991L123.138 30.3502L123.236 30.408L123.321 30.9368L122.996 31.1368L122.778 31.4434L122.463 31.3012L122.596 31.5456L122.303 31.4301L121.747 31.4079L120.699 31.7012L120.156 32.2522L119.29 32.8521L118.121 33.3899L115.526 34.3053L114.268 34.9275L114.144 35.2075L114.601 36.3096L114.468 36.474L114.068 36.5451L113.948 36.6962L113.913 37.3183L114.104 37.3983L113.97 37.8338L112.255 38.536L111.3 39.567L110.869 39.7759L109.918 39.6959L109.322 39.8025L108.033 40.5047L107.682 40.5269L107.451 40.3402L107.104 39.6825L107.113 39.3448L111.095 36.8251L111.7 36.2873L113.188 34.5409L114.259 33.6743L118.392 30.8568L119.348 30.5857L120.747 29.9991H121.276ZM126.222 32.0433L125.996 32.3722L125.703 32.4211L125.329 32.901L125.169 32.9588L124.854 32.8077L124.458 32.8788L124.467 32.6433L124.72 32.6211L124.867 32.4433L124.685 32.1145L124.383 32.0211L125.049 31.3634L125.12 31.1857L125.769 30.7146L126.178 30.1858L126.418 30.0347L126.84 30.0124L127.049 30.4346L127 30.7857L126.614 31.5501L126.222 32.0433ZM129.595 19.9424L129.551 19.8491L129.711 19.6624L129.595 19.9424ZM200.015 17.9426L198.375 20.3335L198.433 21.8267L198.793 22.8843L199.033 23.2621L199.539 23.6842L201.859 24.3197L202.161 24.4841L202.423 24.8841L202.619 25.6262L202.93 26.004L203.423 25.9462L204.245 25.3685L205.414 25.6485L206.703 26.2129L206.33 29.4658L206.356 29.5725L207.018 29.7236L207.201 30.0036L206.787 30.4835L206.174 30.7057L205.956 31.0568L205.921 31.4656L205.712 31.71L205.627 32.2033L205.521 32.2878L204.974 32.1233L204.454 32.43L204.33 33.7809L204.57 34.4742L204.534 34.883H204.339L204.17 35.0608L203.988 34.8253L203.854 34.8386L203.672 35.2119L203.37 35.483L203.405 35.8118L203.285 36.0563L202.859 36.2207L203.063 36.514L203.019 36.6917L202.703 36.9495L202.801 37.2783L202.774 37.8516L201.557 38.0516L201.752 38.7893L202.33 39.247L202.441 39.8114L202.743 40.2203L202.632 40.7446L202.406 41.0024L202.299 41.4379L201.97 41.989L201.512 42.3623L201.259 42.7622L200.837 42.8111L200.628 42.9533L200.53 43.5755L200.068 44.0199L199.957 44.3932L199.055 46.0552L198.859 47.2862L198.748 50.2459L200.006 51.8146L200.89 52.5967L203.205 52.9478L205.147 52.8678L206.174 53.4189L206.689 53.921L207.103 54.5743L208.791 55.2498L208.223 56.8141L212.413 58.4006L212.111 62.369L216.711 69.5149L214.258 74.1589L210.094 77.1363L207.885 74.8699L204.996 74.5899L204.552 74.6255L204.036 74.9499L203.712 75.5765L202.819 76.2875L200.948 78.8917L197.993 80.7848L195.033 83.0735L194.166 83.8067L193.313 84.3534L192.935 84.3889L192.584 84.5844L192.318 84.8644L192.002 84.9222L191.087 84.8155L190.291 85.1399L190.522 86.2998L190.38 86.5531L189.318 87.2019L188.723 87.4464L188.407 86.8064L187.607 86.9131L187.025 87.1086L186.807 87.2953L186.576 87.3086L186.274 87.2508L185.914 86.9264L185.647 86.8686L185.318 85.9754L184.91 85.371L184.692 84.4778L183.741 84.6867L183.43 84.8955L183.345 85.0688L183.421 85.5088L182.768 85.2421L182.43 85.3799L181.697 85.4732L181.67 85.7177L181.381 85.8554L181.488 86.051L181.403 86.1798L181.39 86.762L180.759 87.1797L180.399 87.7263L179.786 88.1085L179.519 88.5262L178.048 89.224L177.71 89.1884L177.062 88.8418L176.733 88.8551L176.479 88.704L175.817 88.7262L175.439 88.5307L175.164 88.784H174.995L174.84 88.3085L173.982 87.8197L173.875 87.6463L172.195 88.8195L171.933 88.9706L171.498 87.2864L168.951 84.7089L167.543 84.2778L167.978 85.5888L167.498 85.7843L166.943 85.2377L166.24 85.0999L166.289 84.6022L166.112 84.7978H165.978L165.858 84.6467L165.641 84.8555L165.472 84.6822L165.498 84.3356L165.329 84.1978L164.41 84.0601L163.556 84.1045L163.698 83.7001L163.605 83.6645L163.45 84.1312L163.21 84.1756L163.196 84.42L162.97 84.2334L162.45 84.2911L162.41 84.14L162.13 83.9178L162.299 83.7801L162.263 82.9891L161.477 82.4069L161.019 82.5224L159.597 83.5001L158.615 83.3624L157.357 83.6512L157.188 83.9667L157.041 84.7222L156.886 84.8866L156.05 85.3755L155.046 85.5843L154.699 85.5399L153.14 84.9355L150.229 83.589L148.442 83.0557L147.98 83.0202L147.762 83.1268L147.714 83.3935L147.038 83.4512L146.034 83.789L145.407 83.5668L144.101 82.8113L143.025 81.6514L142.119 80.9893L141.759 80.5715L141.87 80.3493L141.821 80.2338L141.492 80.176L141.008 79.3761L141.336 79.0872L141.545 78.6695L142.243 77.8652L143.243 77.4119L143.532 77.123L144.256 76.923L144.643 76.3764L144.714 75.1899L144.572 74.9455L144.341 73.3501L143.954 72.8835L144.327 72.5591L144.567 72.6302L144.749 72.5013L144.714 72.3946L144.456 72.4168L144.483 72.3102L144.758 72.288L144.865 72.4257L144.963 72.3191L144.954 71.5058L145.016 71.4258L145.158 71.5769L145.149 71.4481L144.932 71.1236L144.905 70.8348L144.678 70.9148L144.567 70.8437L145.087 70.7148L145.158 70.1771L144.78 68.7683L144.634 68.6039L144.501 68.1017L144.536 67.8573L144.394 67.7773L144.296 67.1596L144.323 66.0175L144.141 65.8087L144.754 64.7732L144.607 64.5421L144.776 63.5733L144.598 63.6311L144.154 63.4L144.238 62.8756L144.043 62.7601L144.114 62.6312H144.367L144.416 62.1424L144.509 62.3068L144.607 62.2846L144.389 61.6447L144.514 60.5381L144.305 61.3203L144.269 62.2313L144.003 62.4179L143.821 62.2179L143.794 61.8224L143.527 61.2625L143.332 60.947L142.838 60.5026L142.727 59.8137L142.825 59.7204H143.27L143.354 60.0226L143.634 60.0359V59.7337L143.932 59.7115L144.149 60.0626L144.474 60.0182L144.145 59.236L143.892 58.9427L143.687 58.4894L143.554 57.965L143.652 57.5695L143.554 57.1029L142.914 55.712L142.794 55.6542L142.532 55.712L140.768 57.3696L140.274 57.6051L140.154 57.8273L139.945 57.8051L139.75 57.1518L139.843 57.0451L139.99 57.1385L140.399 57.0096L140.674 56.8229L142.639 54.9209L142.772 54.7343L142.967 53.7877L142.821 53.53L142.861 53.3878L143.998 52.6989L144.598 51.9168L144.998 50.477L145.194 47.855L145.412 46.8729L145.389 46.3796L145.012 46.1707L144.918 45.913L145.172 45.6552L145.256 45.4108L145.763 45.1309L145.847 44.8376L145.74 44.7664L146.305 43.0466L146.634 42.4378L146.914 41.5934L147.629 40.4469L148.682 39.5092L150.069 37.9405L150.647 37.705L151.478 37.0606L152.322 36.5806L152.482 36.3807L153.242 35.9585L153.931 35.1141L155.202 33.9409L155.628 32.7455L157.584 30.7724L159.566 29.7503L160.437 29.5636L163.081 28.5637L163.734 28.4348L164.365 28.1193L164.836 28.0971L165.596 27.546L169.991 24.973L171.618 24.3375L172.28 23.9509L172.578 23.8931L172.769 23.6931L173.795 23.422L174.604 22.8577L176.124 22.0133L177.195 21.6622L177.595 21.2267L178.346 21.0134L178.661 20.8134L177.901 19.7202L177.95 19.6136L178.146 19.8846L178.301 19.7202L178.435 20.0135L178.337 20.1646L178.737 20.729L179.306 20.8223L179.741 20.5735L180.759 20.7157L182.785 20.1157L183.621 19.738L184.479 19.6224L184.585 19.4002L184.776 19.6002L185.825 19.4136L187.696 18.8136L189.589 18.6625L191.411 18.3337L192.544 18.6403L192.669 18.3337L192.811 18.6403L193.109 18.1826L193.251 18.3959L193.9 18.4181L196.197 18.8314L198.988 17.5116L199.459 17.476L200.015 17.9426ZM153.237 13.7475L152.42 13.8186L152.344 13.9608L152.464 14.103L154.339 13.8453L154.619 13.8808L154.779 14.2008L154.571 15.014L153.486 15.6362L153.157 15.7295L152.664 15.8362L152.531 15.7784L152.593 15.6851L152.517 15.6273L152.397 15.7206L152.237 15.6717L151.415 16.3072L150.184 16.9427L149.531 16.8494L149.158 16.5072L149.109 16.8005L149.291 17.1205L149.282 17.5071L148.598 17.3427L148.033 16.9294L147.647 16.8583L147.598 17.0094L147.682 17.1027L148.549 17.716L149.638 18.1293L149.504 18.2937L149.296 18.1426L149.104 18.2004L148.949 18.3781L149.082 18.4937L148.887 18.6225L148.225 18.7736L145.318 18.6803L144.389 18.7381L143.292 19.0225L142.714 19.2803L141.39 20.6312L140.568 21.1823L140.088 21.3689L139.737 21.3911L139.106 21.8489L138.275 21.5422L138.106 21.8711L137.657 22.0933H137.306L137.101 22.3777L137.039 22.6577L136.408 22.5866L135.755 22.3377L135.053 22.5866L134.439 22.3999L133.342 23.3643L133.293 23.2487L133.013 23.1198L131.853 23.2621L131.56 23.662L131.595 24.2264L131.497 24.3108L131.604 24.5108L131.36 24.5464L131.093 24.3331H130.804L130.693 24.5108L130.346 24.6975L129.947 25.2396L129.582 25.4174L129.271 25.804L128.44 25.604L127.356 24.9108L127.258 24.7463L127.062 23.7687V23.2887L127.458 22.7954L128.578 21.8089L129.275 20.7734L129.551 20.0802H129.635L129.56 20.4312L129.68 20.4179L129.755 19.9691L130.058 19.618L130.022 19.5336L129.751 19.5647L129.955 19.4225L137.439 18.0226L138.79 17.6938L138.981 17.8582L139.346 17.8093L146.189 15.5829L146.518 15.254L147.891 14.8674L152.14 13.1609L153.526 12.9032L154.264 12.9165L154.611 12.9876L154.731 13.1387L154.717 13.3964L154.584 13.5609L153.237 13.7475ZM190.882 12.2543L191.002 13.1965L190.825 12.8898L190.616 13.1609L190.087 13.4187L189.86 13.712L189.474 13.5253L189.327 12.8676L189.736 12.2677L190.282 12.3165L190.829 12.1877L190.882 12.2543ZM167.085 12.2277L170.849 12.0766L175.595 11.5344L176.599 11.7122L181.594 11.4544L184.203 11.5611L184.683 11.6455L184.981 11.9388L184.838 12.2232L184.576 12.3654L183.865 12.5165L182.212 12.4588L181.599 12.5521L180.11 12.4232L179.124 12.8809L178.795 13.152L177.675 13.4364L177.457 13.4142L176.875 13.6986L174.928 14.1208L174.817 14.2408L172.871 14.783L171.569 14.7118L170.88 14.8318L170.809 14.7741L170.769 15.1874L170.627 15.3384L170.471 15.254L170.596 15.1607L170.618 14.583L169.227 14.4985L167.92 14.1697L167.209 14.2186L166.951 14.3963L166.725 14.9385L165.743 15.694L164.681 16.0139L164.09 16.3339L163.205 16.3828L163.192 16.7694L162.987 16.9338L162.579 16.8138L161.561 16.1295L160.57 15.6717L160.05 15.0807L159.419 14.0675L159.397 13.3609L159.699 11.8766L160.001 11.37L160.339 11.0989L160.761 10.9478L161.437 10.9345L162.934 11.5344L165.401 12.1121L167.085 12.2277ZM197.139 6.42828L197.77 6.54827L198.459 7.19709L199.415 7.36152L200.463 7.4104L205.61 6.70381L208.472 6.43273L211.04 6.32607L211.169 6.52605L211.058 6.81935L209.938 7.39707L209.271 7.68148L208.534 7.81036L206.943 8.57472L206.085 8.73026L205.614 9.32131L203.854 10.1479L203.392 10.2545L202.001 10.2901L200.383 10.6545L200.743 11.09L200.717 11.21L200.37 10.9967L200.215 10.7123H198.335L198.344 11.17L198.197 11.1833L198.126 10.7345L197.646 10.8278L197.513 11.1345L197.33 11.2189L197.513 11.4677L196.913 11.3833L196.722 11.5344L196.913 11.8055L196.779 11.9121L196.753 12.1121L196.979 12.4321L196.557 12.5165L195.926 12.281L195.553 12.3165L195.055 12.9876L194.824 13.0231L194.544 12.9032L194.522 12.8098L194.7 12.3032L194.82 11.3255L195.051 10.5256L195.557 9.53462L196.188 8.61472L197.562 7.38818L197.913 7.24597L197.806 7.08155L195.162 6.46828L194.642 7.78814L194.331 7.81036L194.184 7.71704L194.233 7.29041L194.717 6.17053H195.584L197.139 6.42828Z",
            fill: "var(--main-500)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            d: "M177.107 121.8L178.062 121.445L179.155 122.414L180.089 123.578L180.582 124.418L181.315 125.973L181.537 126.689L182.071 126.444L182.346 126.698L183.239 126.031L183.653 125.929L184.262 126.804L187.323 123.636L188.083 123.369L188.381 123.129L188.577 122.783L189.012 123.022L189.288 123.058L190.639 124.12L191.087 125.213L191.447 125.756L192.007 126.213L193.069 126.689L193.669 127.413L193.86 127.978L193.945 129.231L193.874 130.315L194.145 130.671L194.7 131.084L195.745 132.626L196.145 133.777L196.185 134.204L195.794 135.719L195.483 136.341L195.46 137.421L195.309 137.741L195.123 137.95L193.838 138.443L192.443 138.71L192.163 139.096L192.15 139.51L192.278 139.776L192.438 139.914L192.998 140.038L193.221 140.225L193.318 140.465L192.932 140.785L192.221 141.003L192.043 141.305L192.136 142.465L192.038 143.589L191.923 144.402L191.643 144.749L191.634 144.976L192.332 145.069L192.572 145.598L192.612 146.847L193.083 146.998L193.816 147.007L193.963 147.273L194.163 147.913L194.527 148.429L194.585 149.54L195.256 150.366L195.456 151.237L195.847 151.584L195.443 152.166V152.615L196.242 152.797L196.638 153.082L197.509 153.002L197.545 153.233L197.425 153.633L196.931 153.953L196.785 154.33L196.976 155.064L197.451 155.41L198.051 155.557L198.238 155.89L198.22 156.255L198.371 156.392L198.5 156.255L198.513 156.05L198.26 155.752L198.127 155.375L198.402 155.49L198.456 155.384L198.309 155.272L198.473 154.975L198.793 155.317L198.86 154.641L198.958 154.241L199.055 154.193L199.371 154.228L199.66 154.104L200.118 154.241L200.891 154.206L201.917 154.975L203.002 155.121L203.486 155.304L203.971 155.295L204.162 155.441L205.09 155.361L205.77 155.557L205.806 155.224L206.268 155.384L206.432 155.157L207.246 154.939L207.303 155.121L207.517 155.166L207.566 155.361L207.783 155.557L209.237 155.57L209.801 155.41L210.419 155.384L210.459 154.41L210.925 154.104L211.205 154.055L211.348 153.748L212.676 153.668L213.458 153.335L213.858 153.53L214.765 153.473L215.911 153.61L215.889 154.184L216.383 154.881L216.671 155.708L218.089 157.192L218.667 158.361L219.084 158.521L219.547 158.543L219.689 158.681L219.72 159.312L220.107 160.183L220.342 160.49L221.022 160.823L221.253 161.05L221.493 161.13L222.835 160.65L225.63 161.107L228.159 160.592L228.208 160.476L228.421 160.605L228.443 160.787L228.879 160.97L229.017 161.245L229.328 161.463L229.101 161.929L229.15 162.116L229.741 162.663L230.332 162.218L230.63 162.116L230.75 161.747L230.919 161.61L231.292 161.725L231.634 162.205L231.981 161.601L232.274 161.623L233.723 162.387L233.918 162.569L234.047 162.947L234.034 163.223L233.807 163.556L234.003 163.658L233.99 164.285L234.083 164.605L233.99 165.338L233.243 166.538L234.278 167.018L235.812 167.302L237.016 167.862L240.184 167.84L239.882 170.204L239.638 170.591L239.576 170.431L239.398 170.364L238.989 170.751L239.051 171.049L239.385 171.368L239.398 171.688L238.527 172.773L235.536 173.275L235.692 173.573L235.652 173.915L235.207 174.839L234.967 174.941L234.941 175.386L235.047 175.51L235.038 176.843H236.958L239.691 178.154L240.376 178.759L240.607 179.23L241.087 179.421L243.477 181.381L243.482 181.803L243.611 181.95L244.882 182.145L245.108 182.874L245.037 183.669L245.268 184.58L245.135 185.074L244.931 185.434L243.997 185.469L243.651 185.798L242.913 186.06L242.718 187.518L242.731 188.487L241.993 189.238L242.153 189.42L241.038 190.784L239.749 191.317L239.736 191.717L239.593 191.864L238.554 192.228L238.216 192.851L237.554 193.171L236.389 192.251L236.163 192.002L236.114 191.557L235.94 191.646L235.798 191.589L235.549 190.975L235.198 190.966L234.861 190.762L234.518 190.864L231.719 192.193L231.27 192.66L231.021 192.682L230.67 192.557L227.417 193.646L226.573 194.228L226.053 194.726L225.848 194.739L225.537 194.317L225.004 194.535L224.484 195.09L223.786 195.544L222.986 195.877L222.324 196.339L222.12 196.535L222.106 196.864L221.809 197.286L220.382 197.668L219.778 198.023L219.4 197.886L218.689 196.93L218.378 196.668L218 196.979L217.134 197.41L216.911 197.228L216.783 196.592L216.023 196.339L215.84 196.068L214.68 195.966L214.321 195.637L214.165 195.704L214.223 196.001L213.947 196.775L213.996 196.93L215.076 197.797L215.596 197.93L215.632 198.112L215.094 198.863L215.089 199.294L215.405 199.952L215.369 200.032L215.103 199.961L215.334 200.748L215.272 200.93L215.045 201.005L213.863 200.223L212.77 200.192L212.325 199.988L212.423 199.681L212.156 199.01L211.65 198.557L209.552 198.281L208.255 198.406L207.895 196.784L207.712 196.668L207.797 196.33L207.041 195.966L206.69 195.921L206.446 195.681L205.913 195.601L205.601 195.726L205.295 196.135L204.73 196.308L203.588 196.286L203.282 196.184L201.993 196.339L201.655 196.259L200.784 195.748L199.802 194.739L199.753 194.286L200.291 194.184L200.118 193.979L200.14 193.739L198.633 192.593L198.54 192.806L197.705 192.282L196.1 192.557L195.625 192.922L195.105 192.513L194.851 193.011L194.625 192.877L194.483 193.011L194.514 193.308L194.163 193.593L195.251 193.615L195.998 194.148L196.487 194.375L196.873 195.455L196.918 196.081L198.287 197.184L198.26 197.863L198.416 199.428L196.909 198.886L195.856 198.659L194.647 198.703L193.74 198.499L193.163 198.215L192.056 197.228L191.256 196.726L190.87 197.441L190.412 197.637L190.576 198.01L190.51 198.259L189.674 198.726L189.083 199.272L188.852 199.032L188.021 199.339L187.661 200.054L186.835 200.472L185.812 200.588L185.155 200.339L184.617 200.294L184.035 199.93L182.897 201.339L183.83 202.063L183.604 202.356L183.502 202.276L183.404 202.379L183.239 202.254L183.155 202.325L183.044 202.223L182.915 202.37L182.959 202.596L184.155 203.241L184.413 203.698L185.137 204.263L185.168 204.423L184.915 204.583L185.075 204.752L184.808 205.272L185.035 205.738L185.475 205.725L185.795 205.943L186.004 205.871L186.079 205.987L185.617 206.454L185.541 207.054L186.026 207.609L185.99 207.938L183.844 209.533L183.604 209.604L183.457 209.796L183.453 210.36H183.186L183.115 210.578L182.959 210.658L182.151 210.711L181.862 210.409L181.848 210.204L181.551 209.942L181.053 209.693H180.751L180.52 208.422L179.191 206.725L178.747 206.645L177.289 206.836L177.262 207.098L177.644 208.493L177.249 208.911L176.196 208.72L173.023 209.071L171.321 209.511L170.294 209.502L169.81 209.4L169.232 209.116L168.819 208.765L168.45 208.173L168.53 208.107L168.339 207.778L167.805 207.236L167.49 207.054L166.139 206.827L165.801 206.498L165.299 205.636L164.766 205.125L163.45 204.898L162.699 204.423L162.215 203.707L161.797 202.472L161.335 201.961L160.975 201.859L160.095 201.881L159.695 201.721L158.518 200.952L157.624 200.734L156.771 200.908L155.527 200.996L155.211 201.268L154.691 202.13L154.238 202.267L152.776 201.903L151.696 200.828L151.261 200.601H150.998L150.314 200.894L150.061 201.383L149.958 202.027L149.656 202.459L149.27 202.734L148.39 202.947L147.932 203.014L146.954 202.925L146.252 202.632L145.603 201.712L145.381 201.565L144.803 201.574L144.586 201.756L144.235 202.392L143.404 203.458L142.182 205.534L141.746 206.982L141.679 208.471L141.408 208.947L141.053 209.204L138.831 210.227L137.418 211.2L137.089 211.222L136.147 210.884L135.364 210.813L134.867 210.986L134.325 211.44L133.876 211.586L133.36 211.391L132.631 210.738L132.125 210.52L131.725 210.475L130.072 210.835L128.676 210.498L127.765 210.533L125.281 211.164L125.254 210.338L126.068 209.658L126.379 209.196L126.05 208.911L125.921 208.618L125.952 208.165L126.161 207.565L126.112 207.236L124.508 206.454L123.797 205.716L123.446 205.534L123.019 205.467L122.61 205.645L121.926 206.374L121.428 206.44L121.175 206.338L120.557 205.658L120.246 205L119.895 204.76L119.904 204.445L119.739 204.276L117.855 203.414L117.313 202.938L116.935 202.019L116.522 201.721L117.575 201.743L118.033 199.53L116.522 199.326L116.1 198.623L116.251 197.997L115.993 197.17L116.175 196.979L116.975 197.681L118.757 197.637L119.437 197.908L119.668 197.646L120.464 197.33L120.726 197.081L120.242 196.784L120.064 196.397L120.175 196.01L120.753 195.726L121.606 195.717L122.095 195.441L122.561 194.748L122.637 194.193L122.837 194.033L123.255 194.162L123.712 194.046L124.468 194.228L124.628 193.922L124.757 192.953L125.73 191.149L126.063 191.304L126.019 190.589L126.921 189.282L126.863 188.998L127.068 188.953L129.098 185.083L130.063 185.709L130.965 185.905L131.343 185.811L131.845 185.367L132.463 184.172L132.765 183.798L133.298 183.661L133.853 183.989L134.676 184.843L135.458 185.265L136.351 185.505L137.489 185.651L138.577 186.38L139.177 186.416L140.035 185.98L140.733 185.083L141.288 184.696L141.884 184.603L142.906 184.82L143.577 184.776L145.786 183.932L146.71 183.149L147.172 182.896L149.092 183.274L150.332 183.136L150.736 183.238L151.696 183.82L152.158 184.243L154.429 185.083L154.936 185.891L155.442 186.291L155.989 186.527L157.811 186.993L159.002 187.793L159.709 187.167L160.113 187.393L160.082 186.811L160.5 186.709L160.504 186.029L160.197 185.287L160.184 184.98L160.366 184.696L160.442 184.216L159.731 183.376L159.575 182.443L159.273 181.972L158.833 181.883L158.269 181.359L157.98 181.052L157.918 180.323L157.798 180.128L157.58 179.888L157.411 180.07L156.722 179.319L157.109 179.31L157.202 177.315L155.864 176.812L155.958 176.026L155.42 175.795L155.669 175.124L155.842 173.444L155.767 173.288L155.971 172.444L155.856 171.551L155.66 171.542L155.429 171.266L155.371 170.444L155.105 170.262L155.238 169.942L155.118 169.289L154.642 169.36L154.678 169.578L154.527 169.609L154.345 169.395L153.718 169.555L153.767 169.849L153.98 169.92L153.985 170.08L154.162 170.226L153.967 170.684L154.345 170.786L154.309 171.084L154.26 171.311L153.691 171.142L153.465 171.542L153.931 171.733L153.256 172.453L152.669 172.328L152.047 172.373L151.865 172.991L151.038 172.999L150.443 173.208L149.234 172.488L149.19 172.271L148.91 172.204L148.776 172.053L149.536 171.084L150.55 170.444L150.456 169.258L151.069 168.662L151.105 168.444L150.612 168.124L152.065 168.275V168.115L151.772 167.955L151.785 167.564L151.443 167.453L151.118 167.053L151.278 167.084L151.376 166.445L152.145 166.333L152.487 165.671L151.541 165.2L151.492 165.031L151.625 164.365L150.901 164.343L150.332 164.094L150.43 163.325L150.23 163.258L150.274 163.018L149.816 162.823L149.781 162.649L147.097 162.285L145.977 161.885L146.034 161.472L145.919 160.787L145.688 160.556L145.99 160.316L146.519 160.476L146.808 160.236L146.99 159.356L146.968 158.681L144.092 156.335L144.901 152.855L145.941 152.455L153.754 150.22L154.016 149.851L154.487 148.566L156.816 141.372L157.638 140.096L157.811 139.927L158.833 139.705L159.073 139.741L159.393 140.145L159.873 139.754L161.615 138.927L161.868 139.007L162.206 139.363L164.126 137.719L164.779 137.732L165.441 137.537L166.032 136.959L166.806 136.652L167.312 136.283L167.481 135.994L167.841 135.71L167.721 135.488L167.761 135.328L167.912 135.292L168.179 135.537L168.361 135.546L169.459 134.661L169.543 134.501L169.796 134.43L171.294 133.053L171.849 132.719L173.218 131.373L173.365 130.924L174.143 129.795L173.738 129.209L173.76 129.08L174.747 127.204L174.942 127.111L175.436 127.275L175.569 127.138L175.56 126.915L176.005 125.698L176.187 124.591L176.622 123.956L177.107 121.8Z",
            fill: "var(--main-100)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            d: "M242.264 12.4983L242.552 12.6849L242.881 12.6494L243.783 11.8139L244.015 11.7295L245.237 12.2228L245.383 12.6361L245.357 12.9649L245.21 13.2138L243.988 12.9649L243.939 13.1871L245.037 13.4582L244.961 13.7782L244.392 13.6715L244.321 13.8582L245.25 14.0804L245.73 14.0226L246.259 14.1648L246.308 13.9293L245.739 13.8226L245.628 13.6937L245.925 12.565L247.303 13.0005L248.858 14.4848L248.823 15.0403L248.267 16.6045L248.192 18.2532L248.587 18.6532L249.214 19.7242L249.023 20.0308L249.276 20.1375L249.37 20.3508L249.276 21.5862L249.676 22.7861L249.654 23.3505L250.049 25.1014L250.196 25.3503H250.64L251.92 26.4879L252.218 26.8746L252.569 26.9234V27.1234L252.307 27.1812L252.151 27.7456L252.885 27.9811L253.076 28.2522L253.316 28.2166L254.173 28.5322L254.405 28.7099L254.791 28.6966L255.164 28.8121L256.284 29.3543L256.658 29.4387L257.142 29.8831L257.64 30.1764L258.791 30.6208L259.711 30.5853L260.159 30.7364L260.328 30.923L262.408 30.8741L262.701 30.452L263.501 30.3009L263.764 30.0076H263.955L263.968 30.3142L263.777 30.372L263.35 30.9452L262.937 31.2608L262.79 32.3051L262.915 33.1361L262.35 34.696L262.386 35.1404L262.639 35.2692L262.675 35.5981L262.879 35.8336L263.386 36.078L263.564 36.2869L263.844 36.3224L263.972 36.5669L264.879 36.6024L265.208 36.4869L265.217 36.6735L265.301 36.6957L266.403 36.7446L267.119 37.2735L267.95 37.4957L268.434 37.309L269.087 37.8023L269.918 37.9534L270.11 38.1178L271.38 38.4333L271.492 38.54L271.625 38.4244L271.527 38.1534L271.736 37.6379L271.976 39.291L271.949 39.8199L272.243 40.7086L272.336 42.8062L272.229 43.1573L271.785 43.6728L271.674 43.9661L272.967 44.9127L272.989 45.1837L271.892 45.677L271.309 45.6992L270.558 47.1257L270.536 47.4191L270.354 47.7835L270.327 48.9078L269.883 49.7255L269.638 49.8899L269.918 51.8408L271.185 52.774L271.038 56.6625L271.954 64.3773L272.314 65.2972L271.745 66.0082L270.003 72.1009L269.896 72.7897L264.501 81.7354L263.332 83.233L263.177 83.5708L263.528 84.8818L262.115 84.9529L262.177 84.2907L262.355 83.7441L262.279 83.6997L261.99 83.8641L261.799 83.8063L261.524 83.4108L261.342 83.3397L260.302 81.8199L260.039 81.8421L259.822 81.0155L258.146 80.8422L260.142 75.9938L258.657 75.5983L258.062 74.9806L257.977 75.0028L254.333 69.4967L252.329 66.737L248.983 63.3685L244.974 58.7956L243.863 57.7557L242.472 56.1559L239.451 53.0496L239.099 52.5474L238.544 52.1741L235.624 52.023L234.416 52.3519L231.509 53.6717L231.496 52.8674L231.194 52.7163L231.034 52.1652L230.723 51.9208L230.252 50.6232L229.936 50.5076L229.585 50.041L228.678 49.2455H228.096L227.878 48.3923V47.9479L227.567 47.4235L227.399 47.4013L226.505 47.7879L225.999 46.3703L224.959 44.5571L224.692 44.5438L224.719 44.3083L224.248 43.3928L223.754 43.3706L221.848 42.5396L221.257 42.6907L220.626 42.6773L219.439 43.064L218.253 43.2995L218.213 43.7083L217.595 44.3038L217.097 44.4327L217.022 44.5749L216.053 45.2326L215.364 45.5259L215.244 45.7614L215.146 46.6636L214.831 46.9924L214.555 47.0858L214.075 47.8679L213.64 48.299L213.52 49.0944L212.445 51.9475L212.285 53.4451L211.765 53.6095L210.871 55.9559L208.943 55.2449L207.254 54.5694L206.841 53.9161L206.325 53.414L205.299 52.8629L203.357 52.9429L201.041 52.5918L200.157 51.8097L198.899 50.241L199.01 47.2813L199.206 46.0503L200.108 44.3883L200.219 44.015L200.681 43.5706L200.779 42.9484L200.988 42.8062L201.41 42.7573L201.663 42.3574L202.121 41.9841L202.45 41.433L202.557 40.9975L202.783 40.7398L202.881 40.2109L202.579 39.8021L202.468 39.2377L201.89 38.78L201.695 38.0423L202.912 37.8423L202.939 37.269L202.841 36.9402L203.157 36.6824L203.201 36.5047L202.997 36.2113L203.423 36.0469L203.543 35.8025L203.508 35.4736L203.81 35.2026L203.992 34.8293L204.125 34.8159L204.308 35.0515L204.476 34.8737H204.672L204.708 34.4649L204.468 33.7716L204.592 32.4206L205.112 32.114L205.659 32.2784L205.765 32.194L205.85 31.7007L206.059 31.4563L206.094 31.0474L206.312 30.6964L206.925 30.4742L207.338 29.9942L207.156 29.7143L206.494 29.5632L206.467 29.4565L206.841 26.2035L205.552 25.6391L204.383 25.3592L203.561 25.9369L203.068 25.9947L202.757 25.6169L202.561 24.8748L202.299 24.4748L201.997 24.3104L199.677 23.6749L199.17 23.2527L198.93 22.875L198.57 21.8173L198.513 20.3241L200.153 17.9333L200.406 17.7911L200.77 17.7555L200.89 17.8711L200.757 18.0222L200.792 18.1999L201.072 18.2133L203.743 17.4267L204.428 17.3778L205.019 17.5555L207.325 18.8487L207.676 19.0843L208.267 19.9686H208.374L209.392 19.3331L210.191 18.6132L212.329 17.6355L214.32 16.8712L217.275 16.3779L226.016 15.378L228.127 14.7647L229.372 14.2714L230.443 13.6804L232.425 12.8894L235.749 11.805L238.913 11.414H239.677L241.455 11.9917L242.264 12.4983ZM215.302 5.0502L215.631 5.10797L215.595 5.40127L214.64 5.2724L213.889 5.32128L213.791 5.13464L213.916 4.93466L214.311 4.89911L215.302 5.0502ZM227.856 4.36583H228.27L228.345 4.45026L228.31 4.57914L228.07 4.68579L228.127 4.92133L228.052 5.00576L227.083 4.65024L226.923 4.40138L226.985 4.21473L227.456 4.15696L227.856 4.36583ZM229.945 2.35715L229.98 2.49936L229.896 2.59268L229.256 2.37937L228.905 2.46381L228.865 2.71267L229.278 3.06819L229.256 3.32594L227.816 2.64157L227.527 2.5838L226.679 2.74822L226.51 2.52602L226.536 2.33938L226.95 1.84165L227.399 1.71278L228.918 1.8772L229.945 2.35715ZM221.288 2.07274L221.928 2.12162L222.426 2.33493L223.63 3.33927L223.617 3.5037L223.377 3.53925L222.47 3.24595L222.19 3.29483L221.826 2.84599L221.741 2.85932L221.595 3.0593L221.728 3.53036L221.595 3.77922L220.893 3.79256L220.541 3.65479L220.164 3.91254L220.43 4.21918L220.381 4.47693L220.035 4.71246L219.417 4.5347L219.102 4.27695L218.946 3.34371L218.115 2.75267L217.826 2.31716L217.782 1.45503L218.168 0.335144L218.435 0.370696L218.737 1.39725L221.288 2.07274Z",
            fill: "var(--main-200)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            opacity: "0.45",
            d: "M177.239 208.887L177.635 208.469L177.252 207.073L177.279 206.811L178.737 206.62L179.181 206.7L180.51 208.398L180.741 209.669H181.043L181.541 209.918L181.839 210.18L181.852 210.384L182.141 210.686L182.95 210.633L183.105 210.553L183.176 210.335H183.443L183.567 210.971L186.247 211.037L187.034 211.615L188.54 212.264L188.949 212.682L189.171 213.215L189.313 213.317L189.469 213.179L189.807 213.442L189.842 213.619L189.713 213.948L189.158 213.882L188.998 213.948L188.647 214.166L188.562 214.49L188.687 215.241L188.962 215.566L189.011 215.917L189.033 216.312L188.696 216.721L188.864 217.41L188.816 217.877L188.984 218.294L189.544 218.654L190.509 218.925L192.162 219.583L192.26 219.788L192.682 219.641L192.842 219.765V219.956H193.455L194.904 220.316L194.89 221.01L194.673 221.361L194.517 221.436L194.108 223.076L193.984 223.098L193.575 224.352L193.308 224.227L193.313 225.054L193.117 226.014L194.206 226.782L195.228 227.662L195.668 228.507L196.428 229.342L197.57 230.346L198.006 230.88L198.672 232.546L199.228 233.644L199.939 234.115L200.734 234.826L201.739 236.586L201.725 238.937L202.076 239.728L202.441 241.136L202.112 243.523L202.134 243.958L201.85 243.985L201.765 244.118L201.739 246.154L201.21 249.247L202.605 249.18L202.596 249.38L201.774 250.731L202.605 253.597L200.108 255.899L200.081 256.055L200.281 256.561L198.579 256.93L198.561 257.321L198.175 257.761L197.948 258.246L197.921 258.761L197.597 259.277L197.561 259.703L196.788 260.881L195.81 262.125L195.415 263.054L195.366 263.427L192.695 266.374L191.762 266.947L192.757 269.071L192.668 269.24L191.877 269.196L191.513 269.373L191.491 269.533L191.691 270.751L191.935 273.631L192.562 274.848L193.011 274.995L193.757 275.026L193.962 274.826L194.228 274.782L194.69 274.48L195.362 273.773L198.37 272.32L198.819 272.578L199.539 273.226L196.646 274.568L196.561 274.702L198.983 276.213L199.037 276.39L198.175 276.986L198.139 277.453L197.863 277.444L197.672 277.555L197.099 277.497L195.957 277.924L195.735 277.813L195.415 277.99L195.21 278.292L194.228 278.795L193.157 278.986L192.886 279.746L192.722 279.835L192.766 280.181L192.602 280.212L192.402 279.968L192.322 280.395L192.082 280.492L191.86 280.772L191.051 281.03L190.762 281.23L190.669 281.799L189.798 282.808L188.984 283.43L188.202 283.665L187.891 283.474L187.785 283.745L187.469 283.79L187.189 284.212L186.358 284.852L185.936 285.696L185.283 285.932L185.398 286.292L185.025 286.883L184.638 287.216L184.674 287.505L184.509 287.807L184.607 288.051L184.483 288.278L184.496 289.025L184.047 289.314L183.709 289.389L183.843 290.016L183.576 290.105L183.376 289.905L182.807 289.891L182.274 290.082L182.163 289.491L181.479 288.531L181.021 287.038L180.648 286.603L179.083 288.051L177.226 288.309L177.35 289.002L178.19 290.029L178.074 290.585L177.835 290.607L177.795 290.807L178.243 291.944L178.661 292.469L179.403 292.815L179.385 293.473L180.039 293.762L179.87 294.544L179.941 295.344L179.217 296.148L179.545 296.793L180.052 296.824L181.185 296.371L182.096 295.646L183.483 295.335L183.589 295.357L183.723 295.655L184.256 296.024L184.425 296.326L184.856 296.224L185.016 296.055L185.447 295.891L188.496 295.788L188.589 296.002L187.793 298.064L187.638 298.317L187.096 298.753L187.905 299.255L187.878 299.752L188.109 301.303L189.7 301.757L189.967 301.97L190.344 301.837L190.651 302.126L190.571 302.672L191.486 302.77L191.802 302.548L192.006 302.57L192.624 302.77L193.006 303.192L193.188 302.85L193.42 302.739L193.322 303.183L192.891 303.472L192.802 303.814L192.513 304.094L192.033 305.916L192.153 306.538L192.673 307.285L193.011 308.107L192.828 308.365L192.717 308.92L192.535 309.009L192.469 309.218L192.166 309.565L191.957 311.062L191.624 311.018L190.811 310.485L190.669 310.018L188.709 311.009L188.238 311.173L188.082 311.142L188.033 312.684L188.273 313.049L188.469 313.138L188.46 313.351L188.749 314.018L188.504 314.226L188.376 314.218L188.496 314.56L187.842 315.195L187.465 315.804L187.3 315.902L186.962 315.991L186.696 315.746L186.22 315.524L185.931 315.204L185.647 315.16L185.309 315.417L184.954 315.968L184.896 316.635L185.798 316.902L186.345 317.315L186.634 317.955L188.589 319.155L188.602 319.333L188.131 320.252L187.891 320.452L188.193 321.172L187.865 321.159L186.811 321.403L185.403 321.359L185.149 321.261L185.092 320.839L184.798 320.506L184.047 320.328L182.012 320.981L181.439 321.47L180.914 321.639L180.874 321.195L180.719 321.15L180.114 321.217L179.803 321.595L178.945 320.972L178.954 320.341L178.688 319.599L177.012 320.461L176.275 321.226L176.15 321.195L175.848 320.119L174.919 320.719H174.23L173.777 320.959L173.288 321.061L172.142 319.444L171.626 319.733L171.529 319.697L170.933 318.124L170.226 318.244L168.791 319.897L168.413 320.595H167.613L167.022 320.186L166.649 320.652L165.867 320.786L165.467 321.159L165.005 321.084L164.765 320.884L164.418 320.875L164.476 320.452L165.369 319.23L165.707 318.177L165.676 317.888L165.067 316.635L165.08 316.404L165.542 315.746L165.529 315.537L165.129 314.906L164.187 314.826L164.076 314.382H163.676L163.498 314.195L163.098 314.24L162.529 313.973L162.436 313.826L162.529 313.671L162.472 313.462L162.192 313.138L162.023 313.204L162.156 312.618L161.663 311.996L161.783 311.818L161.556 311.707L161.543 311.351L161.383 311.12L161.445 310.765L161.57 310.631L161.445 310.231L161.614 309.942L161.543 309.418L161.605 309.298L161.472 309.107L161.978 308.676L161.712 308.343L161.894 308.098L162.241 308.04L163.485 307.254H164.076L164.329 306.507L164.96 305.938L165.191 305.596L165.347 304.316L165.565 303.992L166.098 303.881L166.831 304.303L167.196 304.116L167.413 303.703V302.881L167.618 302.268L168.462 301.201L168.858 299.957L169.622 299.575L169.604 299.108L169.413 298.708L169.182 298.495L168.689 298.406L168.124 298.899L167.591 299.033L167.111 298.775L167.049 298.206L167.978 297.104L168.222 296.482L168.982 296.055L169.658 294.922L170.071 294.553L170.235 294.251L170.306 293.975L170.24 292.904L170.889 291.7L170.933 291.42L170.649 290.931L169.742 290.553L169.658 290.229L170.129 289.46L170.6 288.323L170.657 287.225L170.764 287.047H171.155L172 287.496L172.422 287.407L172.6 287.18L172.617 286.883L172.364 285.923L172.533 285.629L172.902 285.341L172.951 285.141L172.773 284.714L172.324 284.159L172.297 283.821L172.697 283.43L173.244 283.288L174.279 283.701L174.75 283.288L174.835 283.096L174.933 282.461L174.644 282.034L174.364 281.914L173.595 281.901L173.253 281.701L173.155 281.266L173.266 280.861L174.266 279.635L174.519 279.51L175.439 279.399L175.764 278.381L176.452 277.901L175.141 276.884L175.235 276.493L174.937 276.244L174.897 275.888L174.573 275.644L174.319 275.239L173.746 275.777L173.608 276.213H173.439L173.062 275.844L172.928 275.831L172.653 276.226L172.324 276.413L172.008 276.435L171.409 276.942L171.031 277.039L170.862 276.626L170.933 276.382L171.142 276.155V275.822L170.933 275.777L170.911 275.519L170.586 275.586L170.511 275.253L170.515 275.062L170.973 274.928L171.031 274.813L170.684 274.737L170.862 274.133L170.209 273.462L169.329 273.293L168.715 272.924L168.436 273.146L168.373 273.315L168.111 273.182L167.818 273.36L167.52 273.249L167.422 273.382L166.831 273.395L166.285 273.817L165.729 273.617L165.614 273.484L165.347 273.449L165.285 273.582L165.107 273.573L164.871 273.329L164.005 273.582L163.894 273.373L163.64 273.249L163.085 273.204L162.698 273.302L162.254 272.586L162.232 272.4L162.401 272.275L162.241 271.884L161.418 271.458L156.063 269.276L158.117 268.649L158.103 268.894L159.89 267.996L160.214 266.072L160.321 263.325L163.449 258.872L177.732 254.415L181.861 250.878L177.599 243.612L176.772 238.914L176.235 237.541L175.199 230.404L178.932 231.138L180.114 232.075L182.43 231.835L184.589 231.138L187.056 230.124L187.913 230.809L188.784 231.893L189.193 231.497L189.327 231.475L189.531 231.644L189.735 231.453L189.918 231.431L189.944 231.306L189.62 230.933L189.411 230.462L189.402 228.564L188.664 227.48L188.456 226.96L188.358 226.196L188.398 225.143L188.176 224.374L186.407 222.645L184.86 221.699L184.656 221.472L184.545 220.907L185.078 219.832L185.092 219.312L184.643 219.108H183.878L183.625 218.85L183.594 218.463L184.149 217.606L183.661 215.659L183.509 214.29L183.238 214.042L182.225 213.61L180.279 213.348L179.857 213.246L179.101 212.873L178.612 212.251L178.461 211.073L178.137 209.735L177.239 208.887Z",
            fill: "var(--main-800)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            opacity: "0.7",
            d: "M116.512 201.696L116.925 201.99L117.303 202.91L117.845 203.385L119.729 204.247L119.889 204.416L119.88 204.732L120.231 204.972L120.542 205.629L121.16 206.309L121.413 206.411L121.911 206.345L122.595 205.62L123.004 205.438L123.435 205.505L123.786 205.687L124.498 206.425L126.102 207.207L126.151 207.536L125.942 208.136L125.915 208.589L126.044 208.882L126.373 209.167L126.062 209.629L125.244 210.309L125.271 211.135L127.755 210.5L128.67 210.464L130.061 210.802L131.715 210.438L132.114 210.482L132.621 210.695L133.354 211.353L133.87 211.544L134.319 211.398L134.861 210.944L135.359 210.775L136.141 210.842L137.083 211.18L137.412 211.158L138.825 210.184L141.047 209.167L141.398 208.904L141.673 208.429L141.736 206.945L142.171 205.496L143.393 203.421L144.224 202.354L144.575 201.719L144.793 201.536L145.371 201.523L145.597 201.67L146.246 202.59L146.948 202.883L147.926 202.972L148.384 202.905L149.264 202.687L149.65 202.416L149.953 201.985L150.059 201.336L150.313 200.848L150.997 200.554H151.259L151.695 200.781L152.779 201.861L154.241 202.225L154.69 202.087L155.21 201.225L155.525 200.954L156.77 200.865L157.623 200.696L158.516 200.91L159.698 201.683L160.098 201.843L160.978 201.821L161.338 201.923L161.8 202.434L162.214 203.669L162.698 204.385L163.449 204.86L164.764 205.087L165.298 205.598L165.795 206.46L166.133 206.789L167.484 207.016L167.8 207.198L168.333 207.74L168.524 208.069L168.448 208.136L168.813 208.727L169.226 209.078L169.804 209.362L170.288 209.464L171.315 209.478L173.017 209.033L176.19 208.682L177.243 208.873L178.145 209.722L178.47 211.06L178.616 212.237L179.109 212.86L179.861 213.233L180.283 213.335L182.229 213.597L183.242 214.028L183.518 214.277L183.665 215.646L184.158 217.592L183.602 218.45L183.629 218.837L183.882 219.094H184.647L185.096 219.299L185.082 219.819L184.549 220.894L184.66 221.459L184.864 221.685L186.411 222.636L188.175 224.365L188.402 225.134L188.362 226.183L188.46 226.951L188.673 227.471L189.411 228.556L189.419 230.453L189.628 230.929L189.953 231.302L189.926 231.426L189.744 231.449L189.539 231.64L189.335 231.471L189.202 231.493L188.793 231.889L187.922 230.804L187.064 230.116L184.598 231.133L182.438 231.831L180.123 232.066L178.941 231.129L175.208 230.395L176.243 237.532L176.785 238.906L177.607 243.603L181.869 250.869L177.741 254.406L163.458 258.864L160.329 263.316L160.223 266.063L159.898 267.991L158.112 268.889L158.125 268.645L156.072 269.271L155.805 269.036L155.49 266.849L155.588 265.974L155.494 265.547L155.592 265.423L155.556 265.121L155.654 264.827L155.548 264.276L154.725 263.694L153.494 262.361L152.974 261.645L152.717 260.997L150.917 260.49L150.495 260.455L150.459 260.601L150.144 260.894L149.953 261.468L149.219 262.139L147.508 262.463L147.291 263.125L146.895 263.17L145.74 264.707L143.309 264.125L140.714 263.365L140.616 263.454L140.629 263.779L140.376 264.427L140.016 264.73L138.456 264.707L137.03 264.885L134.905 264.347L133.225 264.921L132.946 264.796L132.306 264.179L133.354 260.232L133.257 259.952L130.506 258.17L128.67 258.775L127.884 259.392L125.902 258.788L125.72 255.117L125.204 254.677L124.902 254.273L124.671 253.633L124.391 253.242L121.484 249.993L122.973 246.98L123.773 245.621L121.929 241.252L119.951 241.194L119.605 241.105L119.107 240.248L119.143 239.506L118.827 239.372L118.623 239.43L117.827 240.083L117.489 240.701L117.054 241.083L116.801 241.781L116.983 243.074L116.609 243.625L116.654 244.145L113.89 246.487L113.454 246.927L111.423 249.593L111.397 249.211L111.188 249.225L111.139 249.46L111.321 249.665L110.872 249.869L110.366 249.722L111.077 249.531L110.917 249.327L110.65 249.407L110.566 249.193L110.13 248.878L110.023 248.394L109.575 247.967L109.432 247.62L109.206 247.607L109.015 247.865L108.859 247.563L108.446 247.483L108.53 247.211L108.313 247.269L108.264 247.425L107.65 247.269L107.602 247.425L107.095 247.851V248.122L107.024 248.136L106.531 247.967L106.539 247.651L106.117 247.616L105.904 247.469L105.544 247.771L105.024 247.536L101.704 248.198L100.967 247.882L100.473 247.523L100.282 247.038L100.296 246.554L100.171 246.034L100.353 245.687L100.584 245.621L101.349 245.554L101.531 245.914L101.891 246.06L102.606 246.14L103.451 246.456L103.829 246.714L104.326 246.816L104.868 247.096L104.877 247.309L105.446 247.296L105.433 247.163L105.224 247.051L105.668 246.669L105.606 246.612L105.135 246.803L104.522 246.758L104.464 246.669L104.153 245.363L104.189 244.878L104.006 244.012L104.015 243.843L104.326 243.607L104.691 243.056L104.753 242.719L104.877 242.932L105.122 243.012L105.508 242.661L104.811 242.132L105.077 241.154L105.188 240.128L104.926 239.981L103.789 239.79L103.66 239.497L103.584 238.763L103.406 238.515L102.135 237.995L101.593 238.052L101.184 237.964L100.918 237.559L100.336 237.492L99.4868 237.684L99.318 237.964L98.9313 238.021L98.6691 238.372L98.678 238.666L98.4336 239.15L97.3004 239.746L96.7449 240.861L96.3583 241.132L96.3316 241.457L95.4739 241.661L95.1717 242.594L94.7496 243.372L94.1363 243.505L92.9275 244.203L93.0964 246.172L92.372 246.407L91.8521 246.216L91.0077 246.656L90.2345 246.26L86.4927 245.563L83.6885 246.607L83.3108 243.683L84.5195 241.039L83.7729 239.946L80.2578 240.497L79.68 240.408L79.2845 240.474L78.5824 241.208L77.0625 241.479L73.1963 243.616L72.943 243.661L72.6186 244.065L72.9963 244.198L73.6229 244.185L73.934 244.678L73.8584 245.185L74.0673 245.376L73.9429 245.625H73.6629L73.8184 246.163H73.5651L73.4807 246.42L73.0363 246.914L73.6362 248.385L76.3915 252.015L76.0049 252.478L76.3693 253.242L75.7294 253.118L73.7873 254.095L72.9785 254.242L72.583 254.206L71.4231 253.913L70.1166 253.869L69.73 252.002L69.5745 251.753L65.4904 251.878L65.2505 251.171L64.8061 251.113L64.575 250.607L64.7172 250.473L64.6905 249.811L64.8727 249.598L64.8505 249.011L64.5128 247.394L64.7883 246.843L65.3571 246.327L65.0105 245.763L62.3441 239.448L61.4242 237.51L61.4109 236.644L61.8686 235.271L61.8197 235.159L62.6019 233.062L62.6863 232.577L62.6419 231.933L62.5663 231.631L62.0197 230.693L59.7 227.849L59.42 227.138L59.3711 226.698L59.4644 225.68L59.971 224.583L60.7709 223.792L62.7041 222.943L63.4507 221.543L65.3571 221.77L67.0458 221.374L68.0102 220.943L69.3967 220.65L70.8588 219.97L71.4765 219.61L72.0942 218.966L72.5741 218.175L72.743 217.441L73.6229 216.57L73.8273 216.117L74.2228 216.104L74.4184 215.593L74.8806 215.299L75.8449 214.393L81.1243 216.157L82.6708 216.317L87.9458 214.868L89.0568 213.975L93.4875 212.708L95.6161 211.202L97.1715 209.82L98.0648 208.833L98.3803 208.336L99.0602 206.282L100.149 204.989L101.202 204.42L102.18 204.105L103.131 204.047L104.926 204.318L105.7 204.149L106.113 203.981L107.202 203.198L108.473 201.883L110.13 201.056L110.917 200.874H112.077L114.121 201.327L116.512 201.696ZM111.254 243.501L111.263 243.714L111.614 243.692L111.49 243.781L111.561 243.905L111.828 243.883L111.534 244.332L111.339 244.39L111.379 244.692L111.232 244.758L111.112 244.363L111.206 244.238L111.17 244.025L110.943 244.216L110.881 244.092L110.992 243.967L110.943 243.798L110.557 244.034L110.819 244.35L110.663 244.598L110.748 244.709L110.699 245.034L110.557 245.012L110.295 245.238L111.112 245.407L111.294 245.834L111.708 245.732L111.89 245.936L112.134 245.589V245.296L111.966 245.305L112.121 245.069L112.25 245.136L112.405 245.003L112.259 244.958L112.321 244.803L112.041 244.745L112.05 244.892L111.917 244.914L111.859 244.758L111.774 244.838L111.69 244.478L112.121 244.523L112.094 244.221L111.97 244.154L111.823 244.234L111.85 244.03L111.974 243.95L112.094 244.052L112.361 243.736L112.517 243.692L112.614 243.927L112.672 243.772L112.974 243.683L112.934 243.514L113.054 243.412L112.899 243.154L112.823 243.359L112.57 243.483L111.677 243.594L111.254 243.501ZM112.437 244.176L112.463 244.301L112.934 244.243L112.619 243.985L112.583 244.118L112.437 244.176ZM108.917 246.58L108.979 246.492L108.904 246.389L108.726 246.625L108.917 246.58ZM107.059 247.727L107.068 247.594L106.935 247.616L106.948 247.74L107.059 247.727ZM111.961 248.607L111.89 248.034L111.552 248.202L111.179 248.238L111.441 248.496L111.148 248.745L111.961 248.607Z",
            fill: "var(--main-800)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            opacity: "0.8",
            d: "M123.938 141.969L123.507 141.658L123.449 141.507L123.898 141.738L124.009 141.898L123.938 141.969ZM118.459 141.267L118.383 141.209L118.481 141.049L118.734 141.062L118.459 141.267ZM121.236 139.6L121.139 139.578L121.13 139.44L121.254 139.453L121.236 139.6ZM117.37 138.787L117.263 138.809L117.254 138.671L117.446 138.614L117.37 138.787ZM120.787 108.479L120.712 108.457L120.756 108.226L120.787 108.479ZM137.608 101.76L141.439 103.462L144.589 106.795L144.429 106.91L141.314 103.613L137.657 102.017L136.737 102.111L136.421 102.017L136.541 101.857L137.608 101.76ZM95.6612 69.492L95.3946 69.6298L94.1503 69.5231L93.4837 69.5809L91.9994 69.9408L91.2484 70.3497L91.2573 70.5141L91.6083 70.7141L91.3639 70.9585L90.7462 70.9141L90.4307 70.6697L90.3596 70.3186L90.4351 69.9097L90.7595 69.572L91.4572 69.1854L92.1105 68.6032L92.5949 68.4877H93.1015L94.0925 68.6965L95.6124 69.2431L95.6612 69.492ZM120.285 73.2783L120.214 73.5227L120.032 73.4871L119.334 74.0471L119.205 74.3359L119.165 75.1492L119.819 75.0336L121.632 78.4866L121.81 78.5799L122.072 79.4154L124.423 84.0282L123.747 92.1518L124.289 92.1874L124.587 92.7073L124.974 94.3205L126.014 95.2715L126.254 95.6403L126.942 96.3247L127.462 96.498L128.249 96.5113L128.875 96.0936L129.129 95.8714L129.995 94.4449L130.911 94.4227L131.982 94.5604L132.284 94.7338L132.728 94.8138L132.973 95.1026L133.484 95.347L133.879 95.3604L134.057 95.2093L134.217 95.2893L135.061 95.0359L135.253 95.2581L134.586 95.4092L134.564 95.5026L135.519 95.4448L135.99 95.8492L136.341 95.987L136.617 96.9735L136.412 97.2402L136.568 97.5646L136.421 97.8756L136.519 98.56L136.697 98.6756L136.648 99.16L137.555 99.8532L137.981 99.8888L138.026 100.004L137.941 100.12L137.492 99.9599L137.337 100.111L137.395 100.573L137.035 100.782L136.875 101.035L136.377 101.058L136.315 101.129L136.448 101.244L136.088 101.315L136.066 101.431L136.417 101.72L136.31 101.893L136.199 101.791L136.004 101.804L135.484 102.257L134.55 102.409L134.422 102.511L134.23 102.44L133.737 102.555L133.559 103.04L133.511 103.964L133.004 105.804L132.653 106.324L131.013 107.239L130.564 107.679L130.346 108.199L129.755 108.212L129.107 108.363L128.6 108.652L127.565 109.75L126.529 109.852L125.32 110.141L125.089 109.781L125.223 109.297L124.8 108.661L123.845 108.732L123.049 108.017L122.676 107.497L121.698 107.786L121.179 108.319L120.996 108.168L121.09 108.017L120.885 108.146L120.485 108.11L120.427 108.386L120.534 108.537L120.316 108.466L120.379 108.061L120.054 107.67L119.641 107.683L119.125 108.239L118.628 108.319L118.374 108.563L117.903 108.794L117.708 109.243L117.672 109.959L117.926 111.381L117.868 111.714L118.001 111.901L117.939 112.145L118.068 112.447L117.877 112.896L118.072 113.461L118.41 114.025L118.374 114.256L118.734 115.283L118.623 116.02L119.472 116.816L119.548 117.322L120.321 118.709L120.272 119.389L120.596 119.736L121.019 120.633L121.081 121.153L121.383 121.682L121.694 121.695L121.618 122.144L122.098 122.535L122.392 123.513L121.69 124.722L121.472 124.851L121.352 125.082L121.112 125.153L120.859 125.051L120.65 125.326L120.601 125.962L120.685 126.539L121.201 127.139L121.25 127.313L121.156 127.637L120.432 127.717L119.948 127.579L119.099 128.006L118.788 127.824L119.032 128.41L119.294 128.512L119.259 128.663L119.37 128.743L119.721 128.272L120.014 128.259L120.196 128.388L119.836 129.126L119.796 129.828L119.965 130.29L119.845 130.761L119.965 130.912H120.392L120.694 131.05L121.152 130.957L121.97 131.165L122.587 131.13L122.698 130.979L122.832 129.53L122.987 129.161L123.263 128.863L123.423 128.828L123.481 128.677L123.374 128.077L123.481 127.824L122.832 125.357L122.916 125.242L123.049 125.255L123.725 127.584L124.272 128.09L124.329 128.321L125.16 128.344L125.64 128.25L125.942 128.308L126 128.446L125.298 128.712L124.934 129.081L124.334 129.161L123.849 129.668H123.329L123.121 131.094L122.641 131.832L122.529 131.983H122.334L122.294 131.832L122.392 131.637L122.298 131.579L122.094 131.876L121.783 132.036L121.605 132.499L121.512 133.165L121.259 133.485L121.112 133.97L120.427 133.832L120.188 133.925L120.028 134.027L119.65 134.716L119.045 134.956L118.672 135.369L118.13 136.885L117.806 137L117.623 137.276H117.263L117.179 137.391L117.37 137.689L117.25 138L117.286 138.182L117.09 138.516L116.93 138.574L116.508 138.311L116.281 137.956L115.784 138.049L115.024 138.485L114.406 137.796L114.25 137.831L113.85 137.431L113.535 137.8L113.744 138.351L114.25 138.422L114.877 138.938L114.801 139.053L114.508 139.089L114.57 139.32L114.339 139.56L114.33 139.165L114.126 139.085H113.917L113.748 139.405L114.059 139.716L114.53 139.853L114.793 140.231L115.215 140.347L115.335 140.68L115.712 140.92L115.921 141.289L116.286 141.644L116.681 141.804L117.33 141.849L117.726 141.458L118.303 141.387L118.801 141.871L118.739 142.284L118.365 142.48V142.711L118.561 142.906L118.89 142.871L118.761 142.609L118.819 142.564L119.45 142.804L120.512 142.586L121.236 142.711L121.805 142.298L122.818 142.449L123.529 142.898L124.147 143.449L124.898 143.564L125.427 143.826L125.765 143.733L125.991 143.915L126.507 144.56L126.987 144.835L131.875 144L136.199 145.088L138.266 146.453L139.959 148.128L137.857 149.63L135.804 149.261L135.137 149.87L134.724 149.688L133.599 152.003L132.057 153.376L132.013 155.989L131.835 156.905L131.28 158.211L129.893 160.785H128.653L125.08 160.522L124.956 160.02L125.751 159.607L126.258 159.149L126.631 158.487L125.747 156.723L124.903 156.252L124.685 156.674L121.667 156.754L120.765 156.501L119.339 155.803L119.348 155.15L119.059 154.83L120.53 154.167L119.561 153.216L119.583 152.816L119.308 151.945L118.934 152.279L118.703 152.199L118.592 151.959L118.614 151.466L118.37 151.212L118.361 150.79L118.037 150.115H118.534L118.761 149.803L119.294 149.586L119.836 149.572L119.912 149.066L120.481 148.768L120.685 148.724L120.783 148.817V149.07L121.014 149.186L120.921 148.648L120.676 148.306L120.423 148.284L120.085 148.524L119.93 148.364L119.383 149.017L118.717 148.457L118.401 148.55L118.206 149.079L117.979 148.884L117.801 149.377L117.539 149.444L117.446 149.741H117.135L116.53 148.319L116.095 148.137L115.361 148.115L115.001 148.217L114.437 148.617L114.042 149.27L113.833 149.386L112.686 149.204L112.419 149.284L111.855 148.972L111.322 149.204L111.415 150.199L111.064 150.323L110.833 150.528L110.749 150.861L110.384 150.954L110.157 151.368L110.166 151.701L109.864 151.954L107.984 151.861L107.744 151.963L106.851 152.008L106.562 152.123L106.02 152.572L105.842 152.745L105.505 153.456L104.385 154.35L103.06 154.958L102.709 154.892L101.963 154.958L101.23 154.798L100.541 155.096L100.132 155.692L99.5186 156.172L98.8698 156.318L98.0121 156.869L97.4655 157.558L97.0566 157.247L97.1411 157.043L97.1055 156.629L96.8522 156.069L96.95 154.763L95.5768 154.545L94.9102 154.821L94.3947 155.358L94.3014 155.292L94.1236 155.407L92.786 155.487L90.5062 156.816L89.0664 156.918L88.0665 156.794L87.0177 156.381L86.2711 156.634L85.2357 156.394L84.7557 156.003L84.6846 155.421L84.7246 155.203L85.3023 154.483L85.8578 151.572L85.729 150.666L87.1199 149.257L87.7732 147.835L87.7821 147.546L88.2443 146.328L88.8131 145.502L87.0888 144.768L86.8977 144.782L85.9467 145.044L85.5023 145.871L80.7295 143.715L84.6313 133.823L85.4268 131.512L85.7067 130.303L85.8045 128.828L86.1645 128.335L85.8978 127.575L86.4533 126.379L87.2621 124.122L88.1198 119.927L89.0619 114.087L89.5063 109.666L90.0529 106.057L90.0796 105.226L90.4662 102.613L91.1284 96.7202L91.7594 93.7428L92.1683 92.6762L92.7727 91.503L93.9592 87.4767L95.5901 80.9619L95.9545 79.2776L96.2656 72.9938L96.5056 71.9806L97.0122 71.0029L97.3988 70.5274L97.6432 70.4208L98.6609 70.2341L99.0564 70.3497L99.603 70.083L100.741 70.1408L100.683 70.3986L100.936 70.2608L101.127 70.3763L101.078 70.5052L100.918 70.4119L100.776 70.6697L100.852 70.9941L101.03 71.194L101.176 71.2074L101.225 71.4873H101.301L101.816 71.3496L101.758 71.0118L102.061 70.7808L101.967 70.5808L101.754 70.7319L101.629 70.3941L102.314 70.2564L102.518 70.723L102.492 71.1674L101.807 72.6339L101.612 73.5982L101.723 73.7049L101.749 73.9715H101.932L101.981 74.3093L102.763 76.0646L102.958 76.3313L107.558 78.971L109.842 78.3666L110.637 77.8422L112.051 77.2512L111.98 77.0423L112.233 77.0201L113.126 75.8913L113.317 75.4736L113.428 74.8559L114.095 74.2604L114.997 74.0382L115.459 74.0515L116.508 73.5627L117.326 73.7938L117.917 73.6782L118.423 73.4338L118.605 73.6071L118.73 73.5627L119.01 73.8515L119.805 73.256L119.712 73.1183L119.819 72.9805L127.858 66.0168L128.147 66.4034L120.285 73.2783ZM108.015 47.8632L108.34 47.7565L108.584 47.9787L109.126 48.7253L109.935 50.294L109.873 50.7029L109.678 50.7251L109.58 51.0406L109.438 50.654L109.255 54.3025L109.398 55.3779L109.384 56.1956L109.109 57.0355L108.798 57.1511L107.953 57.8621L107.86 58.2576L107.398 58.5376L107.336 59.7419L106.78 60.5018L106.585 61.0973L106.34 61.3284L106.233 61.1551L105.838 61.4795L105.882 61.6884L105.571 62.1417L105.269 62.315L104.722 62.5372L103.731 62.5816L103.549 62.7327L101.656 65.1947L101.665 65.4035L101.976 65.5902L101.772 65.8835H101.207L100.976 65.6169L100.785 65.7546L100.314 65.4613H99.9408L98.9009 64.9725L98.7187 65.1236L98.9098 65.2169L98.9853 65.568L99.803 65.8924L99.9097 66.1368L100.372 66.4301L100.443 66.5812L100.239 66.5945L99.7675 66.3723L99.8297 66.4657L99.0076 66.8167L99.0164 66.9456L99.2209 66.9812L99.1986 67.0745L98.4965 67.3056L97.9143 67.2122L97.4833 67.4567L97.6521 67.5144L97.4566 67.7366L97.1944 67.7011L96.6167 67.1634L95.999 65.9146L95.7546 64.6436L95.8657 62.475L96.3145 60.1774L96.6745 58.9642L97.3411 57.3288L98.6831 54.8047L100.763 51.4761L101.514 50.4718L103.798 46.7389L104.754 45.5701L105.602 44.8191L106.251 44.5746H106.429L106.696 44.7613L107.14 45.5212L107.442 45.7212L107.802 46.5033L107.767 47.1566L107.842 47.5788L108.015 47.8632Z",
            fill: "var(--main-800)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            d: "M195.161 83.0676L195.699 83.7653L195.854 84.2186L196.205 84.6808L198.699 87.5827L199.201 87.8627L200.534 89.698L200.992 90.1602L198.699 92.6977L198.614 93.2577L197.25 93.942L195.632 94.3331L195.481 94.4398L195.161 94.9953L196.583 98.6571L197.379 100.395L197.739 101.643L198.174 102.221L198.414 102.372L198.503 102.314L198.734 102.532L199.072 103.23L199.614 103.008L199.783 103.203L200.685 103.31L201.063 102.79L201.44 102.777L202 102.568L202.076 102.87L202.707 103.23L202.907 103.159L202.983 103.319L203.516 103.585L203.902 103.55L204.276 104.177L204.689 104.488L205.191 104.523L205.431 104.848L205.787 105.079L205.978 105.043L206.289 104.652L206.547 104.688L206.978 105.274L207.182 105.39L207.418 105.359L207.671 104.985L208.209 104.95L209.08 104.536L209.502 104.514L209.742 104.928L209.551 105.159L209.573 105.679L210.093 106.145L210.639 106.341L210.817 106.572L211.457 106.954L211.497 107.194L211.639 107.345L211.857 107.323L212.168 107.554L212.097 107.705L212.542 108.261L212.657 108.825L212.959 109.252L212.893 109.727L213.101 110.083L213.137 110.456L213.395 110.802H213.573L213.635 111.122L213.733 111.149L214.368 110.811L214.541 110.514L215.212 110.536L215.435 110.131L216.07 110.456L216.274 110.291L216.323 110.025L216.746 109.714L216.941 109.358L217.017 109.62L217.208 109.807L217.185 110.256L217.421 110.558L217.843 110.825L218.256 110.891L219.03 110.66L219.256 110.42L219.527 109.563L219.839 109.727L219.825 110.016L220.118 110.131L220.198 110.314L220.732 110.371L220.878 110.247L221.327 110.269L221.749 110.456H222.194L222.385 110.811L222.7 110.834L222.82 111.122L222.98 111.136L223.016 111.38H223.18L223.283 111.18L223.087 109.243L222.869 108.594L222.714 107.252L222.763 107.034L224.865 105.301L225.442 104.976L229.931 103.968L234.21 106.003L236.61 106.838L236.25 107.656L236.397 107.705L236.499 107.936L236.046 108.434L236.188 108.674L235.935 109.389L236.743 109.438L236.872 109.46L237.166 109.936L237.357 110.038L237.721 110.745L237.53 112.096L237.286 112.927L237.241 113.838L240.574 116.78L239.614 117.14L239.21 117.162L238.734 116.966L238.348 116.988L237.663 116.815L237.308 118.006L236.948 118.295L236.921 118.468L234.473 117.971L236.223 121.628L235.726 125.601L236.854 126.467L237.286 126.721L237.708 126.801L239.565 128.227L240.147 128.467L241.307 128.814L245.458 128.663L246.258 128.849L246.685 128.805L246.68 129.885L247.405 129.734L250.511 130.631L252.897 131.52L253.373 131.347L254.364 130.378L254.626 129.978L255.399 128.192L256.181 127.641L256.532 128.09L256.572 128.387L257.15 129.769L257.728 132.174L258.514 132.889L259.528 135.187L259.505 135.395L259.905 135.373L260.625 135.658L260.914 135.489L261.119 135.578L261.123 135.933L261.483 136.489L262.172 138.577L262.123 141.026L260.941 142.679L260.816 143.461L260.314 144.203L260.363 144.768L260.194 145.328L259.683 146.35L259.457 146.559L258.892 147.821L258.843 148.163L258.866 148.678L259.021 149.172L259.008 149.461L258.914 149.527L258.95 150.043L259.35 150.469L260.074 152.127L261.483 153.7L261.172 154.42L259.648 155.407L258.474 155.487L258.261 155.655L258.079 155.473L257.199 155.233L256.581 155.371L256.404 155.3L255.919 155.544L255.866 155.793L255.528 156.082L255.577 156.824L255.253 157.673L254.87 158.175L254.248 159.513L253.528 160.615L253.391 160.717L252.177 160.957L250.475 161.206L250.338 161.424L250.275 161.961L248.698 163.428L248.031 166.41L246.849 167.552L244.325 167.814H240.174L237.006 167.836L235.801 167.276L234.268 166.992L233.233 166.512L233.979 165.312L234.073 164.579L233.979 164.259L233.993 163.632L233.797 163.53L234.024 163.197L234.037 162.921L233.908 162.544L233.713 162.361L232.264 161.597L231.971 161.575L231.624 162.179L231.282 161.699L230.908 161.584L230.74 161.722L230.62 162.09L230.322 162.193L229.731 162.637L229.14 162.09L229.091 161.904L229.318 161.437L229.006 161.219L228.869 160.944L228.433 160.762L228.411 160.579L228.198 160.451L228.149 160.566L225.62 161.082L222.825 160.624L221.483 161.104L221.243 161.024L221.012 160.797L220.332 160.464L220.096 160.157L219.71 159.286L219.679 158.655L219.536 158.517L219.074 158.495L218.656 158.335L218.079 157.166L216.661 155.682L216.372 154.856L215.879 154.158L215.901 153.585L214.755 153.447L213.848 153.505L213.448 153.309L212.666 153.642L211.337 153.722L211.195 154.029L210.915 154.078L210.448 154.385L210.408 155.358L209.791 155.384L209.226 155.544L207.773 155.531L207.555 155.336L207.507 155.14L207.293 155.096L207.235 154.913L206.422 155.131L206.258 155.358L205.796 155.198L205.76 155.531L205.08 155.336L204.151 155.416L203.96 155.269L203.476 155.278L202.991 155.096L201.907 154.949L200.881 154.18L200.107 154.216L199.65 154.078L199.361 154.202L199.045 154.167L198.947 154.216L198.85 154.616L198.783 155.291L198.463 154.949L198.299 155.247L198.445 155.358L198.392 155.464L198.116 155.349L198.25 155.727L198.503 156.024L198.49 156.229L198.361 156.367L198.21 156.229L198.227 155.864L198.041 155.531L197.441 155.384L196.965 155.038L196.774 154.305L196.921 153.927L197.414 153.607L197.534 153.207L197.499 152.976L196.628 153.056L196.232 152.771L195.432 152.589V152.14L195.837 151.558L195.446 151.212L195.246 150.34L194.575 149.514L194.517 148.403L194.152 147.887L193.952 147.247L193.806 146.981L193.072 146.972L192.601 146.821L192.561 145.572L192.321 145.043L191.624 144.95L191.633 144.723L191.913 144.377L192.028 143.563L192.126 142.439L192.033 141.279L192.21 140.977L192.921 140.759L193.308 140.439L193.21 140.199L192.988 140.013L192.428 139.888L192.268 139.751L192.139 139.484L192.153 139.071L192.433 138.684L193.828 138.417L195.112 137.924L195.299 137.715L195.45 137.395L195.472 136.315L195.783 135.693L196.174 134.178L196.134 133.751L195.734 132.6L194.69 131.058L194.135 130.645L193.864 130.289L193.935 129.205L193.85 127.952L193.659 127.387L193.059 126.663L191.997 126.188L191.437 125.73L191.077 125.188L190.628 124.094L189.277 123.032L189.002 122.997L188.566 122.757L188.371 123.103L188.073 123.343L187.313 123.61L184.251 126.779L183.642 125.903L183.229 126.005L182.336 126.672L182.06 126.419L181.527 126.663L181.305 125.948L180.572 124.392L180.078 123.552L179.145 122.388L178.052 121.419L177.096 121.775L176.541 118.606L176.323 117.948L173.328 114.749L172.684 114.633L171.021 114.66L171.048 111.611L175.221 111.62L175.412 111.309L175.777 111.158L179.918 110.247L180.478 110.465L180.834 110.847L181.874 110.718L183.491 109.807L183.593 109.554L185.922 109.043L187.504 107.994L187.873 107.567L187.855 107.389L187.682 107.101L186.842 106.27L185.7 105.959L184.58 104.976L183.896 104.745L183.411 104.71L182.478 104.341L182.469 104.501L182.167 104.465L181.998 104.27L181.971 103.874L182.767 102.972L183.42 102.523L183.82 102.048L184.047 102.012L184.229 101.71L184.216 101.306L183.913 100.635L183.678 100.452L183.54 100.461V100.057L183.3 99.6392L182.807 99.3859L182.096 96.9284L181.598 95.6441L181.345 95.2486L179.989 93.9643L179.736 93.9509L179.314 93.6843L178.594 93.071L178.181 92.4889L176.803 91.5378L176.185 91.2401L175.354 91.089L175.208 90.9823H173.919L173.906 90.7646L172.955 89.9291L172.328 88.8137L174.012 87.6405L174.114 87.8182L174.972 88.3026L175.128 88.7781H175.297L175.572 88.5248L175.95 88.7204L176.612 88.6981L176.865 88.8492L177.194 88.8359L177.843 89.187L178.181 89.2181L179.652 88.5248L179.918 88.1071L180.527 87.7249L180.887 87.1783L181.523 86.7606L181.536 86.1784L181.62 86.0495L181.514 85.854L181.803 85.7162L181.829 85.4718L182.562 85.3785L182.9 85.2407L183.553 85.5074L183.474 85.063L183.562 84.8897L183.869 84.6808L184.82 84.4719L185.038 85.3652L185.447 85.9695L185.775 86.8628L186.042 86.9206L186.402 87.2494L186.704 87.3072L186.935 87.2938L187.153 87.1072L187.735 86.9117L188.54 86.805L188.855 87.4449L189.451 87.2005L190.513 86.5517L190.655 86.294L190.424 85.1341L191.219 84.8097L192.13 84.9119L192.45 84.8541L192.717 84.5786L193.068 84.3786L193.446 84.3431L194.299 83.8009L195.161 83.0676Z",
            fill: "var(--main-100)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            d: "M101.976 154.95L102.723 154.879L103.074 154.95L104.394 154.341L105.514 153.448L105.852 152.737L106.025 152.563L106.567 152.119L106.856 152.003L107.749 151.959L107.989 151.857L109.864 151.946L110.167 151.692L110.162 151.363L110.384 150.95L110.753 150.857L110.838 150.524L111.069 150.319L111.42 150.195L111.327 149.195L111.864 148.968L112.424 149.275L112.691 149.195L113.837 149.377L114.051 149.266L114.442 148.613L115.006 148.208L115.366 148.106L116.095 148.128L116.53 148.315L117.135 149.733H117.446L117.539 149.435L117.797 149.368L117.975 148.875L118.197 149.07L118.397 148.542L118.712 148.453L119.379 149.013L119.93 148.359L120.081 148.519L120.419 148.279L120.672 148.302L120.921 148.644L121.01 149.186L120.779 149.07V148.817L120.681 148.724L120.477 148.773L119.908 149.07L119.828 149.573L119.29 149.586L118.752 149.804L118.53 150.115H118.028L118.352 150.79L118.361 151.212L118.606 151.466L118.584 151.959L118.699 152.199L118.93 152.279L119.303 151.946L119.575 152.817L119.557 153.217L120.526 154.168L119.055 154.834L119.343 155.154L119.339 155.807L120.765 156.505L121.663 156.758L124.681 156.678L124.898 156.252L125.743 156.723L126.632 158.487L126.258 159.149L125.752 159.607L124.956 160.02L125.081 160.522L128.654 160.785H129.893L131.28 158.212L131.835 156.905L132.009 155.99L132.053 153.377L133.591 152.003L134.715 149.688L135.133 149.87L135.799 149.266L137.853 149.63L139.955 148.128L143.528 151.661L144.892 152.83L144.083 156.31L146.958 158.656L146.98 159.331L146.798 160.211L146.509 160.451L145.981 160.291L145.678 160.531L145.909 160.762L146.025 161.447L145.967 161.86L147.087 162.26L149.771 162.624L149.807 162.798L150.265 162.993L150.22 163.233L150.42 163.3L150.322 164.069L150.891 164.318L151.616 164.34L151.482 165.006L151.531 165.175L152.478 165.646L152.135 166.309L151.367 166.42L151.269 167.06L151.109 167.028L151.433 167.428L151.776 167.54L151.762 167.931L152.056 168.091V168.251L150.602 168.099L151.096 168.419L151.06 168.637L150.447 169.233L150.54 170.419L149.527 171.059L148.767 172.028L148.9 172.179L149.18 172.246L149.225 172.463L150.433 173.183L151.029 172.974L151.856 172.966L152.038 172.348L152.66 172.303L153.246 172.428L153.922 171.708L153.455 171.517L153.682 171.117L154.251 171.286L154.3 171.059L154.335 170.761L153.958 170.659L154.153 170.201L153.975 170.055L153.971 169.895L153.758 169.824L153.709 169.53L154.335 169.37L154.517 169.584L154.669 169.553L154.633 169.335L155.109 169.264L155.229 169.917L155.095 170.237L155.362 170.419L155.42 171.241L155.651 171.517L155.846 171.526L155.962 172.419L155.757 173.263L155.833 173.419L155.66 175.099L155.411 175.77L155.948 176.001L155.855 176.787L157.193 177.29L157.099 179.285L156.713 179.294L157.402 180.045L157.57 179.863L157.788 180.103L157.908 180.298L157.97 181.027L158.259 181.334L158.824 181.858L159.264 181.947L159.566 182.418L159.721 183.351L160.432 184.191L160.357 184.671L160.175 184.955L160.188 185.262L160.495 186.004L160.49 186.684L160.072 186.786L160.104 187.369L159.699 187.142L158.993 187.768L157.802 186.969L155.98 186.502L155.433 186.266L154.926 185.866L154.42 185.058L152.149 184.218L151.687 183.796L150.727 183.213L150.322 183.111L149.082 183.249L147.163 182.871L146.701 183.125L145.776 183.907L143.568 184.751L142.896 184.795L141.874 184.578L141.279 184.671L140.723 185.058L140.026 185.955L139.168 186.391L138.568 186.355L137.479 185.626L136.342 185.48L135.448 185.24L134.666 184.818L133.844 183.964L133.289 183.636L132.755 183.773L132.453 184.147L131.835 185.342L131.333 185.786L130.956 185.88L130.053 185.684L129.089 185.058L128.636 184.76L127.383 184.547L126.876 184.249L126.525 183.511L126.369 182.338L126.187 181.996L125.272 181.458L124.57 181.196L124.036 181.027L123.232 180.983L122.49 181.196L121.143 181.858L120.899 182.222L119.655 183.2L119.197 183.702L119.015 184.249L118.65 184.751L117.939 184.831L116.855 183.978L116.322 183.876L116.042 183.929L115.402 184.307L114.486 185.604L114.086 185.88L113.548 185.844L112.486 185.422H112.095L111.615 185.924L111.144 187.049L110.611 187.72L109.238 188.075L108.585 188.133L107.856 188.382L107.282 186.138L106.794 185.742L106.616 185.124L106.087 184.795L105.26 183.849L104.527 182.813L104.803 182.689L104.354 182.076L103.816 181.894L103.194 181.063L106.012 180.365L106.074 180.218L105.5 179.112L105.047 179.352L103.883 179.307L103.452 179.614L102.665 179.694L102.47 179.054V178.645L103.087 178.201L102.967 177.343L103.474 176.97L104.381 175.556L105.54 174.677L107.011 174.139L107.367 173.41L105.18 173.432L104.487 173.606L104.514 172.997L104.118 172.521L103.994 171.961L103.874 170.224L103.914 169.188L101.51 167.46L101.625 166.993L102.487 166.606L102.63 165.691L103.03 165.109L103.594 164.869L104.252 164.74L104.661 164.527L104.825 165.198L105.216 165.509L106.589 165.691L107.096 165.611L107.131 165.486L107.447 165.335L107.185 163.54L108.629 162.98L108.647 162.82L106.291 161.185L105.229 160.807L103.221 158.589L103.078 158.234L102.603 158.256L102.487 157.652L102.501 156.527L102.003 155.705L101.976 154.95Z",
            fill: "var(--main-800)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            opacity: "0.9",
            d: "M55.6074 253.415L55.7985 253.429L55.8119 253.26L55.883 253.304L56.2074 254.002L56.083 254.091L55.3941 254.157L54.5276 253.775L54.3098 253.526L54.8386 253.18L55.4697 253.269L55.6074 253.415ZM14.1807 248.989L15.4873 249.407L15.3939 249.678L15.4784 249.736L15.6561 249.656L15.7272 249.744L16.1361 249.758L16.8338 250.58L17.256 250.727L17.9892 250.749L18.3892 250.86L18.5003 251.073L18.9224 251.184L19.0158 251.398L20.7667 251.904L23.953 252.411L24.2997 252.815L24.3485 253.02L24.5085 253.086L24.5574 253.291L25.6906 254.033L26.2328 254.797L26.9083 254.864L27.4549 254.686L29.1791 255.033L29.2502 255.255L28.9703 255.482L28.9303 255.895L29.668 256.286L29.9346 256.299L29.9613 256.175L29.828 254.962L29.8857 254.806L31.2411 254.842L31.4944 255.157L32.0099 255.437L32.6854 255.539L32.481 255.762L32.4677 255.966L32.9832 256.966L33.1298 256.886L32.9343 256.246L33.1743 256.144L33.0054 255.593L33.2143 255.615L33.1521 255.771L33.272 256.139L33.4809 256.028L33.6364 255.615L33.792 255.628L33.6853 255.784L33.8186 255.939L34.3608 256.019L34.903 256.357L36.6983 256.313L37.1694 255.717L37.3249 255.291L38.3737 254.975L38.6759 254.584L39.9336 253.717L40.6935 253.393L41.8489 253.144L41.8889 253.042L42.4177 252.873L42.6488 252.909L42.7955 252.775L43.1421 253.046L43.2354 252.584L43.1599 252.406L43.3998 251.935L43.4487 251.251L43.8354 250.833L43.6709 250.451L43.5998 249.46L44.3331 248.425L45.4796 248.527L45.6485 248.762L47.3994 249.033L47.2572 249.269L47.4261 249.438L47.6083 249.358L47.8838 249.82L48.1282 249.909L48.4171 250.944L48.2971 251.024L48.3815 251.304H48.6704L49.2037 252.451L49.617 252.957L51.6123 253.082L52.5322 253.237L54.1765 254.673L55.4075 256.135L56.1318 256.548L55.3586 255.539L54.9853 254.722L55.7452 254.428L56.1896 254.406L56.4074 254.562L56.6251 255.011H57.0251L57.3362 255.233L57.7139 255.771L57.6783 255.984L57.8117 256.006L57.9939 255.726L57.6828 255.455L57.5361 255.117L58.6382 255.175L58.4694 254.997L57.5006 254.819L57.3317 254.651L58.9227 254.571L59.4648 254.695L59.1715 254.402L57.8161 254.393L57.2028 254.202L56.6207 253.202L56.6562 252.686L57.1184 252.54L57.4073 252.642L57.8428 252.273L58.105 252.215L58.2961 253.584L59.3093 254.18L59.807 254.268L59.0071 253.851L58.576 253.469L58.4427 253.153L58.456 252.58L58.5894 252.455L58.7804 252.602L58.856 253.073L58.9315 252.109L59.2115 251.793H59.8026L60.2825 251.998L60.4869 251.984L60.9847 252.366L61.2735 253.322L61.7046 254.042L62.4556 254.873L61.4513 256.402L57.8561 261.045L53.0833 264.654L51.7901 265.494L51.5101 265.774L50.6035 266.245L50.3636 266.672L49.0704 266.974L48.8748 266.885L48.6482 266.503L48.3593 266.738L47.8305 266.952L47.5772 266.525L47.115 266.538L46.2307 267.032L45.9018 267.4L45.5641 267.534L45.2174 267.858L45.0841 267.689L44.4575 268.729L43.4532 268.907L43.4265 269.231L43.1377 269.769L42.3688 269.827L42.0178 269.938L42.0089 270.085L41.5378 270.231L41.329 269.738L40.9912 269.356L40.7779 269.716L40.3691 269.738L40.1513 270.107L40.2446 270.6L40.0358 270.636L39.7736 270.996L39.0092 271.342L38.6137 271.196L38.4715 271.373L38.0982 271.564L37.956 271.218L37.5471 270.747L38.3648 270.12L38.5693 269.716L38.156 269.022L37.5427 269.338L36.1162 270.356L35.6229 270.053L35.4451 270.187L35.2896 270.12L34.9607 270.298L34.5519 270.129L34.0586 270.511L33.5787 270.622L32.6765 270.218L32.3166 270.16L32.241 270.249L32.3388 270.618L32.2766 270.676L31.9255 270.382L31.7833 270.08L31.3833 269.978L31.2767 269.765L31.2011 268.498L31.2722 266.605L31.7033 265.418L31.6055 265.263L31.0767 265.13L30.4279 264.547L30.1124 264.414L29.9924 264.672L29.6902 264.827L29.3391 264.592L28.9525 263.841L28.677 263.672L28.4014 263.819L26.6594 263.405L26.2106 263.583L24.6552 263.134L24.4863 263.032L24.2419 262.552L23.4953 262.539L23.3131 262.236L22.011 261.472H21.1178L20.7934 261.694L19.789 261.099L19.4646 261.134L19.2602 260.788L17.3137 259.757L16.616 260.588L16.9538 261.192L16.6871 261.383L16.8071 261.685L16.6827 261.899L16.4649 261.685L16.2338 261.743L15.9183 261.472L15.8961 261.317L15.2917 261.014L15.0606 261.103L14.9673 261.397L14.7496 261.552L13.9541 261.45L13.5941 262.214L13.2075 262.272L12.6787 261.743L11.9898 261.877L12.8431 266.485L9.65232 266.867L7.54143 267.014L7.11925 266.969L6.11491 266.587L5.83494 266.609L5.63941 265.569L5.51942 265.414L5.01281 264.934L4.5062 265.258L4.21734 264.663L3.47075 264.192L3.0619 263.383L2.44419 263.228L2.19089 262.912L2.61306 262.565L2.59973 261.925L2.4753 261.725L1.85759 261.299L1.58206 260.601L2.21311 260.263L1.02656 258.859L1.00879 258.659L2.78193 256.65L2.65306 255.642L2.04868 254.855L1.99091 254.508L2.08867 253.677L1.50651 252.553L1.85759 252.046L2.46197 251.62L2.47086 251.798L2.59529 251.784L2.63528 251.558L3.90626 251.095L5.5372 250.273L6.11936 250.184L6.20379 250.531L6.51932 250.633L6.58153 250.407L7.45255 250.136L7.79029 249.864L8.82574 249.718L9.8523 249.38L10.2611 249.189L10.3189 248.998L10.7055 248.976L11.4077 248.358L12.1943 248.278L12.5098 248.411L13.683 248.522L14.1274 248.678L14.1807 248.989ZM25.3307 236.368L25.4062 236.457L25.144 236.39L25.0107 236.23H25.2151L25.3307 236.368ZM24.5041 235.266L24.3708 235.222L24.5263 235.177L24.5041 235.266ZM24.353 234.782L24.2952 234.848L24.2374 234.737L24.313 234.524L24.353 234.782ZM26.3039 234.768L26.2683 234.871L26.1928 234.826L26.135 234.657L26.3617 234.231L26.3039 234.768ZM26.5839 234.262V234.422L26.4861 234.071L26.6461 233.888L26.7172 234.013L26.5839 234.262ZM24.313 233.959H24.2152L24.3974 233.848L24.553 233.511L24.6152 233.804L24.313 233.959ZM29.3613 232.809L29.6991 232.844L29.8235 232.755L30.1701 232.991L29.628 233.115L28.4948 233.093L28.5037 232.813L28.6147 232.666L28.8592 232.644L29.3613 232.809ZM37.2849 232.537L37.125 232.524V232.444L37.3427 232.217L37.4538 232.377L37.2849 232.537ZM30.4012 232.546L29.9524 232.297L29.9879 232.24L30.179 232.195L30.5034 232.329L30.4012 232.546ZM29.7924 232.231L29.868 232.604L29.0991 232.524L28.9081 232.333L29.548 232.142H29.7302L29.7924 232.231ZM23.8508 232.129L24.153 232.262L24.4285 232.533L24.5707 232.848L24.5974 233.275L24.4019 233.217L24.2197 233.782L24.0863 233.68L23.9575 233.048L23.6642 232.395L23.6775 232.057L23.8508 232.129ZM23.1753 231.791L23.1176 231.857L22.9487 231.453L23.1931 231.489L23.1753 231.791ZM22.8154 231.026L22.9576 231.049L23.002 231.262L22.8198 231.151L22.5576 231.186L22.5843 231.053L22.8154 231.026ZM57.7361 225.876L57.4917 225.809L57.6472 225.751L57.7361 225.876ZM34.5919 208.829L35.1741 209.398L37.1872 210.033L37.9027 209.895L39.3292 208.082L40.4135 207.438L41.7645 207.087L43.0666 207.269L44.0664 208.176L46.7506 208.775L46.9461 209.069L47.2261 210.282L48.386 211.926L48.4926 214.122L48.7193 215.015L49.337 216.099L49.8436 216.304L52.9366 216.552L54.2654 217.028L55.5897 216.992L56.6251 217.104L59.847 219.548L62.1401 221.13L63.4555 221.561L62.7089 222.961L60.7758 223.809L59.9759 224.6L59.4693 225.698L59.3759 226.716L59.4248 227.156L59.7048 227.867L62.0245 230.711L62.5712 231.649L62.6467 231.951L62.6911 232.595L62.6067 233.08L61.8246 235.177L61.8735 235.288L61.4157 236.661L61.4291 237.528L62.349 239.466L65.0153 245.78L65.362 246.345L64.7931 246.86L64.5176 247.411L64.8554 249.029L64.8776 249.616L64.6954 249.829L64.722 250.491L64.5798 250.624L64.8109 251.131L65.2553 251.189L65.4953 251.895L62.9622 252.006L63.1044 251.758L63.14 251.149L62.2734 249.709L60.9935 249.122L60.5314 249.211L60.3758 248.771L60.0736 248.491L59.4426 248.558L58.8738 248.927L58.4783 249.073H58.1538L57.0695 249.736H56.6073L55.8074 249.58L55.2386 249.242L53.5454 248.94L51.8567 248.309L51.679 248.265L51.5856 248.345L51.1635 247.86L50.3947 247.634L49.6614 246.891L49.6747 246.723L49.3148 246.216L49.5592 245.834L48.426 245.483L48.3549 244.998L48.2438 244.954L48.1149 245.154L47.0795 244.661L46.7328 244.278L46.3462 244.265L46.2262 244.052L45.9729 243.927L45.8663 243.678L45.1286 243.172L44.7286 242.496L44.3286 242.248L43.4132 242.887L42.5955 242.954L41.4401 243.754L40.8846 243.483L40.7868 243.541L40.8623 244.114L40.5468 244.181L40.8401 244.878L40.8535 245.274L40.4535 246.016L40.2846 246.758L40.1158 246.954L40.0891 247.278L39.9069 247.46L39.107 249.709L38.6137 249.944L37.045 249.473L36.445 249.789L36.094 250.158L35.5651 250.122L35.2274 250.416L34.6586 250.496L34.3119 250.678L33.1165 250.758L33.0587 250.891L32.7565 250.789L32.0099 249.878L31.3122 249.393L31.2278 249.216L30.2101 248.553L30.3301 248.069L29.4635 248.011L28.7036 247.518L28.0415 247.887H27.3616L27.1527 247.785L26.7438 247.447L26.3705 246.14L26.0195 246.176L25.6951 245.478L24.9574 244.745L23.8819 243.878L23.3842 243.607L21.2244 243.518L20.8289 243.892L20.7934 244.23L20.3712 244.478L20.1801 244.558L19.7312 244.492L19.4068 244.616L19.0469 244.527L18.5136 244.661L17.9937 244.243L17.9581 243.376L17.7137 243.412L17.6692 244.345L17.3182 244.243V243.874L17.1715 244.176L17.0871 244.141L17.1271 243.816L17.0204 243.781L16.9271 244.061L16.9049 244.15L17.6515 244.509V244.598L17.0338 244.452L16.6205 244.541L16.5982 244.732L16.4427 244.629L16.105 244.812L15.7406 244.629L15.5628 244.212L14.9451 243.616L14.5318 243.652L14.4118 243.447L13.4075 242.75L12.7542 242.132L12.4254 241.705L11.421 239.79L10.7455 238.821L10.0301 238.132L9.59455 238.021L7.70141 236.635L6.96371 235.644L6.61708 235.453L6.49265 235.093L6.21713 234.933L6.31489 234.022L6.63042 233.155L8.14137 232.275L9.65232 231.066L10.7766 230.342L12.5142 229.302L13.0786 229.111L14.0074 228.4L15.9183 227.418L16.5716 227.294L17.4026 227.485L18.0292 227.44L18.7936 227.6L19.5935 228.142L19.8957 228.129L22.0599 227.307L23.0153 227.094L23.3442 226.889L23.6108 225.511L23.6508 224.663L23.7708 224.596L23.6997 224.338L23.5308 224.303L23.3886 224.134L23.8953 224.045L24.1619 223.627L25.5751 222.17L25.4684 221.956L25.815 221.867L26.2417 220.93L26.015 220.645L26.0861 220.556L26.3617 220.543L26.375 219.263L25.3351 218.934L24.5263 218.268L23.7664 217.09L23.5397 216.401L23.4331 215.713L23.4597 214.944L24.0019 213.677L24.5841 212.637L24.984 212.197L25.3973 212.015L26.8016 211.553L27.5615 211.509L29.7702 210.602L31.5833 210.589L33.4409 210.962L33.7431 209.558L34.0453 209.127L34.5919 208.829ZM32.1344 216.099L32.3744 216.157V216.268L32.0144 216.166L31.9388 215.828L31.6144 215.886L31.2989 216.112L31.0456 216.699L31.1523 216.89L29.0725 218.303L28.6592 218.552L26.6283 219.263L26.6061 220.516L26.9571 220.561L26.8238 221.036L26.4372 221.05L25.855 222.463L25.9973 222.498L26.2283 222.227L26.255 222.387L25.6151 223.09L26.2639 223.427L26.1661 223.494L25.5617 223.223L25.3351 223.427L25.4462 223.912L25.2862 224.205L25.224 224.094L25.2995 223.867L24.8907 223.529L24.2419 224.094L24.2285 224.183L24.3485 224.241L24.1886 224.329L24.1397 224.658L24.0286 224.716L24.0419 224.876L23.8953 225.112L23.7042 226.782L23.8508 226.738L23.8064 226.996L24.1797 226.725L24.193 226.951L24.8907 226.805L25.6862 227.347H25.895L26.0639 227.653L26.8816 227.667L27.5304 227.262L28.037 227.342L28.3481 227.071L29.1836 227.049L29.3747 226.814L30.219 226.996L30.6679 226.569L31.9833 226.445L32.2633 226.187L32.8188 226.174L33.5653 226.431L34.383 226.16H34.7119L34.9874 226.511L36.3428 226.76L37.0939 227.56L38.2537 229.582L38.4848 229.378L38.5248 229.591L38.2626 229.693L38.3115 229.853H38.4448L38.5426 230.258L38.8314 230.787L38.9914 230.831L38.907 231.182L38.4448 231.475L37.2405 231.813L36.4539 232.875L35.8895 233.057L35.6273 232.946L35.1118 232.404L35.0052 232.089L34.343 231.64L33.1209 231.92L32.2899 232.595L31.13 232.324L31.0678 232.391L30.1257 231.84L29.4102 231.751L29.2991 231.862L29.2014 231.782L27.3216 232.875L26.8905 233.395L25.8639 234.093L25.384 234.915L25.1662 235.048L24.8152 234.257L25.0107 233.582L25.2151 233.684L25.4551 234.426L25.5262 234.404L25.4773 234.124L25.3084 233.888L25.2951 233.631L25.0907 233.484L24.8374 232.617L24.1619 231.671L25.0062 230.564L24.9351 230.52L24.7663 230.724L24.2241 231.515L23.9352 231.582L23.4286 231.266L22.9576 230.724L22.2688 230.387L22.171 230.16L21.931 230.262L21.6199 230.071L21.0644 228.662L21.0156 228.333L21.1355 228.276L21.1933 228.071L20.6511 228.205L19.8423 228.631L19.9668 229.68L20.109 229.938L20.7223 230.524L20.6867 230.751L21.0867 231.146L21.1355 231.351L21.7355 231.609L22.5443 232.262L22.7976 232.329L23.4242 233.635L23.8464 234.888L24.7885 236.275L25.064 236.51V236.768L25.1751 236.564L25.4151 237.004L25.2596 236.599L25.4862 236.733L25.4373 236.608L26.3883 236.417L26.1839 236.328L25.5573 236.395L25.3529 236.124L26.0061 235.844L26.3928 235.439L26.9394 235.328L26.9749 235.248L26.7483 235.079L26.6994 234.866L27.1482 234.031L27.2327 233.511L28.0193 233.004L28.357 233.026L28.1659 233.48L28.3837 233.466L28.4681 233.151L30.1568 233.115L31.5922 233.306L32.161 233.115L32.321 232.844L32.5032 232.764L33.1654 232.662L33.2765 232.515L34.1208 232.48L34.4941 232.546L35.0096 232.84L35.1563 233.066L35.1341 233.235L35.3785 233.448L36.2139 233.617L36.4317 233.515L36.3695 233.2L36.6317 233.04L36.765 233.053L36.8939 233.413L38.0049 233.106L38.6315 233.32L38.7915 233.16L38.987 233.293L39.0981 233.022L39.4847 233.008L39.8358 232.782L40.2668 232.973L40.8357 232.951L41.0401 233.231L41.1156 233.795L41.2712 234.124L41.8889 235.013L42.9777 234.537L43.7954 234.684L45.3641 235.248L46.0618 235.599L46.204 235.555L46.1596 236.061L46.2707 236.466H46.4884L46.4129 235.564L46.9195 235.879L47.2839 235.924L47.5772 236.519L47.8083 236.555L47.8572 236.688L48.7148 237.284L49.1015 237.688L49.6214 237.528L49.8614 237.901L49.7192 238.372L49.8792 238.892L50.0258 238.981L50.1947 239.319V239.501L49.9858 239.861L50.048 239.928L49.928 240.074L50.0836 240.23L50.2302 240.701L50.4124 240.79L50.9057 241.501L51.0124 241.465L51.0968 241.599L51.0124 241.656L51.5901 242.559L51.9545 242.572L52.5855 243.945L53.2521 244.101L54.9053 245.145L54.9408 245.065L55.0875 245.101L55.3497 245.505L55.7497 245.563L56.8251 245.38L57.1851 245.043L58.3227 244.447L58.6605 244.425L58.816 244.221L60.1714 244.456L60.3803 244.567L60.5136 244.781L60.8602 244.883L61.6335 244.825L61.4202 242.652L61.5801 242.11L61.1713 239.035L58.1494 237.67L57.8961 237.244L57.8694 236.973L58.2649 236.159L57.7228 235.71L57.5895 235.382L56.7807 235.324L56.5273 235.199L56.3318 235.448L56.0074 235.528L55.8963 235.515L55.4608 235.03L54.8697 235.257L53.1677 235.19L53.0922 235.088L52.4744 235.124L52.4167 234.919L51.2079 234.831L51.0035 234.595L51.1279 234.404L50.9324 234.346L50.9813 234.257L50.8746 234.146L50.608 234.168L50.248 233.208L50.3547 233.026L49.9103 232.631L49.7414 232.697L49.6214 232.564L49.5814 232.035L48.4082 231.822L47.946 231.564L48.1371 231.035L47.6883 230.369L46.9417 230.053L46.6617 229.693L46.2262 229.444L45.2619 229.298L44.8752 228.778L44.9019 228.191L45.3508 227.502L46.1996 227.414L46.1907 227.325L46.6617 227.142L46.8884 227.245L47.3372 226.951L47.6305 227.062L48.7282 226.249L49.2748 226.214L49.7814 225.671L50.168 225.569L50.3458 225.636L50.4169 225.476L51.3323 225.192L51.8878 225.374L52.2611 225.316L53.1322 225.498L53.3499 225.734L53.5677 226.334L53.7499 226.245L53.8921 226.311L54.3365 226.765L54.2431 226.449L55.0031 226.463L56.2252 226.214L56.4651 226.405L57.0473 226.36L57.1317 226.44L57.2517 226.111L58.0694 225.987L58.105 225.738H57.7805L57.7717 225.649L58.2338 225.392L58.2072 224.983L57.8783 224.96L57.5495 225.174L57.3317 225.049L56.9717 225.285L54.892 225.227L54.212 223.858L53.621 223.383L53.3766 223.369L53.1099 223.156L53.0522 221.983L53.1499 221.814L53.461 221.939V221.579L54.5142 220.65L55.2297 220.672L56.5096 221.045L57.0073 221.294L57.3673 221.61L58.3494 222.041L59.5537 222.774L59.5048 222.187L59.2026 221.654L58.4516 221.214L58.3938 221.352L58.185 221.41L57.0606 220.708L56.9629 220.561L57.1717 220.165L57.1317 220.085L56.9762 220.072L56.6518 220.525L55.7097 220.197L55.7719 220.059L55.6297 219.948L55.6563 219.868L55.8474 219.992L57.1184 219.903L57.1451 219.677L56.9051 219.641L56.9184 219.53L56.7718 219.441L56.0207 219.521L55.6563 219.734L55.6119 219.668L55.9363 219.477L56.6251 219.41L56.7096 219.228H56.8029L56.8162 219.365L57.0251 219.388L57.074 219.205L57.2339 219.103L56.9184 218.268L56.7096 218.108L56.5629 217.823L56.1318 218.095L53.6654 218.468L52.7011 219.134L52.7855 219.361L53.461 220.05L53.341 220.388H53.0122L52.8522 220.739L53.0433 220.828L53.0344 220.961L52.9233 221.099L52.7766 221.085L52.75 221.414L51.7856 222.014L51.639 222.387L50.9546 223.112L50.1191 223.521L49.457 223.227L48.7459 223.601L48.6215 223.814L47.6705 224.165L46.6528 224.72L45.4974 224.743L45.1019 225.08L43.9198 225.156L43.3998 224.872L42.5155 223.947H42.32L41.8845 223.054L41.7156 222.894L41.6045 222.907L41.2445 223.472L40.8135 223.609L40.8223 223.472L40.4491 223.436L40.1957 223.538L38.8448 223.121L38.7603 222.961L40.3557 221.547L40.8757 221.343L40.2935 221.49L38.7115 222.912L38.3515 223.023L38.3871 221.947L38.0404 221.316L38.116 221.156L37.9338 220.454L37.6671 220.285L37.3161 219.699L37.3516 219.201L36.1206 218.037L35.9295 217.948L35.3607 218.13L33.6231 217.237L33.7164 216.93L33.6764 216.557L33.312 216.015L32.9965 215.846L32.5521 215.779L32.1033 215.837L32.1344 216.099Z",
            fill: "var(--main-800)"
          }
        ),
        /* @__PURE__ */ jsx152(
          "path",
          {
            opacity: "0.6",
            d: "M70.2465 212.885L70.7176 213.294L70.8509 213.227L71.0553 213.285L71.1309 213.498L71.6241 213.76L71.6464 213.929L70.3576 213.987L69.8777 213.863L69.3933 213.329L70.0732 212.898L70.2465 212.885ZM64.9849 209.352L65.5492 209.365L67.3446 209.81L69.6777 210.863L70.7976 211.227L71.1842 211.432L71.2197 211.796L69.5933 212.432L68.4378 212.387L67.5801 212.174L66.478 211.53L64.2649 210.01L64.1094 209.819L64.1716 209.49L64.4605 209.321L64.9849 209.352ZM80.7743 143.701L85.5471 145.857L85.9915 145.03L86.9425 144.768L87.1336 144.754L88.8579 145.488L88.289 146.314L87.8269 147.532L87.818 147.821L87.1647 149.243L85.7738 150.652L85.9026 151.558L85.3471 154.469L84.7694 155.189L84.7294 155.407L84.8005 155.989L85.2805 156.38L86.3159 156.62L87.0625 156.367L88.1113 156.78L89.1112 156.904L90.551 156.802L92.8308 155.473L94.1684 155.393L94.3462 155.278L94.4395 155.344L94.955 154.807L95.6216 154.531L96.9948 154.749L96.897 156.055L97.1503 156.615L97.1859 157.029L97.1014 157.233L97.5103 157.544L98.0569 156.855L98.9146 156.304L99.5634 156.158L100.177 155.678L100.586 155.082L101.274 154.785L102.008 154.944L102.034 155.7L102.532 156.522L102.519 157.642L102.63 158.251L103.11 158.229L103.252 158.584L105.265 160.802L106.327 161.179L108.682 162.815L108.66 162.975L107.22 163.535L107.483 165.33L107.167 165.477L107.132 165.601L106.625 165.681L105.252 165.499L104.856 165.192L104.696 164.517L104.287 164.735L103.625 164.859L103.061 165.099L102.661 165.681L102.519 166.597L101.652 166.983L101.541 167.45L103.945 169.174L103.905 170.214L104.025 171.947L104.15 172.507L104.545 172.987L104.518 173.591L105.216 173.423L107.403 173.4L107.043 174.129L105.572 174.667L104.412 175.547L103.505 176.96L102.999 177.338L103.119 178.191L102.501 178.635V179.044L102.696 179.684L103.483 179.604L103.914 179.297L105.083 179.342L105.532 179.102L106.109 180.208L106.047 180.355L103.225 181.048L103.852 181.879L104.385 182.062L104.834 182.675L104.558 182.799L105.292 183.835L106.123 184.781L106.652 185.11L106.829 185.723L107.323 186.123L107.891 188.363L108.625 188.114L109.278 188.057L110.651 187.705L111.184 187.034L111.655 185.906L112.135 185.403H112.531L113.593 185.826L114.126 185.861L114.526 185.586L115.442 184.288L116.082 183.91L116.362 183.853L116.895 183.955L117.979 184.808L118.69 184.728L119.055 184.226L119.237 183.679L119.695 183.177L120.939 182.199L121.183 181.835L122.525 181.173L123.272 180.955L124.072 180.999L124.605 181.168L125.307 181.431L126.223 181.964L126.405 182.306L126.561 183.479L126.912 184.221L127.418 184.519L128.671 184.737L129.129 185.035L127.098 188.905L126.894 188.95L126.952 189.234L126.049 190.541L126.094 191.256L125.765 191.096L124.787 192.905L124.663 193.869L124.503 194.176L123.752 193.994L123.294 194.109L122.872 193.985L122.677 194.145L122.601 194.7L122.139 195.394L121.646 195.665L120.797 195.678L120.219 195.962L120.108 196.349L120.286 196.736L120.77 197.033L120.508 197.282L119.712 197.602L119.481 197.864L118.801 197.593L117.024 197.638L116.224 196.936L116.042 197.127L116.304 197.958L116.148 198.584L116.57 199.286L118.081 199.491L117.624 201.704L116.57 201.682L114.193 201.331L112.149 200.877H110.989L110.202 201.06L108.545 201.886L107.274 203.202L106.185 203.984L105.772 204.153L104.998 204.321L103.203 204.05L102.252 204.108L101.274 204.424L100.221 204.993L99.1323 206.286L98.4524 208.339L98.1369 208.837L97.2437 209.823L95.6883 211.205L93.5596 212.712L89.129 213.978L88.018 214.871L82.743 216.32L81.1965 216.16L75.917 214.396L74.9527 215.303L74.4905 215.596L74.295 216.107L73.8995 216.12L73.695 216.574L72.8151 217.445L72.6463 218.178L72.1663 218.969L71.5486 219.613L70.9309 219.973L69.4688 220.653L68.0823 220.946L67.118 221.377L65.4293 221.773L63.5228 221.546L62.2074 221.115L59.9143 219.533L56.6924 217.089L55.657 216.978L54.3326 217.013L53.0039 216.538L49.9109 216.289L49.4043 216.085L48.7866 215L48.5599 214.107L48.4533 211.912L47.2934 210.268L47.0134 209.054L46.8179 208.761L44.1337 208.161L43.1338 207.254L41.8318 207.072L40.4808 207.423L39.3965 208.068L37.9699 209.881L37.2545 210.019L35.2413 209.383L34.6592 208.814L35.5791 208.339L35.9435 207.988L36.5834 206.321L36.7745 205.766L36.7256 205.495L36.0101 203.748L35.9879 203.046L36.5301 202.206L37.1567 201.717L37.8188 201.58L38.8232 201.193L39.632 201.171L40.2364 201.011L42.2895 200.046L44.4004 199.242L45.1958 199.038L46.6446 199.06L47.3467 199.389L47.6489 199.673L47.0179 199.549L46.9201 200.046L47.8356 200.602L48.2844 201.135L48.631 201.282L48.7288 201.18L48.4888 200.895L48.4977 200.691L48.8088 200.771L49.0354 200.602L49.1599 200.695L49.862 199.877L49.7509 199.753L49.822 199.629L50.3642 199.198L50.8975 198.958L50.933 198.562L50.6175 197.811L48.5288 194.709L48.3511 194.287L48.4622 193.425L48.7999 192.936L49.6087 191.207L49.8398 191.038L49.7909 190.687L49.2576 190.221L48.8621 190.052L48.5999 190.483L48.6133 190.972L48.4311 190.79H48.3555L48.3288 190.994L48.2355 190.958L48.0578 190.652L47.6 190.505L47.289 190.754L47.1467 190.696L46.7735 190.923L46.3024 190.967L46.058 190.852L45.9247 190.612L46.0002 189.999L47.0045 186.448L47.0268 185.515L46.929 185.435L46.6757 185.559V184.946L47.1067 183.946L47.4089 183.719L47.9067 183.799L48.4044 183.057L48.5955 182.99L49.2487 183.288L49.3732 183.195L49.8931 183.181L50.3731 183.488L51.253 183.839L51.6752 184.19L53.1594 182.879L54.1727 182.195L54.7682 181.684L55.0926 181.239L55.5281 180.933L56.3102 179.613L57.2035 178.382L62.6251 172.098L64.1449 170.156L64.1583 169.983L64.3271 169.938L64.6293 170.192L64.9582 169.703L64.8649 169.645L64.5893 169.987L64.4427 169.863L64.4205 169.658L65.8692 168.174L66.3536 167.819L67.5001 166.574L70.6287 162.561L72.7396 159.46L75.4549 154.731L77.4235 151.065L80.7743 143.701ZM49.9464 199.993L49.2576 200.833L49.3776 200.948L49.2443 201.344L49.2887 201.584L49.0577 201.54L49.1332 201.731L49.0221 201.855L49.1199 201.944L49.0754 202.055L49.1865 202.091L49.5154 201.748L50.0087 202.011L50.6708 202.611L53.2305 203.757L54.1016 204.406L56.1547 205.428L57.4923 206.326L59.4388 206.97L59.3899 207.059L59.5099 207.148L59.6876 207.103L61.2208 208.779L62.1629 209.596L62.994 210.161L63.1628 210.525L64.3716 212.018L64.7093 212.325L66.2558 213.298L66.918 213.436L68.0645 214.249L69.2955 214.725L70.2243 214.894L72.7974 215.031L73.2906 214.974L73.4995 214.783L73.6462 214.534L73.655 214.138L73.8506 214.125L74.295 212.641L73.7528 212.334L73.7306 212.187L73.5839 212.267L70.8198 210.65L68.3712 209.516V209.414L68.189 209.37L68.1801 209.472L65.2426 208.588L63.5494 207.952L63.0295 207.557L62.2563 206.468L62.1718 206.255L62.2785 206.143L62.2874 205.428L61.2164 204.237L60.212 203.682L59.581 203.637L59.2299 203.784L58.9633 203.668L57.5368 201.931L57.2568 201.784L57.0657 201.535L56.5013 201.615L56.0658 201.535L55.9191 201.58L55.8125 201.784L55.6347 201.806L54.9326 201.762L54.6304 201.535L54.6793 201.162L54.4482 201.104L54.306 201.571L54.0393 201.228L52.364 200.637L51.8351 200.14L51.3063 199.309L50.302 199.784L50.0842 200.069L49.9464 199.993Z",
            fill: "var(--main-600)"
          }
        )
      ] }),
      /* @__PURE__ */ jsx152("defs", { children: /* @__PURE__ */ jsx152("clipPath", { id: "clip0_1_8160", children: /* @__PURE__ */ jsx152(
        "rect",
        {
          width: "272.212",
          height: "321.574",
          fill: "white",
          transform: "translate(0.893555 0.212891)"
        }
      ) }) })
    ]
  }
);
var InteractiveMapImage_default = InteractiveMapImage;

// src/blocks/InteractiveMapBlock/InteractiveMapBlock.tsx
import {
  Text as Text34,
  Image as Image16,
  getFieldValue as getFieldValue27
} from "@sitecore-jss/sitecore-jss-react";
import { useTranslation as useTranslation23 } from "react-i18next";

// src/surfaces-and-overlays/Modal/Modal.tsx
import { AnimatePresence as AnimatePresence4, motion as motion9 } from "framer-motion";
import { Dialog as Dialog2 } from "@headlessui/react";
import { Fragment as Fragment21, jsx as jsx153, jsxs as jsxs82 } from "react/jsx-runtime";
var Modal = (props) => {
  const {
    isOpen,
    onClose,
    unstyled = false,
    children,
    className = "",
    title,
    ...restProps
  } = props;
  const overlayAnimation = {
    hidden: { opacity: 0, transition: { duration: 0.1, ease: "easeInOut" } },
    show: { opacity: 1, transition: { duration: 0.1, ease: "easeInOut" } },
    exit: { opacity: 0, transition: { duration: 0.1, ease: "easeInOut" } }
  };
  const modalAnimation = {
    hidden: {
      scale: 0.95,
      opacity: 0,
      transition: { duration: 0.1, ease: "easeInOut" }
    },
    show: {
      scale: 1,
      opacity: 1,
      transition: { delay: 0.1, duration: 0.2, ease: "easeInOut" }
    },
    exit: {
      scale: 1.03,
      opacity: 0,
      transition: { duration: 0.2, ease: "easeInOut" }
    }
  };
  return /* @__PURE__ */ jsx153(Fragment21, { children: /* @__PURE__ */ jsx153(AnimatePresence4, { children: isOpen && /* @__PURE__ */ jsxs82(
    Dialog2,
    {
      static: true,
      open: isOpen,
      onClose: () => onClose(),
      className: `modal ${unstyled ? "modal_unstyled" : ""}`,
      title: title || "Modal",
      children: [
        /* @__PURE__ */ jsx153(
          Dialog2.Overlay,
          {
            as: motion9.div,
            variants: overlayAnimation,
            initial: "hidden",
            animate: "show",
            exit: "exit",
            className: "modal__overlay"
          }
        ),
        /* @__PURE__ */ jsx153(
          motion9.div,
          {
            variants: modalAnimation,
            initial: "hidden",
            animate: "show",
            exit: "exit",
            className: "modal__content",
            children: unstyled ? children : /* @__PURE__ */ jsx153(Card_default, { className: `modal__card ${className}`, ...restProps, children })
          }
        )
      ]
    }
  ) }) });
};
var ModalTitle = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx153(Dialog2.Title, { className: `${className}`, ...restProps, children });
};
var ModalDescription = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx153(Dialog2.Description, { className: `${className}`, ...restProps, children });
};
Modal.Title = ModalTitle;
Modal.Description = ModalDescription;
var Modal_default = Modal;

// src/blocks/InteractiveMapBlock/InteractiveMapBlock.tsx
import { Fragment as Fragment22, jsx as jsx154, jsxs as jsxs83 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT11 = "white";
var InteractiveMapBlock = (props) => {
  var _a;
  const { fields, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx154(NullDatasourceMessage_default, {});
  const variant = getFieldValue27(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT11
  );
  const { t } = useTranslation23("ui");
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  const [isModalOpen, setIsModalOpen] = useState27(false);
  const [activeItem, setActiveItem] = useState27(null);
  const markers = fields.Items || [];
  const { isImageFieldValid } = useIsFieldValid_default();
  return /* @__PURE__ */ jsxs83(
    PageBlockWrapper_default,
    {
      variant,
      className: `interactive-map-block ${className}`,
      ...restProps,
      children: [
        /* @__PURE__ */ jsxs83("div", { className: "interactive-map-block__container", children: [
          /* @__PURE__ */ jsx154("div", { className: "interactive-map-block__text-container", children: /* @__PURE__ */ jsx154("div", { className: "container_tightest", children: /* @__PURE__ */ jsxs83("div", { className: "interactive-map-block__text", children: [
            /* @__PURE__ */ jsx154(Text34, { tag: "h2", className: "heading-2", field: fields.Title }),
            /* @__PURE__ */ jsx154(
              Text34,
              {
                tag: "p",
                className: "introtext mt-3 xl:mt-4",
                field: fields.SubTitle
              }
            ),
            /* @__PURE__ */ jsx154(
              RichText_default,
              {
                className: "rich-text mt-3 xl:mt-4",
                field: fields.RichTextSimple
              }
            )
          ] }) }) }),
          /* @__PURE__ */ jsxs83("div", { className: "interactive-map-block__markers-container", children: [
            markers && markers.map((marker, index) => {
              var _a2;
              return /* @__PURE__ */ jsx154(
                "div",
                {
                  className: "interactive-map-block__marker",
                  style: {
                    top: `${marker.fields.PositionY.value}%`,
                    left: `${marker.fields.PositionX.value}%`
                  },
                  children: /* @__PURE__ */ jsx154(
                    InteractiveMapMarker_default,
                    {
                      "aria-label": `${t("opens")} ${(_a2 = marker.fields.Title) == null ? void 0 : _a2.value}`,
                      fields: marker.fields,
                      onClick: () => {
                        setActiveItem(marker);
                        setIsModalOpen(true);
                      }
                    }
                  )
                },
                index
              );
            }),
            /* @__PURE__ */ jsx154("div", { className: "interactive-map-block__image-wrapper", children: isImageFieldValid(fields.Image) && !isEditingInSitecore ? /* @__PURE__ */ jsx154(
              Image16,
              {
                field: fields.Image,
                className: "interactive-map-block__image",
                srcSet: [{ mw: 416 }, { mw: 1280 }],
                sizes: "(max-width: 640px) 416px, 1280px"
              }
            ) : /* @__PURE__ */ jsx154(InteractiveMapImage_default, { className: "interactive-map-block__image" }) })
          ] })
        ] }),
        /* @__PURE__ */ jsxs83(
          Modal_default,
          {
            isOpen: isModalOpen,
            onClose: () => setIsModalOpen(false),
            className: "interactive-map-block__modal",
            title: (_a = activeItem == null ? void 0 : activeItem.fields.Title.value) == null ? void 0 : _a.toString(),
            children: [
              activeItem && /* @__PURE__ */ jsxs83(Fragment22, { children: [
                /* @__PURE__ */ jsx154(Modal_default.Title, { children: /* @__PURE__ */ jsx154(
                  Text34,
                  {
                    tag: "h3",
                    className: "heading-3",
                    field: activeItem.fields.Title
                  }
                ) }),
                /* @__PURE__ */ jsxs83(Modal_default.Description, { children: [
                  /* @__PURE__ */ jsx154(
                    Text34,
                    {
                      tag: "p",
                      className: "introtext mt-4",
                      field: activeItem.fields.SubTitle
                    }
                  ),
                  /* @__PURE__ */ jsx154(
                    RichText_default,
                    {
                      className: "rich-text mt-4",
                      field: activeItem.fields.RichTextSimple
                    }
                  )
                ] })
              ] }),
              /* @__PURE__ */ jsx154(
                "button",
                {
                  "aria-label": `${t("closes")} ${t("window")}`,
                  className: "interactive-map-block__modal-close-button",
                  onClick: () => setIsModalOpen(false),
                  children: /* @__PURE__ */ jsx154(TimesIcon_default, {})
                }
              )
            ]
          }
        )
      ]
    }
  );
};
var InteractiveMapBlock_default = InteractiveMapBlock;

// src/blocks/InsuranceSelectorBlock/SelectBrandSlide.tsx
import { Text as Text35 } from "@sitecore-jss/sitecore-jss-react";

// src/surfaces-and-overlays/Slide/Slide.tsx
import { AnimatePresence as AnimatePresence5, motion as motion10 } from "framer-motion";

// src/surfaces-and-overlays/Slide/useSlide.ts
import { useRef as useRef10 } from "react";
var useSlide = (slideName) => {
  const { current, push, replace, back } = useStepHistory();
  const activeSlide = useRef10(current);
  return {
    isActive: typeof slideName !== "undefined" ? activeSlide.current === slideName : void 0,
    push,
    replace,
    back
  };
};

// src/surfaces-and-overlays/Slide/Slide.tsx
import { jsx as jsx155 } from "react/jsx-runtime";
var slideMotionVariants = {
  enter: (custom) => ({
    x: custom.direction === 1 /* FORWARD */ ? "100%" : "-100%",
    transition: {
      duration: custom.duration
    }
  }),
  center: (custom) => ({
    x: 0,
    transition: {
      duration: custom.duration
    }
  }),
  exit: (custom) => ({
    x: custom.direction === 1 /* FORWARD */ ? "-100%" : "100%",
    transition: {
      duration: custom.duration
    }
  })
};
var Slide = (props) => {
  const { children, name, className = "" } = props;
  const { isActive, push, replace, back } = useSlide(name);
  if (!isActive) {
    return null;
  }
  const slideValue = {
    isActive,
    push,
    replace,
    back
  };
  return /* @__PURE__ */ jsx155("div", { className: `slide ${className}`, children: typeof children === "function" ? children(slideValue) : children });
};
var SlideTransition = (props) => {
  const { duration = 0.5, children } = props;
  const { direction = 1 /* FORWARD */ } = useStepHistory();
  return /* @__PURE__ */ jsx155(
    motion10.div,
    {
      className: "slide__transition",
      variants: slideMotionVariants,
      initial: "enter",
      animate: "center",
      exit: "exit",
      custom: { direction, duration },
      children
    }
  );
};
var SlideGroup = (props) => {
  const {
    initialStep,
    mode = "sync",
    transitionDuration = 0.5,
    className = "",
    children,
    ...restProps
  } = props;
  return /* @__PURE__ */ jsx155("div", { className: `slide-group ${className}`, ...restProps, children: /* @__PURE__ */ jsx155(StepHistoryProvider_default, { initialStep, children: ({ current }) => /* @__PURE__ */ jsx155(AnimatePresence5, { initial: false, mode, children: /* @__PURE__ */ jsx155(SlideTransition, { duration: transitionDuration, children }, current) }) }) });
};
Slide.Group = SlideGroup;
var Slide_default = Slide;

// src/blocks/InsuranceSelectorBlock/SelectBrandSlide.tsx
import { jsx as jsx156, jsxs as jsxs84 } from "react/jsx-runtime";
var SelectBrandSlide = (props) => {
  const {
    title,
    brands,
    backButtonTitle,
    onGoBack,
    children,
    className = "",
    ...restProps
  } = props;
  return /* @__PURE__ */ jsxs84(
    Slide,
    {
      name: "select-brand",
      className: `insurance-selector-block__slide ${className}`,
      ...restProps,
      children: [
        /* @__PURE__ */ jsxs84(
          "button",
          {
            className: "insurance-selector-block__back-button",
            onClick: () => {
              onGoBack();
            },
            children: [
              /* @__PURE__ */ jsx156(ChevronLeftIcon_default, { className: "insurance-selector-block__back-button-icon" }),
              /* @__PURE__ */ jsx156(Text35, { tag: "span", field: backButtonTitle })
            ]
          }
        ),
        /* @__PURE__ */ jsx156(Text35, { tag: "h2", className: "insurance-selector-block__slide-title heading-2", field: title }),
        brands.map((brand, index) => /* @__PURE__ */ jsx156(InsuranceSelectorItem_default, { item: brand }, index))
      ]
    }
  );
};
var SelectBrandSlide_default = SelectBrandSlide;

// src/blocks/InsuranceSelectorBlock/SelectCategorySlide.tsx
import { Text as Text36 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx157, jsxs as jsxs85 } from "react/jsx-runtime";
var SelectCategorySlide = (props) => {
  const {
    title,
    categories,
    onCategorySelect,
    className = "",
    children,
    ...restProps
  } = props;
  return /* @__PURE__ */ jsxs85(
    Slide,
    {
      name: "select-category",
      className: `insurance-selector-block__slide ${className}`,
      ...restProps,
      children: [
        /* @__PURE__ */ jsx157(Text36, { tag: "h2", className: "insurance-selector-block__slide-title heading-2", field: title }),
        categories.map((category, index) => /* @__PURE__ */ jsx157(
          InsuranceSelectorItem_default,
          {
            item: category,
            onClick: () => {
              onCategorySelect(category);
            }
          },
          index
        ))
      ]
    }
  );
};
var SelectCategorySlide_default = SelectCategorySlide;

// src/blocks/InsuranceSelectorBlock/SelectProductSlide.tsx
import { Text as Text37 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx158, jsxs as jsxs86 } from "react/jsx-runtime";
var SelectProductSlide = (props) => {
  const {
    title,
    products,
    onProductSelect,
    backButtonTitle,
    onGoBack,
    children,
    className = "",
    ...restProps
  } = props;
  return /* @__PURE__ */ jsxs86(
    Slide,
    {
      name: "select-product",
      className: `insurance-selector-block__slide ${className}`,
      ...restProps,
      children: [
        /* @__PURE__ */ jsxs86(
          "button",
          {
            className: "insurance-selector-block__back-button",
            onClick: () => {
              onGoBack();
            },
            children: [
              /* @__PURE__ */ jsx158(ChevronLeftIcon_default, { className: "insurance-selector-block__back-button-icon" }),
              /* @__PURE__ */ jsx158(Text37, { tag: "span", field: backButtonTitle })
            ]
          }
        ),
        /* @__PURE__ */ jsx158(Text37, { tag: "h2", className: "insurance-selector-block__slide-title heading-2", field: title }),
        products.map((product, index) => /* @__PURE__ */ jsx158(
          InsuranceSelectorItem_default,
          {
            item: product,
            onClick: () => {
              onProductSelect(product);
            }
          },
          index
        ))
      ]
    }
  );
};
var SelectProductSlide_default = SelectProductSlide;

// src/blocks/InsuranceSelectorBlock/InsuranceSelectorBlock.tsx
import { useSearchParams } from "react-router-dom";

// src/blocks/InsuranceSelectorBlock/InsuranceSelectorNavigation.tsx
import { useEffect as useEffect17 } from "react";
var InsuranceSelectorNavigation = (props) => {
  const { currentCategory, currentProduct } = props;
  const { push, back } = useSlide();
  const { current } = useStepHistory();
  useEffect17(() => {
    if (current === "select-category" && currentCategory) {
      push("select-product");
      return;
    }
    if (current === "select-product" && !currentCategory) {
      back();
      return;
    }
    if (current === "select-product" && currentCategory && currentProduct) {
      push("select-brand");
      return;
    }
    if (current === "select-brand" && currentCategory && !currentProduct) {
      back();
      return;
    }
  }, [currentCategory, currentProduct]);
  return null;
};
var InsuranceSelectorNavigation_default = InsuranceSelectorNavigation;

// src/blocks/InsuranceSelectorBlock/InsuranceSelectorBlock.tsx
import { getFieldValue as getFieldValue28 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx159, jsxs as jsxs87 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT12 = "white";
var DEFAULT_SHOW_DECORATIONS22 = true;
var InsuranceSelectorBlock = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx159(NullDatasourceMessage_default, {});
  const variant = getFieldValue28(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT12
  );
  const showDecorations = getFieldValue28(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS22
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const currentCategory = searchParams.get("category");
  const currentProduct = searchParams.get("product");
  const categories = fields.Categories.map(
    (category) => category.hasOwnProperty("fields") ? category.fields : category
  ) || [];
  const getProducts = () => {
    var _a;
    return currentCategory && ((_a = categories.find((category) => category.Title.value === currentCategory)) == null ? void 0 : _a.Products) || [];
  };
  const products = getProducts().map(
    (product) => product.hasOwnProperty("fields") ? product.fields : product
  ) || [];
  const getBrands = () => {
    var _a;
    return currentProduct && ((_a = products.find((product) => product.Title.value === currentProduct)) == null ? void 0 : _a.Brands) || [];
  };
  const brands = getBrands().map(
    (brand) => brand.hasOwnProperty("fields") ? brand.fields : brand
  ) || [];
  return /* @__PURE__ */ jsx159(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `insurance-selector-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsx159("div", { className: "insurance-selector-block__container", children: /* @__PURE__ */ jsxs87(
        Slide.Group,
        {
          initialStep: "select-category",
          mode: "wait",
          transitionDuration: 0.2,
          children: [
            /* @__PURE__ */ jsx159(
              InsuranceSelectorNavigation_default,
              {
                currentCategory,
                currentProduct
              }
            ),
            /* @__PURE__ */ jsx159(
              SelectCategorySlide_default,
              {
                title: fields.SelectCategoryTitle,
                categories,
                onCategorySelect: (category) => {
                  setSearchParams(`category=${category.Title.value}`);
                }
              }
            ),
            /* @__PURE__ */ jsx159(
              SelectProductSlide_default,
              {
                title: fields.SelectProductTitle,
                products,
                onProductSelect: (product) => {
                  setSearchParams(
                    `category=${currentCategory}&product=${product.Title.value}`
                  );
                },
                backButtonTitle: fields.BackToSelectCategoryButtonTitle,
                onGoBack: () => setSearchParams("")
              }
            ),
            /* @__PURE__ */ jsx159(
              SelectBrandSlide_default,
              {
                title: fields.SelectBrandTitle,
                brands,
                backButtonTitle: fields.BackToSelectProductButtonTitle,
                onGoBack: () => setSearchParams(`category=${currentCategory}`)
              }
            )
          ]
        }
      ) })
    }
  );
};
var InsuranceSelectorBlock_default = InsuranceSelectorBlock;

// src/blocks/LargeCardSliderBlock/LargeCardSliderBlock.tsx
import React38 from "react";
import {
  Placeholder as Placeholder16,
  Text as Text38,
  getFieldValue as getFieldValue29
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx160, jsxs as jsxs88 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT13 = "white";
var DEFAULT_SHOW_DECORATIONS23 = true;
var LargeCardSliderBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx160(NullDatasourceMessage_default, {});
  const variant = getFieldValue29(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT13
  );
  const showDecorations = getFieldValue29(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS23
  );
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  const { isTextFieldValid, isRichTextFieldValid } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  return /* @__PURE__ */ jsxs88(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `large-card-slider-block ${className}`,
      ...restProps,
      children: [
        showText && /* @__PURE__ */ jsxs88("div", { className: "container_tight", children: [
          /* @__PURE__ */ jsx160(
            Text38,
            {
              tag: "h2",
              field: fields.Title,
              className: "heading-2 mb-3 large-card-slider-block__title"
            }
          ),
          /* @__PURE__ */ jsx160(
            RichText_default,
            {
              className: "mb-8 md:mb-10 xl:mb-14",
              field: fields.RichTextSimple
            }
          )
        ] }),
        /* @__PURE__ */ jsx160("div", { className: "container_normal", children: /* @__PURE__ */ jsx160(
          Placeholder16,
          {
            name: "container-cards",
            rendering,
            render: (components) => isEditingInSitecore ? /* @__PURE__ */ jsx160("div", { className: "large-card-slider-block__sitecore-ee-wrapper", children: components.map((component, index) => /* @__PURE__ */ jsx160(React38.Fragment, { children: component }, index)) }) : /* @__PURE__ */ jsx160(Reel_default, { className: "large-card-slider-block__reel", children: components.map((component, index) => /* @__PURE__ */ jsx160("div", { className: "large-card-slider-block__card", children: component }, index)) })
          }
        ) })
      ]
    }
  );
};
var LargeCardSliderBlock_default = LargeCardSliderBlock;

// src/blocks/LeadParagraphBlock/LeadParagraphBlock.tsx
import { Text as Text39, getFieldValue as getFieldValue30 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx161, jsxs as jsxs89 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT14 = "white";
var LeadParagraphBlock = (props) => {
  var _a;
  const { fields, rendering, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx161(NullDatasourceMessage_default, {});
  const variant = getFieldValue30(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT14
  );
  return /* @__PURE__ */ jsx161(
    PageBlockWrapper_default,
    {
      variant,
      className: `lead-paragraph-block ${((_a = fields.CenterContent) == null ? void 0 : _a.value) ? "lead-paragraph-block_center" : ""}  ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs89("div", { className: "lead-paragraph-block__container container_tightest", children: [
        /* @__PURE__ */ jsx161(Text39, { tag: "h2", className: "rich-text__title", field: fields.Title }),
        /* @__PURE__ */ jsx161(
          Text39,
          {
            tag: "p",
            className: "rich-text__intro mt-3 sm:mt-4",
            field: fields.SubTitle
          }
        ),
        /* @__PURE__ */ jsx161(RichText_default, { className: "rich-text mt-4", field: fields.Body }),
        /* @__PURE__ */ jsx161(ButtonContainer_default, { className: "mt-8", rendering })
      ] })
    }
  );
};
var LeadParagraphBlock_default = LeadParagraphBlock;

// src/blocks/LinkListBlock/LinkList.tsx
import { jsx as jsx162 } from "react/jsx-runtime";
var LinkList = (props) => {
  const { className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsx162("div", { className: `link-list ${className}`, ...restProps, children });
};
var LinkList_default = LinkList;

// src/blocks/LinkListBlock/LinkListItem.tsx
import { Image as Image17, Text as Text40 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx163, jsxs as jsxs90 } from "react/jsx-runtime";
var LinkListItem = (props) => {
  const { fields, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx163(NullDatasourceMessage_default, {});
  const { isLinkFieldValid, isDateFieldValid, isDateFieldSet } = useIsFieldValid_default();
  return isLinkFieldValid(fields.LinkTarget) ? /* @__PURE__ */ jsx163(
    RoutableSitecoreLink_default,
    {
      className: `link-list__item ${className}`,
      ...restProps,
      field: fields.LinkTarget,
      editable: false,
      children: /* @__PURE__ */ jsx163(
        MediaObject_default,
        {
          className: "link-list__item-media-object",
          media: /* @__PURE__ */ jsx163("div", { className: "link-list__item-media-wrapper", children: /* @__PURE__ */ jsx163(
            Image17,
            {
              className: "link-list__item-media",
              field: fields.Image,
              srcSet: [{ mw: 192 }],
              sizes: "192px"
            }
          ) }),
          children: /* @__PURE__ */ jsxs90("div", { className: "link-list__item-body", children: [
            /* @__PURE__ */ jsxs90("div", { className: "link-list__text", children: [
              isDateFieldValid(fields.Date) && isDateFieldSet(fields.Date) && /* @__PURE__ */ jsx163(
                Date_default,
                {
                  className: "link-list__item-date",
                  field: fields.Date
                }
              ),
              /* @__PURE__ */ jsx163(
                Text40,
                {
                  tag: "h3",
                  className: "link-list__item-title",
                  field: fields.LinkText
                }
              ),
              /* @__PURE__ */ jsx163(
                Text40,
                {
                  tag: "p",
                  className: "link-list__item-subtitle",
                  field: fields.Subtitle
                }
              )
            ] }),
            /* @__PURE__ */ jsx163(ArrowRightIcon_default, { className: "link-list__item-icon" })
          ] })
        }
      )
    }
  ) : null;
};
var LinkListItem_default = LinkListItem;

// src/blocks/LinkListBlock/LinkListBlock.tsx
import {
  Placeholder as Placeholder17,
  RichText as RichText2,
  Text as Text41,
  getFieldValue as getFieldValue31
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx164, jsxs as jsxs91 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT15 = "white";
var DEFAULT_SHOW_DECORATIONS24 = true;
var LinkListBlock = (props) => {
  const { rendering, fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx164(NullDatasourceMessage_default, {});
  const variant = getFieldValue31(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT15
  );
  const showDecorations = getFieldValue31(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS24
  );
  return /* @__PURE__ */ jsx164(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `link-list-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs91("div", { className: "container_tightest", children: [
        /* @__PURE__ */ jsx164(Text41, { className: "link-list-block__title heading-2", tag: "h2", field: fields.Title }),
        /* @__PURE__ */ jsx164(RichText2, { className: "link-list-block__description", field: fields.RichTextSimple }),
        /* @__PURE__ */ jsx164(LinkList_default, { children: /* @__PURE__ */ jsx164(Placeholder17, { name: "container-link-list", rendering }) })
      ] })
    }
  );
};
var LinkListBlock_default = LinkListBlock;

// src/blocks/MultiImageBlock/MultiImageBlock.tsx
import { Image as Image18, Text as Text42, getFieldValue as getFieldValue32 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx165, jsxs as jsxs92 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT16 = "white";
var DEFAULT_SHOW_DECORATIONS25 = true;
var getImageAttributes2 = (imageVariant) => {
  switch (imageVariant) {
    case "full-width":
      return {
        srcSet: [{ mw: 416 }, { mw: 1280 }, { mw: 1600 }],
        sizes: "(max-width: 640px) 416px, (max-width: 1280px) 1280px, 1600"
      };
    case "default":
    default:
      return {
        srcSet: [{ mw: 416 }, { mw: 1280 }],
        sizes: "(max-width: 640px) 416px, 1280px"
      };
  }
};
var MultiImageBlock = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx165(NullDatasourceMessage_default, {});
  const variant = getFieldValue32(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT16
  );
  const showDecorations = getFieldValue32(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS25
  );
  const { isTextFieldValid, isRichTextFieldValid, isImageFieldValid } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  const images = [];
  if (isImageFieldValid(fields.Image1)) {
    images.push({
      image: fields.Image1,
      text: fields.Image1Text
    });
  }
  if (isImageFieldValid(fields.Image2)) {
    images.push({
      image: fields.Image2,
      text: fields.Image2Text
    });
  }
  if (isImageFieldValid(fields.Image3)) {
    images.push({
      image: fields.Image3,
      text: fields.Image3Text
    });
  }
  return /* @__PURE__ */ jsxs92(
    MediaBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `multi-image-block ${className}`,
      ...restProps,
      children: [
        showText && /* @__PURE__ */ jsxs92("div", { className: "container_tightest", children: [
          /* @__PURE__ */ jsx165(
            Text42,
            {
              tag: "h2",
              field: fields.Title,
              className: "multi-image-block__title heading-2"
            }
          ),
          /* @__PURE__ */ jsx165(
            RichText_default,
            {
              field: fields.RichTextSimple,
              className: "multi-image-block__subtitle"
            }
          )
        ] }),
        images.length && /* @__PURE__ */ jsx165("div", { className: "container_normal", children: /* @__PURE__ */ jsx165("div", { className: "multi-image-block__grid", children: images.map((image, index) => {
          var _a;
          const imageObj = ((_a = image.image) == null ? void 0 : _a.value) || image.image;
          return /* @__PURE__ */ jsxs92(
            "div",
            {
              className: `multi-image-block__grid-item ${!isTextFieldValid(image.text) ? "mb-6" : ""}`,
              style: {
                "--imageRatio": getAspectRatio_default(parseInt(`${(imageObj == null ? void 0 : imageObj.width) || 1}`), parseInt(`${(imageObj == null ? void 0 : imageObj.height) || 1}`))
              },
              children: [
                /* @__PURE__ */ jsx165("div", { className: "multi-image-block__image-wrapper", children: /* @__PURE__ */ jsx165(
                  Image18,
                  {
                    field: image.image,
                    className: "multi-image-block__image",
                    ...getImageAttributes2("default")
                  }
                ) }),
                isTextFieldValid(image.text) && /* @__PURE__ */ jsx165("div", { className: "container_tightest multi-image-block__text-wrapper", children: /* @__PURE__ */ jsx165(RichText_default, { field: image.text }) })
              ]
            },
            index
          );
        }) }) })
      ]
    }
  );
};
var MultiImageBlock_default = MultiImageBlock;

// src/blocks/ParagraphBlock/ParagraphBlock.tsx
import { Text as Text43, getFieldValue as getFieldValue33 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx166, jsxs as jsxs93 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT17 = "white";
var ParagraphBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx166(NullDatasourceMessage_default, {});
  const variant = getFieldValue33(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT17
  );
  return /* @__PURE__ */ jsx166(
    PageBlockWrapper_default,
    {
      variant,
      className: `paragraph-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs93("div", { className: "container_tightest", children: [
        /* @__PURE__ */ jsx166(
          Text43,
          {
            tag: "h2",
            className: "rich-text__title mb-3 sm:mb-5",
            field: fields.Title
          }
        ),
        /* @__PURE__ */ jsx166(
          Text43,
          {
            tag: "p",
            className: "rich-text__intro mb-8 sm:mb-10 xl:mb-12",
            field: fields.SubTitle
          }
        ),
        /* @__PURE__ */ jsx166(RichText_default, { className: "rich-text mb-8", field: fields.Body }),
        /* @__PURE__ */ jsx166(ButtonContainer_default, { rendering })
      ] })
    }
  );
};
var ParagraphBlock_default = ParagraphBlock;

// src/blocks/ParagraphWithImageBlock/ParagraphWithImageBlock.tsx
import { Image as Image19, Text as Text44, getFieldValue as getFieldValue34 } from "@sitecore-jss/sitecore-jss-react";
import { Fragment as Fragment23, jsx as jsx167, jsxs as jsxs94 } from "react/jsx-runtime";
var DEFAULT_IMAGE_LOCATION = "default";
var DEFAULT_IMAGE_ALIGNMENT3 = "right";
var DEFAULT_BLOCK_VARIANT18 = "white";
var ParagraphWithImageBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx167(NullDatasourceMessage_default, {});
  const variant = getFieldValue34(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT18
  );
  const imageLocation = getFieldValue34(
    fields,
    "ImageLocation",
    DEFAULT_IMAGE_LOCATION
  );
  const imageAlignment = getFieldValue34(
    fields,
    "ImageAlignment",
    DEFAULT_IMAGE_ALIGNMENT3
  );
  const { isTextFieldValid, isImageFieldValid } = useIsFieldValid_default();
  const showIntroText = isTextFieldValid(fields.Title) || isTextFieldValid(fields.SubTitle);
  const wrapperClassName = `paragraph-with-image-block ${imageLocation === "default" ? "paragraph-with-image-block_location-default" : "paragraph-with-image-block_location-inline"} ${imageAlignment === "left" ? "paragraph-with-image-block_image-left" : "paragraph-with-image-block_image-right"} ${className}`;
  const content = imageLocation === "default" ? /* @__PURE__ */ jsxs94("div", { className: "paragraph-with-image-block__container-default", children: [
    isImageFieldValid(fields.Image) && /* @__PURE__ */ jsx167("div", { className: "paragraph-with-image-block__image-wrapper", children: /* @__PURE__ */ jsx167(
      Image19,
      {
        field: fields.Image,
        className: "paragraph-with-image-block__image",
        srcSet: [{ mw: 1280 }, { mw: 640 }],
        sizes: "(max-width: 1280px) 1280px, 640px"
      }
    ) }),
    /* @__PURE__ */ jsxs94("div", { className: "paragraph-with-image-block__text", children: [
      showIntroText && /* @__PURE__ */ jsxs94("div", { className: "paragraph-with-image-block__text-intro", children: [
        /* @__PURE__ */ jsx167(
          Text44,
          {
            tag: "h2",
            className: "rich-text__title",
            field: fields.Title
          }
        ),
        /* @__PURE__ */ jsx167(
          Text44,
          {
            tag: "p",
            className: "rich-text__intro",
            field: fields.SubTitle
          }
        )
      ] }),
      /* @__PURE__ */ jsx167(RichText_default, { className: "rich-text", field: fields.Body }),
      /* @__PURE__ */ jsx167(ButtonContainer_default, { rendering })
    ] })
  ] }) : /* @__PURE__ */ jsxs94("div", { className: "paragraph-with-image-block__container-inline container_tightest mt-7 sm:mt-10 md:mt-12 mb-7 sm:mb-10 md:mb-12", children: [
    isImageFieldValid(fields.Image) && /* @__PURE__ */ jsx167("div", { className: "paragraph-with-image-block__image-wrapper sm:mb-8", children: /* @__PURE__ */ jsx167(
      Image19,
      {
        field: fields.Image,
        className: "paragraph-with-image-block__image",
        srcSet: [{ mw: 1280 }, { mw: 640 }],
        sizes: "(max-width: 1280px) 1280px, 640px"
      }
    ) }),
    showIntroText && /* @__PURE__ */ jsxs94(Fragment23, { children: [
      /* @__PURE__ */ jsx167(
        Text44,
        {
          tag: "h2",
          className: "rich-text__title",
          field: fields.Title
        }
      ),
      /* @__PURE__ */ jsx167(
        Text44,
        {
          tag: "p",
          className: "rich-text__intro",
          field: fields.SubTitle
        }
      )
    ] }),
    /* @__PURE__ */ jsx167(RichText_default, { className: "rich-text", field: fields.Body }),
    /* @__PURE__ */ jsx167(ButtonContainer_default, { rendering })
  ] });
  return /* @__PURE__ */ jsx167(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations: false,
      className: wrapperClassName,
      ...restProps,
      children: content
    }
  );
};
var ParagraphWithImageBlock_default = ParagraphWithImageBlock;

// src/blocks/ProjectOverviewBlock/ProjectOverviewBlock.tsx
import { useState as useState29, useEffect as useEffect19 } from "react";
import {
  buildContext,
  buildResultList as buildResultList2,
  buildQueryExpression as buildQueryExpression2,
  buildStaticFilterValue as buildStaticFilterValue2,
  buildSort,
  buildFieldSortCriterion,
  SortOrder
} from "@coveo/headless";
import {
  useSitecoreContext as useSitecoreContext23,
  getFieldValue as getFieldValue35
} from "@sitecore-jss/sitecore-jss-react";

// src/util/coveo/getCoveoToken.ts
var COVEO_TOKEN_API = "/coveo/rest/token/";
var getCoveoToken = () => {
  return fetch(COVEO_TOKEN_API).then((response) => {
    if (!response.ok) {
      return response.text().then((err) => Promise.reject(new Error(err)));
    }
    return response.json();
  }).then((jsonResponse) => {
    return jsonResponse.token;
  });
};
var getCoveoToken_default = getCoveoToken;

// src/util/coveo/getCoveoEngine.ts
import { buildSearchEngine, getOrganizationEndpoints } from "@coveo/headless";
var headlessEngine = (props) => getCoveoToken_default().then(
  (token) => {
    const endpoints = getOrganizationEndpoints(props.coveoOrganizationId);
    return buildSearchEngine({
      configuration: {
        organizationId: props.coveoOrganizationId,
        organizationEndpoints: {
          ...endpoints,
          search: "/coveo/rest/search"
        },
        analytics: {
          anonymous: true,
          enabled: true
        },
        accessToken: token,
        renewAccessToken: getCoveoToken_default,
        search: {
          locale: props.language,
          pipeline: props.queryPipeline
        },
        preprocessRequest: (request, clientOrigin, metadata) => {
          var _a;
          if ((metadata == null ? void 0 : metadata.method) === "search" && clientOrigin === "searchApiFetch") {
            const requestUrl = `${request.url}&sitecoreItemUri=${props.itemId}&siteName=${props.siteName}`;
            request.url = requestUrl;
            const body = JSON.parse(((_a = request.body) == null ? void 0 : _a.toString()) || "");
            body.aq = `(@z95xlanguage==${props.language}) (@source=="Coveo_web_index - ${props.datasourceName}")`;
            request.body = JSON.stringify(body);
          }
          return request;
        }
      }
    });
  }
);
var getCoveoEngine_default = headlessEngine;

// src/util/coveo/bindCoveoUrlManager.ts
import {
  buildUrlManager,
  buildSearchStatus
} from "@coveo/headless";
var bindCoveoUrlManager = (engine) => {
  const statusControllers = buildSearchStatus(engine);
  const fragment = () => window.location.hash.slice(1);
  const urlManager = buildUrlManager(engine, {
    initialState: { fragment: fragment() }
  });
  const onHashChange = () => {
    urlManager.synchronize(fragment());
  };
  window.addEventListener("hashchange", onHashChange);
  const unsubscribeManager = urlManager.subscribe(() => {
    const hash = `#${urlManager.state.fragment}`;
    if (!statusControllers.state.firstSearchExecuted) {
      history.replaceState(null, document.title, hash);
      return;
    }
    history.pushState(null, document.title, hash);
  });
  return () => {
    window.removeEventListener("hashchange", onHashChange);
    unsubscribeManager();
  };
};
var bindCoveoUrlManager_default = bindCoveoUrlManager;

// src/util/coveo/context/engine.ts
import { createContext as createContext8 } from "react";
var CoveoContext = createContext8({});

// src/blocks/ProjectOverviewBlock/ProjectOverviewResultsItem.tsx
import {
  Image as Image20,
  useSitecoreContext as useSitecoreContext22
} from "@sitecore-jss/sitecore-jss-react";
import { parse, format as format2 } from "date-fns";
import { jsx as jsx168, jsxs as jsxs95 } from "react/jsx-runtime";
var DEFAULT_SHOW_DATE_TIME2 = true;
var ProjectOverviewResultsItem = (props) => {
  const { engine, item, className = "" } = props;
  const { isImageFieldValid, isDateFieldValid, isDateFieldSet } = useIsFieldValid_default();
  const itemImage = item.raw.previewimage;
  const imageField = {
    value: {
      src: itemImage
    }
  };
  const itemDate = item.raw.publicationdate;
  const parsedDate = format2(parse(itemDate, "yyyy/MM/dd@HH:mm:ss'Z'", /* @__PURE__ */ new Date()), "yyyy-MM-dd");
  const dateField = {
    value: parsedDate
  };
  const { sitecoreContext } = useSitecoreContext22();
  const projectPageProps = sitecoreContext.projectOverviewPageProps;
  const { showDateTime = DEFAULT_SHOW_DATE_TIME2 } = projectPageProps;
  const showDate = showDateTime && isDateFieldValid(dateField) && isDateFieldSet(dateField);
  return /* @__PURE__ */ jsx168("div", { className: `project-overview-results__list-item ${className}`, children: /* @__PURE__ */ jsxs95(SearchResultsLink_default, { engine, item, className: "project-overview-results__list-item-link", children: [
    /* @__PURE__ */ jsx168("div", { className: "project-overview-results__list-item-image-wrapper", children: isImageFieldValid(imageField) && /* @__PURE__ */ jsx168(
      Image20,
      {
        editable: true,
        className: "project-overview-results__list-item-image",
        field: imageField
      }
    ) }),
    /* @__PURE__ */ jsxs95("div", { className: "project-overview-results__list-item-text", children: [
      /* @__PURE__ */ jsxs95("div", { children: [
        /* @__PURE__ */ jsx168("div", { children: item.title }),
        showDate && /* @__PURE__ */ jsx168(
          Date_default,
          {
            field: dateField,
            className: "project-overview-results__list-item-date"
          }
        )
      ] }),
      /* @__PURE__ */ jsx168(ArrowRightIcon_default, {})
    ] })
  ] }) });
};
var ProjectOverviewResultsItem_default = ProjectOverviewResultsItem;

// src/blocks/ProjectOverviewBlock/ProjectOverviewResults.tsx
import { useTranslation as useTranslation24 } from "react-i18next";
import { jsx as jsx169, jsxs as jsxs96 } from "react/jsx-runtime";
var ProjectOverviewResults = (props) => {
  var _a;
  const { engine, resultList, resultListState, buttonText, className = "" } = props;
  const { t } = useTranslation24("ui");
  return /* @__PURE__ */ jsxs96("div", { className: `project-overview-results ${className}`, children: [
    /* @__PURE__ */ jsx169(ResultsPerPage_default, { engine, perPage: 12, resultListState }),
    /* @__PURE__ */ jsx169("div", { className: "project-overview-results__list", children: (resultListState == null ? void 0 : resultListState.results) && ((_a = resultListState.results) == null ? void 0 : _a.length) ? resultListState.results.map((item, index) => /* @__PURE__ */ jsx169(
      ProjectOverviewResultsItem_default,
      {
        engine,
        item
      },
      index
    )) : (resultListState == null ? void 0 : resultListState.isLoading) ? /* @__PURE__ */ jsx169(LoadingIcon_default, {}) : t("nothing-found") }),
    (resultListState == null ? void 0 : resultListState.moreResultsAvailable) && /* @__PURE__ */ jsx169("div", { className: "project-overview-results__load-more", children: /* @__PURE__ */ jsxs96(
      "button",
      {
        className: "btn btn_primary",
        type: "submit",
        "aria-label": buttonText,
        disabled: resultListState == null ? void 0 : resultListState.isLoading,
        onClick: (e) => {
          e.preventDefault();
          resultList == null ? void 0 : resultList.fetchMoreResults();
        },
        children: [
          buttonText,
          /* @__PURE__ */ jsx169(ArrowDownIcon_default, {})
        ]
      }
    ) })
  ] });
};
var ProjectOverviewResults_default = ProjectOverviewResults;

// src/blocks/ProjectOverviewBlock/ProjectOverviewBlock.tsx
import { useTranslation as useTranslation26 } from "react-i18next";

// src/blocks/ProjectOverviewBlock/ProjectOverviewFilters.tsx
import { Text as Text45 } from "@sitecore-jss/sitecore-jss-react";

// src/search/StaticFilter/StaticFilterSelect.tsx
import { useState as useState28, useEffect as useEffect18 } from "react";
import {
  buildStaticFilter as buildStaticFilter2
} from "@coveo/headless";
import { useTranslation as useTranslation25 } from "react-i18next";
import { jsx as jsx170 } from "react/jsx-runtime";
var StaticFilter2 = (props) => {
  var _a;
  const { engine, filterOptions, label, className = "", paginationController } = props;
  const { t } = useTranslation25("ui");
  const [state, setState] = useState28();
  const [staticFilter, setStaticFilter] = useState28();
  useEffect18(() => {
    const filter = buildStaticFilter2(engine, {
      options: filterOptions
    });
    setStaticFilter(filter);
  }, []);
  useEffect18(() => {
    staticFilter == null ? void 0 : staticFilter.subscribe(() => setState(staticFilter.state));
  }, [staticFilter]);
  const options = [
    {
      id: "-1",
      name: t("none")
    },
    ...(_a = filterOptions.values) == null ? void 0 : _a.map((option) => ({
      id: option.expression,
      name: option.caption
    }))
  ];
  return /* @__PURE__ */ jsx170("div", { className: `static-filter ${className}`, children: /* @__PURE__ */ jsx170(
    Select_default,
    {
      placeholder: label,
      options,
      onChange: (e) => {
        const selectedOption = filterOptions.values.find((option) => option.expression === e.target.value);
        if (!!selectedOption) {
          paginationController == null ? void 0 : paginationController.selectPage(1);
          staticFilter == null ? void 0 : staticFilter.toggleSingleSelect(selectedOption);
        } else {
          staticFilter == null ? void 0 : staticFilter.deselectAll();
        }
      }
    }
  ) });
};
var StaticFilterSelect_default = StaticFilter2;

// src/blocks/ProjectOverviewBlock/ProjectOverviewFilters.tsx
import { jsx as jsx171, jsxs as jsxs97 } from "react/jsx-runtime";
var ProjectOverviewFilters = (props) => {
  const { engine, filters, title } = props;
  if (!engine || !filters || !filters.length)
    return null;
  return /* @__PURE__ */ jsxs97("div", { className: "container_tight mt-6 sm:mt-10 xl:mt-16", children: [
    /* @__PURE__ */ jsx171(
      Text45,
      {
        tag: "h2",
        field: {
          value: title
        },
        className: "project-overview-filters__heading heading-2"
      }
    ),
    /* @__PURE__ */ jsx171("div", { className: "project-overview-filters", children: filters.map(
      (filter) => /* @__PURE__ */ jsx171(
        StaticFilterSelect_default,
        {
          engine,
          filterOptions: filter.options,
          label: filter.label,
          className: "project-overview-results__filter"
        },
        filter.options.id
      )
    ) })
  ] });
};
var ProjectOverviewFilters_default = ProjectOverviewFilters;

// src/blocks/ProjectOverviewBlock/ProjectOverviewBlock.tsx
import { Fragment as Fragment24, jsx as jsx172, jsxs as jsxs98 } from "react/jsx-runtime";
var DEFAULT_SHOW_TAGS2 = true;
var DEFAULT_SHOW_STATUS2 = true;
var HeroWithQuickLinksBlock2 = (props) => {
  var _a;
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx172(NullDatasourceMessage_default, {});
  const [engine, setEngine] = useState29();
  const [context, setContext] = useState29();
  const [resultList, setResultList] = useState29();
  const [state, setState] = useState29();
  const { t } = useTranslation26("ui");
  const { sitecoreContext } = useSitecoreContext23();
  const coveoOrgId = sitecoreContext == null ? void 0 : sitecoreContext.coveoOrganizationId;
  const language = sitecoreContext == null ? void 0 : sitecoreContext.language;
  const itemId = sitecoreContext == null ? void 0 : sitecoreContext.itemId;
  const siteName = (_a = sitecoreContext == null ? void 0 : sitecoreContext.site) == null ? void 0 : _a.name;
  const datasourceName = sitecoreContext == null ? void 0 : sitecoreContext.coveoDatasourceName;
  const projectOverviewPageProps = sitecoreContext == null ? void 0 : sitecoreContext.projectOverviewPageProps;
  const statusesTitleField = getFieldValue35(
    fields,
    "StatusesTitle",
    t("status")
  );
  const statusesTitle = statusesTitleField || t("status");
  const tagsTitleField = getFieldValue35(
    fields,
    "TagsTitle",
    t("tags")
  );
  const tagsTitle = tagsTitleField || t("tags");
  const componentTitleField = getFieldValue35(
    fields,
    "Title",
    t("select-projects")
  );
  const componentTitle = componentTitleField || t("select-projects");
  const loadMoreButtonTitleField = getFieldValue35(
    fields,
    "ButtonTitle",
    t("load-more")
  );
  const loadMoreButtonTitle = loadMoreButtonTitleField || t("load-more");
  const { showTags = DEFAULT_SHOW_TAGS2, showStatus = DEFAULT_SHOW_STATUS2 } = projectOverviewPageProps;
  useEffect19(() => {
    var _a2;
    if (coveoOrgId && language) {
      getCoveoEngine_default({
        coveoOrganizationId: coveoOrgId,
        language,
        itemId,
        siteName,
        datasourceName,
        queryPipeline: ((_a2 = fields.QueryPipeline.value) == null ? void 0 : _a2.toString()) || ""
      }).then((coveoEngine) => {
        const ctx = buildContext(coveoEngine);
        ctx.set({ language });
        setContext(ctx);
        setEngine(coveoEngine);
        return coveoEngine;
      }).then((coveoEngine) => {
        bindCoveoUrlManager_default(coveoEngine);
        return coveoEngine;
      }).then((coveoEngine) => {
        buildSort(coveoEngine, {
          initialState: {
            criterion: buildFieldSortCriterion("publicationdate", SortOrder.Descending)
          }
        });
        return coveoEngine;
      }).then((coveoEngine) => {
        const options = {
          fieldsToInclude: ["defaulturi", "z95xlanguage", "z95xname", "tags", "status", "previewimage", "publicationdate"]
        };
        const list = buildResultList2(coveoEngine, { options });
        setResultList(list);
      });
    }
  }, []);
  useEffect19(() => {
    resultList == null ? void 0 : resultList.subscribe(() => setState(resultList.state));
  }, [resultList]);
  const filters = [];
  if (showStatus && fields.Statuses && fields.Statuses.length) {
    const statusExpressions = [];
    fields.Statuses.map((status) => {
      var _a2, _b;
      const expression = buildQueryExpression2().addStringField({
        field: "status",
        operator: "isExactly",
        values: [((_a2 = status.Title.value) == null ? void 0 : _a2.toString()) || ""]
      }).toQuerySyntax();
      const isExpression = buildStaticFilterValue2({
        caption: ((_b = status.Title.value) == null ? void 0 : _b.toString()) || "",
        expression
      });
      statusExpressions.push(isExpression);
    });
    const statusFilterOptions = {
      id: "status",
      values: statusExpressions
    };
    if (showStatus) {
      filters.push({
        label: statusesTitle,
        options: statusFilterOptions
      });
    }
  }
  if (fields.Tags && fields.Tags.length) {
    const tagExpressions = [];
    fields.Tags.map((tag) => {
      var _a2, _b;
      const expression = buildQueryExpression2().addStringField({
        field: "tags",
        operator: "contains",
        values: [((_a2 = tag.Title.value) == null ? void 0 : _a2.toString()) || ""]
      }).toQuerySyntax();
      const isExpression = buildStaticFilterValue2({
        caption: ((_b = tag.Title.value) == null ? void 0 : _b.toString()) || "",
        expression
      });
      tagExpressions.push(isExpression);
    });
    const tagFilterOptions = {
      id: "tags",
      values: tagExpressions
    };
    filters.push({
      label: tagsTitle,
      options: tagFilterOptions
    });
  }
  return /* @__PURE__ */ jsx172(
    "div",
    {
      className: `project-overview-block ${className}`,
      ...restProps,
      children: engine && resultList && state ? /* @__PURE__ */ jsxs98(Fragment24, { children: [
        /* @__PURE__ */ jsx172(ProjectOverviewFilters_default, { engine, filters, title: componentTitle }),
        /* @__PURE__ */ jsx172(ProjectOverviewResults_default, { engine, resultList, resultListState: state, buttonText: loadMoreButtonTitle })
      ] }) : /* @__PURE__ */ jsx172(LoadingIcon_default, {})
    }
  );
};
var ProjectOverviewBlock_default = HeroWithQuickLinksBlock2;

// src/blocks/QuickLinksBlock/QuickLinksBlock.tsx
import {
  Placeholder as Placeholder18,
  Text as Text47,
  getFieldValue as getFieldValue36
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx173, jsxs as jsxs99 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT19 = "white";
var DEFAULT_SHOW_DECORATIONS26 = true;
var DEFAULT_ITEMS_PER_ROW2 = "6";
var QuickLinksBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx173(NullDatasourceMessage_default, {});
  const variant = getFieldValue36(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT19
  );
  const showDecorations = getFieldValue36(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS26
  );
  const itemsPerRow = getFieldValue36(
    fields,
    "ItemsPerRow",
    DEFAULT_ITEMS_PER_ROW2
  );
  const { isTextFieldValid, isRichTextFieldValid } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  return /* @__PURE__ */ jsxs99(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `quicklinks-block ${className}`,
      ...restProps,
      children: [
        showText && /* @__PURE__ */ jsxs99("div", { className: "container_tightest", children: [
          /* @__PURE__ */ jsx173(
            Text47,
            {
              className: "quicklinks-block__title heading-2",
              tag: "h2",
              field: fields.Title
            }
          ),
          /* @__PURE__ */ jsx173(
            RichText_default,
            {
              className: "quicklinks-block__subtitle",
              field: fields.RichTextSimple
            }
          )
        ] }),
        /* @__PURE__ */ jsx173("div", { className: "container_normal", children: /* @__PURE__ */ jsx173(QuickLinks_default, { itemsPerRow, children: /* @__PURE__ */ jsx173(Placeholder18, { name: "container-quicklinks", rendering }) }) })
      ]
    }
  );
};
var QuickLinksBlock_default = QuickLinksBlock;

// src/blocks/QuoteBlock/QuoteBlock.tsx
import { Image as Image21, getFieldValue as getFieldValue37 } from "@sitecore-jss/sitecore-jss-react";

// src/typography/Quote/Quote.tsx
import { Text as Text48 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx174, jsxs as jsxs100 } from "react/jsx-runtime";
var Quote = (props) => {
  const { quote, author, subtitle, className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs100("blockquote", { className: `quote ${className}`, ...restProps, children: [
    /* @__PURE__ */ jsx174(Text48, { tag: "h2", className: "quote__text", field: quote }),
    /* @__PURE__ */ jsxs100("div", { children: [
      /* @__PURE__ */ jsx174(Text48, { className: "quote__author", tag: "p", field: author }),
      /* @__PURE__ */ jsx174(Text48, { className: "quote__subtitle", tag: "p", field: subtitle })
    ] })
  ] });
};
var Quote_default = Quote;

// src/typography/Quote/QuoteDecoration.tsx
import { jsx as jsx175 } from "react/jsx-runtime";
var QuoteDecoration = (props) => /* @__PURE__ */ jsx175(
  "svg",
  {
    width: "241",
    height: "188",
    viewBox: "0 0 241 188",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx175(
      "path",
      {
        d: "M231.472 187.513V173.477C212.229 166.366 197.097 155.231 186.074 140.073C175.052 124.728 169.541 108.821 169.541 92.3527C169.541 88.7971 170.101 86.1772 171.222 84.4929C171.782 83.3701 172.53 82.8087 173.464 82.8087C174.398 82.8087 175.892 83.5572 177.947 85.0544C183.926 89.3585 191.399 91.5106 200.366 91.5106C211.202 91.5106 220.73 87.2064 228.95 78.5981C236.983 69.8026 241 59.4164 241 47.4396C241 34.7142 236.329 23.673 226.988 14.3161C217.647 4.77203 206.158 0 192.52 0C177.013 0 163.562 6.45627 152.166 19.3688C140.583 32.0942 134.792 49.3109 134.792 71.019C134.792 96.2826 142.545 118.926 158.051 138.95C173.557 158.974 198.031 175.161 231.472 187.513ZM96.6801 187.513V173.477C77.4375 166.366 62.305 155.231 51.2825 140.073C40.26 124.728 34.7488 108.821 34.7488 92.3527C34.7488 88.7971 35.3093 86.1772 36.4302 84.4929C36.9906 83.3701 37.7379 82.8087 38.672 82.8087C39.6062 82.8087 41.1007 83.5572 43.1558 85.0544C49.134 89.3585 56.6069 91.5106 65.5743 91.5106C76.41 91.5106 85.9379 87.2064 94.158 78.5981C102.191 69.8026 106.208 59.4164 106.208 47.4396C106.208 34.7142 101.537 23.673 92.1964 14.3161C82.8553 4.77203 71.3658 0 57.7278 0C42.2216 0 28.7705 6.45627 17.3744 19.3688C5.79146 32.0942 -1.52588e-05 49.3109 -1.52588e-05 71.019C-1.52588e-05 96.2826 7.75308 118.926 23.2593 138.95C38.7655 158.974 63.2391 175.161 96.6801 187.513Z",
        fill: "currentColor"
      }
    )
  }
);
var QuoteDecoration_default = QuoteDecoration;

// src/blocks/QuoteBlock/QuoteBlock.tsx
import { jsx as jsx176, jsxs as jsxs101 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT20 = "white";
var DEFAULT_SHOW_DECORATIONS27 = true;
var QuoteBlock = (props) => {
  const { fields, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx176(NullDatasourceMessage_default, {});
  const variant = getFieldValue37(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT20
  );
  const showDecorations = getFieldValue37(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS27
  );
  const { isImageFieldValid } = useIsFieldValid_default();
  return /* @__PURE__ */ jsx176(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `quote-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs101("div", { className: "container_tightest", children: [
        isImageFieldValid(fields.Image) ? /* @__PURE__ */ jsx176(
          Image21,
          {
            field: fields.Image,
            className: "quote-block__decoration-image",
            srcSet: [{ mw: 192 }, { mw: 416 }],
            sizes: "(max-width: 1024px) 192px, 416px"
          }
        ) : /* @__PURE__ */ jsx176(QuoteDecoration_default, { className: "quote-block__decoration" }),
        /* @__PURE__ */ jsx176(Quote_default, { quote: fields.Quote, author: fields.Author, subtitle: fields.Subtitle })
      ] })
    }
  );
};
var QuoteBlock_default = QuoteBlock;

// src/blocks/TabsBlock/TabsBlock.tsx
import React41 from "react";
import {
  getFieldValue as getFieldValue38,
  Placeholder as Placeholder19,
  Text as Text49,
  RichText as RichText3
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx177, jsxs as jsxs102 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT21 = "white";
var TabsBlock = (props) => {
  const { fields, rendering, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx177(NullDatasourceMessage_default, {});
  const variant = getFieldValue38(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT21
  );
  const showDecorations = false;
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  return /* @__PURE__ */ jsx177(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `tabs-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs102("div", { className: "container_tightest", children: [
        /* @__PURE__ */ jsx177(
          Text49,
          {
            className: "tabs-block__title heading-2",
            tag: "h2",
            field: fields.Title
          }
        ),
        /* @__PURE__ */ jsx177(
          RichText3,
          {
            className: "tabs-block__subtitle",
            field: fields.RichTextSimple
          }
        ),
        /* @__PURE__ */ jsx177(
          Placeholder19,
          {
            name: "container-tabs",
            rendering,
            render: (components, placeholderData, placeholderProps) => {
              const titles = components.map((c, index) => {
                const componentData = placeholderData[index];
                const componentFields = componentData.fields;
                const componentTitle = componentFields == null ? void 0 : componentFields.Title.value;
                return (componentTitle == null ? void 0 : componentTitle.toString()) || (index + 1).toString();
              });
              return isEditingInSitecore ? /* @__PURE__ */ jsx177("div", { className: "tabs-block__sitecore-ee-wrapper", children: components.map((component, index) => /* @__PURE__ */ jsx177(React41.Fragment, { children: component }, index)) }) : /* @__PURE__ */ jsx177(Tabs_default, { className: "tabs-block__tabs", titles, children: components });
            }
          }
        )
      ] })
    }
  );
};
var TabsBlock_default = TabsBlock;

// src/blocks/TabsItemBlock/TabsItemBlock.tsx
import { getFieldValue as getFieldValue39 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx178 } from "react/jsx-runtime";
var TabsItemBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx178(NullDatasourceMessage_default, {});
  const tabTitle = getFieldValue39(fields, "Title", null);
  return /* @__PURE__ */ jsx178("div", { className: `tabs-item-block`, "data-title": tabTitle, children: /* @__PURE__ */ jsx178(RichText_default, { className: "rich-text", field: fields.Body }) });
};
var TabsItemBlock_default = TabsItemBlock;

// src/blocks/ReviewSliderBlock/ReviewSliderBlock.tsx
import React42 from "react";
import {
  Placeholder as Placeholder20,
  Text as Text51,
  getFieldValue as getFieldValue40
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx179, jsxs as jsxs103 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT22 = "white";
var DEFAULT_SHOW_DECORATIONS28 = true;
var ReviewSliderBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx179(NullDatasourceMessage_default, {});
  const variant = getFieldValue40(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT22
  );
  const showDecorations = getFieldValue40(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS28
  );
  const isEditingInSitecore = useIsSitecorePageEditing_default();
  return /* @__PURE__ */ jsx179(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `review-slider-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs103("div", { className: "review-slider-block__container", children: [
        /* @__PURE__ */ jsx179(
          Text51,
          {
            className: "review-slider-block__title heading-2",
            tag: "h2",
            field: fields.Title
          }
        ),
        /* @__PURE__ */ jsx179(
          Placeholder20,
          {
            name: "container-reviews",
            rendering,
            render: (components) => isEditingInSitecore ? /* @__PURE__ */ jsx179("div", { className: "review-slider-block__sitecore-ee-wrapper", children: components.map((component, index) => /* @__PURE__ */ jsx179(React42.Fragment, { children: component }, index)) }) : /* @__PURE__ */ jsx179(
              Reel_default,
              {
                className: "review-slider-block__reel",
                slideAmountUnit: "viewport",
                children: components.map((component, index) => /* @__PURE__ */ jsx179("div", { className: "review-slider-block__item", children: component }, index))
              }
            )
          }
        )
      ] })
    }
  );
};
var ReviewSliderBlock_default = ReviewSliderBlock;

// src/blocks/TilesBlock/Tiles.tsx
import { jsx as jsx180 } from "react/jsx-runtime";
var Tiles = (props) => {
  const { className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsx180("div", { className: `tiles ${className}`, ...restProps, children });
};
var Tiles_default = Tiles;

// src/blocks/TilesBlock/TilesItem.tsx
import {
  Text as Text52,
  Image as Image22,
  getFieldValue as getFieldValue41
} from "@sitecore-jss/sitecore-jss-react";

// src/surfaces-and-overlays/FlipContent/FlipContent.tsx
import { useState as useState30 } from "react";
import { AnimatePresence as AnimatePresence6, motion as motion11 } from "framer-motion";
import { jsx as jsx181, jsxs as jsxs104 } from "react/jsx-runtime";
var FlipContent = (props) => {
  const { frontArea, backArea, className = "", children, ...restProps } = props;
  const [flipped, setFlipped] = useState30(false);
  const variantsTimes = [0, 0.5, 0.5, 1];
  const duration = 0.5;
  const flippedContentContainerAnimationVariants = {
    hidden: {
      rotateY: 0
    },
    show: {
      rotateY: 180,
      transition: {
        duration,
        ease: "easeInOut"
      }
    },
    exit: {
      rotateY: 0,
      transition: {
        duration,
        ease: "easeInOut"
      }
    }
  };
  const flippedContentAnimationVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: [0, 0, 1, 1],
      transition: {
        duration,
        times: variantsTimes,
        ease: "easeInOut"
      }
    },
    exit: {
      opacity: [1, 1, 0, 0],
      transition: {
        duration,
        times: variantsTimes,
        ease: "easeInOut"
      }
    }
  };
  return /* @__PURE__ */ jsx181(
    motion11.div,
    {
      variants: flippedContentContainerAnimationVariants,
      initial: "hidden",
      animate: flipped ? "show" : "exit",
      className: `flip-content ${flipped ? "flip-content_flipped" : ""} ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs104(AnimatePresence6, { mode: "popLayout", initial: false, children: [
        !flipped && /* @__PURE__ */ jsx181(
          motion11.button,
          {
            variants: flippedContentAnimationVariants,
            initial: "hidden",
            animate: "show",
            exit: "exit",
            className: "flip-content__front-area",
            onClick: () => setFlipped(true),
            children: frontArea
          },
          "frontside"
        ),
        flipped && /* @__PURE__ */ jsx181(
          motion11.button,
          {
            variants: flippedContentAnimationVariants,
            initial: { rotateY: 180, opacity: 0 },
            animate: "show",
            exit: "exit",
            className: "flip-content__back-area",
            onClick: () => setFlipped(false),
            children: backArea
          },
          "backside"
        )
      ] })
    }
  );
};
var FlipContent_default = FlipContent;

// src/blocks/TilesBlock/TilesItem.tsx
import { jsx as jsx182, jsxs as jsxs105 } from "react/jsx-runtime";
var TilesItem = (props) => {
  const { fields, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx182(NullDatasourceMessage_default, {});
  const hasBackSideContent = Boolean(
    getFieldValue41(fields, "BacksideTitle", null) || getFieldValue41(fields, "BacksideRichTextSimple", null)
  );
  const content = /* @__PURE__ */ jsxs105("div", { className: `tile ${className}`, ...restProps, children: [
    /* @__PURE__ */ jsx182(Image22, { className: "tile__image-icon", field: fields.Icon }),
    /* @__PURE__ */ jsx182(Text52, { tag: "h3", className: "heading-3 tile__title", field: fields.Title }),
    /* @__PURE__ */ jsx182(
      RichText_default,
      {
        className: "tile__text",
        field: fields.RichTextSimple
      }
    ),
    hasBackSideContent && /* @__PURE__ */ jsx182("div", { className: "tile__icon", children: /* @__PURE__ */ jsx182(ArrowRightIcon_default, {}) })
  ] });
  return hasBackSideContent ? /* @__PURE__ */ jsx182(
    FlipContent_default,
    {
      className: "tile-flip-content",
      frontArea: content,
      backArea: /* @__PURE__ */ jsxs105("div", { className: `tile ${className}`, ...restProps, children: [
        /* @__PURE__ */ jsx182(
          Text52,
          {
            tag: "h3",
            className: "heading-6 tile__title",
            field: fields.BacksideTitle
          }
        ),
        /* @__PURE__ */ jsx182(
          RichText_default,
          {
            field: fields.BacksideRichTextSimple,
            className: "rich-text tile__text"
          }
        ),
        /* @__PURE__ */ jsx182("div", { className: "tile__icon", children: /* @__PURE__ */ jsx182(ArrowLeftIcon_default, {}) })
      ] })
    }
  ) : content;
};
var TilesItem_default = TilesItem;

// src/blocks/TilesBlock/TilesBlock.tsx
import {
  Text as Text53,
  Placeholder as Placeholder21,
  getFieldValue as getFieldValue42
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx183, jsxs as jsxs106 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT23 = "white";
var DEFAULT_SHOW_DECORATIONS29 = true;
var TilesBlock = (props) => {
  const { fields, rendering, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx183(NullDatasourceMessage_default, {});
  const variant = getFieldValue42(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT23
  );
  const showDecorations = getFieldValue42(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS29
  );
  const { isTextFieldValid, isRichTextFieldValid } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  return /* @__PURE__ */ jsx183(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `tiles-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs106("div", { className: "container_normal tiles-block__grid", children: [
        showText && /* @__PURE__ */ jsxs106("div", { className: "tiles-block__text", children: [
          /* @__PURE__ */ jsx183(
            Text53,
            {
              tag: "h2",
              className: "heading-2 tiles-block__title",
              field: fields.Title
            }
          ),
          /* @__PURE__ */ jsx183(
            RichText_default,
            {
              className: "rich-text introtext tiles-block__subtitle",
              field: fields.RichTextSimple
            }
          )
        ] }),
        /* @__PURE__ */ jsx183(Tiles_default, { children: /* @__PURE__ */ jsx183(Placeholder21, { name: "container-tiles", rendering }) })
      ] })
    }
  );
};
var TilesBlock_default = TilesBlock;

// src/blocks/TwoColumnArticleOverviewBlock/TwoColumnArticleOverview.tsx
import { jsx as jsx184 } from "react/jsx-runtime";
var TwoColumnArticleOverview = (props) => {
  const { children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx184("div", { className: `two-column-article-overview ${className}`, ...restProps, children });
};
var TwoColumnArticleOverview_default = TwoColumnArticleOverview;

// src/blocks/TwoColumnArticleOverviewBlock/TwoColumnArticleOverviewBlock.tsx
import {
  Text as Text54,
  Placeholder as Placeholder22,
  getFieldValue as getFieldValue43
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx185, jsxs as jsxs107 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT24 = "white";
var DEFAULT_SHOW_DECORATIONS30 = true;
var TwoColumnArticleOverviewBlock = (props) => {
  const { fields, rendering, className = "", ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx185(NullDatasourceMessage_default, {});
  const variant = getFieldValue43(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT24
  );
  const showDecorations = getFieldValue43(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS30
  );
  return /* @__PURE__ */ jsx185(
    PageBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `two-column-article-overview-block ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsxs107("div", { className: "container_tightest", children: [
        /* @__PURE__ */ jsxs107("div", { className: "two-column-article-overview-column", children: [
          /* @__PURE__ */ jsx185(Text54, { tag: "h2", className: "two-column-article-overview-block__title heading-2", field: fields.Title }),
          /* @__PURE__ */ jsx185(TwoColumnArticleOverview_default, { className: "article-overview-block__list", children: /* @__PURE__ */ jsx185(
            Placeholder22,
            {
              name: "container-article-overview-left",
              rendering
            }
          ) }),
          /* @__PURE__ */ jsx185(
            ButtonContainer_default,
            {
              className: "article-overview-block__button-container",
              rendering,
              placeholderName: "container-buttons-left"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs107("div", { className: "two-column-article-overview-column", children: [
          /* @__PURE__ */ jsx185(Text54, { tag: "h2", className: "two-column-article-overview-block__title heading-2", field: fields.Title2 }),
          /* @__PURE__ */ jsx185(TwoColumnArticleOverview_default, { className: "article-overview-block__list", children: /* @__PURE__ */ jsx185(
            Placeholder22,
            {
              name: "container-article-overview-right",
              rendering
            }
          ) }),
          /* @__PURE__ */ jsx185(
            ButtonContainer_default,
            {
              className: "article-overview-block__button-container",
              rendering,
              placeholderName: "container-buttons-right"
            }
          )
        ] })
      ] })
    }
  );
};
var TwoColumnArticleOverviewBlock_default = TwoColumnArticleOverviewBlock;

// src/blocks/VideoBlock/VideoBlock.tsx
import { Text as Text55, getFieldValue as getFieldValue44 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx186, jsxs as jsxs108 } from "react/jsx-runtime";
var DEFAULT_BLOCK_VARIANT25 = "white";
var DEFAULT_SHOW_DECORATIONS31 = true;
var VideoBlock = (props) => {
  const { fields, className = "", children, ...restProps } = props;
  if (!fields)
    return /* @__PURE__ */ jsx186(NullDatasourceMessage_default, {});
  const variant = getFieldValue44(
    fields,
    "BlockVariant",
    DEFAULT_BLOCK_VARIANT25
  );
  const showDecorations = getFieldValue44(
    fields,
    "ShowDecorations",
    DEFAULT_SHOW_DECORATIONS31
  );
  const youtubeUrl = getFieldValue44(fields, "YouTubeUrl", null);
  const vimeoUrl = getFieldValue44(fields, "VimeoUrl", null);
  const youtubeUrlExists = youtubeUrl && youtubeUrl.length > 0;
  const vimeoUrlExists = vimeoUrl && vimeoUrl.length > 0;
  const url = youtubeUrlExists ? youtubeUrl : vimeoUrlExists ? vimeoUrl : "";
  const thumbnail = getImageUrl(fields.PreviewImage);
  const { isTextFieldValid, isRichTextFieldValid } = useIsFieldValid_default();
  const showText = isTextFieldValid(fields.Title) || isRichTextFieldValid(fields.RichTextSimple);
  return /* @__PURE__ */ jsxs108(
    MediaBlockWrapper_default,
    {
      variant,
      showDecorations,
      className: `video-block ${className}`,
      ...restProps,
      children: [
        showText && /* @__PURE__ */ jsxs108("div", { className: "container_tightest", children: [
          /* @__PURE__ */ jsx186(
            Text55,
            {
              tag: "h2",
              field: fields.Title,
              className: "video-block__title heading-2"
            }
          ),
          /* @__PURE__ */ jsx186(
            RichText_default,
            {
              field: fields.RichTextSimple,
              className: "video-block__subtitle"
            }
          )
        ] }),
        /* @__PURE__ */ jsx186("div", { className: "container_normal", children: /* @__PURE__ */ jsx186(
          VideoPlayer_default,
          {
            url,
            thumbnailUrl: thumbnail,
            className: "video-block__video-wrapper w-full aspect-video rounded-2xl"
          }
        ) })
      ]
    }
  );
};
var VideoBlock_default = VideoBlock;

// src/data-display/Table/Table.tsx
import { jsx as jsx187 } from "react/jsx-runtime";
var getTableClassName = (variant) => {
  switch (variant) {
    case "striped":
      return "table_striped";
    case "bordered":
      return "table_bordered";
    case "default":
    default:
      return "";
  }
};
var Table = (props) => {
  const { variant = "default", children, className = "", ...restProps } = props;
  return /* @__PURE__ */ jsx187("div", { className: `table-wrapper ${className}`, ...restProps, children: /* @__PURE__ */ jsx187("table", { className: `table ${getTableClassName(variant)}`, children }) });
};
var Table_default = Table;

// src/locales/translation-en.json
var translation_en_default = {
  en: "EN",
  "nl-NL": "NL",
  de: "DE",
  "de-DE": "DE",
  "fr-CA": "FR",
  "send a message": "Send a message",
  "previous page": "Previous page",
  "back button": "Back button",
  "share-page-on": "Share page on {{ platform }}",
  opens: "Opens",
  closes: "Closes",
  window: "window",
  "send-mail-to": "Send mail to",
  call: "Call",
  "link-to": "Link to",
  "contact-person": "Contact person",
  webpage: "Webpage",
  button: "button",
  "close-button": "close button",
  "send-button": "send button",
  "back-button": "Back button",
  "switch-language-to": "Switch language to",
  previous: "previous",
  next: "next",
  "go-to": "Go to",
  "go-to-previous-carousel-item": "Go to previous carousel item",
  "go-to-next-carousel-item": "Go to next carousel item",
  "go-to-left-in": "Go to left in",
  "go-to-right-in": "Go to right in",
  "language-options": "Language options",
  "drag-and-drop-file": "Drag and drop your file here or click to select a file.",
  "drop-your-file-here": "Drop your file here",
  "this-field-is-required": "This field is required",
  "only-letters-spaces-and-dashes-are-allowed": "Only letters, spaces and dashes are allowed",
  "address-is-too-long": "Address can't be longer than 255 characters",
  "this-is-not-a-valid-address": "This is not a valid address",
  "this-is-not-a-valid-dutch-phone-number": "This is not a valid dutch phone number",
  "this-is-not-a-valid-phone-number": "This is not a valid phone number",
  "this-is-not-a-valid-email": "This is not a valid email",
  "this-is-not-a-valid-postalcode": "This is not a valid postal code",
  "too-many-files": "Too many files have been uploaded",
  "files-are-too-small": "One or more files are too small",
  "files-are-too-big": "One or more files are too big",
  "filetype-does-not-match": "One or more files do not match the allowed file extensions",
  "contact-consent-not-given": "To continue, you must agree to your email address being used for further information",
  "initials-are-too-long": "Initials can't be longer than 80 characters",
  "only-letters-and-spaces-are-allowed": "Only letters and spaces are allowed",
  "dateofbirth-is-too-long": "Date of birth can't be longer than 1000 characters",
  "placeofresidence-is-too-long": "Place of residence can't be longer than 1000 characters",
  "policyandbrand-is-too-long": "Policy number(s) and brand(s) can't be longer than 1000 characters",
  "lastname-is-too-long": "Last name can't be longer than 80 characters",
  "query-summary": "Results {{ from }} - {{ to }} of {{ total }} for {{ query }}",
  "query-summary-noinput": "Results {{ from }} - {{ to }} of {{ total }}",
  "filter-show": "Show",
  "nothing-found": "Nothing found",
  searchResultType: {
    pdf: "PDF Document",
    page: "Page"
  },
  "search-bar-title": "What are you looking for?",
  search: "Search",
  "load-more": "Load more",
  "select-projects": "Select projects",
  none: "Show all",
  status: "Status",
  tags: "Tags",
  "watch-full-video": "Watch the full video",
  "pause-video": "Pause video",
  "play-video": "Play video",
  "scroll-to-content": "Scroll to content"
};

// src/locales/translation-nl-NL.json
var translation_nl_NL_default = {
  en: "EN",
  "nl-NL": "NL",
  de: "DE",
  "de-DE": "DE",
  "fr-CA": "FR",
  "send a message": "Stuur een bericht",
  "previous page": "Vorige pagina",
  "back button": "Terug knop",
  "share-page-on": "Deel pagina op {{ platform }}",
  opens: "Opent",
  closes: "Sluit",
  window: "venster",
  "send-mail-to": "Verstuur email naar",
  call: "Bel",
  "link-to": "Link naar",
  "contact-person": "Contact persoon",
  webpage: "Webpagina",
  button: "knop",
  "close-button": "sluit knop",
  "send-button": "verzend knop",
  "back-button": "Terug knop",
  "switch-language-to": "Wissel van taal naar ",
  previous: "vorige",
  next: "Volgende",
  "go-to": "Ga naar",
  "go-to-previous-carousel-item": "Ga naar vorige carousel item",
  "go-to-next-carousel-item": "Ga naar volgende carousel item",
  "go-to-left-in": "Ga naar links in",
  "go-to-right-in": "Ga naar rechts in",
  "language-options": "Taal opties",
  "drag-and-drop-file": "Sleep uw bestand hierheen of klik om een bestand te selecteren.",
  "drop-your-file-here": "Laat uw bestand hierin los",
  "this-field-is-required": "Dit veld is verplicht",
  "only-letters-spaces-and-dashes-are-allowed": "Alleen letters, spaties en streepjes zijn toegestaan",
  "address-is-too-long": "Adres mag niet langer zijn dan 255 tekens",
  "this-is-not-a-valid-address": "Dit is geen geldig adres",
  "this-is-not-a-valid-dutch-phone-number": "Dit is geen geldig Nederlands telefoonnummer",
  "this-is-not-a-valid-phone-number": "Dit is geen geldig telefoonnummer",
  "this-is-not-a-valid-email": "Dit is geen geldig e-mailadres",
  "this-is-not-a-valid-postalcode": "Dit is geen geldige postcode",
  "too-many-files": "Er zijn teveel bestanden geupload",
  "files-are-too-small": "E\xE9n of meer bestanden zijn te klein",
  "files-are-too-big": "E\xE9n of meer bestanden zijn te groot",
  "filetype-does-not-match": "E\xE9n of meer bestanden behoren niet tot de toegestaande bestandstypen",
  "contact-consent-not-given": "Om door te gaan, moet u toestemming geven om het opgegeven e-mailadres te gebruiken voor verdere informatie",
  "initials-are-too-long": "Voorletters mag niet langer zijn dan 80 tekens",
  "only-letters-and-spaces-are-allowed": "Alleen letters en spaties zijn toegestaan",
  "placeofresidence-is-too-long": "Woonplaats mag niet langer zijn dan 1000 tekens",
  "policyandbrand-is-too-long": "Polisnummer(s) en merk(en) mag niet langer zijn dan 1000 tekens",
  "lastname-is-too-long": "Achternaam mag niet langer zijn dan 80 tekens",
  "query-summary": "Resultaten {{ from }} - {{ to }} van {{ total }} voor {{ query }}",
  "query-summary-noinput": "Resultaten {{ from }} - {{ to }} van {{ total }}",
  "filter-show": "Toon",
  "nothing-found": "Niets gevonden",
  searchResultType: {
    pdf: "PDF-document",
    page: "Pagina"
  },
  "search-bar-title": "Waar ben je naar op zoek?",
  search: "Zoeken",
  "load-more": "Toon meer resultaten",
  "select-projects": "Projecten selecteren",
  none: "Toon alles",
  status: "Status",
  tags: "Speerpunten",
  "watch-full-video": "Bekijk de hele video",
  "pause-video": "Pauzeer video",
  "play-video": "Speel video af",
  "scroll-to-content": "Scroll naar inhoud"
};

// src/locales/translation-de.json
var translation_de_default = {
  en: "EN",
  "nl-NL": "NL",
  de: "DE",
  "de-DE": "DE",
  "fr-CA": "FR",
  "send a message": "Nachricht senden",
  "previous page": "Vorherige Seite",
  "back button": "Zur\xFCck-Button",
  "share-page-on": "Seite teilen auf {{ platform }}",
  opens: "\xD6ffnet",
  closes: "Schlie\xDFt",
  window: "Fenster",
  "send-mail-to": "E-Mail senden an",
  call: "Anrufen",
  "link-to": "Link zu",
  "contact-person": "Kontaktperson",
  webpage: "Webseite",
  button: "Schaltfl\xE4che",
  "close-button": "Schlie\xDFen-Button",
  "send-button": "Senden-Button",
  "back-button": "Zur\xFCck-Button",
  "switch-language-to": "Sprache wechseln zu ",
  previous: "Vorherige",
  next: "N\xE4chste",
  "go-to": "Gehe zu",
  "go-to-previous-carousel-item": "Gehe zum vorherigen Karussell-Element",
  "go-to-next-carousel-item": "Gehe zum n\xE4chsten Karussell-Element",
  "go-to-left-in": "Gehe nach links in",
  "go-to-right-in": "Gehe nach rechts in",
  "language-options": "Sprachoptionen",
  "drag-and-drop-file": "Ziehen Sie Ihre Datei hierher oder klicken Sie, um eine Datei auszuw\xE4hlen.",
  "drop-your-file-here": "Lassen Sie Ihre Datei hier ab",
  "this-field-is-required": "Dieses Feld ist erforderlich",
  "only-letters-spaces-and-dashes-are-allowed": "Nur Buchstaben, Leerzeichen und Bindestriche sind erlaubt",
  "address-is-too-long": "Adresse darf nicht l\xE4nger als 255 Zeichen sein",
  "this-is-not-a-valid-address": "Dies ist keine g\xFCltige Adresse",
  "this-is-not-a-valid-dutch-phone-number": "Dies ist keine g\xFCltige niederl\xE4ndische Telefonnummer",
  "this-is-not-a-valid-phone-number": "Dies ist keine g\xFCltige Telefonnummer",
  "this-is-not-a-valid-email": "Dies ist keine g\xFCltige E-Mail-Adresse",
  "this-is-not-a-valid-postalcode": "Dies ist keine g\xFCltige Postleitzahl",
  "too-many-files": "Es wurden zu viele Dateien hochgeladen",
  "files-are-too-small": "Eine oder mehr Dateien sind zu klein",
  "files-are-too-big": "Eine oder mehr Dateien sind zu gro\xDF",
  "filetype-does-not-match": "Eine oder mehr Dateien geh\xF6ren nicht zu den zul\xE4ssigen Dateitypen",
  "contact-consent-not-given": "Um fortzufahren, m\xFCssen Sie die Zustimmung zur Verwendung der angegebenen E-Mail-Adresse f\xFCr weitere Informationen geben",
  "initials-are-too-long": "Initialen d\xFCrfen nicht l\xE4nger als 80 Zeichen sein",
  "only-letters-and-spaces-are-allowed": "Nur Buchstaben und Leerzeichen sind erlaubt",
  "placeofresidence-is-too-long": "Wohnort darf nicht l\xE4nger als 1000 Zeichen sein",
  "policyandbrand-is-too-long": "Policenummer(n) und Marke(n) d\xFCrfen nicht l\xE4nger als 1000 Zeichen sein",
  "lastname-is-too-long": "Nachname darf nicht l\xE4nger als 80 Zeichen sein",
  "query-summary": "Ergebnisse {{ from }} - {{ to }} von {{ total }} f\xFCr {{ query }}",
  "query-summary-noinput": "Ergebnisse {{ from }} - {{ to }} von {{ total }}",
  "filter-show": "Anzeigen",
  "nothing-found": "Nichts gefunden",
  searchResultType: {
    pdf: "PDF-Dokument",
    page: "Seite"
  },
  "search-bar-title": "Wonach suchen Sie?",
  search: "Suchen",
  "load-more": "Mehr Ergebnisse anzeigen",
  "select-projects": "Projekte ausw\xE4hlen",
  none: "Alles anzeigen",
  status: "Status",
  tags: "Schlagw\xF6rter",
  "watch-full-video": "Ganzes Video ansehen",
  "pause-video": "Video anhalten",
  "play-video": "Video fortsetzen",
  "scroll-to-content": "Zum Inhalt scrollen"
};

// src/locales/translation-fr-CA.json
var translation_fr_CA_default = {
  en: "EN",
  "nl-NL": "NL",
  de: "DE",
  "de-DE": "DE",
  "fr-CA": "FR",
  "send a message": "Envoyer un message",
  "previous page": "Page pr\xE9c\xE9dente",
  "back button": "Bouton retour",
  "share-page-on": "Partager la page sur {{ platform }}",
  opens: "Ouvre",
  closes: "Ferme",
  window: "fen\xEAtre",
  "send-mail-to": "Envoyer un e-mail \xE0",
  call: "Appeler",
  "link-to": "Lien vers",
  "contact-person": "Personne de contact",
  webpage: "Page web",
  button: "bouton",
  "close-button": "bouton de fermeture",
  "send-button": "bouton d'envoi",
  "back-button": "bouton retour",
  "switch-language-to": "Passer \xE0 la langue",
  previous: "pr\xE9c\xE9dent",
  next: "suivant",
  "go-to": "Aller \xE0",
  "go-to-previous-carousel-item": "Aller \xE0 l'\xE9l\xE9ment pr\xE9c\xE9dent du carrousel",
  "go-to-next-carousel-item": "Aller \xE0 l'\xE9l\xE9ment suivant du carrousel",
  "go-to-left-in": "Aller \xE0 gauche dans",
  "go-to-right-in": "Aller \xE0 droite dans",
  "language-options": "Options de langue",
  "drag-and-drop-file": "Glissez-d\xE9posez votre fichier ici ou cliquez pour s\xE9lectionner un fichier.",
  "drop-your-file-here": "D\xE9posez votre fichier ici",
  "this-field-is-required": "Ce champ est obligatoire",
  "only-letters-spaces-and-dashes-are-allowed": "Seules les lettres, les espaces et les tirets sont autoris\xE9s",
  "address-is-too-long": "L'adresse ne peut pas d\xE9passer 255 caract\xE8res",
  "this-is-not-a-valid-address": "Ce n'est pas une adresse valide",
  "this-is-not-a-valid-dutch-phone-number": "Ce n'est pas un num\xE9ro de t\xE9l\xE9phone n\xE9erlandais valide",
  "this-is-not-a-valid-phone-number": "Ce n'est pas un num\xE9ro de t\xE9l\xE9phone valide",
  "this-is-not-a-valid-email": "Ce n'est pas un e-mail valide",
  "this-is-not-a-valid-postalcode": "Ce n'est pas un code postal valide",
  "too-many-files": "Trop de fichiers ont \xE9t\xE9 t\xE9l\xE9charg\xE9s",
  "files-are-too-small": "Un ou plusieurs fichiers sont trop petits",
  "files-are-too-big": "Un ou plusieurs fichiers sont trop volumineux",
  "filetype-does-not-match": "Un ou plusieurs fichiers ne correspondent pas aux extensions de fichier autoris\xE9es",
  "contact-consent-not-given": "Pour continuer, vous devez accepter que votre adresse e-mail soit utilis\xE9e pour des informations suppl\xE9mentaires",
  "initials-are-too-long": "Les initiales ne peuvent pas d\xE9passer 80 caract\xE8res",
  "only-letters-and-spaces-are-allowed": "Seules les lettres et les espaces sont autoris\xE9s",
  "dateofbirth-is-too-long": "La date de naissance ne peut pas d\xE9passer 1000 caract\xE8res",
  "placeofresidence-is-too-long": "Le lieu de r\xE9sidence ne peut pas d\xE9passer 1000 caract\xE8res",
  "policyandbrand-is-too-long": "Le(s) num\xE9ro(s) de police et la/les marque(s) ne peuvent pas d\xE9passer 1000 caract\xE8res",
  "lastname-is-too-long": "Le nom de famille ne peut pas d\xE9passer 80 caract\xE8res",
  "query-summary": "R\xE9sultats {{ from }} - {{ to }} sur {{ total }} pour {{ query }}",
  "query-summary-noinput": "R\xE9sultats {{ from }} - {{ to }} sur {{ total }}",
  "filter-show": "Afficher",
  "nothing-found": "Rien trouv\xE9",
  searchResultType: {
    pdf: "Document PDF",
    page: "Page"
  },
  "search-bar-title": "Que cherchez-vous ?",
  search: "Rechercher",
  "load-more": "Charger plus",
  "select-projects": "S\xE9lectionner des projets",
  none: "Tout afficher",
  status: "Statut",
  tags: "Tags",
  "watch-full-video": "Regarder la vid\xE9o compl\xE8te",
  "pause-video": "Mettre la vid\xE9o en pause",
  "play-video": "Lire la vid\xE9o",
  "scroll-to-content": "Faites d\xE9filer jusqu'au contenu"
};

// src/media/Logo/Logo.tsx
import { jsx as jsx188 } from "react/jsx-runtime";
var Logo = (props) => /* @__PURE__ */ jsx188(
  "svg",
  {
    width: "106",
    height: "36",
    viewBox: "0 0 106 36",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: /* @__PURE__ */ jsx188(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M33.271 16.775V22.7563H29.9439V18.1021C29.9439 16.3077 29.3271 15.6909 28.4112 15.6909C27.0467 15.6909 26.3551 16.6255 26.3551 18.6816V22.7376H23.0467V9.01802C23.0467 9.01802 25.4953 9.01802 25.6636 9.01802C26.0561 9.01802 26.3551 9.20493 26.3551 9.74699C26.3551 9.9339 26.3551 14.5881 26.3551 14.5881H26.3925C27.1028 13.5975 28.1495 12.9619 29.5888 12.9619C32.2243 12.9619 33.271 14.7937 33.271 16.775ZM15.7009 18.0647C15.7009 16.4012 16.8224 15.4292 18.2617 15.4292C18.8224 15.4292 19.2897 15.504 19.7196 15.6722C19.8131 15.7096 19.8878 15.747 19.9626 15.7657C19.9813 15.7657 19.9813 15.7657 20 15.7657C20.1869 15.7657 20.3364 15.6161 20.3364 15.4479V13.4479C19.6262 13.1302 18.785 12.9806 17.8692 12.9806C14.6916 12.9806 12.243 14.7937 12.243 17.99C12.243 21.1675 14.6729 22.9993 17.8505 22.9993C18.7103 22.9993 19.6822 22.8498 20.5047 22.6068V20.1021C19.9439 20.4012 19.1963 20.5694 18.4299 20.5694C17.0093 20.5694 15.7009 19.5975 15.7009 18.0647ZM49.0467 12.9619C47.6822 12.9619 46.4112 13.5227 45.6449 14.7937C45.1215 13.5414 44.0187 12.9619 42.6355 12.9619C41.2336 12.9619 40.1682 13.4666 39.0467 14.5694C39.0467 14.5694 38.8411 14.8124 38.6355 14.8124C38.5047 14.8124 38.3925 14.7563 38.2991 14.6816L36.972 13.3545C36.8785 13.261 36.7477 13.2049 36.5981 13.2049C36.2991 13.2049 36.0748 13.4479 36.0748 13.7283V22.7937H39.3832V18.7376C39.3832 16.6816 40.0935 15.747 41.271 15.747C42.2056 15.747 42.5794 16.5507 42.5794 17.6348V22.7937H45.8878V18.7376C45.8878 16.6816 46.5794 15.747 47.7757 15.747C48.7103 15.747 49.1028 16.5507 49.1028 17.6348V22.7937H52.4112V17.1489C52.4299 14.5133 51.8131 12.9619 49.0467 12.9619ZM10.0374 13.9713V22.0087C10.0374 22.289 9.81308 22.532 9.51402 22.532C9.36448 22.532 9.23364 22.4573 9.14019 22.3825L8.63551 21.8778C8.54205 21.7844 8.41121 21.7283 8.28037 21.7283C8.14953 21.7283 8.03738 21.7844 7.94392 21.8591C7.12149 22.6255 6.2243 22.9993 5.00934 22.9993C2.24299 22.9993 0 20.7563 0 17.99C0 15.2236 2.24299 12.9806 5.00934 12.9806C6.2243 12.9806 7.1028 13.3545 7.94392 14.1208C8.03738 14.1956 8.14953 14.2517 8.28037 14.2517C8.41121 14.2517 8.54205 14.1956 8.63551 14.1021L9.14019 13.5975C9.23364 13.504 9.34579 13.4479 9.51402 13.4479C9.79439 13.4666 10.0374 13.7096 10.0374 13.9713ZM7.28972 17.99C7.28972 16.7376 6.33645 15.7283 5.15888 15.7283C3.98131 15.747 3.02804 16.7563 3.02804 17.99C3.02804 19.2423 3.98131 20.2517 5.15888 20.2517C6.33645 20.2517 7.28972 19.2423 7.28972 17.99ZM76.8037 13.9713V22.0087C76.8037 22.289 76.5794 22.532 76.2804 22.532C76.1308 22.532 76 22.4573 75.9065 22.3825L75.4206 21.8778C75.3271 21.7844 75.1963 21.7283 75.0654 21.7283C74.9346 21.7283 74.8037 21.7844 74.729 21.8591C73.9065 22.6255 73.0093 22.9993 71.8131 22.9993C69.028 22.9993 66.8037 20.7563 66.8037 17.99C66.8037 15.2236 69.0467 12.9806 71.8131 12.9806C73.0093 12.9806 73.9065 13.3545 74.729 14.1208C74.8224 14.1956 74.9346 14.2517 75.0654 14.2517C75.1963 14.2517 75.3271 14.1956 75.4206 14.1021L75.9065 13.5975C76 13.504 76.1308 13.4479 76.2804 13.4479C76.5794 13.4666 76.8037 13.7096 76.8037 13.9713ZM74.0561 17.99C74.0561 16.7376 73.1028 15.7283 71.9439 15.7283C70.7664 15.7283 69.8131 16.7376 69.8131 17.99C69.8131 19.2423 70.7664 20.2517 71.9439 20.2517C73.1028 20.2517 74.0561 19.2423 74.0561 17.99ZM64.7477 18.3638V18.9806H58.1682C58.3551 20.3077 59.3645 20.8124 60.972 20.8124C61.8692 20.8124 62.8785 20.6068 63.8878 20.0834V22.4199C62.8037 22.8498 61.6075 23.018 60.3925 23.018C57.2523 23.018 54.8598 21.1862 54.8598 18.0087C54.8598 14.8498 56.9906 12.9993 59.9626 12.9993C63.4579 12.9806 64.7477 15.5227 64.7477 18.3638ZM61.6822 16.6816C61.5701 15.7657 61.028 15.0741 59.8692 15.0741C58.8224 15.0741 58.2617 15.7844 58.0935 16.7003C58.0561 16.9246 58.1869 17.0928 58.4299 17.0928H61.3458C61.5701 17.1115 61.7196 16.9619 61.6822 16.6816ZM96.1495 8.1582C93.7757 8.1582 91.514 8.92456 89.8878 10.4012C89.7009 10.5694 89.4766 10.6629 89.2149 10.6629C88.9346 10.6629 88.6916 10.5507 88.5234 10.3825L87.4206 9.2797C87.3084 9.16755 87.1402 9.09278 86.9533 9.09278C86.5981 9.09278 86.2991 9.39185 86.2991 9.74699V26.2517C86.2991 26.6255 86.5981 26.9059 86.9533 26.9059C87.1402 26.9059 87.2897 26.8311 87.4206 26.719L88.5234 25.6161C88.7103 25.4479 88.9533 25.3358 89.2149 25.3358C89.4766 25.3358 89.7009 25.4292 89.8878 25.5975C91.514 27.0928 93.757 27.8404 96.1495 27.8404C101.589 27.8404 106 23.4292 106 17.99C105.981 12.5507 101.57 8.1582 96.1495 8.1582ZM95.8318 11.8965C97.3084 11.8965 98.7103 12.3638 99.7009 13.2797C99.8131 13.3732 99.9439 13.4479 100.112 13.4479C100.28 13.4479 100.43 13.3732 100.542 13.2797L101.234 12.6068C101.308 12.532 101.402 12.4947 101.514 12.4947C101.738 12.4947 101.925 12.6816 101.925 12.9059V23.1115C101.925 23.3358 101.738 23.5227 101.514 23.5227C101.402 23.5227 101.308 23.4853 101.234 23.4105L100.542 22.7376C100.43 22.6255 100.28 22.5694 100.112 22.5694C99.9626 22.5694 99.8131 22.6255 99.7009 22.7376C98.6916 23.6535 97.2897 24.1208 95.8318 24.1208C92.4673 24.1208 89.7383 21.3918 89.7383 18.0274C89.757 14.6255 92.4673 11.8965 95.8318 11.8965ZM95.9813 20.6255C97.3458 20.6255 98.4486 19.4479 98.4486 17.99C98.4486 16.5507 97.3458 15.3545 95.9813 15.3545C94.6168 15.3545 93.514 16.532 93.514 17.99C93.514 19.4479 94.6168 20.6255 95.9813 20.6255Z",
        fill: "currentColor"
      }
    )
  }
);
var Logo_default = Logo;

// src/pages/ArticleOverviewPage/ArticleOverviewPage.tsx
import { Placeholder as Placeholder23 } from "@sitecore-jss/sitecore-jss-react";

// src/pages/Page/Page.tsx
import { useSitecoreContext as useSitecoreContext25 } from "@sitecore-jss/sitecore-jss-react";

// src/theme/usePageTheme.tsx
import { useSitecoreContext as useSitecoreContext24 } from "@sitecore-jss/sitecore-jss-react";
var usePageTheme = () => {
  var _a;
  const { sitecoreContext } = useSitecoreContext24();
  const pageFields = ((_a = sitecoreContext.route) == null ? void 0 : _a.fields) || {};
  const currentTheme = pageFields.Theme ? pageFields.Theme.value : (sitecoreContext == null ? void 0 : sitecoreContext.siteSettings) ? sitecoreContext.siteSettings.theme : "";
  return currentTheme;
};

// src/theme/ThemeProvider.tsx
import React44, { useContext as useContext9 } from "react";

// src/theme/constants.ts
var DEFAULT_THEME = "purple";

// src/theme/ThemeProvider.tsx
import { jsx as jsx189 } from "react/jsx-runtime";
var getThemeClassName = (theme) => {
  switch (theme) {
    case "purple":
      return "theme_purple";
    case "fuchsia":
      return "theme_fuchsia";
    case "light-green":
      return "theme_light-green";
    case "fuchsia-gold":
      return "theme_fuchsia_gold";
    case "blue-gold":
      return "theme_blue_gold";
    case "purple-gold":
      return "theme_purple_gold";
    case "gold-dark_green":
      return "theme_gold_dark-green";
    case "blue-dark_green":
      return "theme_blue_dark-green";
    case "chambray":
      return "theme_chambray";
    case "chambray-verdun_green":
      return "theme_chambray_verdun-green";
    case "purple-purple":
      return "theme_purple-purple";
    case "blue":
    default:
      return "theme_blue";
  }
};
var ThemeContext = React44.createContext(null);
var useTheme = () => {
  const context = useContext9(ThemeContext);
  if (!context) {
    throw new Error("Error: useTheme() requires an <ThemeProvider> parent");
  }
  return context;
};
var ThemeProvider = (props) => {
  const { theme = DEFAULT_THEME, children } = props;
  const contextValue = {
    theme
  };
  return /* @__PURE__ */ jsx189(ThemeContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx189("div", { className: `theme ${getThemeClassName(theme)}`, children: typeof children === "function" ? children(
    contextValue
  ) : children }) });
};
var ThemeProvider_default = ThemeProvider;

// src/pages/Page/Page.tsx
import { jsx as jsx190, jsxs as jsxs109 } from "react/jsx-runtime";
var Page = (props) => {
  var _a, _b;
  const {
    headerVariant = "white",
    className = "",
    children,
    ...restProps
  } = props;
  const { sitecoreContext } = useSitecoreContext25();
  const headerLogo = (_a = sitecoreContext == null ? void 0 : sitecoreContext.siteSettings) == null ? void 0 : _a.headerLogo;
  const siteUrl = (_b = sitecoreContext == null ? void 0 : sitecoreContext.currentSite) == null ? void 0 : _b.homepageUrl;
  const theme = usePageTheme();
  return /* @__PURE__ */ jsx190(ThemeProvider_default, { theme, children: /* @__PURE__ */ jsxs109(PageLayout_default, { className: `${className}`, ...restProps, children: [
    /* @__PURE__ */ jsxs109(PageLayout_default.Header, { children: [
      /* @__PURE__ */ jsx190(PreHeader_default, {}),
      /* @__PURE__ */ jsx190(
        Header_default,
        {
          variant: headerVariant,
          startArea: /* @__PURE__ */ jsx190(MainMenuButton_default, { variant: "peek" }),
          centerArea: (headerLogo == null ? void 0 : headerLogo.url) && siteUrl && /* @__PURE__ */ jsx190(
            RoutableSitecoreLink_default,
            {
              field: { href: siteUrl },
              className: "header__logo-link",
              children: /* @__PURE__ */ jsx190(
                "img",
                {
                  src: headerLogo.url,
                  alt: headerLogo.alt,
                  className: "header__logo"
                }
              )
            }
          ),
          endArea: /* @__PURE__ */ jsx190(SearchModalButton_default, {})
        }
      )
    ] }),
    /* @__PURE__ */ jsx190(PageLayout_default.Main, { children }),
    /* @__PURE__ */ jsx190(PageLayout_default.Footer, { children: /* @__PURE__ */ jsx190(Footer_default, {}) })
  ] }) });
};
var Page_default = Page;

// src/pages/ArticleOverviewPage/ArticleOverviewPage.tsx
import { jsx as jsx191, jsxs as jsxs110 } from "react/jsx-runtime";
var ArticleOverviewPage = (props) => {
  const { rendering, className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs110(Page_default, { className: `${className}`, ...restProps, children: [
    /* @__PURE__ */ jsx191(
      Placeholder23,
      {
        name: "layout-article-overview-page-hero",
        rendering
      }
    ),
    /* @__PURE__ */ jsx191(
      Placeholder23,
      {
        name: "layout-article-overview-page-content",
        rendering
      }
    )
  ] });
};
var ArticleOverviewPage_default = ArticleOverviewPage;

// src/pages/ArticlePage/ArticlePage.tsx
import { Placeholder as Placeholder24 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx192, jsxs as jsxs111 } from "react/jsx-runtime";
var ArticlePage = (props) => {
  const { rendering, className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs111(Page_default, { className, ...restProps, children: [
    /* @__PURE__ */ jsx192(Placeholder24, { name: "layout-article-page-hero", rendering }),
    /* @__PURE__ */ jsx192(Placeholder24, { name: "layout-article-page-content", rendering })
  ] });
};
var ArticlePage_default = ArticlePage;

// src/pages/DetailPage/DetailPage.tsx
import { Placeholder as Placeholder25 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx193, jsxs as jsxs112 } from "react/jsx-runtime";
var DetailPage = (props) => {
  const { rendering, className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs112(Page_default, { className, ...restProps, children: [
    /* @__PURE__ */ jsx193(Placeholder25, { name: "layout-detail-page-hero", rendering }),
    /* @__PURE__ */ jsx193(Placeholder25, { name: "layout-detail-page-content", rendering })
  ] });
};
var DetailPage_default = DetailPage;

// src/pages/InsurancesPage/InsurancesPage.tsx
import { Placeholder as Placeholder26 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx194, jsxs as jsxs113 } from "react/jsx-runtime";
var InsurancesPage = (props) => {
  const { rendering, className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs113(Page_default, { className, ...restProps, children: [
    /* @__PURE__ */ jsx194(Placeholder26, { name: "layout-insurances-page-hero", rendering }),
    /* @__PURE__ */ jsx194(
      Placeholder26,
      {
        name: "layout-insurances-page-content",
        rendering
      }
    )
  ] });
};
var InsurancesPage_default = InsurancesPage;

// src/pages/OnsiteSubsitePage/OnsiteSubsitePage.tsx
import {
  Placeholder as Placeholder27,
  useSitecoreContext as useSitecoreContext26
} from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx195, jsxs as jsxs114 } from "react/jsx-runtime";
var OnsiteSubsitePage = (props) => {
  var _a, _b;
  const { rendering, className = "", children, ...restProps } = props;
  const { sitecoreContext } = useSitecoreContext26();
  const headerLogo = (_a = sitecoreContext == null ? void 0 : sitecoreContext.siteSettings) == null ? void 0 : _a.headerLogo;
  const siteUrl = (_b = sitecoreContext == null ? void 0 : sitecoreContext.currentSite) == null ? void 0 : _b.homepageUrl;
  const { navigation = { mainMenu: [] } } = sitecoreContext;
  const { mainMenu } = navigation;
  const theme = usePageTheme();
  return /* @__PURE__ */ jsx195(ThemeProvider_default, { theme, children: /* @__PURE__ */ jsxs114(PageLayout_default, { className: `${className}`, ...restProps, children: [
    /* @__PURE__ */ jsxs114(PageLayout_default.Header, { children: [
      /* @__PURE__ */ jsx195(PreHeader_default, {}),
      /* @__PURE__ */ jsx195(
        Header_default,
        {
          variant: "white",
          startArea: null,
          centerArea: (headerLogo == null ? void 0 : headerLogo.url) && siteUrl && /* @__PURE__ */ jsx195(
            RoutableSitecoreLink_default,
            {
              field: { href: siteUrl },
              className: "header__logo-link",
              children: /* @__PURE__ */ jsx195(
                "img",
                {
                  src: headerLogo.url,
                  alt: headerLogo.alt,
                  className: "header__logo"
                }
              )
            }
          ),
          endArea: /* @__PURE__ */ jsx195(SearchModalButton_default, {})
        }
      ),
      /* @__PURE__ */ jsx195(SubMenu_default, { links: mainMenu })
    ] }),
    /* @__PURE__ */ jsxs114(PageLayout_default.Main, { children: [
      /* @__PURE__ */ jsx195(
        Placeholder27,
        {
          name: "layout-onsite-subsite-page-hero",
          rendering
        }
      ),
      /* @__PURE__ */ jsx195(
        Placeholder27,
        {
          name: "layout-onsite-subsite-page-content",
          rendering
        }
      )
    ] }),
    /* @__PURE__ */ jsx195(PageLayout_default.Footer, { children: /* @__PURE__ */ jsx195(Footer_default, {}) })
  ] }) });
};
var OnsiteSubsitePage_default = OnsiteSubsitePage;

// src/pages/OverviewPage/OverviewPage.tsx
import { Placeholder as Placeholder28 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx196, jsxs as jsxs115 } from "react/jsx-runtime";
var OverviewPage = (props) => {
  const { rendering, className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs115(Page_default, { className, ...restProps, children: [
    /* @__PURE__ */ jsx196(Placeholder28, { name: "layout-overview-page-hero", rendering }),
    /* @__PURE__ */ jsx196(Placeholder28, { name: "layout-overview-page-content", rendering })
  ] });
};
var OverviewPage_default = OverviewPage;

// src/pages/ProjectOverviewDetailsPage/ProjectOverviewDetailsPage.tsx
import { Placeholder as Placeholder29 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx197, jsxs as jsxs116 } from "react/jsx-runtime";
var ProjectOverviewDetailsPage = (props) => {
  const { rendering, className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs116(Page_default, { className, ...restProps, children: [
    /* @__PURE__ */ jsx197(Placeholder29, { name: "layout-project-page-hero", rendering }),
    /* @__PURE__ */ jsx197(Placeholder29, { name: "layout-project-page-content", rendering })
  ] });
};
var ProjectOverviewDetailsPage_default = ProjectOverviewDetailsPage;

// src/pages/ProjectOverviewPage/ProjectOverviewPage.tsx
import { Placeholder as Placeholder30 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx198, jsxs as jsxs117 } from "react/jsx-runtime";
var ProjectOverviewPage = (props) => {
  const { rendering, className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs117(Page_default, { className, ...restProps, children: [
    /* @__PURE__ */ jsx198(Placeholder30, { name: "layout-project-overview-page-hero", rendering }),
    /* @__PURE__ */ jsx198(Placeholder30, { name: "layout-project-overview-page-content", rendering })
  ] });
};
var ProjectOverviewPage_default = ProjectOverviewPage;

// src/pages/SearchPage/SearchPage.tsx
import { useState as useState31, useEffect as useEffect20 } from "react";
import {
  buildContext as buildContext2
} from "@coveo/headless";
import { useSitecoreContext as useSitecoreContext27 } from "@sitecore-jss/sitecore-jss-react";
import { useTranslation as useTranslation27 } from "react-i18next";
import { Fragment as Fragment25, jsx as jsx199, jsxs as jsxs118 } from "react/jsx-runtime";
var SearchPage = (props) => {
  var _a;
  const { rendering, className = "", children, ...restProps } = props;
  const { t } = useTranslation27("ui");
  const [engine, setEngine] = useState31();
  const [context, setContext] = useState31();
  const { sitecoreContext } = useSitecoreContext27();
  const siteSettings = sitecoreContext == null ? void 0 : sitecoreContext.siteSettings;
  const coveoOrgId = sitecoreContext == null ? void 0 : sitecoreContext.coveoOrganizationId;
  const language = sitecoreContext == null ? void 0 : sitecoreContext.language;
  const itemId = sitecoreContext == null ? void 0 : sitecoreContext.itemId;
  const siteName = (_a = sitecoreContext == null ? void 0 : sitecoreContext.site) == null ? void 0 : _a.name;
  const datasourceName = sitecoreContext == null ? void 0 : sitecoreContext.coveoDatasourceName;
  useEffect20(() => {
    if (coveoOrgId && language) {
      getCoveoEngine_default({
        coveoOrganizationId: coveoOrgId,
        language,
        itemId,
        siteName,
        datasourceName,
        queryPipeline: (siteSettings == null ? void 0 : siteSettings.searchQueryPipeline) || ""
      }).then((coveoEngine) => {
        const ctx = buildContext2(coveoEngine);
        ctx.set({ language });
        setContext(ctx);
        setEngine(coveoEngine);
        return coveoEngine;
      }).then((coveoEngine) => bindCoveoUrlManager_default(coveoEngine));
    }
  }, []);
  return /* @__PURE__ */ jsx199(Page_default, { className: `search-page ${className}`, ...restProps, children: engine ? /* @__PURE__ */ jsxs118(Fragment25, { children: [
    /* @__PURE__ */ jsx199(SearchContainer_default, { title: t("search-bar-title"), engine }),
    /* @__PURE__ */ jsx199(SearchResults_default, { engine })
  ] }) : /* @__PURE__ */ jsx199(LoadingIcon_default, {}) });
};
var SearchPage_default = SearchPage;

// src/pages/ThemeOverviewPage/ThemeOverviewPage.tsx
import { Placeholder as Placeholder31 } from "@sitecore-jss/sitecore-jss-react";
import { jsx as jsx200, jsxs as jsxs119 } from "react/jsx-runtime";
var ThemeOverviewPage = (props) => {
  const { rendering, className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsxs119(Page_default, { className, ...restProps, children: [
    /* @__PURE__ */ jsx200(
      Placeholder31,
      {
        name: "layout-theme-overview-page-hero",
        rendering
      }
    ),
    /* @__PURE__ */ jsx200(
      Placeholder31,
      {
        name: "layout-theme-overview-page-content",
        rendering
      }
    )
  ] });
};
var ThemeOverviewPage_default = ThemeOverviewPage;

// src/pages/ThemePage/ThemePage.tsx
import { Placeholder as Placeholder32 } from "@sitecore-jss/sitecore-jss-react";

// src/scrollytelling/HeaderComposition/HeaderComposition.tsx
import { useScroll, useTransform as useTransform4 } from "framer-motion";

// src/scrollytelling/HeaderComposition/ScrollLinkedHeaderLayer.tsx
import {
  motion as motion12,
  useTransform as useTransform2
} from "framer-motion";
import { jsx as jsx201 } from "react/jsx-runtime";
var useScrollLinkedHeaderTransform = (progress, keyframes) => {
  const color = useTransform2(progress, keyframes, [
    "var(--white)",
    "var(--accent-text-color-dark)"
  ]);
  const background = useTransform2(progress, keyframes, [
    "linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(79, 79, 79, 0.5) 50%, rgba(217, 217, 217, 0) 88%)",
    "linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 1) 100%)"
  ]);
  return { color, background };
};
var ScrollLinkedHeaderLayer = (props) => {
  const {
    progress,
    keyframes,
    className = "",
    children,
    style,
    ...restProps
  } = props;
  if (keyframes.length !== 2) {
    throw new Error(
      "Length of ScrollHeaderContainerLayer keyframes does not match output animation length"
    );
  }
  const { color, background } = useScrollLinkedHeaderTransform(
    progress,
    keyframes
  );
  return /* @__PURE__ */ jsx201(
    motion12.div,
    {
      className: `scroll-header-container-composition__header ${className}`,
      style: { ...style, color, background },
      ...restProps,
      children
    }
  );
};
var ScrollLinkedHeaderLayer_default = ScrollLinkedHeaderLayer;

// src/scrollytelling/HeaderComposition/ScrollLinkedLogoLayer.tsx
import { motion as motion13, useTransform as useTransform3 } from "framer-motion";
import { useSitecoreContext as useSitecoreContext28 } from "@sitecore-jss/sitecore-jss-react";
import { Fragment as Fragment26, jsx as jsx202, jsxs as jsxs120 } from "react/jsx-runtime";
var useScrollLinkedLogoTransform = (progress, keyframes) => {
  const logoOpacity = useTransform3(progress, keyframes, [0, 1]);
  const transparentLogoOpacity = useTransform3(progress, keyframes, [1, 0]);
  return { logoOpacity, transparentLogoOpacity };
};
var ScrollLinkedLogoLayer = (props) => {
  var _a, _b, _c;
  const { progress, keyframes, className = "", ...restProps } = props;
  if (keyframes.length !== 2) {
    throw new Error(
      "Length of ScrollHeaderContainerLogoLayer keyframes does not match output animation length"
    );
  }
  const { sitecoreContext } = useSitecoreContext28();
  const { logoOpacity, transparentLogoOpacity } = useScrollLinkedLogoTransform(
    progress,
    keyframes
  );
  const siteUrl = (_a = sitecoreContext == null ? void 0 : sitecoreContext.currentSite) == null ? void 0 : _a.homepageUrl;
  const headerLogo = (_b = sitecoreContext == null ? void 0 : sitecoreContext.siteSettings) == null ? void 0 : _b.headerLogo;
  const headerTransparentLogo = (_c = sitecoreContext == null ? void 0 : sitecoreContext.siteSettings) == null ? void 0 : _c.headerTransparentLogo;
  return siteUrl && (headerLogo || headerTransparentLogo) ? /* @__PURE__ */ jsxs120(
    RoutableSitecoreLink_default,
    {
      "aria-label": "Go to home page",
      field: { href: siteUrl },
      className: `scroll-header-container-composition__logo-link ${className}`,
      ...restProps,
      children: [
        (headerLogo == null ? void 0 : headerLogo.url) && /* @__PURE__ */ jsx202(
          motion13.img,
          {
            src: headerLogo.url,
            alt: headerLogo.alt,
            className: "scroll-header-container-composition__logo",
            style: { opacity: logoOpacity }
          }
        ),
        (headerTransparentLogo == null ? void 0 : headerTransparentLogo.url) && /* @__PURE__ */ jsx202(
          motion13.img,
          {
            src: headerTransparentLogo.url,
            alt: headerTransparentLogo.alt,
            className: "scroll-header-container-composition__logo",
            style: { opacity: transparentLogoOpacity }
          }
        )
      ]
    }
  ) : /* @__PURE__ */ jsx202(Fragment26, {});
};
var ScrollLinkedLogoLayer_default = ScrollLinkedLogoLayer;

// src/scrollytelling/HeaderComposition/HeaderComposition.tsx
import { jsx as jsx203 } from "react/jsx-runtime";
var HeaderComposition = (props) => {
  const {
    scrollEffectRange = [0, 1],
    keyframes = {},
    scrollStartOffset = 0,
    showInitialHint = false,
    variant = "default",
    className = "",
    ...restProps
  } = props;
  const { logo: logoKeyframes = [0, 1], header: headerKeyframes = [0, 1] } = keyframes;
  const { scrollYProgress } = useScroll({
    target: void 0,
    offset: [`${scrollStartOffset * 100}vh start`, "end end"]
  });
  const progress = useTransform4(scrollYProgress, scrollEffectRange, [0, 1]);
  return /* @__PURE__ */ jsx203(
    "div",
    {
      className: `scroll-header-container-composition ${className}`,
      ...restProps,
      children: /* @__PURE__ */ jsx203(ScrollLinkedHeaderLayer_default, { progress, keyframes: headerKeyframes, children: /* @__PURE__ */ jsx203(
        Header_default,
        {
          variant: "transparent",
          startArea: /* @__PURE__ */ jsx203(
            MainMenuButton_default,
            {
              showInitialHint,
              variant
            }
          ),
          centerArea: /* @__PURE__ */ jsx203(
            ScrollLinkedLogoLayer_default,
            {
              progress,
              keyframes: logoKeyframes
            }
          ),
          endArea: /* @__PURE__ */ jsx203(SearchModalButton_default, {})
        }
      ) })
    }
  );
};
var HeaderComposition_default = HeaderComposition;

// src/pages/ThemePage/ThemePage.tsx
import { jsx as jsx204, jsxs as jsxs121 } from "react/jsx-runtime";
var ThemePage = (props) => {
  const { rendering, className = "", children, ...restProps } = props;
  const theme = usePageTheme();
  return /* @__PURE__ */ jsx204(ThemeProvider_default, { theme, children: /* @__PURE__ */ jsxs121(PageLayout_default, { className: `${className}`, ...restProps, children: [
    /* @__PURE__ */ jsxs121(PageLayout_default.Header, { position: "fixed", children: [
      /* @__PURE__ */ jsx204(PreHeader_default, {}),
      /* @__PURE__ */ jsx204(
        HeaderComposition_default,
        {
          keyframes: {
            logo: [0, 1],
            header: [0, 1]
          },
          scrollEffectRange: [0, 0.06]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs121(PageLayout_default.Main, { children: [
      /* @__PURE__ */ jsx204(Placeholder32, { name: "layout-theme-page-hero", rendering }),
      /* @__PURE__ */ jsx204(Placeholder32, { name: "layout-theme-page-content", rendering })
    ] }),
    /* @__PURE__ */ jsx204(PageLayout_default.Footer, { children: /* @__PURE__ */ jsx204(Footer_default, {}) })
  ] }) });
};
var ThemePage_default = ThemePage;

// src/surfaces-and-overlays/Panel/Panel.tsx
import React46 from "react";
import { jsx as jsx205 } from "react/jsx-runtime";
var Panel = (props) => {
  const { className = "", children, ...restProps } = props;
  return /* @__PURE__ */ jsx205("div", { className: `panel ${className}`, ...restProps, children });
};
var PanelHeader = React46.forwardRef(
  (props, ref) => {
    const { className = "", children, ...restProps } = props;
    return /* @__PURE__ */ jsx205("div", { className: `panel__header ${className}`, ref, ...restProps, children });
  }
);
var PanelBody = React46.forwardRef(
  (props, ref) => {
    const { className = "", children, ...restProps } = props;
    return /* @__PURE__ */ jsx205("div", { className: `panel__body ${className}`, ref, ...restProps, children });
  }
);
var PanelFooter = React46.forwardRef(
  (props, ref) => {
    const { className = "", children, ...restProps } = props;
    return /* @__PURE__ */ jsx205("div", { className: `panel__footer ${className}`, ref, ...restProps, children });
  }
);
PanelHeader.displayName = "PanelHeader";
Panel.Header = PanelHeader;
PanelBody.displayName = "PanelBody";
Panel.Body = PanelBody;
PanelFooter.displayName = "PanelFooter";
Panel.Footer = PanelFooter;
var Panel_default = Panel;
export {
  Accordion_default as Accordion,
  AccordionBlock_default as AccordionBlock,
  AccordionItem_default as AccordionItem,
  AccordionProvider_default as AccordionProvider,
  AccordionWithImages_default as AccordionWithImages,
  AccordionWithImagesBlock_default as AccordionWithImagesBlock,
  AccordionWithImagesItem_default as AccordionWithImagesItem,
  AccordionWithSideImageBlock_default as AccordionWithSideImageBlock,
  ArrowDownIcon_default as ArrowDownIcon,
  ArrowDownWideIcon_default as ArrowDownWideIcon,
  ArrowLeftIcon_default as ArrowLeftIcon,
  ArrowRightIcon_default as ArrowRightIcon,
  ArticleOverview_default as ArticleOverview,
  ArticleOverviewAutomaticBlock_default as ArticleOverviewAutomaticBlock,
  ArticleOverviewBlock_default as ArticleOverviewBlock,
  ArticleOverviewItem_default as ArticleOverviewItem,
  ArticleOverviewPage_default as ArticleOverviewPage,
  ArticlePage_default as ArticlePage,
  Breadcrumbs_default as Breadcrumbs,
  Button_default as Button,
  ButtonContainer_default as ButtonContainer,
  CallToActionBlock_default as CallToActionBlock,
  Card_default as Card,
  CardSliderBlock_default as CardSliderBlock,
  Carousel_default as Carousel,
  CarouselBlock_default as CarouselBlock,
  CarouselCardItem_default as CarouselCardItem,
  CarouselImageItem_default as CarouselImageItem,
  CarouselProvider_default as CarouselProvider,
  CarouselVideoItem_default as CarouselVideoItem,
  Checkbox_default as Checkbox,
  CheckboxEmpty_default as CheckboxEmpty,
  CheckboxFull_default as CheckboxFull,
  CheckboxGroup_default as CheckboxGroup,
  CheckmarkIcon_default as CheckmarkIcon,
  ChevronDownIcon_default as ChevronDownIcon,
  ChevronLeftIcon_default as ChevronLeftIcon,
  ChevronRightIcon_default as ChevronRightIcon,
  ChevronUpIcon_default as ChevronUpIcon,
  Collapsible_default as Collapsible,
  CollapsibleProvider_default as CollapsibleProvider,
  ContactCards_default as ContactCards,
  ContactCardsBlock_default as ContactCardsBlock,
  ContactCardsItem_default as ContactCardsItem,
  CoverImage_default as CoverImage,
  DEFAULT_THEME,
  Date_default as Date,
  Decorations_default as Decorations,
  DetailPage_default as DetailPage,
  DocumentIcon_default as DocumentIcon,
  DragSlider_default as DragSlider,
  ErrorIcon_default as ErrorIcon,
  ExternalLinkIcon_default as ExternalLinkIcon,
  FacebookIcon_default as FacebookIcon,
  FileDropzone_default as FileDropzone,
  FlipContent_default as FlipContent,
  Footer_default as Footer,
  FooterLinks_default as FooterLinks,
  FooterMenu_default as FooterMenu,
  FormGroup_default as FormGroup,
  Hashtag_default as Hashtag,
  Header_default as Header,
  HeaderComposition_default as HeaderComposition,
  HeroArticleBlock_default as HeroArticleBlock,
  HeroBlock_default as HeroBlock,
  HeroLinkContainer_default as HeroLinkContainer,
  HeroLinkItem_default as HeroLinkItem,
  HeroWithCarouselBlock_default as HeroWithCarouselBlock,
  HeroWithCoverImageBlock_default as HeroWithCoverImageBlock,
  HeroWithCoverVideoBlock_default as HeroWithCoverVideoBlock,
  HeroWithEmbedBlock_default as HeroWithEmbedBlock,
  HeroWithImageBlock_default as HeroWithImageBlock,
  HeroWithLargeCardSliderBlock_default as HeroWithLargeCardSliderBlock,
  HeroWithOverviewBlock_default as HeroWithOverviewBlock,
  HeroWithQuickLinksBlock_default as HeroWithQuickLinksBlock,
  HeroWithVideoBlock_default as HeroWithVideoBlock,
  ImageBlock_default as ImageBlock,
  ImageCard_default as ImageCard,
  Input_default as Input,
  InstagramIcon_default as InstagramIcon,
  InsuranceSelectorBlock_default as InsuranceSelectorBlock,
  InsuranceSelectorItem_default as InsuranceSelectorItem,
  InsurancesPage_default as InsurancesPage,
  InteractiveMapBlock_default as InteractiveMapBlock,
  InteractiveMapMarker_default as InteractiveMapMarker,
  LanguageSwitcher_default as LanguageSwitcher,
  LargeCardSliderBlock_default as LargeCardSliderBlock,
  LeadParagraphBlock_default as LeadParagraphBlock,
  LinkList_default as LinkList,
  LinkListBlock_default as LinkListBlock,
  LinkListItem_default as LinkListItem,
  LinkedInIcon_default as LinkedInIcon,
  LoadingIcon_default as LoadingIcon,
  Logo_default as Logo,
  MagnifierIcon_default as MagnifierIcon,
  MainMenu_default as MainMenu,
  MainMenuButton_default as MainMenuButton,
  MainMenuLinkGroup_default as MainMenuLinkGroup,
  MainMenuLinkItem_default as MainMenuLinkItem,
  MainMenuLinkItemWithLargeImage_default as MainMenuLinkItemWithLargeImage,
  MainMenuLinkItemWithSmallImage_default as MainMenuLinkItemWithSmallImage,
  MediaBlockWrapper_default as MediaBlockWrapper,
  MediaObject_default as MediaObject,
  MenuIcon_default as MenuIcon,
  Modal_default as Modal,
  ModalDrawer_default as ModalDrawer,
  ModalDrawerProvider_default as ModalDrawerProvider,
  ModalDrawerSlide_default as ModalDrawerSlide,
  MultiImageBlock_default as MultiImageBlock,
  NoIndex_default as NoIndex,
  NonDraggableElement_default as NonDraggableElement,
  Notification_default as Notification,
  OnsiteSubsitePage_default as OnsiteSubsitePage,
  OverviewPage_default as OverviewPage,
  Page_default as Page,
  PageBlockWrapper_default as PageBlockWrapper,
  PageLayout_default as PageLayout,
  Panel_default as Panel,
  ParagraphBlock_default as ParagraphBlock,
  ParagraphWithImageBlock_default as ParagraphWithImageBlock,
  PauseIcon_default as PauseIcon,
  PhoneIcon_default as PhoneIcon,
  PlayIcon_default as PlayIcon,
  PreHeader_default as PreHeader,
  PreHeaderMenu_default as PreHeaderMenu,
  PreHeaderMenuItem_default as PreHeaderMenuItem,
  ProjectOverviewBlock_default as ProjectOverviewBlock,
  ProjectOverviewDetailsPage_default as ProjectOverviewDetailsPage,
  ProjectOverviewPage_default as ProjectOverviewPage,
  QuickLinks_default as QuickLinks,
  QuickLinksBlock_default as QuickLinksBlock,
  QuickLinksItem_default as QuickLinksItem,
  Quote_default as Quote,
  QuoteBlock_default as QuoteBlock,
  QuoteDecoration_default as QuoteDecoration,
  Reel_default as Reel,
  ReelProvider_default as ReelProvider,
  ResponsiveSubmenu_default as ResponsiveSubmenu,
  ResponsiveSubmenuLink_default as ResponsiveSubmenuLink,
  ReviewSliderBlock_default as ReviewSliderBlock,
  RichText_default as RichText,
  RoutableSitecoreLink_default as RoutableSitecoreLink,
  SearchBar_default as SearchBar,
  SearchContainer_default as SearchContainer,
  SearchModalButton_default as SearchModalButton,
  SearchPage_default as SearchPage,
  SearchResults_default as SearchResults,
  Select_default as Select,
  ShareButtons_default as ShareButtons,
  Slide_default as Slide,
  Socials_default as Socials,
  StepHistoryProvider_default as StepHistoryProvider,
  SubMenu_default as SubMenu,
  SubMenuLinkItem_default as SubMenuLinkItem,
  SubmenuCollapsible_default as SubmenuCollapsible,
  SubmenuDragSlider_default as SubmenuDragSlider,
  Table_default as Table,
  Tabs_default as Tabs,
  TabsBlock_default as TabsBlock,
  TabsItemBlock_default as TabsItemBlock,
  TabsProvider_default as TabsProvider,
  TextArea_default as TextArea,
  ThemeOverviewPage_default as ThemeOverviewPage,
  ThemePage_default as ThemePage,
  ThemeProvider_default as ThemeProvider,
  Tiles_default as Tiles,
  TilesBlock_default as TilesBlock,
  TilesItem_default as TilesItem,
  TimesIcon_default as TimesIcon,
  TwitterIcon_default as TwitterIcon,
  TwoColumnArticleOverview_default as TwoColumnArticleOverview,
  TwoColumnArticleOverviewBlock_default as TwoColumnArticleOverviewBlock,
  VideoBlock_default as VideoBlock,
  VideoPlayer_default as VideoPlayer,
  XIcon_default as XIcon,
  YoutubeIcon_default as YoutubeIcon,
  getAspectRatio_default as getAspectRatio,
  getFieldArrayErrors,
  getImageUrl,
  getTableClassName,
  getThemeClassName,
  hasValidFileType,
  isImageFieldValue,
  putElementInWrapper,
  translation_de_default as translationsDE,
  translation_en_default as translationsEN,
  translation_fr_CA_default as translationsFR,
  translation_nl_NL_default as translationsNL,
  useBreakpoints_default as useBreakpoints,
  useCarousel,
  useCollapsible,
  useDebouncedCallback_default as useDebouncedCallback,
  useIsClientSide_default as useIsClientSide,
  useIsFieldValid_default as useIsFieldValid,
  useIsSitecorePageEditing_default as useIsSitecorePageEditing,
  useMediaQuery_default as useMediaQuery,
  usePageTheme,
  useReel,
  useResponsiveValue_default as useResponsiveValue,
  useScreenDimensions_default as useScreenDimensions,
  useSlide,
  useSmoothScroll_default as useSmoothScroll,
  useStepHistory,
  useTabs,
  useTheme
};
